/**
 * languages.ts - Enhanced Version 0.3.1
 * Centralized language management for Lashing Calculation Tool
 * All material keys standardized to snake_case: asm, metal_metal, metal_wood, wood_wood
 * Backward compatibility via LanguageManager
 */

export interface LanguageData {
  code: string;
  name: string;
  flag: string;

  // App titles and headers
  hub?: any;
  blog?: any;
  app: {
    title: string;
    logout: string;
    theme_toggle: string;
    profile: string;
    reports: string;
    login: {
      title: string;
      subtitle: string;
      email: string;
      email_placeholder: string;
      password: string;
      password_placeholder: string;
      forgot_password: string;
      submit: string;
      no_account: string;
      signup: string;
      back_to_home: string;
      consent: string;
      surveyor_portal: string;
    };
    cog3d: {
      title: string;
      subtitle: string;
      drag_hint?: string;
    };
    lashing3d: {
      title: string;
      subtitle: string;
    };
    calculator: {
      title: string;
      cargo_details: string;
      weight: string;
      cog: {
        title: string;
        x: string;
        y: string;
        z: string;
      };
      securing_elements: string;
      wooden_dunnage: {
        label: string;
        description: string;
        details: string;
        width: string;
      };
      loop_lashing: string;
      direct_lashing: string;
      select_rope_type: string;
      stopper_forces: string;
      direction: {
        forward: string;
        backward: string;
        right: string;
        left: string;
      };
    };
    results: {
      title: string;
      lashing_diagram: string;
      error: string;
      loading: string;
      no_results: string;
      values: {
        sliding: string;
        tipping: string;
        friction: string;
        securing_element: string;
        net_sliding: string;
        net_tipping: string;
      };
    };
  };
  landing: {
    hero_badge: string;
    hero_badge_imo: string;
    hero_badge_imo_sub: string;
    hero_badge_3d: string;
    hero_badge_3d_sub: string;
    hero_title_1: string;
    hero_title_2: string;
    hero_subtitle: string;
    start_calculating: string;
    view_demo: string;
    stats: {
      modes: string;
      platform: string;
      platform_status: string;
      access: string;
    };
    features_title_1: string;
    features_title_2: string;
    features_subtitle: string;
    features: {
      calculations: {
        title: string;
        desc: string;
      };
      pdf: {
        title: string;
        desc: string;
      };
      compliance: {
        title: string;
        desc: string;
      };
      multimodal: {
        title: string;
        desc: string;
      };
      global: {
        title: string;
        desc: string;
      };
      secure: {
        title: string;
        desc: string;
      };
    };
    transport: {
      title_1: string;
      title_2: string;
      subtitle: string;
      modes: {
        road: { name: string; desc: string; };
        rail: { name: string; desc: string; };
        sea: { name: string; desc: string; };
        combined: { name: string; desc: string; };
      };
    };
    how_it_works: {
      badge: string;
      title_1: string;
      title_2: string;
      subtitle: string;
      step1: {
        title: string;
        desc: string;
      };
      step2: {
        title: string;
        desc: string;
        desc_highlight?: string;
      };
      step3: {
        title: string;
        desc: string;
      };
    };
    benefits: {
      title: string;
      subtitle: string;
      list: string[];
      cta_card_title: string;
      cta_card_desc: string;
      cta_card_trial: string;
    };
    pricing_simple: string;
    pricing_transparent: string;
    pricing_title: string;
    compare_features: string;
    footer: {
      desc: string;
      columns: {
        service: string;
        company: string;
        support: string;
      };
      links: {
        privacy: string;
        compliance: string;
        cookies: string;
        security: string;
        help: string;
        enterprise: string;
        contact: string;
        force_analysis: string;
        pdf_generator: string;
        optimizer: string;
        api_docs: string;
        cookie_settings: string;
      };
    };
    footer_copyright: string;
    footer_contact?: string;
    footer_global: string;
    footer_encrypted: string;
    footer_high_availability: string;
    contact: {
      label: string;
      title_1: string;
      title_2: string;
      description: string;
      email_us: string;
      enterprise_support: string;
      support_desc: string;
      success_title: string;
      success_desc: string;
      form: {
        name: string;
        placeholder_name: string;
        company: string;
        placeholder_company: string;
        email: string;
        placeholder_email: string;
        message: string;
        placeholder_message: string;
        send: string;
      };
    };
    demo_request: {
      title: string;
      subtitle: string;
      cta_title: string;
      cta_subtitle: string;
      form: {
        full_name: string;
        work_email: string;
        company: string;
        job_title: string;
        phone: string;
        fleet_size: string;
        industry: string;
        message: string;
        submit: string;
        success_title: string;
        success_message: string;
      };
    };
    price_inquiry: {
      title: string;
      subtitle: string;
      form: {
        full_name: string;
        work_email: string;
        company: string;
        phone: string;
        message: string;
        submit: string;
        success_title: string;
        success_message: string;
      };
    };
  };

  // Common UI elements
  labels: {
    no_results_data: string;
    pass: string;
    fail: string;
    reports: string;
    user_responsible_for: string;
    page_title: string;
    three_d_view: string;
    weight: string;
    transport_mode: string;
    transport_unit: string;
    friction_surface: string;
    select: string;
    select_transport_mode_first: string;
    cargo_dimensions: string;
    cargo_specs: string;
    width: string;
    length: string;
    height: string;
    cog_width: string;
    cog_length: string;
    cog_height: string;
    cog_width_help: string;
    cog_length_help: string;
    cog_height_help: string;
    wooden_dunnage_dimensions: string;
    wooden_dunnage_width: string;
    wooden_dunnage_length: string;
    stopper_qty: string;
    force_analysis: string;
    generate_pdf: string;
    download_pdf: string;
    wooden_dunnage: string;
    stopper: string;
    loop_lashing: string;
    max_weight: string;
    direct_lashing: string;
    loop_rope_select: string;
    loop_lashing_quantity_per_side: string;
    direct_lashing_quantity: string;
    forward: string;
    backward: string;
    right: string;
    left: string;
    lashing_diagram: string;
    language: string;
    calculate: string;
    configuration_panel: string;
    operation_mode: string;
    manual_mode: string;
    lashing_calculator: string;
    direct_lashing_selection: string;
    reset: string;
    print: string;
    configuration: string;
    use_stopper: string;
    logout: string;
    results_will_appear_here: string;
    continue_with_overweight: string;
    optimizer_mode: string;
    calculator_mode: string;
    project_description: string;
    enter_project_description_hint: string;
    cog_above: string;
    cog_below: string;
    show_3d_view: string;
    edit_in_manual_mode: string;
    breakbulk_settings: string;
    flatrack_type: string;
    orientation: string;
    longitudinal: string;
    transverse: string;
    flatrack_count: string;
    flatrack_gap: string;
  };

  common: {
    info: string;
    yes: string;
    no: string;
    ok: string;
    cancel: string;
    confirm: string;
    close: string;
    save: string;
    download: string;
    delete: string;
    edit: string;
    view: string;
    loading: string;
    processing: string;
    please_wait: string;
    save_changes: string;
    saving: string;
    cancel: string;
  };

  transport_modes: Record<
    | "road"
    | "rail"
    | "sea_a"
    | "sea_b"
    | "sea_c"
    | "road_sea_a"
    | "road_sea_b"
    | "road_sea_c"
    | "road_rail"
    | "road_rail_sea_a"
    | "road_rail_sea_b"
    | "road_rail_sea_c",
    string
  >;

  transport_unit_groups: Record<string, string>;

  transport_units: Record<string, string>;

  materials: Record<"asm" | "asm_06" | "metal_metal" | "metal_wood" | "wood_wood", string>;

  directions: Record<"forward" | "backward" | "right" | "left", string>;

  units: Record<"mt" | "kn" | "meters" | "kg" | "tonnes", string>;

  errors: {
    zero_input: string;
    no_loop_rope: string;
    no_direct_rope: string;
    calculation_error: string;
    unexpected_error: string;
    diagram_error: string;
    pdf_error: string;
    loop_config_error: string;
    error_halat: string;
    invalid_transport_unit: string;
    network_error: string;
    file_not_found: string;
    select_transport_mode: string;
    select_transport_unit: string;
    select_friction_surface: string;
    invalid_weight: string;
    invalid_dimensions: string;
    invalid_cog: string;
    invalid_wooden_dunnage: string;
    invalid_stopper: string;
    invalid_cargo_dimensions: string;
    select_at_least_one_rope: string;
    select_rope_type: string;
    dimension_exceeded: string;
    dim_width: string;
    dim_length: string;
    dim_height: string;
    cargo_width_greater_than_length: string;
    and_conjunction: string;
    breakbulk_dim_overflow_width: string;
    breakbulk_dim_overflow_length: string;
  };

  validation: {
    required_field: string;
    invalid_number: string;
    positive_number: string;
    min_value: string;
    max_value: string;
    out_of_range: string;
    invalid_email: string;
    invalid_format: string;
  };

  warnings: {
    weight_exceeded: string;
    overweight_confirmation: string;
    oversized_width_loop_disabled: string;
    oversized_width_side_wedges_disabled: string;
    high_cog: string;
    insufficient_lashing: string;
    unsaved_changes: string;
    flatrack_dunnage_required: string;
    terms_acceptance_required: string;
    flatrack_count_low: string;
  };

  success: {
    pdf_created: string;
    calculation_complete: string;
    data_saved: string;
    export_complete: string;
  };

  table_columns: {
    direction: string;
    sliding: string;
    tipping: string;
    friction: string;
    wooden_dunnage: string;
    stopper: string;
    looplash: string;
    direct_lashing: string;
    cordstrap_loop: string;
    cordstrap_direct: string;
    total_securing: string;
    net_sliding: string;
    net_tipping: string;
    net_sliding_tooltip: string;
    net_tipping_tooltip: string;
    status: string;
    safety_factor: string;
  };

  results: {
    safe: string;
    unsafe: string;
    marginal: string;
    critical: string;
    acceptable: string;
    status: string;
    safety_factor: string;
    summary: string;
    details: string;
    notes_title: string;
    notes_assumed: string;
    notes_cargo_unitized: string;
    notes_lashing_above_cog: string;
    notes_angle_range: string;
    notes_edge_protection: string;
    notes_pretension_force: string;
    notes_strength_limitations: string;
    notes_distributed_uniformly: string;
    flatrack_no_dunnage_warning: string;
    table_tab?: string;
    diagram_tab?: string;
    pdf_reports_history?: string;
    fullscreen_zoom_tip?: string;
  };

  tooltips: {
    weight_info: string;
    cog_info: string;
    friction_info: string;
    transport_mode_info: string;
    lashing_info: string;
  };

  legal: {
    tos_title: string;
    privacy_title: string;
    sales_agreement_title: string;
    legal_notice_title: string;
    cookie_policy_title: string;
    last_updated: string;
    return_home: string;
    detailed_terms?: { title: string; content: string[] }[];
    sections: {
      acceptance: { title: string; content: string };
      payment_terms: { title: string; content: string };
      termination: { title: string; content: string };
      disclaimer: { title: string; important: string; content: string };
      license: { title: string; subtitle: string; items: string[] };
      liability: {
        title: string;
        content: string;
        cap_title: string;
        cap_content: string;
        not_responsible: string;
        not_responsible_items: string[];
      };
      third_party: { title: string; content: string; sub_content: string };
      accuracy: { title: string; content: string };
      source_responsibility: {
        title: string;
        important: string;
        utilize_content: string;
        data_sources: { title: string; desc: string }[];
        user_responsible_items: string[];
      };
      verification: { title: string; content: string };
      indemnification: { title: string; content: string; items: string[] };
      governing_law: { title: string; content: string };
    };
    detailed_privacy?: {
      title: string;
      content: string[];
    }[];
    detailed_cookie_policy?: {
      title: string;
      content: string[];
    }[];
    privacy_sections: {
      introduction: { title: string; content: string };
      collection: {
        title: string;
        personal_title: string;
        personal_desc: string;
        calc_title: string;
        calc_desc: string;
      };
      usage: { title: string; intro: string; items: string[] };
      compliance: { title: string; content: string; note: string };
      retention: { title: string; content: string };
      contact: { title: string; content: string; email: string };
    };
    cookie_policy: {
      title: string;
      last_updated: string;
      intro_title: string;
      intro_content: string;
      types_title: string;
      essential_title: string;
      essential_desc: string;
      analytics_title: string;
      analytics_desc: string;
      manage_title: string;
      manage_content: string;
      contact_title: string;
      contact_text: string;
    };
    sales_agreement: {
      title: string;
      content: string;
    };
    legal_notice: {
      title: string;
      content: string;
    };
    footer_note: string;
  };

  profile: {
    title: string;
    subtitle: string;
    logo_label: string;
    logo_note: string;
    company_name: string;
    address: string;
    phone: string;
    email: string;
    tax_office: string;
    tax_number: string;
    company_email_label: string;
    user_name_label: string;
    terms_accepted: string;
    terms_not_accepted: string;
    view_terms: string;
    accept_terms: string;
    admin_note: string;
    review_accept: string;
    cancel: string;
    accept: string;
    modal_title: string;
    modal_subtitle: string;
    modal_agree: string;
    modal_ack: string;
    checkbox_label: string;
    save: string;
    saving: string;
    success_message: string;
    error_message: string;
    credits_title: string;
    calc_credits: string;
    pdf_credits: string;
    monthly_increment: string;
    next_increment: string;
    remaining: string;
    lashing_display_label: string;
    lashing_display_msl: string;
    lashing_display_cordstrap: string;
    pdf_theme_title: string;
    theme_teal_title: string;
    theme_teal_desc: string;
    theme_navy_title: string;
    theme_navy_desc: string;
    theme_standart_title: string;
    theme_standart_desc: string;
  };
  surveys: {
    title: string;
    create_new: string;
    total_ops: string;
    pending_photos: string;
    completed_assignments: string;
    current_reports: string;
    search_placeholder: string;
    no_reports_title: string;
    no_reports_desc: string;
    create_now: string;
    unnamed_project: string;
    created_date: string;
    data_status: string;
    uploaded: string;
    waiting: string;
    surveyor: string;
    waiting_response: string;
    under_review: string;
    report_ready: string;
    cancelled_rejected: string;
    send_link: string;
    upload_manually: string;
    assign_new: string;
    assign_surveyor: string;
    assigned: string;
    summary_pdf: string;
    surveyor_pdf: string;
    assignment_success: string;
    old_report_error: string;
    session_expired: string;
    pdf_error: string;
    download_failed: string;
    wait: string;
    connecting: string;
    access_error: string;
    modal: {
      title: string;
      search_placeholder: string;
      list_error: string;
      assign_failed: string;
      loading: string;
      not_found: string;
      unknown_location: string;
      assigning: string;
      assign_button: string;
    };
    guest: {
      title: string;
      subtitle: string;
      invalid_link: string;
      upload_success_title: string;
      upload_success_desc: string;
      close_window: string;
      info_title: string;
      date: string;
      company_name: string;
      company_placeholder: string;
      implementer_name: string;
      phone: string;
      email: string;
      email_placeholder: string;
      photos_title: string;
      photos_desc: string;
      take_photo: string;
      uploading: string;
      confirm_upload: string;
      min_photo_error: string;
      compression_error: string;
      categories: {
        floor: string;
        cargo: string;
        front_right: string;
        front_left: string;
        rear_right: string;
        rear_left: string;
        plate: string;
        seal: string;
        overall: string;
        other_1: string;
        other_2: string;
        other_3: string;
      };
    };
    portal: {
      title: string;
      subtitle: string;
      login_error: string;
      login_button: string;
      logging_in: string;
      dashboard: {
        connecting: string;
        pending_tab: string;
        active_tab: string;
        completed_tab: string;
        logout: string;
        connected: string;
        account_type: string;
        no_records: string;
        no_records_desc: string;
        customer: string;
        task_date: string;
        waiting_response: string;
        under_review: string;
        delivered: string;
        rejected_by_you: string;
        accept_button: string;
        reject_button: string;
        go_to_report: string;
        download_pdf: string;
        downloading_pdf: string;
        download_success: string;
        download_error: string;
        update_error: string;
        accept_success: string;
        reject_success: string;
      };
      editor: {
        details_title: string;
        project_name: string;
        weight: string;
        photos_count: string;
        success_title: string;
        success_desc: string;
        back_to_dashboard: string;
        download_pdf: string;
        creator_title: string;
        assessment_label: string;
        assessment_desc: string;
        assessment_default: string;
        auto_info_note: string;
        back_button: string;
        generate_button: string;
        generating: string;
        generate_error: string;
        not_found: string;
      };
    };
  };
};

export const LANGUAGES: Record<string, LanguageData> = {
  // ===================================================================
  // TÜRKÇE
  // ===================================================================
  "Türkçe": {
    code: "tr",
    name: "Türkçe",
    flag: "Turkey",

    hub: {
      nav: {
        products: "Ürünler",
        insights: "Pazar Analizleri",
        resources: "Kaynaklar",
        go_to_app: "Uygulamaya Git"
      },
      hero: {
        status: "SİSTEM DURUMU: OPTİMAL",
        badge: "YENİ NESİL YÜK LOJİSTİĞİ MERKEZİ",
        title_main: "HESAPLANMIŞ GÜÇ",
        title_italic: "mutlak güvenlik",
        title_1: "Maksimum Güvenlik İçin",
        title_2: "Planlama.",
        description: "LashingPlan; profesyonel kargo sabitleme araçları için, uluslararası standartlara uygun ve hassasiyetle hesaplanmış merkezi bir ekosistemdir.",
        explore: "Ürünleri Keşfet",
        launch_product: "ÜRÜNÜ BAŞLAT",
        read_blog: "Blog Yazıları",
        portal_access: "PORTAL ERİŞİMİ V2.0",
        stability_index: "STABİLİTE ENDEKSİ",
        archives: "ARŞİV",
        archives_subtitle: "Profesyonel güvenlik standartları ve lojistik trendleri ile bir adım önde olun."
      },
      footer: {
        tagline: "Teknik mükemmeliyetin ürünü. Deniz güvenliğinde mutlak hassasiyet talep edenler için tasarlandı.",
        systems: "SİSTEMLER",
        resources: "KAYNAKLAR",
        legal: "YASAL",
        privacy: "Gizlilik",
        compliance: "Uyumluluk",
        terms: "Şartlar",
        status_all: "LASHING PLAN GLOBAL // TÜM SİSTEMLER OPERASYONEL",
        follow: "Takip Edin"
      },
      products: {
        title: "Ürünlerimiz",
        subtitle: "Küresel nakliye uyumluluğu ve kargo güvenliği için tasarlanmış profesyonel araçlar.",
        context: "01 / Ürünler",
        calculator: {
          title: "Lashing Calculator",
          description: "Karayolu, Denizyolu ve Demiryolu için kargo sabitleme gereksinimlerini hesaplayan endüstri standardı motor.",
          button: "Uygulamayı Başlat"
        },
        inspection: {
          title: "Inspection Pro",
          description: "Saha sörveyleri için fotoğraf destekli dijital denetim ve anında sertifika oluşturma aracı.",
          status: "YAKINDA"
        },
        risk: {
          title: "Risk Advisor",
          description: "Yapay zeka destekli stabilite analizi ve sevkiyat risk değerlendirme sistemi.",
          status: "YAKINDA"
        },
        stats: {
          calculations: "HESAPLAMA",
          countries: "ÜLKE",
          compliance: "UYUMLULUK",
          precision: "HASSASİYET"
        },
        blog_articles: {
          article_1: {
            title: "Kargo Sabitleme: Lashing",
            description: "Kargoyu sevkiyattan önce taşıma ünitesine sabitlemek, güvenliği sağlayan lojistiğin..."
          },
          article_2: {
            title: "Yenilikçi Kargo Navlun Çözümleri",
            description: "Öncü inovasyon ve güvenilir lashing çözümlerine vurgu yapan ileri görüşlü kargo..."
          },
          article_3: {
            title: "IMO/CTU Kod Uyumluluğunu Anlamak",
            description: "Konteynerlerde kargo taşımacılığını düzenleyen uluslararası standartlara..."
          },
          article_4: {
            title: "Kargo Güvenlik Standartlarını Geliştirmek",
            description: "Modern protokoller, teknoloji entegrasyonu ve kanıtlanmış güvenlik uygulamalarıyla..."
          }
        }
      },
      insights: {
        title: "Pazar Analizleri",
        subtitle: "Profesyonel güvenlik standartları ve lojistik trendleri ile bir adım önde olun.",
        all_articles: "Tüm Yazılar"
      }
    },

    blog: {
      nav: {
        back: "Hub'a Dön"
      },
      header: {
        title_1: "Endüstri",
        title_2: "İçgörüleri.",
        subtitle: "Kargo güvenliği, uluslararası standartlar ve lojistik teknolojisindeki en son güncellemelerden haberdar olun."
      },
      article: {
        read_time: "okuma",
        share: "PAYLAŞ",
        read_more: "Yazıyı Oku",
        cta_title: "Hassas hesaplamalara mı ihtiyacınız var?",
        cta_desc: "Bu bilgileri Lashing Calculator ile pratiğe dökün.",
        cta_button: "Hesaplayıcıyı Başlat"
      },
      footer: {
        copyright: "© 2026 LashingPlan Blog. Lashing Calculator ekosisteminin bir parçasıdır."
      }
    },

    app: {
      title: "Yük Sabitleme Hesaplayıcısı",
      logout: "Çıkış Yap",
      theme_toggle: "Koyu / Açık Tema",
      profile: "Profil",
      reports: "Saha Raporları",
      login: {
        title: "Lashing Calculator",
        subtitle: "Secure Lashing Solutions",
        email: "E-posta veya Kullanıcı Adı",
        email_placeholder: "E-posta adresinizi veya kullanıcı adınızı giriniz",
        password: "Şifre",
        password_placeholder: "Şifrenizi giriniz",
        forgot_password: "Şifremi Unuttum",
        submit: "Giriş Yap",
        no_account: "Hesabınız yok mu?",
        signup: "Kayıt Ol",
        back_to_home: "Anasayfaya Dön",
        consent: "Devam ederek, {terms} ve {privacy} kabul etmiş olursunuz.",
        surveyor_portal: "Denetçi (Surveyor) misiniz? Denetçi Portal'a Geçin"
      },
      cog3d: {
        title: "3D Ağırlık Merkezi (CoG) Görünümü",
        subtitle: "Ağırlık Merkezi (CoG) kargonun denge noktasıdır ve devrilme davranışını belirler.",
        drag_hint: "Döndürmek için sürükleyin"
      },
      lashing3d: {
        title: "3D Bağlama Diyagramı",
        subtitle: "Bağlama konfigürasyonunun etkileşimli görünümü"
      },
      calculator: {
        title: "Yük Hesaplayıcı",
        cargo_details: "Yük Detayları",
        weight: "Ağırlık (MT)",
        cog: {
          title: "Ağırlık Merkezi (sol alt köşeden cm cinsinden)",
          x: "X (genişlik)",
          y: "Y (derinlik)",
          z: "Z (yükseklik)"
        },
        securing_elements: "Sabitleme Elemanları",
        wooden_dunnage: {
          label: "Ahşap Takoz Kullan",
          description: "Sürtünmeyi artırır ve kaymayı önler",
          details: "Ahşap Takoz Detayları",
          width: "Genişlik (cm)"
        },
        loop_lashing: "Dairesel Sabitleme",
        direct_lashing: "Doğrudan Bağlama",
        select_rope_type: "Halat Türü Seçin",
        stopper_forces: "Stoper Kuvvetleri (daN)",
        direction: {
          forward: "İleri",
          backward: "Geri",
          right: "Sağ",
          left: "Sol"
        }
      },
      results: {
        title: "Sonuçlar",
        lashing_diagram: "Bağlama Diyagramı",
        error: "Hata",
        loading: "Yükleniyor...",
        no_results: "Sonuç bulunamadı",
        values: {
          sliding: "Kayma:",
          tipping: "Devrilme:",
          friction: "Sürtünme:",
          securing_element: "Sabitleme Elemanı:",
          net_sliding: "Net Kayma:",
          net_tipping: "Net Devrilme:"
        }
      }
    },
    landing: {
      contact: {
        label: "Satış ile İletişime Geç",
        title_1: "Kargo operasyonlarınızı güvence altına almaya ",
        title_2: "hazır mısınız?",
        description: "Kurumsal düzeyde sörveyör algoritmalarına yükseltin. Özel API entegrasyonlarını, yüksek hacimli lisanslamayı veya premium özellikleri görüşmek için mühendislik ekibimizle iletişime geçin.",
        email_us: "Bize E-posta Gönder",
        enterprise_support: "Kurumsal Destek",
        support_desc: "7/24 Öncelikli SLA Mevcut",
        success_title: "Mesaj Gönderildi!",
        success_desc: "Mühendislik ekibimiz 24 saat içinde size ulaşacaktır.",
        form: {
          name: "Ad Soyad",
          placeholder_name: "Ahmet Yılmaz",
          company: "Şirket",
          placeholder_company: "Lojistik A.Ş.",
          email: "E-posta Adresi",
          placeholder_email: "ahmet@sirket.com",
          message: "Mesajınız",
          placeholder_message: "Lashing operasyonlarınızı ölçeklendirmenize nasıl yardımcı olabiliriz?",
          send: "İsteği Gönder"
        }
      },
      hero_badge: "Dünya Çapında Profesyoneller Tarafından Güveniliyor",
      hero_badge_imo: "IMO Uyumlu",
      hero_badge_imo_sub: "Uluslararası Standartlar",
      hero_badge_3d: "3D Görselleştirme",
      hero_badge_3d_sub: "Gerçek Zamanlı",
      hero_title_1: "Profesyonel",
      hero_title_2: "Lashing Analizi",
      hero_subtitle: "Global standartlar. Hassas algoritmalar. Profesyonel raporlar. Sektörün en gelişmiş kargo sabitleme platformu.",
      start_calculating: "Giriş Yap",
      view_demo: "Demoyu İzle",
      stats: {
        modes: "6+ Taşıma Modu",
        platform: "Bulut Platformu",
        platform_status: "Güvenli",
        access: "Global Erişim"
      },
      features_title_1: "Güçlü ve",
      features_title_2: "Uyumlu",
      features_subtitle: "Kritik sevkiyat operasyonları için kargo güvenliği uzmanları tarafından geliştirildi.",
      features: {
        calculations: {
          title: "Anlık Hesaplamalar",
          desc: "Gelişmiş algoritmamızla lashing kuvvetlerini gerçek zamanlı hesaplayın."
        },
        pdf: {
          title: "Profesyonel PDF Raporları",
          desc: "Kurumsal kimliğinizle uyumlu, detaylı analiz içeren endüstri standardı raporlar oluşturun."
        },
        compliance: {
          title: "CTU Tabanlı Değerlendirme",
          desc: "IMO / ILO / UNECE CTU Code’dan yararlanan basitleştirilmiş karar desteği."
        },
        multimodal: {
          title: "Çok Modlu Taşıma",
          desc: "Yol, demiryolu ve deniz taşımacılığı desteği. Konteyner, flatrack, gemi ve daha fazlası."
        },
        global: {
          title: "Global Standartlar",
          desc: "CTU Code 2014 ve ilgili Informative Material’dan yararlanan izlenebilir değerlendirme."
        },
        secure: {
          title: "Güvenli ve Gizli",
          desc: "Hesaplama verileriniz kurumsal düzeyde güvenlikle gizli tutulur."
        }
      },
      transport: {
        title_1: "Birleşik Taşıma",
        title_2: "Güvenliği",
        subtitle: "Tüm lojistik zincirinde kesintisiz hesaplamalar.",
        modes: {
          road: { name: "Karayolu", desc: "Tır Dorsesi ve Lowbed" },
          rail: { name: "Demiryolu", desc: "Vagon, Konteyner, Flatrack ve Tır Dorsesi" },
          sea: { name: "Denizyolu", desc: "Konteyner, Tır Dorsesi, Flatrack, Lowbed, Roll Trailer, Gemi" },
          combined: { name: "Kombine Modlar", desc: "Kara-Deniz, Kara-Tren, Kara-Deniz-Tren" }
        }
      },
      how_it_works: {
        badge: "Nasıl Çalışır",
        title_1: "3 Basit Adımda",
        title_2: "Profesyonel Analiz",
        subtitle: "Karmaşık lashing hesaplamalarını dakikalar içinde tamamlayın",
        step1: {
          title: "Yük Bilgilerini Girin",
          desc: "Kargo boyutları, ağırlık, ağırlık merkezi ve konteyner tipini sisteme girin."
        },
        step2: {
          title: "Otomatik Hesaplama",
          desc: "Sistem IMO standartlarına göre optimal lashing düzenini otomatik hesaplar",
          desc_highlight: " ve planda elle değişiklik yapılmasına imkan tanır."
        },
        step3: {
          title: "Rapor Alın",
          desc: "3D görselleştirme ve detaylı hesaplamalarla profesyonel PDF raporu oluşturun."
        }
      },
      benefits: {
        title: "Hataya Yer Olmayan Operasyonlar İçin Geliştirildi",
        subtitle: "Kritik kargo güvenliği için uzmanlar, sörveyörler ve terminal operatörleri tarafından güveniliyor.",
        list: [
          "Manuel hesaplama saatlerinden tasarruf edin",
          "Optimizasyon ile malzeme israfını azaltın",
          "Yasal uyumluluğu garanti altına alın",
          "Müşteriler için profesyonel dökümantasyon",
          "Çok dilli destek (EN, TR, ES, DE, ZH, JA)",
          "Kurumsal markalı PDF raporları"
        ],
        cta_card_title: "Lashing Planınızı Bugün Standartlaştırın",
        cta_card_desc: "Ekibiniz için kurumsal düzeyde hesaplayıcımızı kullanın ve tüm sevkiyatlarda %100 uyumluluk sağlayın.",
        cta_card_trial: "Deneme için kredi kartı gerekmez"
      },
      pricing_simple: "Basit, ",
      pricing_transparent: "Şeffaf",
      pricing_title: " Fiyatlandırma",
      compare_features: "Tüm özellikleri karşılaştır",
      footer: {
        desc: "Akıllı lashing hesaplayıcımızı kullanan dünya çapındaki profesyonellere katılın.",
        columns: {
          service: "Servis",
          company: "Şirket",
          support: "Destek"
        },
        links: {
          privacy: "Gizlilik Politikası",
          compliance: "Uyum Şartları",
          cookies: "Çerez Politikası",
          security: "Güvenlik",
          help: "Yardım Merkezi",
          enterprise: "Kurumsal",
          contact: "Uzmana Danışın",
          force_analysis: "Kuvvet Analizi",
          pdf_generator: "PDF Oluşturucu",
          optimizer: "Optimizer",
          api_docs: "API Dokümanları",
          cookie_settings: "Çerez Ayarları"
        }
      },
      footer_copyright: "2025 Lashing Calculator. JADE CONSULTANCY AND TRADING LTD tarafından sunulan bir hizmettir. Tüm hakları saklıdır.",
      footer_global: "Küresel",
      footer_encrypted: "Şifreli",
      footer_high_availability: "Yüksek Erişilebilirlik",
      demo_request: {
        title: "Demo Talep Edin",
        subtitle: "Lashing Calculator'ın kargo güvenliği operasyonlarınızı nasıl dönüştürebileceğini görün.",
        cta_title: "Harekete Geçmeye Hazır mısınız?",
        cta_subtitle: "Kargo uzmanlarımızla kişiselleştirilmiş bir demo randevusu alın ve lashing operasyonlarınızı nasıl dünya standartlarına getirebileceğimizi keşfedin.",
        form: {
          full_name: "Ad Soyad",
          work_email: "İş E-postası",
          company: "Şirket Adı",
          job_title: "Pozisyon / Unvan",
          phone: "Telefon Numarası",
          fleet_size: "Filo Büyüklüğü / Aylık Sevkiyat",
          industry: "Sektör",
          message: "Mesajınız (Opsiyonel)",
          submit: "Demo Talebi Gönder",
          success_title: "Talebiniz Alındı",
          success_message: "Demo talebiniz başarıyla iletilmiştir. Uzman ekibimiz en kısa sürede sizinle iletişime geçecektir."
        }
      },
      price_inquiry: {
        title: "Fiyat Teklifi Alın",
        subtitle: "Özel fiyatlandırma ve kurumsal çözümler için bizimle iletişime geçin.",
        form: {
          full_name: "Ad Soyad",
          work_email: "İş E-postası",
          company: "Şirket Adı",
          phone: "Telefon Numarası",
          message: "Mesajınız (Opsiyonel)",
          submit: "Bilgi Al",
          success_title: "Talebiniz Alındı",
          success_message: "Fiyat talebiniz başarıyla iletilmiştir. Satış ekibimiz size özel teklifimizle en kısa sürede dönüş yapacaktır."
        }
      }
    },

    profile: {
      title: "Kullanıcı & Şirket Profili",
      subtitle: "Kullanıcı ve Şirket bilgilerinizi buradan yönetebilirsiniz.",
      logo_label: "Şirket Logosu",
      logo_note: "Logo yalnızca yöneticiler tarafından güncellenebilir.",
      company_name: "Profilim",
      address: "Açık Adres",
      phone: "Telefon / Cep",
      email: "E-posta (Hesap)",
      tax_office: "Vergi Dairesi",
      tax_number: "Vergi No",
      company_email_label: "Şirket E-postası (PDF'te görünür)",
      user_name_label: "Kullanıcı Adı (Hesap)",
      terms_accepted: "Hizmet Şartları kabul edildi:",
      terms_not_accepted: "Hizmet Şartları Henüz Kabul Edilmedi",
      view_terms: "Şartları Görüntüle",
      accept_terms: "Şartları Kabul Et",
      review_accept: "İncele & Kabul Et",
      admin_note: "Not: Şirket detayları yöneticiler tarafından yönetilir.",
      cancel: "İptal",
      accept: "Şartları Kabul Et",
      modal_title: "Hizmet Şartları",
      modal_subtitle: "Devam etmek için lütfen şartlarımızı inceleyip kabul edin.",
      modal_agree: "Kabul ederek, {tos} ve {privacy} hükümlerine tabi olmayı kabul edersiniz.",
      modal_ack: "Yazılımın bir araç olduğunu ve lashing planları için nihai sorumluluğun sertifikalı profesyonelde olduğunu kabul edersiniz.",
      checkbox_label: "Şartlar ve Koşulları okudum ve kabul ediyorum",
      save: "Değişiklikleri Kaydet",
      saving: "Kaydediliyor...",
      success_message: "Profil başarıyla güncellendi.",
      error_message: "Güncelleme hatası: {error}",
      credits_title: "Kredi ve Kullanım Bilgileri",
      calc_credits: "Hesaplama Kredisi",
      pdf_credits: "PDF Rapor Kredisi",
      monthly_increment: "Aylık Artış",
      next_increment: "Sonraki Yükleme",
      remaining: "Kalan",
      lashing_display_label: "Bağlama Gösterim Formatı",
      lashing_display_msl: "MSL (ör. MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (ör. CL200 HDB12L)",
      pdf_theme_title: "PDF Tasarım Teması",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Koyu Turkuaz ve Arduvaz Grisi",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Kurumsal Lacivert ve Açık Gri",
      theme_standart_title: "Standart",
      theme_standart_desc: "Orijinal Uygulama Renkleri"
    },
    surveys: {
      title: "Saha Raporları",
      create_new: "Yeni Rapor Oluştur",
      total_ops: "Toplam İşlem",
      pending_photos: "Fotoğraf Bekleyen",
      completed_assignments: "Tamamlanan Atamalar",
      current_reports: "Mevcut Raporlar",
      search_placeholder: "ID veya Proje Ara...",
      no_reports_title: "Henüz rapor bulunmuyor",
      no_reports_desc: "İlk lashing hesaplamanızı yaparak saha raporu hazırlamaya başlayın.",
      create_now: "Şimdi Oluştur",
      unnamed_project: "İsimsiz Proje",
      created_date: "OLUŞTURULMA",
      data_status: "Veriler",
      uploaded: "Uygulama fotoğrafları yüklendi",
      waiting: "Uygulama fotoğrafları bekleniyor",
      surveyor: "Surveyor",
      waiting_response: "Yanıt Bekliyor",
      under_review: "İncelemede",
      report_ready: "Rapor Hazır",
      cancelled_rejected: "İptal / Ret",
      send_link: "Sahaya Yükleme Linki Gönder",
      upload_manually: "Saha fotoğraflarını manuel yükle",
      assign_new: "Yeni bir denetçi ata",
      assign_surveyor: "Denetçi Ata",
      assigned: "Atandı",
      summary_pdf: "Özet PDF",
      surveyor_pdf: "Surveyor PDF",
      assignment_success: "Denetçi ataması başarıyla gerçekleşti!",
      old_report_error: "Eski raporlar için güvenli link üretilmemiştir. Lütfen raporu yeniden oluşturun.",
      session_expired: "Oturum süresi doldu. Lütfen sayfayı yenileyin veya tekrar giriş yapın.",
      pdf_error: "PDF oluşturulurken hata oluştu.",
      download_failed: "İndirme başarısız: ",
      wait: "Bekleyin...",
      connecting: "Bağlantı Kuruluyor...",
      access_error: "Erişim Hatası",
      modal: {
        title: "Denetçi (Surveyor) Ata",
        search_placeholder: "Şehir, Liman veya İsim ile ara...",
        list_error: "Denetçi listesi alınamadı.",
        assign_failed: "Atama işlemi başarısız oldu.",
        loading: "Yükleniyor...",
        not_found: "Denetçi bulunamadı.",
        unknown_location: "Bilinmiyor",
        assigning: "Atanıyor...",
        assign_button: "Bu Denetçiyi Ata"
      },
      guest: {
        title: "Saha Uygulama Gözetim Portalı",
        subtitle: "Lashing uygulamanıza ait saha fotoğraflarını buradan yükleyebilirsiniz.",
        invalid_link: "Geçersiz veya süresi dolmuş bağlantı.",
        upload_success_title: "Yükleme Başarılı",
        upload_success_desc: "Lashing uygulamanıza ait saha fotoğrafları başarıyla kaydedildi. Raporunuz artık inceleme ve PDF onayı için hazır.",
        close_window: "Pencereyi Kapat",
        info_title: "1. Uygulayıcı Bilgileri (Opsiyonel)",
        date: "Tarih",
        company_name: "Firma Adı",
        company_placeholder: "Örn: X Lojistik",
        implementer_name: "Uygulayıcı Ad Soyad",
        phone: "Telefon",
        email: "E-posta",
        email_placeholder: "isim@firma.com",
        photos_title: "2. Saha Uygulama Görselleri (En fazla 12 adet)",
        photos_desc: "Lütfen aşağıdaki kategorilere uygun saha fotoğraflarını yükleyin veya çekin. Fotoğraf başı sınır ~5MB olup cihazınızda otomatik olarak ~1MB seviyesine sıkıştırılacaktır.",
        take_photo: "Fotoğraf Çek / Yükle",
        uploading: "Fotoğraflar Yükleniyor...",
        confirm_upload: "Tümünü Yükle ve Onayla",
        min_photo_error: "Lütfen en az bir fotoğraf ekleyin.",
        compression_error: "Fotoğraf işlenemedi, lütfen tekrar deneyin.",
        categories: {
          floor: "Taşıma Ünitesi Zemin Durumu",
          cargo: "Yük Yerleşimi ve Zemin Takozlama",
          front_right: "Lashing: Ön Sağ",
          front_left: "Lashing: Ön Sol",
          rear_right: "Lashing: Arka Sağ",
          rear_left: "Lashing: Arka Sol",
          plate: "Plaka Görseli (Varsa)",
          seal: "Mühür Numarası (Varsa)",
          overall: "Ana Genel Görünüm (Final)",
          other_1: "Diğer 1 - Opsiyonel",
          other_2: "Diğer 2 - Opsiyonel",
          other_3: "Diğer 3 - Opsiyonel"
        }
      },
      portal: {
        title: "Surveyor Portalı",
        subtitle: "Giriş yaparak size atanan denetim görevlerini yönetin.",
        login_error: "Lütfen e-posta ve şifrenizi girin.",
        login_button: "Giriş Yap",
        logging_in: "Giriş yapılıyor...",
        dashboard: {
          connecting: "Panellere bağlanılıyor...",
          pending_tab: "Gelen Talepler",
          active_tab: "Aktif Görevler",
          completed_tab: "Geçmiş İşler",
          logout: "Güvenli Çıkış",
          connected: "Connected",
          account_type: "Surveyor Hesabı",
          no_records: "Kayıt bulunamadı",
          no_records_desc: "Bu sekmede görüntülenecek aktif bir denetim görevi yok.",
          customer: "Müşteri",
          task_date: "GÖREV TARİHİ",
          waiting_response: "Yanıt Bekliyor",
          under_review: "Müşteri Onayında",
          delivered: "Rapor Teslim Edildi",
          rejected_by_you: "Görevi Reddettiniz",
          accept_button: "Görevi Kabul Et",
          reject_button: "Reddet",
          go_to_report: "Rapor Menüsüne Git",
          download_pdf: "PDF Dosyasını İndir",
          downloading_pdf: "Rapor PDF'i indiriliyor...",
          download_success: "Rapor başarıyla indirildi.",
          download_error: "Rapor indirilirken hata oluştu.",
          update_error: "Durum güncellenemedi, lütfen tekrar deneyin.",
          accept_success: "Görev başarıyla kabul edildi!",
          reject_success: "Görev reddedildi."
        },
        editor: {
          details_title: "Proje Detayları",
          project_name: "Proje Adı",
          weight: "Yük Ağırlığı",
          photos_count: "Saha Fotoğrafları",
          success_title: "Rapor Başarıyla Oluşturuldu!",
          success_desc: "Denetim raporunuz oluşturuldu, imzalandı ve güvenli bulut ortamına yüklendi. Aşağıdaki butona tıklayarak PDF dosyasını indirebilir veya inceleyebilirsiniz.",
          back_to_dashboard: "Panele Geri Dön",
          download_pdf: "PDF İndir",
          creator_title: "Final Survey Raporu Oluşturucu",
          assessment_label: "Surveyor Değerlendirmesi",
          assessment_desc: "Bu metin final PDF'teki Methodology & Conclusion bölümüne eklenecektir.",
          assessment_default: "Yüklerin denetimi uluslararası standartlara uygun olarak tamamlanmıştır. Herhangi bir hasar veya uygunsuzluk gözlemlenmemiştir.",
          auto_info_note: "Not: Sistemdeki adınız, imzanız ve firma logonuz gibi varsayılan bilgiler PDF raporuna otomatik olarak yerleştirilir.",
          back_button: "Geri",
          generate_button: "PDF Raporu Sertifikala & Gönder",
          generating: "Oluşturuluyor...",
          generate_error: "Rapor oluşturulamadı",
          not_found: "Görev bulunamadı veya yetkiniz yok."
        }
      }
    },

    labels: {
      three_d_view: "3D Görünüm",
      no_results_data: "Hesaplama sonucu bulunamadı",
      pass: "Geçti",
      fail: "Kaldı",
      reports: "Raporlar",
      user_responsible_for: "Şunlardan tek başınıza sorumlusunuz:",
      page_title: "Lashing Hesaplama Aracı",
      weight: "Ağırlık (MT)",
      transport_mode: "Taşıma Modu",
      transport_unit: "Taşıma Ünitesi",
      friction_surface: "Sürtünme Yüzeyi",
      select: "Seçiniz",
      select_transport_mode_first: "Önce taşıma modu seçiniz",
      cargo_dimensions: "",
      cargo_specs: "CARGO SPECS",
      width: "Yük Genişliği (m)",
      length: "Yük Uzunluğu (m)",
      height: "Yük Yüksekliği (m)",
      cog_width: "Ağırlık Merkezi Gen. (m)",
      cog_length: "Ağırlık Merkezi Uzun. (m)",
      cog_height: "Ağırlık Merkezi Yük. (m)",
      cog_width_help: "Ağırlık merkezi default olarak enin yarısı kabul edilir. Farklı ise değiştirin.",
      cog_length_help: "Ağırlık merkezi default olarak boyun yarısı kabul edilir. Farklı ise değiştirin.",
      cog_height_help: "Ağırlık merkezi default olarak yüksekliğin yarısı kabul edilir. Farklı ise değiştirin.",
      wooden_dunnage_dimensions: "Ahşap Takoz Boyutları ve Adet",
      wooden_dunnage_width: "Genişlik (cm):",
      wooden_dunnage_length: "Yükseklik (cm):",
      stopper_qty: "Metal Stopper (Adet)",
      force_analysis: "Kuvvet Analizi Sonuçları (kN)",
      generate_pdf: "PDF Oluştur",
      download_pdf: "PDF İndir",
      wooden_dunnage: "Ahşap Takoz",
      stopper: "Stopper",
      loop_lashing: "Dairesel Sabitleme",
      max_weight: "Maksimum Ağırlık",
      direct_lashing: "Doğrusal Sabitleme",
      loop_rope_select: "",
      loop_lashing_quantity_per_side: "Adet (Her Yön)",
      direct_lashing_quantity: "Adet",
      cog_above: "COG'un Üstünde",
      cog_below: "COG'un Altında",
      forward: "İleri",
      backward: "Geri",
      right: "Sağ",
      left: "Sol",
      lashing_diagram: "Lashing Diagramı",
      language: "Dil",
      calculate: "Hesapla",
      configuration_panel: "Konfigürasyon Paneli",
      operation_mode: "Çalışma Modu",
      manual_mode: "Hesaplayıcı",
      lashing_calculator: "Lashing Hesaplayıcı",
      direct_lashing_selection: "Doğrudan Bağlama Seçimi",
      reset: "Sıfırla",
      print: "Yazdır",
      configuration: "Konfigürasyon",
      use_stopper: "Stoper Kullan",
      logout: "Çıkış Yap",
      results_will_appear_here: "Sonuçlar burada görünecek",
      continue_with_overweight: "Aşırı ağırlık ile devam",
      optimizer_mode: "Optimize Et",
      calculator_mode: "Hesapla",
      project_description: "Proje Açıklaması (Opsiyonel)",
      enter_project_description_hint: "Lütfen PDF raporunda görünecek proje açıklamasını giriniz.",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "Hesaplayıcıda Düzenle",
      breakbulk_settings: "Breakbulk Ayarları",
      flatrack_type: "Flatrack Tipi",
      orientation: "Yük Pozisyonu",
      longitudinal: "Boylamasına",
      transverse: "Enlemesine",
      flatrack_count: "Önerilen Flatrack Sayısı",
      flatrack_gap: "Flatrackler Arası Mesafe",
    },

    common: {
      info: "Bilgi",
      yes: "Evet",
      no: "Hayır",
      ok: "Tamam",
      cancel: "İptal",
      confirm: "Onayla",
      close: "Kapat",
      save: "Kaydet",
      download: "İndir",
      delete: "Sil",
      edit: "Düzenle",
      view: "Görüntüle",
      loading: "Yükleniyor...",
      processing: "İşleniyor...",
      please_wait: "Lütfen bekleyin...",
      save_changes: "Değişiklikleri Kaydet",
      saving: "Kaydediliyor..."
    },

    transport_modes: {
      road: "Kara",
      rail: "Tren",
      sea_a: "Deniz_A (Dalga<8m)",
      sea_b: "Deniz_B (8m<Dalga<12m)",
      sea_c: "Deniz_C (Dalga>12m)",
      road_sea_a: "Kara+Deniz_A",
      road_sea_b: "Kara+Deniz_B",
      road_sea_c: "Kara+Deniz_C",
      road_rail: "Kara+Tren",
      road_rail_sea_a: "Kara+Tren+Deniz_A",
      road_rail_sea_b: "Kara+Tren+Deniz_B",
      road_rail_sea_c: "Kara+Tren+Deniz_C"
    },

    transport_unit_groups: {
      containers: "Konteynerler",
      open_tops: "Open Top",
      flatracks: "Flatrack",
      trailers: "Dorseler",
      special: "Diğer / Özel",
    },
    transport_units: {
      container_20: "20' Konteyner",
      container_40: "40' Konteyner",
      container_40_hc: "40' Konteyner HC",
      container_45_hc: "45' Konteyner HC",
      open_top_20: "20' Open Top",
      open_top_20_hc: "20' Open Top HC",
      open_top_40: "40' Open Top",
      open_top_40_hc: "40' Open Top HC",
      flatrack_20: "20' Flatrack",
      flatrack_40: "40' Flatrack",
      lowbed: "Lowbed",
      mafi: "Mafi",
      vessel: "Gemi",
      breakbulk: "Break Bulk",
      truck_trailer_standard: "Tır Dorsesi (Standart)",
      truck_trailer_mega: "Tır Dorsesi (Mega / Jumbo)",
      open_top_container: "Açık Konteyner"
    },

    materials: {
      asm: "Kaymaz Mat (Statik 0,8µ)",
      asm_06: "Kaymaz Mat (Statik 0,6µ)",
      metal_metal: "Metal-Metal",
      metal_wood: "Metal-Ahşap",
      wood_wood: "Ahşap-Ahşap"
    },

    directions: {
      forward: "İleri Hareketine Karşı",
      backward: "Geri Hareketine Karşı",
      right: "Sağ Hareketine Karşı",
      left: "Sol Hareketine Karşı"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "ton"
    },

    errors: {
      zero_input: "Lütfen tüm gerekli alanları doldurun.",
      no_loop_rope: "Uygun dairesel sabitleme halatı bulunamadı.",
      no_direct_rope: "Uygun doğrusal sabitleme halatı bulunamadı.",
      calculation_error: "Hesaplama Hatası: {error}",
      unexpected_error: "Beklenmeyen hata: {error}",
      diagram_error: "Diagram oluşturulamadı: {error}",
      pdf_error: "PDF hatası: {error}",
      loop_config_error: "Loop konfigürasyon hatası: {error}",
      error_halat: "Hiçbir doğrusal sabitleme halatı seçilmedi.",
      invalid_transport_unit: "Geçersiz taşıma ünitesi: {unit}. Lütfen geçerli bir ünite seçin.",
      network_error: "Ağ hatası. Lütfen internet bağlantınızı kontrol edin.",
      file_not_found: "Dosya bulunamadı.",
      invalid_cargo_dimensions: "UYARI: Seçilen taşıma ünitesi yük için uygun değildir",
      select_transport_mode: "Lütfen bir taşıma modu seçiniz",
      select_transport_unit: "Lütfen bir taşıma ünitesi seçiniz",
      select_friction_surface: "Lütfen bir sürtünme yüzeyi seçiniz",
      invalid_weight: "Lütfen geçerli bir ağırlık giriniz",
      invalid_dimensions: "Lütfen geçerli yük boyutları giriniz",
      invalid_cog: "Lütfen geçerli ağırlık merkezi koordinatları giriniz",
      invalid_wooden_dunnage: "Lütfen geçerli ahşap takoz boyutları giriniz",
      invalid_stopper: "Lütfen geçerli stopper adetleri giriniz",
      select_at_least_one_rope: "Lütfen halat seçimi yapınız",
      select_rope_type: "Lütfen halat türü seçiniz",
      dimension_exceeded: "UYARI: Yük {dims} ölçüsü, seçilen taşıma ünitesi sınırlarını aşmaktadır.",
      dim_width: "EN",
      dim_length: "UZUNLUK",
      dim_height: "YÜKSEKLİK",
      cargo_width_greater_than_length: "Kargo eni boyundan büyük olamaz. Değer otomatik olarak boya (length) eşitlendi.",
      and_conjunction: " ve ",
      breakbulk_dim_overflow_width: "⚠️ Yük eni ({cargo_width}m), seçilen flatrack boyundan ({flatrack_length}m) büyük veya eşittir! Hesaplama yapılamaz.",
      breakbulk_dim_overflow_length: "⚠️ Yük boyu ({cargo_length}m), seçilen flatrack boyundan ({flatrack_length}m) büyük veya eşittir! Hesaplama yapılamaz."
    },

    validation: {
      required_field: "Bu alan zorunludur",
      invalid_number: "Geçerli bir sayı girin",
      positive_number: "Pozitif bir sayı giriniz",
      min_value: "Minimum değer: {min}",
      max_value: "Maksimum değer: {max}",
      out_of_range: "Değer {min} ile {max} arasında olmalıdır",
      invalid_email: "Geçerli bir e-posta adresi girin",
      invalid_format: "Geçersiz format"
    },

    warnings: {
      weight_exceeded: "Uyarı: {unit} için maksimum taşıma kapasitesi ({max_weight} MT) aşıldı!",
      overweight_confirmation: "Girilen yük ağırlığı ({weight} MT) taşıma tipinin maksimum ağırlığını ({max_weight} MT) aşıyor. Hesaplamaya devam etmek için lütfen tekrar onaylayın.",
      oversized_width_loop_disabled: "Yük eni taşıma ünitesini aştığından Loop lashing kullanılamaz.",
      oversized_width_side_wedges_disabled: "Geniş yük nedeniyle yan (sağ/sol) takozlar devre dışı bırakıldı.",
      high_cog: "Uyarı: Yüksek ağırlık merkezi devrilme riskini artırır.",
      insufficient_lashing: "Uyarı: Yetersiz sabitleme tespit edildi.",
      unsaved_changes: "Kaydedilmemiş değişiklikler var. Devam etmek istiyor musunuz?",
      flatrack_dunnage_required: "Flatrack taşıma ünitelerinde yükün ileri – geri yönlerde kaymasını kısıtlamak için ahşap takoz kullanımı önerilmektedir. Ahşap takoz eklemeden hesaplamaya devam etmek istiyor musunuz?",
      terms_acceptance_required: "Hesaplama yapabilmek için Hizmet Koşulları'nı okuyup kabul etmeniz gerekmektedir. Hizmet Koşulları'na Profil sayfanızdan ulaşabilirsiniz.",
      flatrack_count_low: "Dikkat: Seçilen Flatrack adedi ({selected}) önerilen düzenden ({recommended}) daha az!"
    },
    success: {
      pdf_created: "PDF başarıyla oluşturuldu!",
      calculation_complete: "Hesaplama tamamlandı!",
      data_saved: "Veriler kaydedildi.",
      export_complete: "Dışa aktarma tamamlandı."
    },

    table_columns: {
      direction: "Yön",
      sliding: "Kayma",
      tipping: "Devrilme",
      friction: "Sürtünme",
      wooden_dunnage: "Ahşap Takoz",
      stopper: "Stopper",
      looplash: "Dairesel\nSabitleme",
      direct_lashing: "Doğrusal Sabitleme",
      cordstrap_loop: "Cordstrap Döngü",
      cordstrap_direct: "Cordstrap Direkt",
      total_securing: "Toplam Sabitleme",
      net_sliding: "Net Kayma",
      net_tipping: "Net Devrilme",
      net_sliding_tooltip: "Negatif değerler güvenli sabitlemeyi gösterir.",
      net_tipping_tooltip: "Negatif değerler güvenli sabitlemeyi gösterir.",
      status: "Durum",
      safety_factor: "Güvenlik Faktörü"
    },

    results: {
      safe: "GÜVENLİ",
      unsafe: "GÜVENSİZ",
      marginal: "MARJİNAL",
      critical: "KRİTİK",
      acceptable: "KABUL EDİLEBİLİR",
      status: "Durum",
      safety_factor: "Güvenlik Faktörü",
      summary: "Özet",
      details: "Detaylar",
      notes_title: "Notlar / Varsayımlar",
      notes_assumed: "",
      notes_cargo_unitized: "Yük, tek parça olarak veya tek bir yük birimi hâlinde konsolide edilmiş ve birimleştirilmiş kabul edilir.",
      notes_lashing_above_cog: "“Malzeme Kullanımı” tablosunda aksi belirtilmedikçe, direkt bağlamaların yükün ağırlık merkezinin üzerinden uygulanmış olduğu varsayılır.",
      notes_angle_range: "Taşıma ünitesi üzerindeki bağlama noktalarına açılı gelen direkt bağlar ile direkt bağların çalışma kuvveti yönündeki iz düşümü arasındaki dar açılar 30° ile 60° arasında olmalıdır.",
      notes_edge_protection: "Yüke ve/veya sabitleme malzemelerine zarar gelmesini önlemek amacıyla, gerekli durumlarda uygun kenar ve köşe koruyucular kullanılmalıdır.",
      notes_pretension_force: "Sabitleme halatları gerilirken uygulanan ön germe kuvveti, bağ sisteminin MSL değerinin %50’sini aşmamalıdır.",
      notes_strength_limitations: "Yük üzerindeki sabitleme mapalarının mukavemet kısıtı olmadığı varsayılmıştır.",
      notes_distributed_uniformly: "Yükün ağırlığının taşıma ünitesinin kapasite kriterleri dikkate alınmak suretiyle homojen olarak dağıtıldığı varsayılmıştır.",
      flatrack_no_dunnage_warning: "Not: Flatrack taşıma ünitelerinde yükün ileri - geri yönlerde kaymasını kısıtlamak için ahşap takoz kullanımı önerilir",
      table_tab: "Kuvvet Tablosu",
      diagram_tab: "Bağlama Çizimi & 3D",
      pdf_reports_history: "PDF Raporlarım",
      fullscreen_zoom_tip: "Ekranı yatay çevirerek daha geniş inceleyebilirsiniz."
    },

    tooltips: {
      weight_info: "Toplam yük ağırlığını metrik ton cinsinden girin",
      cog_info: "COG, Ağırlık Merkezi (Center of Gravity) anlamına gelir. Varsayılan olarak kargonun geometrik merkezinde bulunduğu kabul edilir. Ancak gerçekte ağırlık merkezi farklı bir noktada olabilir. Eğer ağırlık merkezi biliniyorsa, doğru koordinatlar buradan girilmelidir. COG'un doğru tanımlanması, özellikle devrilme (tipping) hesaplamaları için kritik öneme sahiptir.",
      friction_info: "Sürtünme katsayısı için malzeme kombinasyonunu seçin",
      transport_mode_info: "Taşıma moduna göre ivmelenme katsayıları değişir",
      lashing_info: "Sabitleme kuvvetleri kayma ve devrilmeyi önler"
    },
    legal: {
      tos_title: "Hizmet Koşulları",
      privacy_title: "Gizlilik Politikası",
      sales_agreement_title: "Satış Sözleşmesi",
      legal_notice_title: "Yasal Uyarı",
      cookie_policy_title: "Çerez Politikası",
      last_updated: "Yürürlük Tarihi: 4 Şubat 2026",
      return_home: "Anasayfaya Dön",
      sections: {
        acceptance: {
          title: "Sözleşmenin Kurulması",
          content: "Bu Hizmet Koşulları, Lashing Calculator (\"Platform\") kullanımı için yasal olarak bağlayıcı bir sözleşmedir. Platform'a erişerek veya kullanarak, bu şartlara bağlı kalmayı geri dönülemez şekilde kabul etmiş sayılırsınız."
        },
        payment_terms: {
          title: "Ödeme Koşulları ve Abonelik",
          content: "Hizmet abonelik bazında faturalandırılır. Ödemeler iade edilmez. Fiyatları önceden bildirimde bulunarak değiştirme hakkımız saklıdır."
        },
        termination: {
          title: "Fesih ve Sonlandırma",
          content: "Aboneliğinizi istediğiniz zaman iptal edebilirsiniz. Erişim, mevcut faturalandırma döngüsünün sonuna kadar devam edecektir."
        },
        disclaimer: {
          title: "Önemli Güvenlik Bildirimi ve Profesyonel Sorumluluk Reddi",
          important: "KRİTİK UYARI: Sağlanan hesaplama araçları yalnızca kargo sabitleme planlaması için bir 'Karar Destek Sistemi' olarak tasarlanmıştır.",
          content: "Platform profesyonel mühendislik tavsiyesi sağlamaz. Tüm sonuçlar, uygulamadan önce IMO/ILO/UNECE CTU Kodu ve IMO CSS Kodu tarafından tanımlandığı şekilde yetkili bir kargo sörveyörü, lashing uzmanı veya 'Yetkin Kişi' tarafından incelenmeli ve onaylanmalıdır. Uluslararası deniz hukukuna göre nihai sorumluluk Taşıyıcı ve Kaptan'a aittir."
        },
        license: {
          title: "Lisans ve Fikri Mülkiyet",
          subtitle: "Platform'un kaynak kodları, algoritması ve tasarımı 5846 sayılı Fikir ve Sanat Eserleri Kanunu kapsamında korunmaktadır:",
          items: [
            "Kullanıcıya yalnızca Platform'u kullanma lisansı verilmiş olup, mülkiyet devredilmez.",
            "Doğru kargo, taşıma ünitesi ve bağlama malzemesi verileri sağlamak kullanıcının sorumluluğundadır.",
            "Servis'in kaynak kodunu tersine mühendislik yöntemleriyle çözmeye çalışamazsınız.",
            "Oluşturulan raporlar tanımlanan kargoya özeldir ve farklı konfigürasyonlar için kullanılamaz."
          ]
        },
        liability: {
          title: "Sorumluluğun Sınırlandırılması",
          content: "Lashing Calculator veya tedarikçileri, Servis'in kullanılmasından veya kullanılamamasından kaynaklanan hiçbir zarardan (veri veya kar kaybı dahil) hiçbir durumda sorumlu tutulamaz.",
          cap_title: "Parasal Sorumluluk Sınırı",
          cap_content: "Yasaların izin verdiği azami ölçüde, toplam kümülatif sorumluluğumuz, hak talebinden önceki on iki (12) ay boyunca ödenen tutarı veya Yüz ABD Dolarını ($100.00) aşamaz.",
          not_responsible: "Aşağıdakilerden sorumlu değiliz:",
          not_responsible_items: [
            "Fiziksel uygulamanın (bağlamanın) hatalı yapılmasından doğan hasarlar",
            "Kullanıcı tarafından girilen hatalı veri parametreleri",
            "Hesaplama dışındaki aşırı ortam ve hava koşulları",
            "Taşıma ünitesinin veya bağlama noktalarının yapısal kusurları",
            "Üretici verilerindeki (MSL vb.) yanlışlıklar",
            "Her türlü dolaylı, arızi veya cezai zararlar",
            "İtibar veya iş fırsatı kaybı"
          ]
        },
        third_party: {
          title: "Üçüncü Taraf Veri Kaynakları",
          content: "Bu Platform, üreticiler ve uluslararası standartlardan gelen üçüncü taraf verilerini içerir.",
          sub_content: "Bu verilerin doğruluğu konusunda garanti vermiyoruz. Üretici spesifikasyonları ilgili üreticiler tarafından sağlanmaktadır."
        },
        accuracy: {
          title: "Teknik Doğruluk",
          content: "Doğruluk için çaba göstersek de, materyaller teknik veya tipografik hatalar içerebilir. Materyallerin her zaman güncel olduğunu garanti etmiyoruz."
        },
        source_responsibility: {
          title: "Veri Kaynakları ve Kullanıcı Sorumluluğu",
          important: "Tüm hesaplamaların doğruluğu tamamen sağladığınız parametrelere bağlıdır.",
          utilize_content: "Servis aşağıdaki veri kaynaklarını temel alır:",
          data_sources: [
            { title: "MSL Değerleri", desc: "Üretici spesifikasyonlarından türetilmiştir. Bağımsız doğrulama yapılmaz." },
            { title: "Sürtünme Katsayıları", desc: "Endüstri standartlarına dayanır. Gerçek koşullara göre değişebilir." },
            { title: "Bağlama Noktaları", desc: "Ünite spesifikasyonlarına dayanır. Fiziksel durum kullanıcı sorumluluğundadır." },
            { title: "Uluslararası Kodlar", desc: "CTU ve CSS Code standartları baz alınmıştır." }
          ],
          user_responsible_items: [
            "Giriş parametrelerinin (ağırlık, boyut, CoG) fiziksel olarak doğrulanması",
            "Ekipman özelliklerinin gerçek malzemelerle eşleştiğinin kontrolü",
            "Sürtünme katsayısının uygulama alanına uygunluğunun teyidi",
            "Bağlama noktası bütünlüğünün fiziksel kontrolü",
            "Uygulama öncesi kalifiye bir uzman onayı alınması"
          ]
        },
        verification: {
          title: "Profesyonel Onay Gerekliliği",
          content: "Tüm sonuçlar, uygulama öncesinde kalifiye bir kargo sörveyörü veya lashing uzmanı tarafından onaylanmalıdır. Platform mülkiyetin devri söz konusu olmadan sadece hesaplama desteği sağlar."
        },
        indemnification: {
          title: "Tazminat Yükümlülüğü",
          content: "Aşağıdaki durumlardan kaynaklanan tüm üçüncü taraf hak taleplerine karşı Platformu tazmin etmeyi kabul edersiniz:",
          items: [
            "Hizmet Koşulları'nın ihlal edilmesi",
            "Yanlış veya eksik veri girişi",
            "Eski veya geçersiz ekipman verisi kullanımı",
            "Uzman onayı alınmadan lashing yapılması",
            "Fiziksel uygulama hataları",
            "Platform çıktılarına dayanan kargo hasar talepleri"
          ]
        },
        governing_law: {
          title: "Geçerli Kanun ve Yetki",
          content: "Bu şartlar uluslararası deniz hukuku ve şirketin kayıtlı olduğu yargı bölgesi yasalarına tabidir. İhtilaf halinde ilgili bölge mahkemeleri yetkilidir."
        }
      },
      detailed_terms: [
        {
          title: "1. Sözleşmenin Kurulması",
          content: [
            "Bu Hizmet Koşulları ('Koşullar'), siz ('Kullanıcı', 'Müşteri') ve JADE CONSULTANCY AND TRADING LTD ('Sağlayıcı', 'biz') arasında Lashing Calculator web uygulaması platformuna erişimi ve kullanımını düzenleyen yasal olarak bağlayıcı bir anlaşmadır.",
            "Platforma erişerek, kullanarak veya Hizmet için bir abonelik ya da satış sözleşmesi imzalayarak, bu Koşullara bağlı kalmayı gayrikabili rücu kabul etmiş olursunuz.",
            "Bir Satış Sözleşmesinin kabulü, bu Koşulların otomatik olarak kabulü anlamına gelir."
          ]
        },
        {
          title: "2. Sadece Ticari Kullanım",
          content: [
            "Platform kesinlikle işletmeler arası (B2B) bazda sağlanmaktadır.",
            "Herhangi bir tüketici koruma mevzuatı kapsamında tüketici olarak değil, ticari faaliyetleriniz kapsamında hareket ettiğinizi onaylarsınız.",
            "Platform, deniz, kara ve demiryolu taşımacılık modları ile bunların multimodal kombinasyonları dahil olmak üzere kargo sabitleme ve bağlama hesaplamalarına yardımcı olmak için tasarlanmıştır. Herhangi bir Platform kullanımı için geçerli mevzuat çerçevesi, ilgili taşımacılık moduna ve yükleme, transit ile boşaltma yetki alanlarına bağlıdır. Kullanıcılar tüm geçerli taşıma modu-spesifik mevzuatı belirlemek ve bunlara uymaktan yalnızca kendileri sorumludur."
          ]
        },
        {
          title: "3. Abonelik, Ücreler ve Ödeme",
          content: [
            "3.1 Abonelik Süresi: Abonelik süresi aktivasyon tarihinden itibaren bir (1) yıldır. Otomatik yenileme uygulanmaz. Yenileme, Kullanıcı'dan yazılı talep ve yeni abonelik dönemi için tam ödeme gerektirir.",
            "3.2 Ücretler ve Para Birimi: Tüm ücretler GBP (£) cinsinden faturalandırılır ve ödenir. Ödeme, Sağlayıcı'nın GBP banka hesabına yapılmalıdır. Ücretler peşin ödenir.",
            "3.3 Bitiş Bildirimleri: Platform, bitiş tarihinden bir (1) ay, bir (1) hafta ve bir (1) gün önce hatırlatma bildirimleri gönderebilir. Bu bildirimler yalnızca kolaylık sağlamak amaçlıdır ve yenileme anlamına gelmez."
          ]
        },
        {
          title: "4. İptal ve Fesih",
          content: [
            "4.1 Kullanıcı Tarafından Fesih: Kullanıcı istediği zaman iptal edebilir. Erişim, mevcut abonelik süresinin sonuna kadar devam edecektir. Aşağıda açıkça belirtilen durumlar dışında geri ödeme yapılmaz.",
            "4.2 Sağlayıcı Tarafından Fesih: Sağlayıcı, meşru ticari, yasal, teknik veya güvenlik nedenleriyle Hizmeti herhangi bir zamanda feshedebilir veya askıya alabilir; ancak makul ölçüde mümkün olduğu durumlarda Sağlayıcı önceden bildirimde bulunacaktır. Fesih Sağlayıcı tarafından başlatılırsa, Kullanıcı abonelik süresinin kullanılmayan kısmı için gün bazlı geri ödeme alma hakkına sahip olacaktır."
          ]
        },
        {
          title: "5. Hizmet Kesintisi ve İadeler",
          content: [
            "Platform, Kullanıcı'dan kaynaklanmayan teknik nedenlerle beş (5) günden fazla kesintisiz bir süre boyunca esaslı şekilde kullanılamaz hale gelirse, Kullanıcı kullanılmayan günler için yıllık abonelik ücreti üzerinden gün bazında hesaplanan kısmi iadeye hak kazanır.",
            "Geçici kesintiler, performans düşüklükleri, bakım pencereleri veya uyumluluk sorunları kullanılamazlık teşkil etmez."
          ]
        },
        {
          title: "6. Kullanıcı Hesapları ve Lisanslama",
          content: [
            "6.1 Lisans Verilmesi: Sağlayıcı, Kullanıcıya Platformu yalnızca dahili iş amaçları için kullanmak üzere sınırlı, münhasır olmayan, devredilemez bir lisans verir. Mülkiyet hakları devredilmez.",
            "6.2 Tek Cihaz Politikası: Her kullanıcı hesabı, geçerli bir Satış Sözleşmesinde yazılı olarak aksi kararlarştırılmadıkça, aynı anda tek bir cihazda kullanılabilir. Eşzamanlı oturum açmak yasaktır. Sağlayıcı, kötüye kullanımı tespit etmek için kullanım modellerini izleme hakkını saklı tutar. Kötüye kullanımın makul şüphe durumunda, Sağlayıcı inceleme süresince erişimi askıya alabilir. Onaylanan ihlaller, geri ödeme yapılmaksızın fesihle sonuçlanabilir.",
            "6.3 Kullanıcı Yükümlülükleri: Rapor oluşturmak için Kullanıcı şunları sağlamalıdır: Şirket adı, Şirket logosu, Cep telefonu, Sabit hat, E-posta adresi. Faturalama için geçerli şirket fatura bilgileri sağlanmalıdır."
          ]
        },
        {
          title: "7. Önemli Güvenlik Bildirimi & Profesyonel Sorumluluk Reddi",
          content: [
            "KRİTİK BİLDİRİM: Platform, deniz, kara ve demiryolu taşımacılık modları ve bunların multimodal kombinasyonları dahil olmak üzere yalnızca kargo sabitleme planlaması için bir Karar Destek Sistemidir.",
            "Platform profesyonel mühendislik, hukuk veya güvenlik tavsiyesi sağlamaz.",
            "Platform herhangi bir bayrak devleti, klas kuruluşu, liman otoritesi, kara taşımacılığı otoritesi, demiryolu altyapı yöneticisi veya düzenleyici kurum tarafından sertifikalandırılmamış, onaylanmamış veya tasdik edilmemiştir.",
            "Tüm hesaplama sonuçları, söz konusu taşımacılık modu için geçerli standartta tanımlandığı üzere nitelikli bir kargo profesyoneli veya 'Yetkin Kişi' tarafından incelenip onaylanmalıdır: IMO/ILO/UNECE CTU Kodu ve IMO CSS Kodu (deniz); EN 12195 / VDI 2700 ve geçerli ulusal kara taşımacılığı rehberi (kara); UIC Yükleme Kılavuzları (demiryolu).",
            "Kargo sabitleme için nihai sorumluluk söz konusu taşımacılık modu için geçerli çerçeve kapsamındaki sorumlu tarafa aittir: deniz için geçerli uluslararası deniz hukuku kapsamında taşıyıcı, gemi sahibi ve/veya kaptan; kara için CMR Sözleşmesi kapsamında taşıyıcı, araç operatörü ve/veya sürücü; demiryolu için CIM/KOTIF çerçevesi kapsamında taşıyıcı ve demiryolu işletmecisi. Multimodal taşımacılıkta her mod taşıyıcısı kendi segmentinden sorumludur.",
            "Sağlayıcı, Platform çıktılarına dayanarak alınan kararlar için hiçbir sorumluluk kabul etmez.",
            "Deniz taşımacılığı için: Platform çıktıları, SOLAS Bölüm VI Düzenleme 5-2 kapsamındaki yasal kargo belgeleme yükümlülüklerini yerine getirmez. P&I uyumluluğu, ISM Kodu yükümlülükleri ve IACS klas gereksinimleri Kullanıcı'nın sorumluluğundadır.",
            "Kara taşımacılığı için: Platform çıktıları EN 12195 serisi ve VDI 2700 ile doğrulanmalıdır. İngiltere'deki operatörler DfT Yük Sabitleme: Uygulama Kodu'na da uymak zorundadır.",
            "Demiryolu taşımacılığı için: Platform çıktıları geçerli UIC Yükleme Kılavuzları, TSI gereksinimleri ve ulusal demiryolu altyapı yöneticisi gereksinimleri ile doğrulanmalıdır.",
            "Bireysel liman, kara ve demiryolu otoriteleri ek gereksinimler uygulayabilir. Tüm yerel mevzuata uyum Kullanıcı'nın sorumluluğundadır.",
            "Tehlikeli Madde Bildirimi: Platform yalnızca genel kargo sabitleme için tasarlanmıştır. ADR (kara), IMDG Kodu (deniz), RID (demiryolu) kapsamındaki tehlikeli maddeler için tasarlanmamıştır. Tehlikeli madde taşıyan Kullanıcılar operasyonel kullanımdan önce yetkili bir Tehlikeli Madde Güvenlik Danışmanı'ndan (DGSA) uzman görüşü almalıdır."
          ]
        },
        {
          title: "8. Fikri Mülkiyet",
          content: [
            "Platform, kaynak kodu, algoritmaları, hesaplamaları, yapısı ve tasarımı dahil olmak üzere fikri mülkiyet yasalarıyla korunmaktadır.",
            "Tersine mühendislik, çıkarma veya çoğaltma kesinlikle yasaktır.",
            "PDF raporları yalnızca açıklanan belirli kargo konfigürasyonu için geçerlidir ve yeniden kullanılamaz.",
            "Doğru veri girişi tamamen Kullanıcı'nın sorumluluğundadır."
          ]
        },
        {
          title: "9. Veri Kaynakları, Raporlar ve Kullanıcı Sorumluluğu",
          content: [
            "Hesaplama doğruluğu tamamen Kullanıcı tarafından sağlanan parametrelere bağlıdır.",
            "Veri kaynakları şunları içerir: MSL değerleri (Üretici spesifikasyonları, bağımsız doğrulama yok), Sürtünme katsayıları (Endüstri standartları, gerçek koşullar değişebilir), Bağlama noktaları (Ünite spesifikasyonları, fiziksel bütünlük Kullanıcı sorumluluğundadır).",
            "Taşıma moduna göre geçerli mevzuat çerçeveleri: CTU Kodu (IMO/ILO/UNECE — multimodal, tüm modlar için geçerli), CSS Kodu (IMO Kargo Sabitleme Kılavuzu — yalnızca deniz taşımacılığı), VDI 2700 / EN 12195-1'den EN 12195-4'e (kara taşımacılığı, Avrupa standardı), UK DfT Yük Sabitleme: Uygulama Kodu (kara taşımacılığı, İngiltere), UIC Yükleme Kılavuzları / TSI (demiryolu taşımacılığı), CIM/KOTIF Tekdüze Kurallar (uluslararası demiryolu sözleşmeleri). Kullanıcılar kendi taşıma modlarına ve yetki alanlarına uygun standardı belirlemek ve uygulamaktan yalnızca kendileri sorumludur.",
            "Kullanıcı şunlardan tek başına sorumludur: Tüm giriş verilerini doğrulamak, Doğru ekipman seçimini sağlamak, Gerçek dünya uygulanabilirliğini teyit etmek, Bağlama düzenlemelerini fiziksel olarak doğrulamak, Uygulama öncesinde yetkili profesyonellerle çalışmak.",
            "Raporlar oluşturulma anındaki girdilere dayalı olarak üretilir. Sağlayıcı'nın hesaplama verilerini veya raporları saklama yükümlülüğü yoktur ve arşivleme sorumluluğu Kullanıcı'ya aittir."
          ]
        },
        {
          title: "10. Teknik Doğruluk Sorumluluk Reddi",
          content: [
            "Platform, SaaS hizmetleri için genel kabul görmüş endüstri standartlarıyla uyumlu makul beceri ve özen kullanılarak sağlanmaktadır. Doğruluk için makul çabalar gösterilse de, Platform teknik veya tipografik hatalar içerebilir. Materyallerin eksiksiz, güncel veya hatasız olduğuna dair hiçbir garanti verilmemektedir.",
            "Üçüncü taraf verileri ve standartları zamanla değişebilir. Güncellemeler, önceden oluşturulmuş raporlara geriye dönük olarak uygulanmaz."
          ]
        },
        {
          title: "11. Sorumluluğun Sınırlandırılması",
          content: [
            "İngiliz yasalarının izin verdiği azami ölçüde: Sağlayıcı, Platform'un kullanımından veya Platform'a güvenilmesinden kaynaklanan hiçbir kayıp veya hasardan sorumlu olmayacaktır, bunlar şunları içerir: Kargo kaybı veya hasarı, Gemi hasarı, Araç hasarı, Demiryolu araç/vagon hasarı, Kişisel yaralanma veya ölüm (yasaların izin verdiği azami ölçüde), İş kesintisi, Kar, itibar veya fırsat kaybı, Dolaylı, arızi veya cezai zararlar.",
            "Geçerli yasaların izin verdiği azami ölçüde, Sağlayıcı, ihmal, yanlış beyan veya yasal görev ihlalinden kaynaklanan tüm sorumluluğu, yasalarca yasaklanan durumlar haricinde reddeder.",
            "Sorumluluk Sınırı: İmzalı bir Satış Sözleşmesinde yazılı olarak aksi kararlaştırılmadıkça, Sağlayıcı'nın toplam kümülatif sorumluluğu, talep tarihinden önceki on iki (12) ay içinde Kullanıcı tarafından ödenen ücretleri aşamaz.",
            "Yasalarca yasaklandığı durumlarda, ihmalden kaynaklanan dolandırıcılık veya ölüm sorumluluğu hariç tutulmaz."
          ]
        },
        {
          title: "12. Tazminat",
          content: [
            "Kullanıcı, aşağıdaki nedenlerden kaynaklanan tüm üçüncü taraf taleplerine karşı Sağlayıcı'yı tazmin etmeyi ve zarar görmesini engellemeyi kabul eder: Bu Koşulların İhlali, Hatalı veya eksik giriş verileri, Yanlış veya eski spesifikasyonların kullanımı, Profesyonel doğrulama alınmaması, Bağlama düzenlemelerinin fiziksel uygulaması, Kullanıcı kaynaklı kimlik bilgisi kötüye kullanımı veya yetkisiz erişim."
          ]
        },
        {
          title: "13. İhracat Kontrolü & Yaptırım Uyumu",
          content: [
            "Kullanıcı, Avrupa Birliği, Birleşik Krallık, Birleşmiş Milletler veya diğer ilgili makamlar tarafından uygulanan herhangi bir yaptırıma tabi olmadığını beyan ve taahhüt eder.",
            "Kullanıcı, Platformu geçerli ihracat kontrolü veya yaptırım yasalarını ihlal edecek şekilde kullanamaz ve tüm yetki alanlarında yasal kullanımı sağlamaktan tek başına sorumludur."
          ]
        },
        {
          title: "14. Siber Güvenlik Sorumluluk Reddi",
          content: [
            "Sağlayıcı, işlenen verilerin niteliğini dikkate alarak endüstri uygulamalarıyla uyumlu makul teknik ve organizasyonel güvenlik önlemleri uygular. Ancak, Sağlayıcı, Platform'un siber olaylardan, kötü amaçlı yazılımlardan, fidye yazılımlarından, yetkisiz erişimden veya üçüncü taraf sistemlerden veya ağlardan kaynaklananlar dahil olmak üzere güvenlik ihlallerinden tamamen arınmış olacağını garanti etmez."
          ]
        },
        {
          title: "15. Sigorta Bildirimi",
          content: [
            "Sağlayıcı, endüstri uygulamalarına uygun olarak profesyonel mali mesuliyet ve siber sigorta bulundurmaktadır. Bu Sözleşmedeki hiçbir şey, Kullanıcıya veya herhangi bir üçüncü tarafa sigorta kapsamı, garanti veya tazminat sağladığı şeklinde yorumlanamaz."
          ]
        },
        {
          title: "16. Güvenmeme",
          content: [
            "Kullanıcı, bu Sözleşmede açıkça belirtilmeyen herhangi bir beyana, temsile veya garantiye dayanmadığını kabul eder.",
            "Platform, profesyonel muhakeme, sertifikasyon, düzenleyici onay veya yasal uyumluluğun yerine geçmez."
          ]
        },
        {
          title: "17. Profesyonel Kullanıcı Varsayımı",
          content: [
            "Platform, deneyimli denizcilik ve lojistik profesyonellerinin kullanımı içindir. Sağlayıcı, denizcilik prensiplerini, güvenlik yükümlülüklerini veya düzenleyici gereklilikleri Kullanıcı'ya açıklama yükümlülüğü üstlenmez."
          ]
        },
        {
          title: "18. Güvenlik Kritik Kullanım Sorumluluk Reddi",
          content: [
            "Platform, aktif kargo operasyonları veya acil durumlar sırasında gerçek zamanlı operasyonel karar verme için tasarlanmamıştır ve amaçlanmamıştır."
          ]
        },
        {
          title: "19. Üçüncü Taraf Hakları Yoktur",
          content: [
            "Bu Sözleşmenin tarafları olmayan hiç kimse, Sözleşmeler (Üçüncü Taraf Hakları) Yasası 1999 (Contracts (Rights of Third Parties) Act 1999) kapsamında bu Sözleşmenin herhangi bir şartını uygulama hakkına sahip değildir."
          ]
        },
        {
          title: "20. Mücbir Sebep",
          content: [
            "Sağlayıcı, doğal afetler, savaş, siber olaylar, bulut hizmeti arızaları, işçi anlaşmazlıkları veya hükümet eylemleri dahil olmak üzere makul kontrolünün ötesindeki olaylardan kaynaklanan performans başarısızlığı veya gecikmesinden sorumlu olmayacaktır."
          ]
        },
        {
          title: "21. Geçerli Hukuk, AB Zorunlu Hukuku & Yetki",
          content: [
            "Bu Koşullar İngiliz Hukukuna tabidir.",
            "İngiltere ve Galler mahkemeleri münhasır yargı yetkisine sahip olacaktır.",
            "Bu Koşullardaki hiçbir şey, uygulanabilir olduğu durumlarda, Yönetmelik (EC) No 593/2008 (Roma I) uyarınca sözleşme ile sapılamayan AB hukukunun zorunlu hükümlerinin uygulanmasını hariç tutmaz."
          ]
        },
        {
          title: "22. Dil Önceliği",
          content: [
            "Bu Sözleşme İngilizce olarak hazırlanmıştır. Herhangi bir çeviri durumunda, İngilizce sürüm geçerli olacaktır."
          ]
        },
        {
          title: "23. Genel",
          content: [
            "Bu Koşulların herhangi bir hükmü geçersiz veya uygulanamaz bulunursa, kalan hükümler tam olarak yürürlükte kalacaktır.",
            "Sağlayıcı, bu Koşulları bir birleşme, satış veya iş devrinin bir parçası olarak devredebilir.",
            "Sorumluluk, tazminat, fikri mülkiyet, geçerli hukuk ve sorumluluk reddi ile ilgili maddeler fesih sonrasında da geçerliliğini koruyacaktır."
          ]
        },
        {
          title: "24. Veri Koruma & Gizlilik",
          content: [
            "24.1 Platformun sağlanmasıyla bağlantılı olarak, Kullanıcı Veri Sorumlusu (Controller) ve Sağlayıcı UK GDPR, EU GDPR ve 2018 Veri Koruma Yasası anlamında Veri İşleyen (Processor) olarak hareket eder.",
            "24.2 Sağlayıcı, Kişisel Verileri yalnızca Kullanıcı'nın belgelenmiş talimatları doğrultusunda ve Geçerli Veri Koruma Yasalarına uygun olarak işleyecek.",
            "24.3 Ek 1'de (Schedule 1) belirtilen Veri İşleme Ek Sözleşmesi, bu Koşulların ayrılmaz bir parçasını oluşturur ve Kişisel Verilerin Sağlayıcı tarafından Kullanıcı adına işlenmesini düzenler.",
            "24.4 Bu Koşullar ile Veri İşleme Ek Sözleşmesi arasında herhangi bir çelişki olması durumunda, veri koruma konularında Veri İşleme Ek Sözleşmesi geçerli olacaktır."
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. Tanımlar",
            "Bu Veri İşleme Ek Sözleşmesi ('DPA') amaçları doğrultusunda, 'Kişisel Veri', 'İşleme', 'Veri Sorumlusu', 'Veri İşleyen' ve 'Denetim Otoritesi' terimleri UK GDPR ve EU GDPR'da verilen anlamlara sahip olacaktır.",
            "",
            "2. Kapsam & Roller",
            "2.1 Kullanıcı, Platform kullanımıyla bağlantılı olarak işlenen Kişisel Verilerin Veri Sorumlusudur.",
            "2.2 Sağlayıcı yalnızca Veri İşleyen olarak hareket eder ve Kişisel Verileri Platformu sağlamak dışında herhangi bir amaçla işlemeyecektir.",
            "",
            "3. İşlemenin Niteliği & Amacı",
            "Amaç: SaaS hizmetlerinin sağlanması, hesap yönetimi, teknik destek ve raporların oluşturulması.",
            "Veri Konusu Kategorileri: Yetkili kullanıcılar, çalışanlar, yükleniciler.",
            "Kişisel Veri Türleri: Profesyonel iletişim bilgileri (isim, e-posta, telefon), şirket kimlik bilgileri.",
            "Özel Kategoriler: Amaçlanmamıştır.",
            "",
            "4. Veri İşleyenin Yükümlülükleri",
            "Sağlayıcı şunları yapacaktır:",
            "a) Kişisel Verileri yalnızca Kullanıcı'nın belgelenmiş talimatları doğrultusunda işlemek",
            "b) Kişisel Verileri işlemeye yetkili personelin gizlilik yükümlülüklerine tabi olmasını sağlamak",
            "c) Kişisel Verileri korumak için uygun teknik ve organizasyonel önlemleri uygulamak",
            "d) Veri sahibi hakları talepleri konusunda makul talep üzerine Kullanıcıya yardımcı olmak",
            "e) Bir Kişisel Veri İhlalinden haberdar olduktan sonra gecikmeksizin Kullanıcıyı bilgilendirmek",
            "f) Hizmetlerin feshi üzerine, saklama yasalarca gerekmedikçe Kişisel Verileri silmek veya iade etmek",
            "",
            "5. Alt İşleyiciler",
            "5.1 Kullanıcı, Sağlayıcı'nın barındırma, altyapı ve destek hizmetleri için alt işleyicilerle (sub-processors) çalışmasına yetki verir.",
            "5.2 Sağlayıcı, alt işleyicilerin bu DPA'da belirtilenlerden daha az koruyucu olmayan veri koruma yükümlülüklerine tabi olmasını sağlayacaktır.",
            "",
            "6. Uluslararası Transferler",
            "Kişisel Verilerin Birleşik Krallık veya AEA dışına aktarılması durumunda, Sağlayıcı, Birleşik Krallık Uluslararası Veri Transfer Anlaşmaları veya AB Standart Sözleşme Maddeleri dahil olmak üzere uygun güvenlik önlemlerinin mevcut olduğundan emin olacaktır.",
            "",
            "7. Güvenlik Önlemleri",
            "Sağlayıcı, verilerin niteliğine ve risklere uygun, erişim kontrolleri, uygun olduğunda şifreleme ve olay müdahale prosedürleri dahil olmak üzere makul teknik ve organizasyonel güvenlik önlemlerini sürdürecektir.",
            "",
            "8. Denetimler",
            "Makul yazılı talep üzerine ve yılda bir defadan fazla olmamak kaydıyla, Sağlayıcı, gizlilik ve güvenlik gerekliliklerine tabi olarak, bu DPA'ya uyumu göstermek için gerekli bilgileri sunacaktır.",
            "",
            "9. Sorumluluk",
            "Bu DPA'dan kaynaklanan sorumluluk, Hizmet Koşulları'nda belirtilen sorumluluk sınırlamalarına ve istisnalarına tabi olacaktır.",
            "",
            "10. Geçerli Hukuk",
            "Bu DPA, İngiltere ve Galler yasalarına tabi olacak ve buna göre yorumlanacaktır."
          ]
        }
      ],
      detailed_privacy: [
        {
          title: "1. Giriş",
          content: [
            "Bu Gizlilik Politikası, JADE CONSULTANCY AND TRADING LTD, İngiltere ve Galler yasalarına göre faaliyet gösteren bir şirket ('Sağlayıcı', 'biz', 'bizi', 'bizim'), Lashing Calculator web uygulamasını ('Platform') kullandığınızda kişisel verileri nasıl topladığını, kullandığını, sakladığını ve koruduğunu açıklar.",
            "Kişisel verileri Birleşik Krallık Genel Veri Koruma Yönetmeliği (UK GDPR) ve 2018 Veri Koruma Yasası uyarınca korumayı taahhüt ediyoruz.",
            "JADE CONSULTANCY AND TRADING LTD, Birleşik Krallık Bilgi Komisyonu Ofisi (ICO) nezdinde 00013348469 kayıt numarası ile kayıtlı bir veri sorumlusudur ve CSN1184299 sertifikası ile siber güvenlik standartlarını sürdürmektedir."
          ]
        },
        {
          title: "2. Veri Sorumlusu",
          content: [
            "Geçerli veri koruma kanunu amaçları doğrultusunda, Sağlayıcı, Platform aracılığıyla işlenen kişisel verilerle ilgili olarak veri sorumlusu sıfatıyla hareket eder."
          ]
        },
        {
          title: "3. Topladığımız Bilgiler",
          content: [
            "3.1 Hesap ve İletişim Verileri: Ad, Şirket adı, İş e-posta adresi, Telefon numaraları, Giriş bilgileri, Fatura ve ödeme detayları. Bu veriler kesinlikle hesap yönetimi, kimlik doğrulama, faturalandırma ve hizmet iletişimleri için kullanılır.",
            "3.2 Endüstriyel ve Operasyonel Veriler: Yük parametreleri ve konfigürasyonları, Lashing hesaplamaları ve rapor girdileri. Bu veriler Gizli Endüstriyel Bilgi olarak ele alınır. Ticari yeniden satış, profilleme veya pazarlama amaçları için analiz edilmez.",
            "3.3 Teknik Veriler: IP adresi, Tarayıcı tipi, Cihaz ve oturum bilgileri, Erişim günlükleri. Bu veriler güvenlik, performans izleme ve kötüye kullanım tespiti için toplanır."
          ]
        },
        {
          title: "4. İşleme İçin Yasal Dayanak",
          content: [
            "Kişisel verileri İngiltere GDPR Madde 6 uyarınca aşağıdaki yasal dayanaklara göre işliyoruz:",
            "Sözleşmesel gereklilik – Platformu sağlamak ve işletmek için",
            "Meşru menfaatler – güvenliği sağlamak, kötüye kullanımı önlemek ve hizmet performansını iyileştirmek için. Meşru menfaatlerimizi üç aşamalı bir testle değerlendiriyoruz: (1) Amaç Testi (meşru menfaatin belirlenmesi), (2) Gereklilik Testi (işlemenin bu amaca ulaşmak için gerekli olması) ve (3) Denge Testi (bireyin hak ve özgürlüklerinin bu menfaatten üstün gelmemesi).",
            "Yasal yükümlülük – muhasebe, vergi ve düzenleyici gerekliliklere uymak için",
            "Rıza, açıkça alındığı durumlarda (örn. isteğe bağlı iletişimler)"
          ]
        },
        {
          title: "5. Bilgilerinizi Nasıl Kullanıyoruz",
          content: [
            "Toplanan verileri şu amaçlarla kullanırız:",
            "Platformu sağlamak, işletmek ve sürdürmek",
            "Hesaplamaları işlemek ve profesyonel raporlar oluşturmak",
            "Abonelikleri, faturalandırmayı ve müşteri desteğini yönetmek",
            "Uluslararası güvenlik çerçeveleriyle (CTU Kodu / CSS Kodu) uyumu sağlamak",
            "Sistem güvenliğini izlemek ve yetkisiz erişimi önlemek",
            "Platform işlevselliğini geliştirmek ve iyileştirmek",
            "Kişisel verileri üçüncü taraflara satmayız."
          ]
        },
        {
          title: "6. Veri Paylaşımı ve Üçüncü Taraflar",
          content: [
            "Kişisel veriler yalnızca şunlarla paylaşılabilir:",
            "Barındırma ve bulut altyapı sağlayıcıları",
            "Ödeme ve faturalandırma hizmet sağlayıcıları",
            "Profesyonel danışmanlar (hukuk, muhasebe, sigorta)",
            "Kanunen gerekli görüldüğünde yetkililer",
            "Tüm üçüncü taraflar uygun gizlilik ve veri koruma yükümlülüklerine tabidir."
          ]
        },
        {
          title: "7. Uluslararası Veri Transferleri",
          content: [
            "Kişisel verilerin Birleşik Krallık veya AEA dışına aktarıldığı durumlarda, aşağıdakiler dahil olmak üzere uygun güvenlik önlemlerinin alındığından emin oluruz:",
            "Yeterlilik kararları",
            "Standart Sözleşme Maddeleri (SCC'ler)",
            "Eşdeğer yasal transfer mekanizmaları"
          ]
        },
        {
          title: "8. Veri Saklama",
          content: [
            "Kişisel verileri yalnızca gerekli olduğu sürece saklarız:",
            "Hesap ve fatura verileri: sözleşme süresince ve kanunen gerekli olduğu şekilde",
            "Teknik günlükler: sınırlı süreler için güvenlik ve denetim amaçlarıyla saklanır",
            "Endüstriyel hesaplama verileri: yalnızca Hizmeti sağlamak için gerekli olduğu şekilde saklanır",
            "Kullanıcılar kendi raporlarını ve hesaplama çıktılarını arşivlemekten sorumludur."
          ]
        },
        {
          title: "9. Veri Güvenliği",
          content: [
            "Kişisel verileri yetkisiz erişim, kayıp, değiştirme veya ifşaya karşı korumak için erişim kontrolleri, şifreleme ve izleme dahil olmak üzere makul teknik ve organizasyonel önlemler uygularız.",
            "Ancak, hiçbir sistemin tamamen güvenli olduğu garanti edilemez."
          ]
        },
        {
          title: "10. Haklarınız",
          content: [
            "Birleşik Krallık GDPR kapsamında şu haklara sahipsiniz:",
            "Kişisel verilerinize erişme",
            "Hatalı verileri düzeltme",
            "Silme talebinde bulunma (yasal yükümlülüklere tabi olarak)",
            "İşlemeyi kısıtlama veya itiraz etme",
            "Veri taşınabilirliği",
            "Varsa rızayı geri çekme",
            "Talepler aşağıdaki iletişim bilgileri kullanılarak yapılabilir."
          ]
        },
        {
          title: "11. Şikayetler",
          content: [
            "Veri koruma haklarınızın ihlal edildiğine inanıyorsanız, Birleşik Krallık Bilgi Komisyonu Ofisi'ne (ICO) şikayette bulunma hakkınız vardır."
          ]
        },
        {
          title: "12. Bu Politikadaki Değişiklikler",
          content: [
            "Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Güncellenmiş sürümler, revize edilmiş yürürlük tarihi ile Platformda sunulacaktır."
          ]
        },
        {
          title: "13. Çocukların Gizliliği",
          content: [
            "Hizmetimiz 18 yaşın altındaki bireylere hitap etmemektedir.",
            "18 yaşın altındaki kişilerden bilerek kişisel veri toplamıyoruz. Eğer bir ebeveynseniz ve çocuğunuzun bize veri sağladığını fark ederseniz lütfen bizimle iletişime geçin. Rıza dışı veri toplandığını tespit edersek bu bilgileri sunucularımızdan derhal sileriz."
          ]
        },
        {
          title: "14. Pazarlama ve İletişim",
          content: [
            "Kullanıcılarımıza yalnızca bir işlem sırasında topladığımız veya rıza gösterdikleri durumlarda pazarlama materyali göndeririz.",
            "Tüm pazarlama e-postalarımızda 'Abonelikten Ayrıl' seçeneği bulunur. İptal talepleri 48 saat içinde işleme alınır."
          ]
        },
        {
          title: "15. Bize Ulaşın",
          content: [
            "Gizlilik veya veri koruma soruları için:",
            "E-posta: info@lashingplan.com",
            "Adres: JADE CONSULTANCY AND TRADING LTD, C/O IDA&CO BUILDING 3 CHISWICK BUSINESS, 566 CHISWICK HIGH ROAD, W4 5YA, LONDON, UK."
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "Veri Koruma ve Gizlilik",
          content: "Gizliliğinizi korumaya kararlıyız. Bu politika, bilgilerinizin nasıl toplandığını ve global standartlarda nasıl korunduğunu açıklar."
        },
        collection: {
          title: "Toplanan Bilgiler",
          personal_title: "Hesap Bilgileri",
          personal_desc: "Kimlik doğrulama ve teknik raporlama için kullanılan temel profesyonel bilgiler.",
          calc_title: "Endüstriyel Bilgi",
          calc_desc: "Yük parametreleri ve konfigürasyonlar. Bu veriler 'Gizli Endüstriyel Bilgi' olarak korunur."
        },
        usage: {
          title: "Veri Kullanımı",
          intro: "Toplanan veriler şu amaçlarla kullanılır:",
          items: [
            "Güvenli hizmet sağlamak ve işletmek",
            "Profesyonel PDF raporları oluşturmak",
            "Küresel emniyet standartlarına uyumu optimize etmek",
            "Kullanıcı deneyimini geliştirmek"
          ]
        },
        compliance: {
          title: "Global Uyumluluk",
          content: "Verileriniz üzerinde kontrol sahibi olmanız için UK GDPR ve Data Protection Act 2018 standartlarında tam şeffaflık sağlıyoruz.",
          note: "Verileriniz satılmaz, ticari amaçla üçüncü taraflarla paylaşılmaz."
        },
        retention: {
          title: "Veri Saklama",
          content: "Bilgileriniz sadece hizmet sunumu için gerekli olduğu sürece ve yasal süreler dahilinde güvenli sunucularda saklanır."
        },
        contact: {
          title: "İletişim",
          content: "Veri haklarınız veya gizlilik hakkında bize ulaşın:",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. Giriş",
          content: [
            "Bu Çerez Politikası, JADE CONSULTANCY AND TRADING LTD (“biz”, “bizi”, “bizim”) olarak Lashing Calculator web sitesinde ve web uygulamasında (“Platform”) çerezlerin ve benzer teknolojilerin nasıl kullanıldığını açıklar.",
            "Bu politika, Gizlilik Politikamız ile birlikte okunmalıdır."
          ]
        },
        {
          title: "2. Çerez Nedir?",
          content: [
            "Çerezler, bir web sitesini ziyaret ettiğinizde cihazınızda saklanan küçük metin dosyalarıdır. Web sitesi işlevselliğini sağlamak, kullanıcı deneyimini geliştirmek ve web sitesi operatörlerine bilgi sağlamak için yaygın olarak kullanılırlar."
          ]
        },
        {
          title: "3. Yasal Çerçeve",
          content: [
            "Çerezleri şunlara uygun olarak kullanıyoruz:",
            "Birleşik Krallık Gizlilik ve Elektronik İletişim Yönetmeliği (PECR)",
            "AB e-Gizlilik Direktifi",
            "Birleşik Krallık Genel Veri Koruma Yönetmeliği (UK GDPR)",
            "Kesinlikle gerekli çerezler haricinde, çerezler yalnızca önceden onayınızla kullanılır."
          ]
        },
        {
          title: "4. Kullandığımız Çerez Türleri",
          content: [
            "4.1 Kesinlikle Gerekli Çerezler",
            "Bu çerezler Platformun çalışması için gereklidir ve şu temel işlevleri sağlar:",
            "Kullanıcı kimlik doğrulaması",
            "Oturum yönetimi",
            "Güvenlik ve dolandırıcılık önleme",
            "Bu çerezler onay gerektirmez ve çerez ayarlarımızdan devre dışı bırakılamaz.",
            "Yasal dayanak: Kullanıcı tarafından talep edilen hizmetin sağlanması için gereklidir.",
            "",
            "4.2 Analitik Çerezler",
            "Sitemizi nasıl kullandığınızı anlamak için onayınızla Google Analytics veya benzeri analitik araçlar kullanabiliriz.",
            "Veriler anonimleştirilmiş ve kümelenmiş şekilde performans ölçümü için kullanılır.",
            "Analitik çerezler, yalnızca açık onayınızı verdikten sonra yerleştirilir.",
            "Yasal dayanak: UK GDPR ve e-Gizlilik kuralları uyarınca onay."
          ]
        },
        {
          title: "5. Çerez Sağlayıcıları",
          content: [
            "Analitik çerezler, güvenilir üçüncü taraf analitik sağlayıcıları tarafından sağlanabilir. Bu sağlayıcılar, verileri kendi gizlilik politikalarına ve geçerli veri koruma yasalarına uygun olarak işler.",
            "Çerezleri hedefli reklamcılık veya pazarlama profili oluşturma amacıyla kullanmayız."
          ]
        },
        {
          title: "6. Çerez Süresi",
          content: [
            "Çerezler şunlar olabilir:",
            "Oturum çerezleri, tarayıcınızı kapattığınızda sona erer",
            "Kalıcı çerezler, tanımlanmış bir süre boyunca veya silinene kadar cihazınızda kalır",
            "Belirli çerez ömürleri, amaçlarına ve sağlayıcılarına bağlıdır."
          ]
        },
        {
          title: "7. Çerez Tercihlerinizi Yönetme",
          content: [
            "Çerez onayınızı istediğiniz zaman şu yollarla yönetebilir veya geri çekebilirsiniz:",
            "Platformda mevcut olan çerez ayarlarını veya onay başlığını kullanarak",
            "Tarayıcı ayarlarınızı çerezleri engelleyecek veya silecek şekilde ayarlayarak",
            "Lütfen belirli çerezleri devre dışı bırakmanın Platformun işlevselliğini etkileyebileceğini unutmayın."
          ]
        },
        {
          title: "8. Bu Çerez Politikasındaki Değişiklikler",
          content: [
            "Bu Çerez Politikasını zaman zaman güncelleyebiliriz. Herhangi bir değişiklik, güncellenmiş bir yürürlük tarihi ile Platformda yayınlanacaktır."
          ]
        },
        {
          title: "9. Bize Ulaşın",
          content: [
            "Çerez kullanımımız hakkında herhangi bir sorunuz varsa, lütfen bizimle iletişime geçin:",
            "E-posta: info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "Çerez Politikası",
        last_updated: "Yürürlük Tarihi: 4 Şubat 2026",
        intro_title: "Giriş",
        intro_content: "Bu Çerez Politikası, Lashing Calculator web sitesini ziyaret ettiğinizde çerezlerin nasıl kullanıldığını açıklar.",
        types_title: "Kullandığımız Çerez Türleri",
        essential_title: "Temel Çerezler",
        essential_desc: "Bu çerezler web sitesinin düzgün çalışması için kesinlikle gereklidir ve sistemlerimizde kapatılamaz.",
        analytics_title: "Analitik Çerezler",
        analytics_desc: "Bu çerezler, web sitemizin nasıl kullanıldığını anlamamıza yardımcı olmak için ziyaretleri ve trafik kaynaklarını saymamızı sağlar.",
        manage_title: "Çerez Tercihlerinizi Yönetme",
        manage_content: "Çerez tercihlerinizi istediğiniz zaman tarayıcı ayarlarınız aracılığıyla yönetebilirsiniz.",
        contact_title: "Bize Ulaşın",
        contact_text: "Çerez politikamız hakkında sorularınız varsa, lütfen bizimle iletişime geçin."
      },
      sales_agreement: {
        title: "Satış Sözleşmesi",
        content: "Bu satış sözleşmesi, Lashing Calculator hizmetlerinin sağlanması ve kullanılmasına ilişkin hüküm ve koşulları belirler. Hizmetlerimizin satın alınması, bu sözleşmenin tüm şartlarını kabul ettiğiniz anlamına gelir."
      },
      legal_notice: {
        title: "Yasal Uyarı",
        content: "Bu web sitesindeki bilgiler yalnızca genel bilgi amaçlıdır. Lashing Calculator, bilgilerin güncel ve doğru olmasını sağlamak için çaba gösterse de, herhangi bir beyan veya garanti vermez."
      },
      footer_note: "Platformu kullanarak, yukarıdaki teknik riskleri ve yasal koşulları okuduğunuzu ve kargo güvenliği için profesyonel doğrulama sorumluluğunu kabul ettiğinizi onaylamış olursunuz."
    }
  },

  // ===================================================================
  // ENGLISH
  // ===================================================================
  "English": {
    code: "en",
    name: "English",
    flag: "United Kingdom",

    hub: {
      nav: {
        products: "Products",
        insights: "Market Insights",
        resources: "Resources",
        go_to_app: "Go to App"
      },
      hero: {
        status: "SYSTEM STATUS: OPTIMAL",
        badge: "NEXT-GEN CARGO LOGISTICS HUB",
        title_main: "CALCULATED STRENGTH",
        title_italic: "absolute security",
        title_1: "Planning for",
        title_2: "Maximum Security.",
        description: "LashingPlan is a central ecosystem for professional cargo securing tools, calculated with precision and compliant with international standards.",
        explore: "Explore Products",
        launch_product: "LAUNCH PRODUCT",
        read_blog: "Blog Articles",
        portal_access: "PORTAL ACCESS V2.0",
        stability_index: "STABILITY INDEX",
        archives: "ARCHIVES",
        archives_subtitle: "Stay ahead with professional safety standards and logistics trends."
      },
      footer: {
        tagline: "A product of technical excellence. Engineered for those who demand absolute precision in maritime safety.",
        systems: "SYSTEMS",
        resources: "RESOURCES",
        legal: "LEGAL",
        privacy: "Privacy",
        compliance: "Compliance",
        terms: "Terms",
        status_all: "LASHING PLAN GLOBAL // ALL SYSTEMS OPERATIONAL",
        follow: "Follow Us"
      },
      products: {
        title: "Our Products",
        subtitle: "Professional tools designed for global shipping compliance and cargo safety.",
        context: "01 / Products",
        calculator: {
          title: "Lashing Calculator",
          description: "The industry-standard engine for calculating cargo securing requirements for road, sea, and rail.",
          button: "Launch App"
        },
        inspection: {
          title: "Inspection Pro",
          description: "Photo-supported digital inspection and instant certificate creation tool for field surveys.",
          status: "COMING SOON"
        },
        risk: {
          title: "Risk Advisor",
          description: "AI-powered stability analysis and shipment risk assessment system.",
          status: "COMING SOON"
        },
        stats: {
          calculations: "CALCULATIONS",
          countries: "COUNTRIES",
          compliance: "COMPLIANCE",
          precision: "PRECISION"
        },
        blog_articles: {
          article_1: {
            title: "Cargo Securing: Lashing",
            description: "Securing cargo to the transport unit before shipment is the foundation of logistics safety..."
          },
          article_2: {
            title: "Innovative Cargo Freight Solutions",
            description: "Forward-thinking cargo solutions emphasizing pioneering innovation and reliable lashing..."
          },
          article_3: {
            title: "Understanding IMO/CTU Code Compliance",
            description: "A deep dive into the international standards regulating cargo transport in containers..."
          },
          article_4: {
            title: "Enhancing Cargo Safety Standards",
            description: "Improving safety with modern protocols, technology integration, and proven safety practices..."
          }
        }
      },
      insights: {
        title: "Market Insights",
        subtitle: "Stay ahead with professional safety standards and logistics trends.",
        all_articles: "All Articles"
      }
    },

    blog: {
      nav: {
        back: "Back to Hub"
      },
      header: {
        title_1: "Industry",
        title_2: "Insights.",
        subtitle: "Stay updated with the latest in cargo safety, international standards, and logistics technology."
      },
      article: {
        read_time: "read",
        share: "SHARE",
        read_more: "Read Article",
        cta_title: "Need precise calculations?",
        cta_desc: "Put this knowledge into practice with our Lashing Calculator.",
        cta_button: "Launch Calculator"
      },
      footer: {
        copyright: "© 2026 LashingPlan Blog. Part of Lashing Calculator ecosystem."
      }
    },

    app: {
      title: "Lashing Calculator",
      logout: "Logout",
      theme_toggle: "Dark / Light Mode",
      profile: "Profile",
      reports: "Field Reports",
      login: {
        title: "Lashing Calculator",
        subtitle: "Secure Lashing Solutions",
        email: "Email or Username",
        email_placeholder: "Enter your email or username",
        password: "Password",
        password_placeholder: "Enter your password",
        forgot_password: "Forgot Password?",
        submit: "Sign In",
        no_account: "Don't have an account?",
        signup: "Sign Up",
        back_to_home: "Back to Home",
        consent: "By continuing, I agree to the {terms} and {privacy}.",
        surveyor_portal: "Are you a Surveyor? Go to Surveyor Portal"
      },
      cog3d: {
        title: "3D Center of Gravity (CoG) View",
        subtitle: "Center of Gravity (CoG) is the balance point of the cargo and determines the tipping behavior.",
        drag_hint: "Drag to rotate"
      },
      lashing3d: {
        title: "3D Lashing Diagram",
        subtitle: "Interactive view of lashing configuration"
      },
      calculator: {
        title: "Cargo Calculator",
        cargo_details: "Cargo Details",
        weight: "Weight (MT)",
        cog: {
          title: "Center of Gravity (cm from bottom left corner)",
          x: "X (width)",
          y: "Y (depth)",
          z: "Z (height)"
        },
        securing_elements: "Securing Elements",
        wooden_dunnage: {
          label: "Use Wooden Dunnage",
          description: "Adds friction and prevents sliding",
          details: "Wooden Dunnage Details",
          width: "Width (cm)"
        },
        loop_lashing: "Loop Lashing",
        direct_lashing: "Direct Lashing",
        select_rope_type: "Select Rope Type",
        stopper_forces: "Stopper Forces (daN)",
        direction: {
          forward: "Forward",
          backward: "Backward",
          right: "Right",
          left: "Left"
        }
      },
      results: {
        title: "Results",
        lashing_diagram: "Lashing Diagram",
        error: "Error",
        loading: "Loading...",
        no_results: "No results found",
        values: {
          sliding: "Sliding:",
          tipping: "Tipping:",
          friction: "Friction:",
          securing_element: "Securing Element:",
          net_sliding: "Net Sliding:",
          net_tipping: "Net Tipping:"
        }
      }
    },
    landing: {
      contact: {
        label: "Contact Sales",
        title_1: "Ready to secure your ",
        title_2: "cargo operations?",
        description: "Upgrade to enterprise-grade surveyor algorithms. Get in touch with our engineering team to discuss custom API integrations, high-volume licensing, or premium features.",
        email_us: "Email Us",
        enterprise_support: "Enterprise Support",
        support_desc: "24/7 Priority SLA Available",
        success_title: "Message Sent!",
        success_desc: "Our engineering team will reach out to you within 24 hours.",
        form: {
          name: "Full Name",
          placeholder_name: "John Doe",
          company: "Company",
          placeholder_company: "Logistics Inc.",
          email: "Email Address",
          placeholder_email: "john@company.com",
          message: "Your Message",
          placeholder_message: "How can we help you scale your lashing operations?",
          send: "Send Request"
        }
      },
      hero_badge: "Trusted by Cargo Securing Professionals Worldwide",
      hero_badge_imo: "IMO Compliant",
      hero_badge_imo_sub: "International Standards",
      hero_badge_3d: "3D Visualization",
      hero_badge_3d_sub: "Real-time",
      hero_title_1: "Professional",
      hero_title_2: "Lashing Intelligence",
      hero_subtitle: "Global standards. Precision algorithms. Professional reports. The industry's most advanced cargo securing platform.",
      start_calculating: "Sign In",
      view_demo: "View Demo",
      stats: {
        modes: "6+ Transport Modes",
        platform: "Cloud Platform",
        platform_status: "Secured",
        access: "Global Access"
      },
      features_title_1: "Power Meets",
      features_title_2: "Compliance",
      features_subtitle: "Built by cargo safety experts for high-stakes shipping operations.",
      features: {
        calculations: {
          title: "Instant Calculations",
          desc: "Calculate lashing forces in real-time with our advanced algorithm."
        },
        pdf: {
          title: "Professional PDF Reports",
          desc: "Generate industry-standard reports with company branding and detailed analysis."
        },
        compliance: {
          title: "CTU-Informed Assessment",
          desc: "Simplified decision support informed by the IMO / ILO / UNECE CTU Code."
        },
        multimodal: {
          title: "Multi-Mode Transport",
          desc: "Support for road, rail, and sea transport. Containers, flatracks, vessels, and more."
        },
        global: {
          title: "Global Standards",
          desc: "Traceable assessment informed by the CTU Code 2014 and its Informative Material."
        },
        secure: {
          title: "Secure & Confidential",
          desc: "Your calculation data is treated as confidential with enterprise-grade security."
        }
      },
      transport: {
        title_1: "Unified Transport",
        title_2: "Security",
        subtitle: "Seamless calculations across the entire logistics chain.",
        modes: {
          road: { name: "Road Transport", desc: "Truck Trailer and Lowbed" },
          rail: { name: "Rail Transport", desc: "Rail Wagon, Container, Flatrack and Truck Trailer" },
          sea: { name: "Sea Transport", desc: "Container, Truck Trailer, Flatrack, Lowbed, Roll Trailer, Vessel" },
          combined: { name: "Combined Modes", desc: "Road-Sea, Road-Rail, Road-Sea-Rail" }
        }
      },
      how_it_works: {
        badge: "How It Works",
        title_1: "Professional Analysis in",
        title_2: "3 Simple Steps",
        subtitle: "Complete complex lashing calculations in minutes",
        step1: {
          title: "Enter Cargo Details",
          desc: "Input cargo dimensions, weight, center of gravity, and container type into the system."
        },
        step2: {
          title: "Automatic Calculation",
          desc: "The system automatically calculates the optimal lashing arrangement according to IMO standards",
          desc_highlight: " and allows manual changes in the plan."
        },
        step3: {
          title: "Get Report",
          desc: "Generate a professional PDF report with 3D visualization and detailed calculations."
        }
      },
      benefits: {
        title: "Engineered for No-Fail Operations",
        subtitle: "Trusted by cargo experts, surveyors and terminal operators for mission-critical cargo securing.",
        list: [
          "Save hours of manual calculations",
          "Reduce material waste with optimization",
          "Ensure regulatory compliance",
          "Professional documentation for clients",
          "Multi-language support (EN, TR, ES, DE, ZH, JA)",
          "Company-branded PDF reports"
        ],
        cta_card_title: "Standardize Your Lashing Plan Today",
        cta_card_desc: "Deploy our enterprise-grade calculator for your team and ensure 100% compliance.",
        cta_card_trial: "No credit card required for trial"
      },
      footer: {
        desc: "Join professionals worldwide using our intelligent lashing calculator.",
        columns: {
          service: "Service",
          company: "Company",
          support: "Support"
        },
        links: {
          privacy: "Privacy Policy",
          compliance: "Terms of Service",
          cookies: "Cookie Policy",
          security: "Security",
          help: "Help Center",
          enterprise: "Enterprise",
          contact: "Contact Expert",
          force_analysis: "Force Analysis",
          pdf_generator: "PDF Generator",
          optimizer: "Optimizer",
          api_docs: "API Docs",
          cookie_settings: "Cookie Settings"
        }
      },
      footer_copyright: "2025 Lashing Calculator. A service provided by JADE CONSULTANCY AND TRADING LTD. All rights reserved.",

      pricing_simple: "Simple, ",
      pricing_transparent: "Transparent",
      pricing_title: " Pricing",
      compare_features: "Compare all features",
      footer_global: "Global",
      footer_encrypted: "Encrypted",
      footer_high_availability: "High Availability",
      demo_request: {
        title: "Request a Demo",
        subtitle: "See how Lashing Calculator can transform your cargo security operations.",
        cta_title: "Ready to See it in Action?",
        cta_subtitle: "Schedule a personalized walkthrough with our cargo specialists and discover how we can standardize your lashing operations globally.",
        form: {
          full_name: "Full Name",
          work_email: "Work Email",
          company: "Company Name",
          job_title: "Job Title",
          phone: "Phone Number",
          fleet_size: "Fleet Size / Monthly Shipments",
          industry: "Industry",
          message: "Message (Optional)",
          submit: "Submit Demo Request",
          success_title: "Request Received",
          success_message: "Your demo request has been successfully submitted. Our team will contact you shortly."
        }
      },
      price_inquiry: {
        title: "Get a Quote",
        subtitle: "Contact us for custom pricing and enterprise solutions.",
        form: {
          full_name: "Full Name",
          work_email: "Work Email",
          company: "Company Name",
          phone: "Phone Number",
          message: "Your Message (Optional)",
          submit: "Send Inquiry",
          success_title: "Inquiry Sent",
          success_message: "Your price inquiry has been received. Our sales team will get back to you with a custom proposal."
        }
      }
    },

    profile: {
      title: "User & Company Profile",
      subtitle: "Manage your User and Company information here.",
      logo_label: "Company Logo",
      logo_note: "Logo can only be updated by administrators.",
      company_name: "My Profile",
      address: "Full Address",
      phone: "Phone / Mobile",
      email: "Email (Account)",
      tax_office: "Tax Office",
      tax_number: "Tax Number",
      company_email_label: "Company Email (Visible on PDF)",
      user_name_label: "User Name (Account)",
      terms_accepted: "Terms of Service Accepted on",
      terms_not_accepted: "Terms of Service Not Yet Accepted",
      view_terms: "View Terms of Service",
      accept_terms: "Accept Terms of Service",
      review_accept: "Review & Accept Terms of Service",
      admin_note: "Note: Company details are managed by administrators.",
      cancel: "Cancel",
      accept: "Accept Terms of Service",
      modal_title: "Terms of Service",
      modal_subtitle: "Please review and agree to our terms to proceed.",
      modal_agree: "By accepting, you agree to be bound by the {tos} and {privacy}.",
      modal_ack: "You acknowledge that the software is a tool and the ultimate responsibility for lashing plans lies with the certified professional.",
      checkbox_label: "I have read and agree to the Terms and Conditions",
      save: "Save Changes",
      saving: "Saving...",
      success_message: "Profile updated successfully.",
      error_message: "Update error: {error}",
      credits_title: "Credit & Usage Information",
      calc_credits: "Calculation Credits",
      pdf_credits: "PDF Report Credits",
      monthly_increment: "Monthly Increment",
      next_increment: "Next Reload",
      remaining: "Remaining",
      lashing_display_label: "Lashing Display Format",
      lashing_display_msl: "MSL (e.g. MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (e.g. CL200 HDB12L)",
      pdf_theme_title: "PDF Design Theme",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Dark Turquoise & Slate Gray",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Corporate Navy & Light Gray",
      theme_standart_title: "Standard",
      theme_standart_desc: "Original App Colors"
    },
    surveys: {
      title: "Field Reports",
      create_new: "Create New Report",
      total_ops: "Total Operations",
      pending_photos: "Pending Photos",
      completed_assignments: "Completed Assignments",
      current_reports: "Current Reports",
      search_placeholder: "Search ID or Project...",
      no_reports_title: "No reports yet",
      no_reports_desc: "Start by making your first lashing calculation to prepare a field report.",
      create_now: "Create Now",
      unnamed_project: "Unnamed Project",
      created_date: "CREATED",
      data_status: "Data",
      uploaded: "Application photos uploaded",
      waiting: "Waiting for application photos",
      surveyor: "Surveyor",
      waiting_response: "Waiting for Response",
      under_review: "Under Review",
      report_ready: "Report Ready",
      cancelled_rejected: "Cancelled / Rejected",
      send_link: "Send Field Upload Link",
      upload_manually: "Upload field photos manually",
      assign_new: "Assign a new surveyor",
      assign_surveyor: "Assign Surveyor",
      assigned: "Assigned",
      summary_pdf: "Summary PDF",
      surveyor_pdf: "Surveyor PDF",
      assignment_success: "Surveyor assignment successful!",
      old_report_error: "Secure link not generated for old reports. Please recreate the report.",
      session_expired: "Session expired. Please refresh the page or log in again.",
      pdf_error: "Error creating PDF.",
      download_failed: "Download failed: ",
      wait: "Wait...",
      connecting: "Connecting...",
      access_error: "Access Error",
      modal: {
        title: "Assign Surveyor",
        search_placeholder: "Search by City, Port, or Name...",
        list_error: "Could not retrieve surveyor list.",
        assign_failed: "Assignment failed.",
        loading: "Loading...",
        not_found: "No surveyor found.",
        unknown_location: "Unknown",
        assigning: "Assigning...",
        assign_button: "Assign This Surveyor"
      },
      guest: {
        title: "Field Implementation Oversight Portal",
        subtitle: "You can upload field photos for your lashing application here.",
        invalid_link: "Invalid or expired link.",
        upload_success_title: "Upload Successful",
        upload_success_desc: "Field photos for your lashing application have been successfully saved. Your report is now ready for review and PDF approval.",
        close_window: "Close Window",
        info_title: "1. Implementer Information (Optional)",
        date: "Date",
        company_name: "Company Name",
        company_placeholder: "e.g.: X Logistics",
        implementer_name: "Implementer Full Name",
        phone: "Phone",
        email: "Email",
        email_placeholder: "name@company.com",
        photos_title: "2. Field Implementation Images (Max 12)",
        photos_desc: "Please upload or take field photos matching the categories below. The limit per photo is ~5MB and it will be compressed automatically to ~1MB on your device.",
        take_photo: "Take Photo / Upload",
        uploading: "Uploading photos...",
        confirm_upload: "Upload All and Confirm",
        min_photo_error: "Please add at least one photo.",
        compression_error: "Photo could not be processed, please try again.",
        categories: {
          floor: "Transport Unit Floor Condition",
          cargo: "Cargo Placement and Floor Chocking",
          front_right: "Lashing: Front Right",
          front_left: "Lashing: Front Left",
          rear_right: "Lashing: Rear Right",
          rear_left: "Lashing: Rear Left",
          plate: "License Plate (If any)",
          seal: "Seal Number (If any)",
          overall: "Main General View (Final)",
          other_1: "Other 1 - Optional",
          other_2: "Other 2 - Optional",
          other_3: "Other 3 - Optional"
        }
      },
      portal: {
        title: "Surveyor Portal",
        subtitle: "Manage your assigned inspection tasks by logging in.",
        login_error: "Please enter your email and password.",
        login_button: "Login",
        logging_in: "Logging in...",
        dashboard: {
          connecting: "Connecting to dashboards...",
          pending_tab: "Incoming Requests",
          active_tab: "Active Tasks",
          completed_tab: "Past Jobs",
          logout: "Secure Logout",
          connected: "Connected",
          account_type: "Surveyor Account",
          no_records: "No records found",
          no_records_desc: "There are no active inspection tasks to display in this tab.",
          customer: "Customer",
          task_date: "TASK DATE",
          waiting_response: "Waiting for Response",
          under_review: "Customer Review",
          delivered: "Report Delivered",
          rejected_by_you: "Task Rejected",
          accept_button: "Accept Task",
          reject_button: "Reject",
          go_to_report: "Go to Report Menu",
          download_pdf: "Download PDF File",
          downloading_pdf: "Downloading report PDF...",
          download_success: "Report downloaded successfully.",
          download_error: "Error downloading report.",
          update_error: "Status could not be updated, please try again.",
          accept_success: "Task accepted successfully!",
          reject_success: "Task rejected."
        },
        editor: {
          details_title: "Project Details",
          project_name: "Project Name",
          weight: "Cargo Weight",
          photos_count: "Field Photos",
          success_title: "Report Created Successfully!",
          success_desc: "Your inspection report has been created, signed, and uploaded to the secure cloud environment. You can download or review the PDF file by clicking the button below.",
          back_to_dashboard: "Back to Dashboard",
          download_pdf: "Download PDF",
          creator_title: "Final Survey Report Creator",
          assessment_label: "Surveyor Assessment",
          assessment_desc: "This text will be added to the Methodology & Conclusion section of the final PDF.",
          assessment_default: "The inspection of the cargoes has been completed in accordance with international standards. No damage or non-compliance was observed.",
          auto_info_note: "Note: Default information such as your name, signature, and company logo in the system will be automatically placed in the PDF report.",
          back_button: "Back",
          generate_button: "Certify & Send PDF Report",
          generating: "Generating...",
          generate_error: "Report could not be created",
          not_found: "Task not found or you are not authorized."
        }
      }
    },

    labels: {
      three_d_view: "3D View",
      no_results_data: "No results data available",
      pass: "Pass",
      fail: "Fail",
      reports: "Reports",
      user_responsible_for: "You are solely responsible for:",
      page_title: "Lashing Calculation Tool",
      weight: "Weight (MT)",
      transport_mode: "Transport Mode",
      transport_unit: "Transport Unit",
      friction_surface: "Friction Surface",
      select: "Select",
      select_transport_mode_first: "Please select transport mode first",
      cargo_dimensions: "",
      cargo_specs: "CARGO SPECS",
      width: "Cargo Width (m)",
      length: "Cargo Length (m)",
      height: "Cargo Height (m)",
      cog_width: "CoG Width (m)",
      cog_length: "CoG Length (m)",
      cog_height: "CoG Height (m)",
      cog_width_help: "CoG defaults to half the width. Change if different.",
      cog_length_help: "CoG defaults to half the length. Change if different.",
      cog_height_help: "CoG defaults to half the height. Change if different.",
      wooden_dunnage_dimensions: "Wooden Dunnage Dimensions & Qty",
      wooden_dunnage_width: "Width (cm):",
      wooden_dunnage_length: "Height (cm):",
      stopper_qty: "Metal Stopper(Qty)",
      force_analysis: "Force Analysis Results (kN)",
      generate_pdf: "Generate PDF",
      download_pdf: "Download PDF",
      wooden_dunnage: "Wooden\nDunnage",
      stopper: "Stopper",
      loop_lashing: "Loop Lashing",
      max_weight: "Max Weight",
      direct_lashing: "Direct\nLashing",
      loop_rope_select: "",
      loop_lashing_quantity_per_side: "Qty (per side)",
      direct_lashing_quantity: "Qty",
      cog_above: "Above CoG",
      cog_below: "Below CoG",
      forward: "Forward",
      backward: "Backward",
      right: "Right",
      left: "Left",
      lashing_diagram: "Lashing Diagram",
      language: "Language",
      calculate: "Calculate",
      configuration_panel: "Configuration Panel",
      operation_mode: "Operation Mode",
      manual_mode: "Calculator",
      lashing_calculator: "Lashing Calculator",
      direct_lashing_selection: "Direct Lashing Selection",
      reset: "Reset",
      print: "Print",
      configuration: "Configuration",
      use_stopper: "Use Stopper",
      logout: "Logout",
      results_will_appear_here: "Results will appear here",
      continue_with_overweight: "Continue with overweight",
      optimizer_mode: "Optimizer",
      calculator_mode: "Calculator",
      project_description: "Project Description (Optional)",
      enter_project_description_hint: "Please enter the project description that will appear on the PDF report.",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "Edit in Calculator",
      breakbulk_settings: "Breakbulk Settings",
      flatrack_type: "Flatrack Type",
      orientation: "Cargo Orientation",
      longitudinal: "Longitudinal",
      transverse: "Transverse",
      flatrack_count: "Suggested Number of Flatracks",
      flatrack_gap: "Gap Between Flatracks",
    },

    common: {
      info: "Info",
      yes: "Yes",
      no: "No",
      ok: "OK",
      cancel: "Cancel",
      confirm: "Confirm",
      close: "Close",
      save: "Save",
      download: "Download",
      delete: "Delete",
      edit: "Edit",
      view: "View",
      loading: "Loading...",
      processing: "Processing...",
      please_wait: "Please wait...",
      save_changes: "Save Changes",
      saving: "Saving..."
    },

    transport_modes: {
      road: "Road",
      rail: "Rail",
      sea_a: "Sea_A (Wave<8m)",
      sea_b: "Sea_B (8m<Wave<12m)",
      sea_c: "Sea_C (Wave>12m)",
      road_sea_a: "Road+Sea_A",
      road_sea_b: "Road+Sea_B",
      road_sea_c: "Road+Sea_C",
      road_rail: "Road+Rail",
      road_rail_sea_a: "Road+Rail+Sea_A",
      road_rail_sea_b: "Road+Rail+Sea_B",
      road_rail_sea_c: "Road+Rail+Sea_C"
    },

    transport_unit_groups: {
      containers: "Containers",
      open_tops: "Open Tops",
      flatracks: "Flatracks",
      trailers: "Trailers",
      special: "Others / Special",
    },
    transport_units: {
      container_20: "20' Container",
      container_40: "40' Container",
      container_40_hc: "40' Container HC",
      container_45_hc: "45' Container HC",
      open_top_20: "20' Open Top",
      open_top_20_hc: "20' Open Top HC",
      open_top_40: "40' Open Top",
      open_top_40_hc: "40' Open Top HC",
      flatrack_20: "20' Flatrack",
      flatrack_40: "40' Flatrack",
      lowbed: "Lowbed",
      mafi: "Roll Trailer",
      vessel: "Vessel",
      breakbulk: "Break Bulk",
      truck_trailer_standard: "Truck Trailer (Standard)",
      truck_trailer_mega: "Truck Trailer (Mega / Jumbo)",
      open_top_container: "Open Top Container"
    },

    materials: {
      asm: "Anti Slip Mat (Static 0.8µ)",
      asm_06: "Anti Slip Mat (Static 0.6µ)",
      metal_metal: "Metal-Metal",
      metal_wood: "Metal-Wood",
      wood_wood: "Wood-Wood"
    },

    directions: {
      forward: "Against Forward Movement",
      backward: "Against Backward Movement",
      right: "Against Right Movement",
      left: "Against Left Movement"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "tonnes"
    },

    errors: {
      zero_input: "Please fill all required fields.",
      no_loop_rope: "No compatible loop lashing webbing found.",
      no_direct_rope: "No compatible direct lashing webbing found.",
      calculation_error: "Calculation Error: {error}",
      unexpected_error: "Unexpected error: {error}",
      diagram_error: "Diagram could not be generated: {error}",
      pdf_error: "PDF error: {error}",
      loop_config_error: "Loop configuration error: {error}",
      error_halat: "No direct lashing webbing selected.",
      invalid_transport_unit: "Invalid transport unit: {unit}. Please select a valid unit.",
      network_error: "Network error. Please check your internet connection.",
      file_not_found: "File not found.",
      invalid_cargo_dimensions: "WARNING: Selected transport unit is not suitable for the cargo",
      select_transport_mode: "Please select a transport mode",
      select_transport_unit: "Please select a transport unit",
      select_friction_surface: "Please select a friction surface",
      invalid_weight: "Please enter a valid weight",
      invalid_dimensions: "Please enter valid cargo dimensions",
      invalid_cog: "Please enter valid center of gravity coordinates",
      invalid_wooden_dunnage: "Please enter valid wooden dunnage dimensions",
      invalid_stopper: "Please enter valid stopper quantities",
      select_at_least_one_rope: "Please select lashings",
      select_rope_type: "Please select rope type",
      dimension_exceeded: "WARNING: Cargo {dims} exceeds the limits of the selected transport unit.",
      dim_width: "WIDTH",
      dim_length: "LENGTH",
      dim_height: "HEIGHT",
      cargo_width_greater_than_length: "Cargo width cannot be greater than length. Value automatically adjusted to match length.",
      and_conjunction: " and ",
      breakbulk_dim_overflow_width: "⚠️ Cargo width ({cargo_width}m) is greater than or equal to the selected flatrack length ({flatrack_length}m)! Calculation cannot proceed.",
      breakbulk_dim_overflow_length: "⚠️ Cargo length ({cargo_length}m) is greater than or equal to the selected flatrack length ({flatrack_length}m)! Calculation cannot proceed."
    },

    validation: {
      required_field: "This field is required",
      invalid_number: "Please enter a valid number",
      positive_number: "Please enter a positive number",
      min_value: "Minimum value: {min}",
      max_value: "Maximum value: {max}",
      out_of_range: "Value must be between {min} and {max}",
      invalid_email: "Please enter a valid email address",
      invalid_format: "Invalid format"
    },

    warnings: {
      weight_exceeded: "Warning: The maximum weight capacity for {unit} ({max_weight} MT) has been exceeded!",
      overweight_confirmation: "Entered cargo weight ({weight} MT) exceeds the maximum allowed weight ({max_weight} MT). Please confirm again to proceed.",
      oversized_width_loop_disabled: "Loop lashing disabled due to oversized load width.",
      oversized_width_side_wedges_disabled: "Side wedges disabled due to oversized load width.",
      high_cog: "Warning: High center of gravity increases tipping risk.",
      insufficient_lashing: "Warning: Insufficient lashing detected.",
      unsaved_changes: "You have unsaved changes. Do you want to continue?",
      flatrack_dunnage_required: "Wooden dunnage is recommended to restrict cargo sliding in forward-backward directions on Flatrack transport units. Do you want to continue the calculation without adding wooden dunnage?",
      terms_acceptance_required: "You must read and accept the Terms of Service to perform calculations. You can access the Terms of Service from your Profile page.",
      flatrack_count_low: "Attention: Selected flatrack count ({selected}) is lower than the recommended count ({recommended})!"
    },

    success: {
      pdf_created: "PDF created successfully!",
      calculation_complete: "Calculation completed!",
      data_saved: "Data saved successfully.",
      export_complete: "Export completed successfully."
    },

    table_columns: {
      direction: "Direction",
      sliding: "Sliding",
      tipping: "Tipping",
      friction: "Friction",
      wooden_dunnage: "Wooden\nDunnage",
      stopper: "Stopper",
      looplash: "LOOP\nLASHING",
      direct_lashing: "Direct Lashing",
      cordstrap_loop: "Cordstrap Loop",
      cordstrap_direct: "Cordstrap Direct",
      total_securing: "Total Securing",
      net_sliding: "Net Sliding",
      net_tipping: "Net Tipping",
      net_sliding_tooltip: "Negative values indicate safe securing.",
      net_tipping_tooltip: "Negative values indicate safe securing.",
      status: "Status",
      safety_factor: "Safety Factor"
    },

    results: {
      safe: "SAFE",
      unsafe: "UNSAFE",
      marginal: "MARGINAL",
      critical: "CRITICAL",
      acceptable: "ACCEPTABLE",
      status: "Status",
      safety_factor: "Safety Factor",
      summary: "Summary",
      details: "Details",
      notes_title: "Notes / Assumptions",
      notes_assumed: "",
      notes_cargo_unitized: "The cargo is considered to be a single item or consolidated and unitized into one cargo unit.",
      notes_lashing_above_cog: "Unless otherwise stated in the “Material Usage” table, it is assumed that direct lashing is applied above the center of gravity of the load.",
      notes_angle_range: "The acute angles between the direct lashings attached at an angle to the lashing points on the transport unit and the vector component of the direct lashings in the direction of the acting force must be between 30° and 60°.",
      notes_edge_protection: "Suitable edge and corner protection shall be provided where necessary to prevent damage to the cargo and/or the lashing materials.",
      notes_pretension_force: "When tensioning the web lashings, the applied pretension force shall not exceed 50% of the web lashing’s MSL value.",
      notes_strength_limitations: "It is assumed that the lashing points on the cargo have no strength limitations.",
      notes_distributed_uniformly: "It is assumed that the load is distributed uniformly, in accordance with the capacity criteria of the transport unit.",
      flatrack_no_dunnage_warning: "Note: Wooden dunnage is recommended to restrict cargo sliding in forward-backward directions on Flatrack transport units",
      table_tab: "Force Analysis",
      diagram_tab: "Lashing Diagram & 3D",
      pdf_reports_history: "My PDF Reports",
      fullscreen_zoom_tip: "Turn device horizontally for landscape inspection."
    },

    tooltips: {
      weight_info: "Enter total cargo weight in metric tons",
      cog_info: "COG stands for Center of Gravity. By default, it is assumed to be in the center of the cargo. However, in reality, the center of gravity can be in a different location. If the specific COG is known, its correct position can be entered here. Accurate COG positioning is critical, especially for tipping calculations.",
      friction_info: "Select material combination for friction coefficient",
      transport_mode_info: "Acceleration coefficients vary by transport mode",
      lashing_info: "Lashing forces prevent sliding and tipping"
    },
    legal: {
      tos_title: "Terms of Service",
      privacy_title: "Privacy Policy",
      sales_agreement_title: "Sales Agreement",
      legal_notice_title: "Legal Notice",
      cookie_policy_title: "Cookie Policy",
      last_updated: "Effective Date: 4 February 2026",
      return_home: "Back to Home",
      detailed_terms: [
        {
          title: "1. Formation of Agreement",
          content: [
            "These Terms of Service ('Terms') constitute a legally binding agreement between you ('User', 'Customer') and JADE CONSULTANCY AND TRADING LTD, a company operating under the laws of England and Wales ('Provider', 'we', 'us', 'our'), governing access to and use of the Lashing Calculator web application platform.",
            "By accessing or using the Platform, or by entering into a subscription or sales agreement for the Service, you agree to be bound by these Terms.",
            "Acceptance of a Sales Agreement constitutes automatic acceptance of these Terms.",
            "Document Version: v4.0. Effective Date: 1 March 2026."
          ]
        },
        {
          title: "2. Business Use Only",
          content: [
            "The Platform is provided strictly on a business-to-business (B2B) basis.",
            "You confirm that you are acting in the course of business and not as a consumer under any consumer protection legislation.",
            "The Platform is designed to assist with cargo securing and lashing calculations across sea, road, and rail transport modes, including multimodal combinations of the foregoing. The regulatory framework applicable to any given use of the Platform depends on the transport mode(s) involved and the jurisdictions of loading, transit, and unloading. Users are solely responsible for identifying and complying with all applicable transport mode-specific regulations."
          ]
        },
        {
          title: "3. Acceptable Use Policy",
          content: [
            "3.1 Prohibited Actions: You agree not to:",
            "a) Use the Platform for any illegal purpose or in violation of any local, state, national, or international law.",
            "b) Attempt to decipher, decompile, disassemble, or reverse engineer any of the software used to provide the Platform.",
            "c) Use any robot, spider, scraper, or other automated means to access the Platform for any purpose without our express written permission.",
            "d) Transmit any viruses, worms, defects, Trojan horses, or other items of a destructive nature.",
            "e) Attempt to interfere with, compromise the system integrity or security, or decipher any transmissions to or from the servers running the Platform.",
            "3.2 Authority Orders: Where the Provider receives a legally binding order from a competent EU Member State authority or other competent authority relating to the User's use of the Platform, the Provider will comply with such order and will notify the User to the extent permitted by law."
          ]
        },
        {
          title: "4. Subscription, Fees and Payment",
          content: [
            "4.1 Subscription Term: The subscription term is one (1) year from the activation date. No automatic renewal applies. Renewal requires a written request from the User, and full payment for the new subscription period.",
            "4.2 Fees and Currency: All fees are invoiced and payable in GBP (£) to the Provider’s GBP bank account. Fees are payable in advance.",
            "4.3 Expiry Notifications: The Platform may send reminder notifications one (1) month, one (1) week, and one (1) day before expiry. Such notifications are provided for convenience only and do not constitute renewal.",
            "4.4 Set-Off Waiver: The User shall pay all amounts due without any right of set-off, counterclaim, deduction, or withholding of any kind, except where required by law.",
            "4.5 Late Payment: Without prejudice to any other right or remedy, if the User fails to make payment by the due date, the Provider may: (a) charge interest at the statutory rate under the Late Payment of Commercial Debts (Interest) Act 1998; and (b) suspend access to the Platform until payment is received in full."
          ]
        },
        {
          title: "5. Cancellation and Termination",
          content: [
            "5.1 Termination by User: The User may cancel at any time. Access will continue until the end of the current subscription period. No refund applies except as expressly stated below.",
            "5.2 Termination by Provider: The Provider may terminate or suspend the Service for legitimate business, legal, technical, or security reasons. In cases other than material breach, security incidents, legal compulsion, or insolvency, the Provider will give not less than thirty (30) days' written notice before terminating the Service. Where termination is initiated by the Provider, the User shall be entitled to a pro-rated refund for the unused portion of the subscription period.",
            "5.3 Data Export on Termination: Upon termination or expiry of the subscription, the User shall have fourteen (14) days to export or download their Platform data before it is permanently deleted. The Provider shall provide reasonable assistance with data export upon written request submitted before the termination date."
          ]
        },
        {
          title: "6. Service Failure and Refunds",
          content: [
            "If the Platform becomes materially unusable for technical reasons not caused by the User for a continuous period of more than five (5) consecutive days, the User shall be entitled to a pro-rated refund, calculated on a daily basis against the annual subscription fee for unused days.",
            "Temporary outages, degraded performance, maintenance windows, or compatibility issues shall not constitute unusability.",
            "The Provider aims to maintain Platform availability of not less than 99% uptime measured on a rolling monthly basis, excluding scheduled maintenance. Scheduled maintenance will be notified with at least forty-eight (48) hours' notice where possible. The Provider does not guarantee any specific uptime level and this section does not create a binding SLA unless expressly incorporated into a signed Sales Agreement."
          ]
        },
        {
          title: "7. User Accounts and Licensing",
          content: [
            "7.1 Licence Grant: The Provider grants the User a limited, non-exclusive, non-transferable licence to access and use the Platform for internal business purposes only. No ownership rights are transferred.",
            "7.2 Single Device Policy: Each user account may be used on one device at a time, unless otherwise agreed in writing in an applicable Sales Agreement. Concurrent logins are prohibited. The Provider reserves the right to monitor usage patterns to detect misuse. Where misuse is reasonably suspected, the Provider may suspend access while investigating. Confirmed breaches may result in termination without refund.",
            "7.3 User Obligations: To generate reports, the User must provide: Company name, Company logo, Mobile number, Landline number, Email address. Valid company billing details must be provided for invoicing.",
            "7.4 Account Security: You are responsible for maintaining the confidentiality of your password and account details. You are fully responsible for all activities that occur under your account."
          ]
        },
        {
          title: "8. Important Safety Notice & Professional Disclaimer",
          content: [
            "CRITICAL NOTICE: The Platform is a Decision Support System only for cargo securing planning across sea, road, and rail transport modes, and multimodal combinations thereof.",
            "The Platform does not provide professional engineering, legal, or safety advice.",
            "The Platform is not certified, approved, or endorsed by any flag state, classification society, port authority, road transport authority, railway infrastructure manager, or regulatory body.",
            "All calculation results must be reviewed and approved by a qualified cargo professional or 'Competent Person' as defined under the applicable standard for the transport mode in question: IMO/ILO/UNECE CTU Code and IMO CSS Code (sea transport); EN 12195 / VDI 2700 and applicable national road transport authority guidance (road transport); UIC Loading Guidelines and applicable national railway infrastructure manager requirements (rail transport).",
            "Final responsibility for cargo securing rests solely with the responsible party under the applicable legal and regulatory framework for the transport mode in question: for sea transport, the carrier, ship owner, operator, and/or master under applicable international maritime law; for road transport, the carrier, vehicle operator, and/or driver under the CMR Convention and applicable national road transport regulations; for rail transport, the carrier and railway undertaking under the CIM/COTIF framework and applicable national rail regulations. In multimodal transport, each modal carrier bears responsibility for their respective segment.",
            "The Provider accepts no responsibility for decisions taken based on Platform outputs.",
            "For sea transport: Platform outputs do not constitute or satisfy any statutory cargo documentation obligation under SOLAS Chapter VI Regulation 5-2 or any equivalent national implementing legislation. The User is solely responsible for compliance with all statutory cargo documentation requirements.",
            "The Provider makes no warranty that use of the Platform satisfies any obligation that may arise under the User's Protection & Indemnity (P&I) Club rules or cover terms. Users must satisfy themselves as to their P&I obligations independently.",
            "The Platform does not constitute an approved procedure, instruction, or checklist for the purposes of any vessel's Safety Management System (SMS) under the ISM Code (SOLAS Chapter IX). Integration of Platform outputs into any operator's SMS procedures is solely the responsibility of that operator.",
            "Platform outputs have not been reviewed, approved, or endorsed by any IACS member classification society, including but not limited to Lloyd's Register, DNV, and Bureau Veritas. Results must be independently verified against any applicable classification society requirements before operational use.",
            "Platform outputs are not designed for, and should not be used as, documentation satisfying cargo securing obligations under the Hague-Visby Rules, the Hamburg Rules, the Rotterdam Rules, or any equivalent international carriage of goods convention. Documentary obligations under applicable conventions remain solely with the carrier.",
            "For road transport: Platform outputs must be validated against EN 12195 series (Load restraint assemblies on road vehicles), VDI 2700 (Securing of loads on road vehicles), and applicable national road authority requirements. UK operators must additionally comply with the DfT Load Securing: Code of Practice. The User is solely responsible for all road transport regulatory compliance.",
            "For rail transport: Platform outputs must be validated against applicable UIC Loading Guidelines, TSI (Technical Specifications for Interoperability) requirements, and national railway infrastructure manager requirements before operational use. The User is solely responsible for all rail regulatory compliance.",
            "Individual port authorities, road transport authorities, and railway infrastructure managers may impose additional or more stringent cargo securing requirements beyond international standards. Compliance with all local authority regulations is the sole responsibility of the User.",
            "Dangerous Goods Notice: The Platform is designed for general cargo securing planning only. It has not been designed or validated for use with dangerous goods (DG) cargo subject to ADR (road), the IMDG Code (sea), RID (rail), or equivalent international hazardous materials regulations. Users handling dangerous goods must obtain specialist dangerous goods securing advice from a qualified Dangerous Goods Safety Adviser (DGSA) or equivalent specialist before any operational use."
          ]
        },
        {
          title: "9. Intellectual Property",
          content: [
            "The Platform, including its source code, algorithms, calculations, structure, and design, is protected by intellectual property laws.",
            "Reverse engineering, extraction, or reproduction is strictly prohibited.",
            "PDF reports are valid only for the specific cargo configuration described and may not be reused.",
            "Accurate data input is the sole responsibility of the User.",
            "Any feedback relating to the Platform's general functionality (not including any User's proprietary data, methods, or trade secrets) provided voluntarily without expectation of compensation may be used by the Provider to improve the Platform. This does not grant the Provider any rights over the User's proprietary information, data, or industrial processes."
          ]
        },
        {
          title: "10. Data Sources, Reports and User Responsibility",
          content: [
            "Calculation accuracy depends entirely on User-provided parameters.",
            "Data sources include: MSL values (Manufacturer specifications, no independent verification), Friction coefficients (Industry standards, actual conditions may vary), Lashing points (Unit specifications, physical integrity is User responsibility).",
            "Applicable regulatory frameworks by transport mode: CTU Code (IMO/ILO/UNECE — multimodal, applicable to all modes), CSS Code (IMO Cargo Securing Manual — sea transport only), VDI 2700 / EN 12195-1 to EN 12195-4 (road transport, European standard for load restraint assemblies on road vehicles), UK DfT Load Securing: Code of Practice (road transport, UK jurisdiction), UIC Loading Guidelines / TSI (Technical Specifications for Interoperability) (rail transport), CIM/COTIF Uniform Rules (international rail carriage contracts).",
            "Users are solely responsible for identifying and applying the correct regulatory standard for their specific transport mode and jurisdiction. Platform outputs must be validated against the applicable standard before operational use.",
            "In multimodal transport chains, Users must ensure that cargo securing records meet the documentation requirements of any applicable Multimodal Transport Document (MTD) framework. Platform-generated reports are not designed to substitute for MTD documentation obligations under the UNCTAD/ICC Rules for Multimodal Transport Documents or equivalent frameworks.",
            "The User is solely responsible for: Verifying all input data, Ensuring correct equipment selection, Confirming real-world applicability, Physically validating lashing arrangements, Engaging qualified professionals prior to implementation.",
            "Reports are generated based on inputs at the time of creation. The Provider has no obligation to retain calculation data or reports, and the User is responsible for archiving."
          ]
        },
        {
          title: "11. Technical Accuracy Disclaimer",
          content: [
            "The Platform is provided using reasonable skill and care consistent with generally accepted industry standards for SaaS services. While reasonable efforts are made to ensure accuracy, the Platform may contain technical or typographical errors. No warranty is given that materials are complete, current, or error-free.",
            "Third-party data and standards may change over time. Updates do not apply retroactively to previously generated reports.",
            "THE PLATFORM IS PROVIDED 'AS IS' AND 'AS AVAILABLE', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. THE PROVIDER DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE FROM DEFECTS.",
            "Any performance metrics shown in demonstrations, documentation, or marketing materials are indicative only and are not contractually binding representations.",
            "The Provider acknowledges that Directive (EU) 2024/2853 on product liability may apply in certain EU Member States from its national implementation date. The limitations on liability set out in these Terms apply to the maximum extent permitted by applicable mandatory law, and nothing in these Terms shall be construed to exclude liability where prohibited by the Product Liability Directive as implemented in the User's jurisdiction.",
            "For the avoidance of doubt, any output generated by the Platform, including any document described as a 'Lashing Plan' or 'Lashing Calculation', constitutes a Decision Support Output only and does not constitute a formal lashing plan, lashing manual, cargo stowage plan, or securing manual for regulatory, insurance, or documentary purposes under any applicable law, transport convention, or port regulation."
          ]
        },
        {
          title: "12. Limitation of Liability",
          content: [
            "To the maximum extent permitted by English law: The Provider shall not be liable for any loss or damage arising from use of, or reliance on, the Platform, including: Cargo loss or damage, Vessel damage, Vehicle damage, Rolling stock damage, Personal injury or death (to the maximum extent permitted by law), Business interruption, Loss of profits, goodwill, or opportunity, Consequential, incidental, or punitive damages.",
            "To the maximum extent permitted by applicable law, the Provider excludes all liability arising from negligence, misrepresentation, or breach of statutory duty, except where such exclusion is prohibited by law.",
            "Liability Cap: Unless otherwise agreed in writing in a signed Sales Agreement, the Provider’s total cumulative liability shall not exceed the fees paid by the User in the twelve (12) months preceding the claim.",
            "Nothing excludes liability for fraud or death caused by negligence where prohibited by law."
          ]
        },
        {
          title: "13. Indemnification",
          content: [
            "The User agrees to indemnify and hold harmless the Provider from any third-party claims arising from: Breach of these Terms, Inaccurate or incomplete input data, Use of incorrect or outdated specifications, Failure to obtain professional verification, Physical execution of lashing arrangements, Misuse of credentials or unauthorised access caused by the User.",
            "The User's indemnification obligations under this clause shall be subject to the same monetary cap as set out in Art. 12 of these Terms, unless the claim arises from the User's wilful misconduct or fraud."
          ]
        },
        {
          title: "14. Export Control & Sanctions Compliance",
          content: [
            "The User represents and warrants that it is not subject to any sanctions imposed by the European Union, United Kingdom, United Nations, or other applicable authorities.",
            "The User shall not use the Platform in violation of applicable export control or sanctions laws and shall be solely responsible for ensuring lawful use in all jurisdictions."
          ]
        },
        {
          title: "15. Cybersecurity Disclaimer",
          content: [
            "The Provider implements reasonable technical and organisational security measures in line with industry practice, taking into account the nature of the data processed. However, the Provider does not warrant that the Platform will be entirely free from cyber incidents, malware, ransomware, unauthorised access, or security breaches, including those arising from third-party systems or networks.",
            "In the event of a cyber security incident that materially affects the availability or integrity of the Platform, the Provider will notify affected Users within forty-eight (48) hours of the Provider becoming aware of such incident, and will provide reasonable updates regarding remediation.",
            "The Provider maintains an information security programme aligned with recognised industry frameworks. Users who are subject to Directive (EU) 2022/2065 (NIS2) or equivalent national cybersecurity legislation are responsible for assessing whether the Provider's security measures meet their specific regulatory obligations. The Provider will provide reasonable information about its security practices upon written request to support such assessments."
          ]
        },
        {
          title: "16. Insurance Disclosure",
          content: [
            "The Provider maintains professional indemnity and cyber insurance appropriate to its business activities as a software provider. This insurance covers the Provider's general business operations and does not extend to cover cargo loss, maritime incidents, road transport incidents, rail transport incidents, personal injury, or any claim excluded by the liability limitations in Art. 12. The User should not rely on the Provider's insurance as a substitute for the User's own insurance arrangements, including any cargo liability insurance, CMR insurance, CIM/COTIF rail liability insurance, or other transport mode-specific coverage the User may require."
          ]
        },
        {
          title: "17. No Reliance",
          content: [
            "The User acknowledges that it has not relied on any statement, representation, or warranty not expressly set out in this Agreement.",
            "The Platform is not relied upon as a substitute for professional judgment, certification, regulatory approval, or legal compliance."
          ]
        },
        {
          title: "18. Professional User Assumption",
          content: [
            "The Platform is intended for use by experienced maritime, road transport, railway, and logistics professionals who are familiar with the applicable cargo securing standards and regulatory requirements for their respective transport modes. The Provider assumes no duty to explain the applicable principles, safety obligations, or regulatory requirements of any transport mode to the User."
          ]
        },
        {
          title: "19. Safety-Critical Use Disclaimer",
          content: [
            "The Platform is not designed or intended for real-time operational decision-making during active cargo operations or emergency situations."
          ]
        },
        {
          title: "20. No Third-Party Rights",
          content: [
            "No person other than the parties to this Agreement shall have any rights under the Contracts (Rights of Third Parties) Act 1999 to enforce any term of this Agreement."
          ]
        },
        {
          title: "21. Publicity",
          content: [
            "The User grants the Provider the right to use the User's Company name and logo as a reference for marketing or promotional purposes on the Provider's website and in other public or private communications.",
            "The User may opt out of this permission at any time by notifying the Provider in writing at info@lashingplan.com."
          ]
        },
        {
          title: "22. Changes to Terms",
          content: [
            "We reserve the right to modify these Terms at any time. For material changes, we will provide at least fourteen (14) days' advance notice via the Platform or by email to the registered account email address before such changes take effect.",
            "Material changes will require positive acknowledgement via the Platform before continued access is granted. Continued use of the Platform after non-material changes take effect constitutes acceptance of those changes."
          ]
        },
        {
          title: "23. Dispute Resolution",
          content: [
            "Before initiating court proceedings in relation to any dispute arising out of or in connection with these Terms, the parties agree to attempt to resolve the dispute through good-faith negotiations for a period of thirty (30) days from the date either party gives written notice of the dispute to the other.",
            "Nothing in this clause prevents either party from seeking urgent interlocutory or injunctive relief from a court of competent jurisdiction.",
            "For the purposes of this clause, 'written notice' includes email to the registered account email address (for the User) or to info@lashingplan.com (for the Provider), and shall be deemed received on the next business day following transmission."
          ]
        },
        {
          title: "24. Force Majeure",
          content: [
            "The Provider shall not be liable for failure or delay in performance caused by events beyond its reasonable control, including acts of God, war, cyber incidents, cloud service failures, labour disputes, or governmental actions.",
            "If a Force Majeure event continues for more than sixty (60) consecutive days, either party may terminate this Agreement immediately on written notice, without liability to the other party, subject to a pro-rated refund for any prepaid unused subscription period."
          ]
        },
        {
          title: "25. Governing Law, EU Mandatory Law & Jurisdiction",
          content: [
            "These Terms are governed by English Law.",
            "The courts of England and Wales shall have non-exclusive jurisdiction. Where mandatory applicable law prevents a party from submitting to the jurisdiction of the courts of England and Wales, the parties agree to submit to the non-exclusive jurisdiction of those courts, without prejudice to the rights of either party to bring proceedings in any other competent jurisdiction.",
            "Where a User is established in an EU Member State, mandatory provisions of the law of that Member State shall apply to the extent required by Regulation (EC) No 593/2008 (Rome I) Article 9, including in particular any provisions relating to limitation of liability that cannot be excluded under local law. In all other respects, English law shall govern."
          ]
        },
        {
          title: "26. Language Precedence",
          content: [
            "This Agreement is drafted in English. In the event of any translation, the English version shall prevail."
          ]
        },
        {
          title: "27. General",
          content: [
            "If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall remain in full force.",
            "The Provider may assign these Terms as part of a merger, sale, or business transfer. The User may not assign, transfer, or sublicense any rights or obligations under these Terms without the prior written consent of the Provider.",
            "The following clauses shall survive termination or expiry of these Terms for any reason: Art. 9 (Intellectual Property), Art. 12 (Limitation of Liability), Art. 13 (Indemnification), Art. 14 (Export Control & Sanctions), Art. 25 (Governing Law), Art. 27 (General), and Schedule 1 (Data Processing Addendum).",
            "Failure or delay by either party in exercising any right or remedy shall not constitute a waiver of that right or remedy and shall not affect the right of that party to exercise that right or remedy in the future.",
            "These Terms constitute the entire agreement between the parties relating to the Platform and supersede all prior communications, representations, or agreements, whether oral or written, relating to its subject matter.",
            "The Provider reserves the right, upon reasonable notice of not less than five (5) business days, to audit the User's use of the Platform to verify compliance with these Terms. The User shall cooperate reasonably with any such audit."
          ]
        },
        {
          title: "28. Data Protection & Privacy",
          content: [
            "28.1 In connection with the provision of the Platform, the User acts as the Data Controller and the Provider acts as the Data Processor within the meaning of the UK GDPR, EU GDPR, and the Data Protection Act 2018.",
            "28.2 The Provider shall process Personal Data solely on documented instructions from the User and in accordance with Applicable Data Protection Laws.",
            "28.3 The Data Processing Addendum set out in Schedule 1 forms an integral part of these Terms and governs the processing of Personal Data by the Provider on behalf of the User.",
            "28.4 In the event of any conflict between these Terms and the Data Processing Addendum, the Data Processing Addendum shall prevail in relation to data protection matters."
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. Definitions",
            "For the purposes of this Data Processing Addendum (“DPA”), the terms “Personal Data”, “Processing”, “Controller”, “Processor”, and “Supervisory Authority” shall have the meanings given in the UK GDPR and EU GDPR.",
            "",
            "2. Scope & Roles",
            "2.1 The User is the Controller of Personal Data processed in connection with use of the Platform.",
            "2.2 The Provider acts solely as a Processor and shall not process Personal Data for any purpose other than providing the Platform.",
            "",
            "3. Nature & Purpose of Processing",
            "Purpose: Provision of SaaS services, account administration, technical support, and generation of reports.",
            "Categories of Data Subjects: Authorised users, employees, contractors.",
            "Types of Personal Data: Professional contact details (name, email, phone), company identification details.",
            "Special Categories: None intended.",
            "",
            "4. Processor Obligations",
            "The Provider shall:",
            "a) Process Personal Data only on documented instructions of the User",
            "b) Ensure personnel authorised to process Personal Data are subject to confidentiality obligations",
            "c) Implement appropriate technical and organisational measures to protect Personal Data",
            "d) Assist the User, where reasonably requested, with data subject rights requests",
            "e) Notify the User without undue delay upon becoming aware of a Personal Data Breach",
            "f) Delete or return Personal Data upon termination of the Services, unless retention is required by law",
            "g) Maintain records of all categories of processing activities carried out on behalf of the User in accordance with Article 30(2) GDPR, and make such records available to the User or competent supervisory authority upon written request.",
            "",
            "5. Sub-Processors",
            "5.1 The User authorises the Provider to engage sub-processors for hosting, infrastructure, and support services.",
            "5.2 The Provider shall ensure that sub-processors are subject to data protection obligations no less protective than those set out in this DPA.",
            "5.3 The Provider shall notify the User of any intended addition or replacement of sub-processors with reasonable prior notice, giving the User a reasonable opportunity to object on reasonable grounds. If the User objects and the parties cannot agree, either party may terminate the Agreement on reasonable written notice.",
            "",
            "6. International Transfers",
            "Where Personal Data is transferred outside the UK or EEA, the Provider shall ensure appropriate safeguards are in place, including UK International Data Transfer Agreements or EU Standard Contractual Clauses, as applicable.",
            "",
            "7. Security Measures",
            "The Provider shall maintain reasonable technical and organisational security measures appropriate to the nature of the data and risks involved, including access controls, encryption where appropriate, and incident response procedures.",
            "",
            "8. Audits",
            "Upon reasonable written request, and no more than once per year, the Provider shall make available information necessary to demonstrate compliance with this DPA, subject to confidentiality and security requirements.",
            "",
            "9. Liability",
            "Liability arising from this DPA shall be subject to the limitations and exclusions of liability set out in the Terms of Service.",
            "",
            "10. Governing Law",
            "This DPA shall be governed by and construed in accordance with the laws of England and Wales."
          ]
        }
      ],
      sections: {
        acceptance: {
          title: "1. Formation of Agreement & 2. Business Use",
          content: "These Terms of Service constitute a legally binding agreement between you and the Provider governing access to the Lashing Calculator. By accessing the Platform, you irrevocably agree to be bound by these Terms. Acceptance of a Sales Agreement constitutes automatic acceptance. The Platform is provided strictly on a B2B basis. You confirm that you are acting in the course of business and not as a consumer."
        },
        payment_terms: {
          title: "3. Subscription, Fees and Payment",
          content: "The subscription term is 1 year. No automatic renewal applies. Renewal requires a written request and full payment. Fees are invoiced and payable in GBP (£) to the Provider's GBP bank account in advance. Expiry notifications (1 month, 1 week, 1 day) are for convenience only."
        },
        termination: {
          title: "4. Cancellation & 5. Refunds",
          content: "User may cancel at any time; access continues to end of term with no refund. Provider may terminate for any reason; if Provider terminates, User gets pro-rated refund. If Platform is materially unusable for >5 days (technical reasons), User gets pro-rated refund. Temporary outages do not constitute unusability."
        },
        disclaimer: {
          title: "7. Important Safety Notice & Professional Disclaimer",
          important: "CRITICAL NOTICE: The Platform is a Decision Support System only.",
          content: "It does not provide professional engineering or safety advice. Results must be reviewed by a qualified cargo surveyor or 'Competent Person' (IMO/ILO/UNECE CTU Code). Final responsibility remains with the carrier/master. Provider accepts no responsibility for decisions based on outputs."
        },
        license: {
          title: "6. User Licensing & 8. Intellectual Property",
          subtitle: "Provider grants a limited, non-exclusive, non-transferable licence for internal business use. 1 account = 1 device. Concurrent logins prohibited.",
          items: [
            "Source code, algorithms, and design are protected by IP laws.",
            "Reverse engineering is strictly prohibited.",
            "PDF reports are valid only for the specific cargo configuration.",
            "Accurate data input is the User's sole responsibility."
          ]
        },
        liability: {
          title: "11. Limitation of Liability",
          content: "Provider is not liable for cargo loss, vessel damage, personal injury, or business interruption. Liability is excluded for negligence to the fullest extent permitted.",
          cap_title: "Liability Cap",
          cap_content: "Total cumulative liability shall not exceed fees paid by the User in the 12 months preceding the claim.",
          not_responsible: "Provider is not responsible for:",
          not_responsible_items: [
            "Loss arising from use of or reliance on Platform",
            "Consequential, incidental, or punitive damages",
            "Cargo accidents or structural failures"
          ]
        },
        third_party: {
          title: "Data Sources & Third Party Standards",
          content: "Data sources include Manufacturer specifications (MSL), Industry standards (Friction), and Unit specifications. Third-party standards may change over time.",
          sub_content: "Updates do not apply retroactively."
        },
        accuracy: {
          title: "10. Technical Accuracy",
          content: "While reasonable efforts are made, the Platform may contain errors. No warranty is given that materials are complete, current, or error-free."
        },
        source_responsibility: {
          title: "9. User Responsibility",
          important: "Calculation accuracy depends entirely on User-provided parameters.",
          utilize_content: "The User is solely responsible for:",
          data_sources: [
            { title: "Verification", desc: "Verifying all input data" },
            { title: "Equipment", desc: "Ensuring correct equipment selection" },
            { title: "Validation", desc: "Physically validating lashing arrangements" },
            { title: "Professional", desc: "Engaging qualified professionals" }
          ],
          user_responsible_items: [
            "Verifying inputs",
            "Equipment selection",
            "Real-world applicability",
            "Physical validation"
          ]
        },
        verification: {
          title: "13. Export Control & 14. Force Majeure",
          content: "User represents they are not subject to sanctions (UK/EU/UN). Provider not liable for Force Majeure events (war, cyber incidents, etc)."
        },
        indemnification: {
          title: "12. Indemnification",
          content: "User agrees to indemnify Provider from third-party claims arising from:",
          items: [
            "Breach of Terms",
            "Inaccurate input data",
            "Failure to obtain professional verification",
            "Physical execution of lashing",
            "Misuse of credentials"
          ]
        },
        governing_law: {
          title: "15. Governing Law & 17. General",
          content: "Governing Law: English Law. Exclusive Jurisdiction: Courts of England and Wales. Mandatory EU law applies where required. English language prevails. If any provision is invalid, others remain. Clauses regarding liability/indemnity survive termination."
        }
      },

      detailed_privacy: [
        {
          title: "1. Introduction",
          content: [
            "This Privacy Policy explains how JADE CONSULTANCY AND TRADING LTD, a company operating under the laws of England and Wales ('Provider', 'we', 'us', 'our'), collects, uses, stores, and protects personal data when you use the Lashing Calculator web application ('Platform').",
            "We are committed to protecting personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.",
            "JADE CONSULTANCY AND TRADING LTD is a registered data controller with the UK Information Commissioner's Office (ICO) under registration number 00013348469 and maintains cyber security standards with certificate CSN1184299.",
            "Our registered office is at: JADE CONSULTANCY AND TRADING LTD, C/O IDA&CO BUILDING 3 CHISWICK BUSINESS, 566 CHISWICK HIGH ROAD, W4 5YA, LONDON, UK."
          ]
        },
        {
          title: "2. Data Controller",
          content: [
            "For the purposes of applicable data protection law, the Provider acts as the data controller in relation to personal data processed through the Platform.",
            "Where personal data of EU data subjects is processed, the EU General Data Protection Regulation (Regulation (EU) 2016/679) also applies, and data subjects located in EU Member States retain all rights conferred by the EU GDPR, including the right to lodge a complaint with their local EU supervisory authority."
          ]
        },
        {
          title: "3. Information We Collect",
          content: [
            "3.1 Account and Contact Data: Name, Company name, Business email address, Telephone numbers, Login credentials, Billing and invoicing details. This data is used strictly for account administration, authentication, billing, and service communications.",
            "3.2 Industrial and Operational Data: Cargo parameters and configurations, Lashing calculations and report inputs. This data does not typically constitute personal data within the meaning of GDPR Article 4(1). However, where such data is associated with an identified or identifiable natural person, it will be treated as personal data and processed accordingly. It is not analysed for commercial resale, profiling, or marketing purposes.",
            "3.3 Technical Data: IP address, Browser type, Device and session information, Access logs. This data is collected for security, performance monitoring, and misuse detection."
          ]
        },
        {
          title: "4. Legal Basis for Processing",
          content: [
            "We process personal data on the following lawful bases under Article 6 UK GDPR:",
            "Contractual necessity – to provide and operate the Platform",
            "Legitimate interests – to ensure security, prevent misuse, and improve service performance. We conduct a three-part test to ensure our legitimate interests do not override your rights: (1) Purpose test (is there a legitimate interest?), (2) Necessity test (is the processing necessary for that purpose?), and (3) Balancing test (do the individual’s interests override the legitimate interest?).",
            "Legal obligation – to comply with accounting, tax, and regulatory requirements",
            "Consent, where explicitly obtained (e.g. optional communications)"
          ]
        },
        {
          title: "5. How We Use Your Information",
          content: [
            "We use collected data to:",
            "Provide, operate, and maintain the Platform",
            "Process calculations and generate professional reports",
            "Manage subscriptions, billing, and customer support",
            "Ensure alignment with international safety frameworks (CTU Code / CSS Code)",
            "Monitor system security and prevent unauthorised access",
            "Improve and develop Platform functionality",
            "We do not sell personal data to third parties."
          ]
        },
        {
          title: "6. Data Sharing and Third Parties",
          content: [
            "Kişisel veriler yalnızca şunlarla paylaşılabilir:",
            "Hosting and cloud infrastructure providers",
            "Payment and invoicing service providers",
            "Professional advisers (legal, accounting, insurance)",
            "Authorities where required by law",
            "All third parties are subject to appropriate confidentiality and data protection obligations.",
            "A list of our current sub-processors is available upon written request."
          ]
        },
        {
          title: "7. International Data Transfers",
          content: [
            "Where personal data is transferred outside the UK or EEA, we ensure appropriate safeguards are in place, including:",
            "Adequacy decisions",
            "Standard Contractual Clauses (SCCs)",
            "Equivalent lawful transfer mechanisms"
          ]
        },
        {
          title: "8. Data Retention",
          content: [
            "We retain personal data only for as long as necessary:",
            "Account and billing data: retained for 7 years from the end of the contract (in accordance with HMRC requirements and applicable accounting legislation)",
            "Technical logs (IP addresses, access records): retained for 12 months for security and audit purposes",
            "Industrial calculation data and report inputs: deleted within 90 days of contract termination, unless archived by the User prior to termination",
            "Users remain responsible for archiving their own reports and calculation outputs."
          ]
        },
        {
          title: "9. Data Security",
          content: [
            "We implement reasonable technical and organisational measures to protect personal data against unauthorised access, loss, alteration, or disclosure, including access controls, encryption, and monitoring.",
            "However, no system can be guaranteed to be completely secure.",
            "In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will notify the Information Commissioner's Office (ICO) within 72 hours of becoming aware of the breach and, where required, will also notify affected individuals without undue delay.",
            "Where required under Article 35 UK/EU GDPR, we conduct Data Protection Impact Assessments (DPIAs) prior to implementing new processing activities that may result in a high risk to the rights and freedoms of individuals. Records of completed DPIAs are retained internally and are available to competent supervisory authorities upon request."
          ]
        },
        {
          title: "10. Your Rights",
          content: [
            "Under UK GDPR, you have the right to:",
            "Access your personal data",
            "Rectify inaccurate data",
            "Request erasure (subject to legal obligations)",
            "Restrict or object to processing",
            "Data portability",
            "Withdraw consent where applicable",
            "Requests can be made using the contact details below.",
            "We will respond to all legitimate requests within one (1) calendar month of receipt. We may extend this by a further two (2) months in exceptional circumstances and will notify you of any extension."
          ]
        },
        {
          title: "11. Complaints",
          content: [
            "If you believe your data protection rights have been infringed, you have the right to lodge a complaint with the UK Information Commissioner’s Office (ICO)."
          ]
        },
        {
          title: "12. Changes to This Policy",
          content: [
            "We may update this Privacy Policy from time to time. Updated versions will be made available on the Platform with a revised effective date.",
            "We will notify you by email for changes that materially affect your rights under this Policy."
          ]
        },
        {
          title: "13. Children's Privacy",
          content: [
            "Our Service does not address anyone under the age of 18 ('Children').",
            "We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers."
          ]
        },
        {
          title: "14. Contact Us",
          content: [
            "For privacy or data protection enquiries:",
            "Email: info@lashingplan.com",
            "Postal Address: JADE CONSULTANCY AND TRADING LTD, C/O IDA&CO BUILDING 3 CHISWICK BUSINESS, 566 CHISWICK HIGH ROAD, W4 5YA, LONDON, UK."
          ]
        },
        {
          title: "15. Marketing and Communications",
          content: [
            "We will only send marketing material of our services by email to subscribers who have provided consent or during a transaction.",
            "All marketing emails include an 'Unsubscribe' link, and opt-out requests are processed within 48 hours."
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "Data Protection & Privacy",
          content: "We are committed to protecting your privacy. This policy explains how your information is collected, used, and protected according to global standards."
        },
        collection: {
          title: "Information We Collect",
          personal_title: "Account Data",
          personal_desc: "Basic professional info used for account management and technical reporting.",
          calc_title: "Industrial Data",
          calc_desc: "Cargo parameters and configurations. This data is treated as 'Confidential Industrial Information'."
        },
        usage: {
          title: "How We Use Information",
          intro: "We use collected information in the following ways:",
          items: [
            "To provide, operate, and maintain the secure service",
            "To process calculations and generate professional PDF reports",
            "To ensure compliance with international safety regulations (CTU/CSS)",
            "To improve and expand service capabilities"
          ]
        },
        compliance: {
          title: "Global Compliance",
          content: "We take reasonable steps to ensure you have control over your data in accordance with UK GDPR and Data Protection Act 2018.",
          note: "Note: we do not sell your personal data to third parties."
        },
        retention: {
          title: "Data Retention",
          content: "We retain your personal info only as long as necessary for the service and as required by legal obligations."
        },
        contact: {
          title: "Contact Us",
          content: "For questions about data rights or privacy:",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. Introduction",
          content: [
            "This Cookie Policy explains how JADE CONSULTANCY AND TRADING LTD (“we”, “us”, “our”) uses cookies and similar technologies on the Lashing Calculator website and web application (“Platform”).",
            "This policy should be read together with our Privacy Policy."
          ]
        },
        {
          title: "2. What Are Cookies?",
          content: [
            "Cookies are small text files stored on your device when you visit a website. They are widely used to enable website functionality, enhance user experience, and provide information to website operators."
          ]
        },
        {
          title: "3. Legal Framework",
          content: [
            "We use cookies in accordance with:",
            "The UK Privacy and Electronic Communications Regulations (PECR)",
            "The EU ePrivacy Directive",
            "The UK General Data Protection Regulation (UK GDPR)",
            "Except for strictly necessary cookies, cookies are used only with your prior consent."
          ]
        },
        {
          title: "4. Types of Cookies We Use",
          content: [
            "4.1 Strictly Necessary Cookies",
            "These cookies are essential for the operation of the Platform and enable core functionality such as:",
            "User authentication",
            "Session management",
            "Security and fraud prevention",
            "These cookies do not require consent and cannot be disabled through our cookie settings.",
            "Legal basis: Strictly necessary cookies are exempt from the consent requirement under PECR Regulation 6(4). No consent is required.",
            "",
            "4.2 Analytics Cookies",
            "With your consent, we may use Google Analytics or similar tools to understand how visitors interact with our Platform.",
            "We receive anonymized, aggregated data that helps us measure performance without identifying you.",
            "Legal basis: Consent under UK GDPR and ePrivacy regulations."
          ]
        },
        {
          title: "5. Cookie Inventory",
          content: [
            "The following cookies are currently in use on the Platform:",
            "| Name | Type | Purpose | Duration | Legal Basis |",
            "|:---|:---|:---|:---|:---|",
            "| session | Strictly Necessary | User authentication and session management | Session (deleted on browser close) | PECR Reg. 6(4) — exempt |",
            "| csrf_token | Strictly Necessary | Security: Cross-Site Request Forgery prevention | Session | PECR Reg. 6(4) — exempt |",
            "| auth_token | Strictly Necessary | Maintains authenticated user state | 30 days | PECR Reg. 6(4) — exempt |",
            "We do not currently use any third-party, analytics, advertising, or tracking cookies."
          ]
        },
        {
          title: "6. Cookie Duration",
          content: [
            "Cookies may be:",
            "Session cookies, which expire when you close your browser",
            "Persistent cookies, which remain on your device for a defined period or until deleted",
            "Specific cookie lifetimes depend on their purpose and provider."
          ]
        },
        {
          title: "7. Managing Your Cookie Preferences",
          content: [
            "You can manage or withdraw your cookie consent at any time by:",
            "Using the cookie settings or consent banner available on the Platform",
            "Adjusting your browser settings to block or delete cookies",
            "Please note that disabling certain cookies may affect the functionality of the Platform."
          ]
        },
        {
          title: "8. Changes to This Cookie Policy",
          content: [
            "We may update this Cookie Policy from time to time. Any changes will be published on the Platform with an updated effective date."
          ]
        },
        {
          title: "9. Contact Us",
          content: [
            "If you have any questions about our use of cookies, please contact us at:",
            "Email: info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "Cookie Policy",
        last_updated: "Effective Date: February 4, 2026",
        intro_title: "Introduction",
        intro_content: "This Cookie Policy explains how cookies are used when you visit the Lashing Calculator website.",
        types_title: "Types of Cookies We Use",
        essential_title: "Essential Cookies",
        essential_desc: "These cookies are strictly necessary for the website to function properly and cannot be switched off in our systems.",
        analytics_title: "Analytics Cookies",
        analytics_desc: "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.",
        manage_title: "Managing Your Cookie Preferences",
        manage_content: "You can manage your cookie preferences at any time through your browser settings.",
        contact_title: "Contact Us",
        contact_text: "If you have any questions about our cookie policy, please contact us."
      },
      sales_agreement: {
        title: "Sales Agreement",
        content: "This sales agreement outlines the terms and conditions for the provision and use of Lashing Calculator services. The purchase of our services signifies your acceptance of all terms within this agreement."
      },
      legal_notice: {
        title: "Legal Notice",
        content: "The information on this website is for general information purposes only. While Lashing Calculator endeavors to keep the information up to date and correct, we make no representations or warranties of any kind."
      },
      footer_note: "By using the platform, you confirm that you have read and accept the safety notices, technical risks, and legal terms, including the requirement for professional verification of all results."
    }
  },

  // ===================================================================
  // ESPAÑOL
  // ===================================================================
  "Español": {
    code: "es",
    name: "Español",
    flag: "Spain",

    hub: {
      nav: {
        products: "Productos",
        insights: "Perspectivas del Mercado",
        resources: "Recursos",
        go_to_app: "Ir a la Aplicación"
      },
      hero: {
        badge: "CENTRO DE LOGÍSTICA DE CARGA DE ÚLTIMA GENERACIÓN",
        status: "Estado del Sistema: Óptimo",
        title_main: "Resistencia Calculada",
        title_italic: "seguridad absoluta",
        title_1: "Planificación de",
        title_2: "Máxima Seguridad.",
        description: "LashingPlan es un ecosistema central para herramientas profesionales de trincaje de carga, calculadas con precisión y conformes a los estándares internacionales.",
        explore: "Explorar Productos",
        launch_product: "INICIAR PRODUCTO",
        read_blog: "Leer Blog",
        portal_access: "ACCESO AL PORTAL V2.0",
        stability_index: "ÍNDICE DE ESTABILIDAD",
        archives: "ARCHIVOS",
        archives_subtitle: "Manténgase a la vanguardia con estándares de seguridad profesional y tendencias logísticas."
      },
      footer: {
        tagline: "Un producto de excelencia técnica. Diseñado para quienes exigen precisión absoluta en la seguridad marítima.",
        systems: "SISTEMAS",
        resources: "RECURSOS",
        legal: "LEGAL",
        privacy: "Privacidad",
        compliance: "Cumplimiento",
        terms: "Términos",
        status_all: "LASHING PLAN GLOBAL // TODOS LOS SISTEMAS OPERATIVOS",
        follow: "Síguenos"
      },
      products: {
        title: "Nuestros Productos",
        subtitle: "Herramientas profesionales diseñadas para el cumplimiento del transporte global y la seguridad de la carga.",
        context: "01 / Productos",
        calculator: {
          title: "Calculadora de Trincaje",
          description: "El motor estándar de la industria para calcular los requisitos de trincaje de carga por carretera, mar y ferrocarril.",
          button: "Iniciar Aplicación"
        },
        inspection: {
          title: "Inspection Pro",
          description: "Herramienta de inspección digital con soporte fotográfico y creación instantánea de certificados para estudios de campo.",
          status: "PRÓXIMAMENTE"
        },
        risk: {
          title: "Asesor de Riesgos",
          description: "Sistema de análisis de estabilidad y evaluación de riesgos de envío impulsado por IA.",
          status: "PRÓXIMAMENTE"
        },
        stats: {
          calculations: "CÁLCULOS",
          countries: "PAÍSES",
          compliance: "CUMPLIMIENTO",
          precision: "PRECISIÓN"
        },
        blog_articles: {
          article_1: {
            title: "Cargo Securing: Lashing",
            description: "Securing cargo to the transport unit before shipment is the foundation of logistics safety..."
          },
          article_2: {
            title: "Innovative Cargo Freight Solutions",
            description: "Forward-thinking cargo solutions emphasizing pioneering innovation and reliable lashing..."
          },
          article_3: {
            title: "Understanding IMO/CTU Code Compliance",
            description: "A deep dive into the international standards regulating cargo transport in containers..."
          },
          article_4: {
            title: "Enhancing Cargo Safety Standards",
            description: "Improving safety with modern protocols, technology integration, and proven safety practices..."
          }
        }
      },
      insights: {
        title: "Perspectivas del Mercado",
        subtitle: "Aprenda sobre los estándares de seguridad de carga, cumplimiento de la industria y las últimas tendencias logísticas de nuestros expertos.",
        all_articles: "Ver Todos los Artículos"
      }
    },
    blog: {
      nav: {
        back: "Volver al Inicio"
      },
      header: {
        title_1: "Perspectivas de",
        title_2: "LashingPlan"
      },
      article: {
        read_time: "min de lectura",
        share: "Compartir",
        read_more: "Leer Más",
        cta_title: "¿Listo para asegurar su carga?",
        cta_desc: "Únase a miles de profesionales que confían en nuestras herramientas para cálculos de trincaje precisos y conformes.",
        cta_button: "Comience a Calcular Ahora"
      },
      footer: {
        copyright: "© 2026 LashingPlan Blog. Todas las perspectivas reservadas."
      }
    },

    app: {
      title: "Calculadora de Trincaje",
      logout: "Cerrar Sesión",
      theme_toggle: "Modo Oscuro / Claro",
      profile: "Perfil",
      reports: "Informes de Campo",
      login: {
        title: "Iniciar Sesión",
        subtitle: "Inicia sesión en tu cuenta",
        email: "Correo electrónico o Nombre de usuario",
        email_placeholder: "Ingrese su correo electrónico o nombre de usuario",
        password: "Contraseña",
        password_placeholder: "Ingrese su contraseña",
        forgot_password: "¿Olvidó su contraseña?",
        submit: "Iniciar Sesión",
        no_account: "¿No tienes una cuenta?",
        signup: "Regístrate",
        back_to_home: "Volver al Inicio",
        consent: "Al continuar, acepto los {terms} y la {privacy}.",
        surveyor_portal: "¿Eres Inspector (Surveyor)? Ir al Portal de Inspector"
      },
      cog3d: {
        title: "Vista 3D del Centro de Gravedad (CoG)",
        subtitle: "El Centro de Gravedad (CoG) es el punto de equilibrio de la carga y determina el comportamiento de vuelco.",
        drag_hint: "Arrastra para girar"
      },
      lashing3d: {
        title: "Diagrama de Trincaje 3D",
        subtitle: "Vista interactiva de la configuración de trincaje"
      },
      calculator: {
        title: "Calculadora de Carga",
        cargo_details: "Detalles de la Carga",
        weight: "Peso (MT)",
        cog: {
          title: "Centro de Gravedad (cm desde la esquina inferior izquierda)",
          x: "X (ancho)",
          y: "Y (profundidad)",
          z: "Z (altura)"
        },
        securing_elements: "Elementos de Sujeción",
        wooden_dunnage: {
          label: "Usar Tacos de Madera",
          description: "Añade fricción ve previene el deslizamiento",
          details: "Detalles de los Tacos de Madera",
          width: "Ancho (cm)"
        },
        loop_lashing: "Trincaje en Lazo",
        direct_lashing: "Trincaje Directo",
        select_rope_type: "Seleccionar Tipo de Cuerda",
        stopper_forces: "Fuerzas del Tope (daN)",
        direction: {
          forward: "Contra Fuerzas Hacia Adelante",
          backward: "Contra Fuerzas Hacia Atrás",
          right: "Contra Fuerzas Hacia la Derecha",
          left: "Contra Fuerzas Hacia la Izquierda"
        }
      },
      results: {
        title: "Resultados",
        lashing_diagram: "Diagrama de Trincaje",
        error: "Error",
        loading: "Cargando...",
        no_results: "No se encontraron resultados",
        values: {
          sliding: "Deslizamiento:",
          tipping: "Volcado:",
          friction: "Fricción:",
          securing_element: "Elemento de Sujeción:",
          net_sliding: "Deslizamiento Neto:",
          net_tipping: "Volcado Neto:"
        }
      }
    },
    landing: {
      contact: {
        label: "Contactar a Ventas",
        title_1: "¿Listo para asegurar sus ",
        title_2: "operaciones de carga?",
        description: "Actualice a algoritmos de inspector de nivel empresarial. Póngase en contacto con nuestro equipo de ingeniería para discutir integraciones de API personalizadas, licencias por volumen o funciones premium.",
        email_us: "Envíenos un Correo",
        enterprise_support: "Soporte Empresarial",
        support_desc: "SLA Prioritario 24/7 Disponible",
        success_title: "¡Mensaje Enviado!",
        success_desc: "Nuestro equipo de ingeniería se comunicará con usted dentro de las 24 horas.",
        form: {
          name: "Nombre Completo",
          placeholder_name: "Juan Rodríguez",
          company: "Empresa",
          placeholder_company: "Logistics Inc.",
          email: "Dirección de Correo",
          placeholder_email: "juan@empresa.com",
          message: "Su Mensaje",
          placeholder_message: "¿Cómo podemos ayudar a escalar sus operaciones de trincaje?",
          send: "Enviar Solicitud"
        }
      },
      hero_badge: "Con la confianza de profesionales de la sujeción de carga en todo el mundo",
      hero_badge_imo: "Compatible con IMO",
      hero_badge_imo_sub: "Estándares Internacionales",
      hero_badge_3d: "Visualización 3D",
      hero_badge_3d_sub: "Tiempo Real",
      hero_title_1: "Profesional",
      hero_title_2: "Inteligencia de Trincaje",
      hero_subtitle: "Estándares globales. Algoritmos de precisión. Informes profesionales. La plataforma de sujeción de carga más avanzada de la industria.",
      start_calculating: "Iniciar Sesión",
      view_demo: "Ver Demostración",
      stats: {
        modes: "6+ Modos de Transporte",
        platform: "Plataforma en la Nube",
        platform_status: "Seguro",
        access: "Acceso Global"
      },
      features_title_1: "Potencia y",
      features_title_2: "Cumplimiento",
      features_subtitle: "Construido por expertos en seguridad de carga para operaciones de envío de alto riesgo.",
      features: {
        calculations: {
          title: "Cálculos Instantáneos",
          desc: "Calcule las fuerzas de trincaje en tiempo real con nuestro algoritmo avanzado."
        },
        pdf: {
          title: "Informes PDF Profesionales",
          desc: "Genere informes estándar de la industria con imagen corporativa y análisis detallado."
        },
        compliance: {
          title: "Evaluación basada en CTU",
          desc: "Apoyo simplificado para decisiones basado en el Código CTU de OMI / OIT / CEPE."
        },
        multimodal: {
          title: "Transporte Multimodal",
          desc: "Soporte para transporte por carretera, ferrocarril y mar. Contenedores, flatracks, buques y más."
        },
        global: {
          title: "Estándares Globales",
          desc: "Evaluación trazable basada en el Código CTU 2014 y su Material Informativo."
        },
        secure: {
          title: "Seguro y Confidencial",
          desc: "Sus datos de cálculo están protegidos con seguridad de nivel empresarial."
        }
      },
      transport: {
        title_1: "Transporte",
        title_2: "Unificado",
        subtitle: "Cálculos sin fisuras en toda la cadena logística.",
        modes: {
          road: { name: "Carretera", desc: "Remolque de camión y Lowbed" },
          rail: { name: "Ferrocarril", desc: "Vagón, Contenedor, Flatrack y Remolque de camión" },
          sea: { name: "Mar", desc: "Contenedor, Remolque de camión, Flatrack, Lowbed, Roll Trailer, Buque" },
          combined: { name: "Combinado", desc: "Carretera-Mar, Carretera-Ferrocarril, Carretera-Mar-Ferrocarril" }
        }
      },
      how_it_works: {
        badge: "Cómo Funciona",
        title_1: "Análisis Profesional en",
        title_2: "3 Pasos Simples",
        subtitle: "Complete cálculos complejos de trincaje en minutos",
        step1: {
          title: "Ingrese Detalles de Carga",
          desc: "Ingrese las dimensiones de la carga, peso, centro de gravedad y tipo de contenedor en el sistema."
        },
        step2: {
          title: "Cálculo Automático",
          desc: "El sistema calcula automáticamente la disposición óptima de trincaje según los estándares IMO",
          desc_highlight: " y permite cambios manuales en el plan."
        },
        step3: {
          title: "Obtenga Informe",
          desc: "Genere un informe PDF profesional con visualización 3D y cálculos detallados."
        }
      },
      benefits: {
        title: "Diseñado para Operaciones Críticas",
        subtitle: "Con la confianza de expertos, peritos y operadores de terminales de todo el mundo para garantizar la seguridad de cargas críticas.",
        list: [
          "Ahorre horas de cálculos manuales",
          "Reduzca el desperdicio de material mediante la optimización",
          "Asegure el cumplimiento total de las regulaciones",
          "Proporcione documentación profesional a los clientes",
          "Soporte multi-idioma (EN, TR, ES, DE, ZH, JA)",
          "Informes PDF con imagen corporativa"
        ],
        cta_card_title: "Estandarice su Plan de Trincaje Hoy",
        cta_card_desc: "Utilice una calculadora de nivel empresarial para su equipo y asegure que todos los envíos cumplan al 100%.",
        cta_card_trial: "Pruébelo sin tarjeta de crédito"
      },
      footer: {
        desc: "Únase a los profesionales de todo el mundo que utilizan nuestra calculadora de trincaje inteligente.",
        columns: {
          service: "Servicios",
          company: "Empresa",
          support: "Soporte"
        },
        links: {
          privacy: "Política de Privacidad",
          compliance: "Cumplimiento",
          cookies: "Política de Cookies",
          security: "Seguridad",
          help: "Centro de Ayuda",
          enterprise: "Empresa",
          contact: "Hablar con un Experto",
          force_analysis: "Análisis de Fuerzas",
          pdf_generator: "Generador de PDF",
          optimizer: "Optimizador",
          api_docs: "Docs de API",
          cookie_settings: "Configuración de Cookies"
        }
      },
      footer_copyright: "2025 Lashing Calculator. Un servicio proporcionado por JADE CONSULTANCY AND TRADING LTD. Todos los derechos reservados.",

      pricing_simple: "Precios ",
      pricing_transparent: "Simples y Transparentes",
      pricing_title: "",
      compare_features: "Comparar todas las funciones",
      footer_global: "Global",
      footer_encrypted: "Encriptado",
      footer_high_availability: "Alta Disponibilidad",
      demo_request: {
        title: "Solicite una Demo",
        subtitle: "Vea cómo Lashing Calculator puede transformar sus operaciones de seguridad de carga.",
        cta_title: "¿Listo para verlo en acción?",
        cta_subtitle: "Programe una demostración personalizada con nuestros especialistas y descubra cómo podemos estandarizar sus operaciones de trincaje a nivel global.",
        form: {
          full_name: "Nombre Completo",
          work_email: "Correo Electrónico de Trabajo",
          company: "Nombre de la Empresa",
          job_title: "Cargo",
          phone: "Número de Teléfono",
          fleet_size: "Tamaño de Flota / Envíos Mensuales",
          industry: "Industria",
          message: "Mensaje (Opcional)",
          submit: "Enviar Solicitud de Demo",
          success_title: "Solicitud Recibida",
          success_message: "Su solicitud de demo ha sido enviada con éxito. Nuestro equipo se pondrá en contacto con usted pronto."
        }
      },
      price_inquiry: {
        title: "Obtener una Cotización",
        subtitle: "Contáctenos para precios personalizados y soluciones corporativas.",
        form: {
          full_name: "Nombre Completo",
          work_email: "Correo Electrónico de Trabajo",
          company: "Nombre de la Empresa",
          phone: "Número de Teléfono",
          message: "Mensaje (Opcional)",
          submit: "Enviar Consulta",
          success_title: "Consulta Enviada",
          success_message: "Su solicitud de precio ha sido recibida. Nuestro equipo de ventas se pondrá en contacto con usted pronto."
        }
      }
    },

    profile: {
      title: "Perfil de la Empresa",
      subtitle: "Administre la información de su empresa para los informes PDF",
      logo_label: "Logotipo de la Empresa",
      logo_note: "El logotipo solo puede ser actualizado por administradores.",
      company_name: "Mi Perfil",
      address: "Dirección Completa",
      phone: "Teléfono / Móvil",
      email: "Correo Electrónico (Cuenta)",
      tax_office: "Oficina de Impuestos",
      tax_number: "Número de Identificación Fiscal",
      company_email_label: "Correo de la Empresa (Visible en PDF)",
      user_name_label: "Nombre de Usuario (Cuenta)",
      terms_accepted: "Términos Aceptados el",
      terms_not_accepted: "Términos Aún No Aceptados",
      view_terms: "Ver Términos",
      accept_terms: "Aceptar Términos",
      review_accept: "Revisar y Aceptar Términos",
      admin_note: "Nota: Los detalles de la empresa son gestionados por administradores.",
      cancel: "Cancelar",
      accept: "Aceptar Términos",
      modal_title: "Términos de Servicio",
      modal_subtitle: "Por favor revise y acepte nuestros términos para proceder.",
      modal_agree: "Al aceptar, usted acepta estar sujeto a los {tos} y la {privacy}.",
      modal_ack: "Usted reconoce que el software es una herramienta y la responsabilidad final de los planes de trincaje recae en el profesional certificado.",
      checkbox_label: "He leído y acepto los Términos y Condiciones",
      save: "Guardar Cambios",
      saving: "Guardando...",
      success_message: "Perfil actualizado correctamente.",
      error_message: "Error de actualización: {error}",
      credits_title: "Información de Créditos y Uso",
      calc_credits: "Créditos de Cálculo",
      pdf_credits: "Créditos de Reporte PDF",
      monthly_increment: "Incremento Mensual",
      next_increment: "Próxima Recarga",
      remaining: "Restante",
      lashing_display_label: "Formato de Visualización",
      lashing_display_msl: "MSL (ej. MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (ej. CL200 HDB12L)",
      pdf_theme_title: "PDF Design Theme",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Dark Turquoise & Slate Gray",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Corporate Navy & Light Gray",
      theme_standart_title: "Standard",
      theme_standart_desc: "Original App Colors"
    },
    surveys: {
      title: "Informes de Campo",
      create_new: "Crear Nuevo Informe",
      total_ops: "Operaciones Totales",
      pending_photos: "Fotos Pendientes",
      completed_assignments: "Asignaciones Completadas",
      current_reports: "Informes Actuales",
      search_placeholder: "Buscar ID o Proyecto...",
      no_reports_title: "No hay informes todavía",
      no_reports_desc: "Comience haciendo su primer cálculo de trincaje para preparar un informe de campo.",
      create_now: "Crear Ahora",
      unnamed_project: "Proyecto sin nombre",
      created_date: "CREADO",
      data_status: "Datos",
      uploaded: "Fotos de la aplicación subidas",
      waiting: "Esperando fotos de la aplicación",
      surveyor: "Inspector",
      waiting_response: "Esperando Respuesta",
      under_review: "Bajo Revisión",
      report_ready: "Informe Listo",
      cancelled_rejected: "Cancelado / Rechazado",
      send_link: "Enviar Enlace de Carga de Campo",
      upload_manually: "Subir fotos de campo manualmente",
      assign_new: "Asignar un nuevo inspector",
      assign_surveyor: "Asignar Inspector",
      assigned: "Asignado",
      summary_pdf: "Resumen PDF",
      surveyor_pdf: "Inspector PDF",
      assignment_success: "¡Asignación de inspector exitosa!",
      old_report_error: "Enlace seguro no generado para informes antiguos. Por favor recree el informe.",
      session_expired: "Sesión expirada. Por favor refresque la página o inicie sesión de nuevo.",
      pdf_error: "Error al crear PDF.",
      download_failed: "Descarga fallida: ",
      wait: "Espere...",
      connecting: "Conectando...",
      access_error: "Error de Acceso",
      modal: {
        title: "Asignar Inspector",
        search_placeholder: "Buscar por Ciudad, Puerto o Nombre...",
        list_error: "No se pudo recuperar la lista de inspectores.",
        assign_failed: "Fallo en la asignación.",
        loading: "Cargando...",
        not_found: "No se encontró inspector.",
        unknown_location: "Desconocido",
        assigning: "Asignando...",
        assign_button: "Asignar este Inspector"
      },
      guest: {
        title: "Portal de Supervisión de Implementación en Campo",
        subtitle: "Puede cargar fotos de campo para su aplicación de trincaje aquí.",
        invalid_link: "Enlace inválido o expirado.",
        upload_success_title: "Carga Exitosa",
        upload_success_desc: "Las fotos de campo para su aplicación de trincaje se han guardado correctamente. Su informe ya está listo para revisión y aprobación de PDF.",
        close_window: "Cerrar Ventana",
        info_title: "1. Información del Implementador (Opcional)",
        date: "Fecha",
        company_name: "Nombre de la Empresa",
        company_placeholder: "ej.: X Logistics",
        implementer_name: "Nombre Completo del Implementador",
        phone: "Teléfono",
        email: "Correo Electrónico",
        email_placeholder: "nombre@empresa.com",
        photos_title: "2. Imágenes de Implementación en Campo (Máx 9)",
        photos_desc: "Cargue o tome fotos de campo que coincidan con las categorías a continuación. El límite por foto es de ~5MB y se comprimirá automáticamente a ~1MB en su dispositivo.",
        take_photo: "Tomar Foto / Cargar",
        uploading: "Cargando fotos...",
        confirm_upload: "Cargar Todo y Confirmar",
        min_photo_error: "Agregue al menos una foto.",
        compression_error: "No se pudo procesar la foto, inténtelo de nuevo.",
        categories: {
          floor: "Estado del Piso de la Unidad de Transporte",
          cargo: "Colocación de Carga y Calzado de Piso",
          front_right: "Trincaje: Frontal Derecho",
          front_left: "Trincaje: Frontal Izquierdo",
          rear_right: "Trincaje: Trasero Derecho",
          rear_left: "Trincaje: Trasero Izquierdo",
          plate: "Matrícula (si hay)",
          seal: "Número de Precinto (si hay)",
          overall: "Vista General Principal (Final)"
        }
      },
      portal: {
        title: "Portal del Inspector",
        subtitle: "Administre sus tareas de inspección asignadas iniciando sesión.",
        login_error: "Ingrese su correo electrónico y contraseña.",
        login_button: "Iniciar Sesión",
        logging_in: "Iniciando sesión...",
        dashboard: {
          connecting: "Conectando a los paneles...",
          pending_tab: "Solicitudes Entrantes",
          active_tab: "Tareas Activas",
          completed_tab: "Trabajos Pasados",
          logout: "Cerrar Sesión Segura",
          connected: "Conectado",
          account_type: "Cuenta de Inspector",
          no_records: "No se encontraron registros",
          no_records_desc: "No hay tareas de inspección activas para mostrar en esta pestaña.",
          customer: "Cliente",
          task_date: "FECHA DE TAREA",
          waiting_response: "Esperando Respuesta",
          under_review: "Revisión del Cliente",
          delivered: "Informe Entregado",
          rejected_by_you: "Tarea Rechazada",
          accept_button: "Aceptar Tarea",
          reject_button: "Rechazar",
          go_to_report: "Ir al Menú de Informes",
          download_pdf: "Descargar Archivo PDF",
          downloading_pdf: "Descargando PDF del informe...",
          download_success: "Informe descargado con éxito.",
          download_error: "Error al descargar el informe.",
          update_error: "No se pudo actualizar el estado, inténtelo de nuevo.",
          accept_success: "¡Tarea aceptada con éxito!",
          reject_success: "Tarea rechazada."
        },
        editor: {
          details_title: "Detalles del Proyecto",
          project_name: "Nombre del Proyecto",
          weight: "Peso de la Carga",
          photos_count: "Fotos de Campo",
          success_title: "¡Informe Creado con Éxito!",
          success_desc: "Su informe de inspección ha sido creado, firmado y cargado en el entorno seguro en la nube. Puede descargar o revisar el archivo PDF haciendo clic en el botón a continuación.",
          back_to_dashboard: "Volver al Panel",
          download_pdf: "Descargar PDF",
          creator_title: "Creador de Identificación de Informe Final",
          assessment_label: "Evaluación del Inspector",
          assessment_desc: "Este texto se añadirá a la sección de Metodología y Conclusión del PDF final.",
          assessment_default: "La inspección de las cargas se ha completado de acuerdo con las normas internacionales. No se observaron daños ni incumplimientos.",
          auto_info_note: "Nota: La información predeterminada, como su nombre, firma y logotipo de la empresa en el sistema, se colocará automáticamente en el informe PDF.",
          back_button: "Atrás",
          generate_button: "Certificar y Enviar Informe PDF",
          generating: "Generando...",
          generate_error: "No se pudo crear el informe",
          not_found: "Tarea no encontrada o no está autorizado."
        }
      }
    },

    labels: {
      three_d_view: "Vista 3D",
      no_results_data: "No hay datos de resultados disponibles",
      pass: "Aprobado",
      fail: "Reprobado",
      reports: "Informes",
      user_responsible_for: "Usted es el único responsable de:",
      page_title: "Herramienta de Cálculo de Trincaje",
      weight: "Peso (MT)",
      transport_mode: "Modo de Transporte",
      transport_unit: "Unidad de Transporte",
      friction_surface: "Superficie de Fricción",
      select: "Seleccionar",
      select_transport_mode_first: "Seleccione el modo de transporte primero",
      cargo_dimensions: "Dimensiones de Carga ve CdG (m)",
      cargo_specs: "CARGO SPECS",
      width: "Ancho de Carga",
      length: "Longitud de Carga",
      height: "Altura de Carga",
      cog_width: "Desplazamiento CdG en Ancho (m):",
      cog_length: "Desplazamiento CdG en Longitud (m):",
      cog_height: "Altura del CdG (m):",
      cog_width_help: "El CdG predeterminado es la mitad del ancho. Cambie si es diferente.",
      cog_length_help: "El CdG predeterminado es la mitad de la longitud. Cambie si es diferente.",
      cog_height_help: "El CdG predeterminado es la mitad de la altura. Cambie si es diferente.",
      wooden_dunnage_dimensions: "Dimensiones ve Cantidad de Calzos de Madera",
      wooden_dunnage_width: "Ancho (cm):",
      wooden_dunnage_length: "Longitud (cm):",
      stopper_qty: "Topes Metálicos (Cantidad)",
      force_analysis: "Resultados de Análisis de Fuerzas (kN)",
      generate_pdf: "Generar PDF",
      download_pdf: "Descargar PDF",
      wooden_dunnage: "Calzos de Madera",
      stopper: "Topes",
      loop_lashing: "Trincaje de Bucle",
      max_weight: "Peso Máximo",
      direct_lashing: "Trincaje Directo",
      loop_rope_select: "Seleccionar Cinta de Trincaje de Bucle",
      loop_lashing_quantity_per_side: "Cantidad (por lado)",
      direct_lashing_quantity: "Cantidad",
      cog_above: "Por encima del CdG",
      cog_below: "Por debajo del CdG",
      forward: "Contra Fuerzas Hacia Adelante",
      backward: "Contra Fuerzas Hacia Atrás",
      right: "Contra Fuerzas Hacia la Derecha",
      left: "Contra Fuerzas Hacia la Izquierda",
      lashing_diagram: "Diagrama de Trincaje",
      language: "Idioma",
      calculate: "Calcular",
      configuration_panel: "Panel de Configuración",
      operation_mode: "Modo de Operación",
      manual_mode: "Calculadora",
      lashing_calculator: "Calculadora de Trincaje",
      direct_lashing_selection: "Selección de Trincaje Directo",
      reset: "Restablecer",
      print: "Imprimir",
      configuration: "Configuración",
      use_stopper: "Usar tope",
      logout: "Cerrar sesión",
      results_will_appear_here: "Los resultados aparecerán aquí",
      continue_with_overweight: "Continuar con sobrepeso",
      optimizer_mode: "Optimizador",
      calculator_mode: "Calculadora",
      project_description: "Descripción del Proyecto (Opcional)",
      enter_project_description_hint: "Ingrese la descripción del proyecto que aparecerá en el informe PDF.",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "Editar en Calculadora",
      breakbulk_settings: "Configuración de Breakbulk",
      flatrack_type: "Tipo de Flatrack",
      orientation: "Orientación de Carga",
      longitudinal: "Longitudinal",
      transverse: "Transversal",
      flatrack_count: "Número Sugerido de Flatracks",
      flatrack_gap: "Espacio entre Flatracks",
    },

    common: {
      info: "Información",
      yes: "Sí",
      no: "No",
      ok: "Aceptar",
      cancel: "Cancelar",
      confirm: "Confirmar",
      close: "Cerrar",
      save: "Guardar",
      download: "Descargar",
      delete: "Eliminar",
      edit: "Editar",
      view: "Ver",
      loading: "Cargando...",
      processing: "Procesando...",
      please_wait: "Por favor espere...",
      save_changes: "Guardar Cambios",
      saving: "Guardando..."
    },

    transport_modes: {
      road: "Carretera",
      rail: "Ferrocarril",
      sea_a: "Mar_A (Ola <8m)",
      sea_b: "Mar_B (8m < Ola <12m)",
      sea_c: "Mar_C (Ola >12m)",
      road_sea_a: "Carretera+Mar_A",
      road_sea_b: "Carretera+Mar_B",
      road_sea_c: "Carretera+Mar_C",
      road_rail: "Carretera+Ferrocarril",
      road_rail_sea_a: "Carretera+Ferrocarril+Mar_A",
      road_rail_sea_b: "Carretera+Ferrocarril+Mar_B",
      road_rail_sea_c: "Carretera+Ferrocarril+Mar_C"
    },

    transport_unit_groups: {
      containers: "Contenedores",
      open_tops: "Cielo Abierto",
      flatracks: "Flatracks",
      trailers: "Remolques",
      special: "Otros / Especial",
    },
    transport_units: {
      container_20: "Contenedor 20'",
      container_40: "Contenedor 40'",
      container_40_hc: "Contenedor 40' HC",
      container_45_hc: "Contenedor 45' HC",
      open_top_20: "Open Top 20'",
      open_top_20_hc: "Open Top 20' HC",
      open_top_40: "Open Top 40'",
      open_top_40_hc: "Open Top 40' HC",
      flatrack_20: "Flatrack 20'",
      flatrack_40: "Flatrack 40'",
      lowbed: "Plataforma Baja",
      mafi: "Mafi",
      vessel: "Buque",
      breakbulk: "Break Bulk",
      truck_trailer_standard: "Remolque de Camión (Estándar)",
      truck_trailer_mega: "Remolque de Camión (Mega / Jumbo)",
      open_top_container: "Contenedor Open Top"
    },

    materials: {
      asm: "Alfombrilla Antideslizante (Estático 0.8µ)",
      asm_06: "Alfombrilla Antideslizante (Estático 0.6µ)",
      metal_metal: "Metal-Metal",
      metal_wood: "Metal-Madera",
      wood_wood: "Madera-Madera"
    },

    directions: {
      forward: "Contra Fuerzas Hacia Adelante",
      backward: "Contra Fuerzas Hacia Atrás",
      right: "Contra Fuerzas Hacia la Derecha",
      left: "Contra Fuerzas Hacia la Izquierda"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "toneladas"
    },

    errors: {
      zero_input: "Por favor complete todos los campos requeridos.",
      no_loop_rope: "No se encontró cuerda de bucle compatible.",
      no_direct_rope: "No se encontró cuerda de trincaje directo compatible.",
      calculation_error: "Error de Cálculo: {error}",
      unexpected_error: "Error inesperado: {error}",
      diagram_error: "No se pudo generar el diagrama: {error}",
      pdf_error: "Error de PDF: {error}",
      loop_config_error: "Error de configuración de bucle: {error}",
      error_halat: "No se seleccionó cuerda de trincaje directo.",
      invalid_transport_unit: "Unidad de transporte no válida: {unit}. Por favor seleccione una unidad válida.",
      network_error: "Error de red. Por favor verifique su conexión a internet.",
      file_not_found: "Archivo no encontrado.",
      invalid_cargo_dimensions: "ADVERTENCIA: La unidad de transporte seleccionada no es adecuada para la carga",
      select_transport_mode: "Por favor seleccione un modo de transporte",
      select_transport_unit: "Por favor seleccione una unidad de transporte",
      select_friction_surface: "Por favor seleccione una superficie de fricción",
      invalid_weight: "Por favor ingrese un peso válido",
      invalid_dimensions: "Por favor ingrese dimensiones de carga válidas",
      invalid_cog: "Por favor ingrese coordenadas válidas del centro de gravedad",
      invalid_wooden_dunnage: "Por favor ingrese dimensiones válidas de calzos de madera",
      invalid_stopper: "Por favor ingrese cantidades válidas de topes",
      select_at_least_one_rope: "Por favor seleccione trincajes",
      select_rope_type: "Por favor seleccione el tipo de cuerda",
      dimension_exceeded: "ADVERTENCIA: La carga {dims} excede los límites de la unidad de transporte seleccionada.",
      dim_width: "ANCHO",
      dim_length: "LARGO",
      dim_height: "ALTURA",
      cargo_width_greater_than_length: "El ancho de la carga no puede ser mayor que la longitud. Valor ajustado automáticamente para coincidir con la longitud.",
      and_conjunction: " y ",
      breakbulk_dim_overflow_width: "⚠️ ¡El ancho de la carga ({cargo_width}m) es mayor o igual a la longitud de la base seleccionada ({flatrack_length}m)! No se puede calcular.",
      breakbulk_dim_overflow_length: "⚠️ ¡La longitud de la carga ({cargo_length}m) es mayor o igual a la longitud de la base seleccionada ({flatrack_length}m)! No se puede calcular."
    },

    validation: {
      required_field: "Este campo es obligatorio",
      invalid_number: "Por favor ingrese un número válido",
      positive_number: "Por favor ingrese un número positivo",
      min_value: "Valor mínimo: {min}",
      max_value: "Valor máximo: {max}",
      out_of_range: "El valor debe estar entre {min} ve {max}",
      invalid_email: "Por favor ingrese una dirección de correo válida",
      invalid_format: "Formato no válido"
    },

    warnings: {
      weight_exceeded: "Advertencia: ¡Se ha excedido la capacidad máxima de peso para {unit} ({max_weight} MT)!",
      overweight_confirmation: "El peso de la carga introducido ({weight} MT) excede el peso máximo permitido ({max_weight} MT). Por favor, confirme nuevamente para continuar.",
      oversized_width_loop_disabled: "Sujeción de bucle desactivada debido al ancho de carga sobredimensionado.",
      oversized_width_side_wedges_disabled: "Cuñas laterales desactivadas debido al ancho de carga sobredimensionado.",
      high_cog: "Advertencia: Un centro de gravedad alto aumenta el riesgo de vuelco.",
      insufficient_lashing: "Advertencia: Se detectó trincaje insuficiente.",
      unsaved_changes: "Tiene cambios sin guardar. ¿Desea continuar?",
      flatrack_dunnage_required: "Se recomienda el uso de calzos de madera para restringir el deslizamiento de la carga en direcciones hacia adelante y hacia atrás en unidades de transporte Flatrack. ¿Desea continuar el cálculo sin agregar calzos de madera?",
      terms_acceptance_required: "You must read and accept the Terms of Service to perform calculations. You can access the Terms of Service from your Profile page.",
      flatrack_count_low: "Atención: ¡La cantidad de flatracks seleccionada ({selected}) es inferior a la cantidad recomendada ({recommended})!"
    },

    success: {
      pdf_created: "¡PDF creado exitosamente!",
      calculation_complete: "¡Cálculo completado!",
      data_saved: "Datos guardados exitosamente.",
      export_complete: "Exportación completada exitosamente."
    },

    table_columns: {
      direction: "Dirección",
      sliding: "Deslizamiento",
      tipping: "Vuelco",
      friction: "Fricción",
      wooden_dunnage: "Calzos de Madera",
      stopper: "Topes",
      looplash: "Trincaje de\nBucle",
      direct_lashing: "Trincaje Directo",
      cordstrap_loop: "Bucle Cordstrap",
      cordstrap_direct: "Cordstrap Directo",
      total_securing: "Aseguramiento Total",
      net_sliding: "Deslizamiento Neto",
      net_tipping: "Vuelco Neto",
      net_sliding_tooltip: "Los valores negativos indican aseguramiento seguro.",
      net_tipping_tooltip: "Los valores negativos indican aseguramiento seguro.",
      status: "Estado",
      safety_factor: "Factor de Seguridad"
    },

    results: {
      safe: "SEGURO",
      unsafe: "INSEGURO",
      marginal: "MARGINAL",
      critical: "KRÍTICO",
      acceptable: "ACEPTABLE",
      status: "Estado",
      safety_factor: "Factor de Seguridad",
      summary: "Resumen",
      details: "Detalles",
      notes_title: "Notas / Supuestos",
      notes_assumed: "",
      notes_cargo_unitized: "La carga se considera como un solo artículo o consolidada y unitizada en una sola unidad de carga.",
      notes_lashing_above_cog: "A menos que se indique lo contrario en la tabla de “Uso de Materiales”, se asume que los amarres directos se aplican por encima del centro de gravedad de la carga.",
      notes_angle_range: "Los ángulos agudos entre los amarres directos fijados en ángulo a los puntos de amarre en la unidad de transporte y la componente vectorial de los amarres directos en la dirección de la fuerza actuante deben estar entre 30° y 60°.",
      notes_edge_protection: "Se proporcionará protección adecuada en bordes y esquinas donde sea necesario para evitar daños a la carga y/o los materiales de amarre.",
      notes_pretension_force: "Al tensar los amarres textiles, la fuerza de pretensado aplicada no debe exceder el 50% del valor MSL del amarre.",
      notes_strength_limitations: "Se asume que los puntos de amarre en la carga no tienen limitaciones de resistencia.",
      notes_distributed_uniformly: "Se asume que la carga se distribuye uniformemente, de acuerdo con los criterios de capacidad de la unidad de transporte.",
      flatrack_no_dunnage_warning: "Nota: Se recomienda el uso de calzos de madera para restringir el deslizamiento de la carga en direcciones hacia adelante y hacia atrás en unidades de transporte Flatrack"
    },

    tooltips: {
      weight_info: "Ingrese el peso total de la carga en toneladas métricas",
      cog_info: "COG significa Centro de Gravedad (Center of Gravity). Por defecto, se asume que está en el centro de la carga. Sin embargo, en realidad, el centro de gravedad puede estar en una ubicación diferente. Si se conoce el COG específico, su posición correcta se puede ingresar aquí. El posicionamiento preciso de COG es crítico, especialmente para los cálculos de vuelco.",
      friction_info: "Seleccione la combinación de materiales para el coeficiente de fricción",
      transport_mode_info: "Los valores de aceleración varían según el modo de transporte",
      lashing_info: "Las fuerzas de trincaje previenen el deslizamiento y el vuelco"
    },
    legal: {
      tos_title: "Términos de Servicio",
      privacy_title: "Política de Privacidad",
      sales_agreement_title: "Acuerdo de Venta",
      legal_notice_title: "Aviso Legal",
      cookie_policy_title: "Política de Cookies",
      last_updated: "Fecha de Vigencia: 4 de febrero de 2026",
      return_home: "Volver al Inicio",
      sections: {
        acceptance: {
          title: "Aceptación de Términos",
          content: "Al acceder y utilizar Lashing Calculator, acepta estar sujeto a estos términos. Si no está de acuerdo, no utilice el servicio."
        },
        payment_terms: {
          title: "Términos de Pago y Suscripción",
          content: "El servicio se factura por suscripción. Los pagos no son reembolsables. Nos reservamos el derecho de cambiar los precios con previo aviso."
        },
        termination: {
          title: "Cancelación y Terminación",
          content: "Puede cancelar su suscripción en cualquier momento. El acceso continuará hasta el final del ciclo de facturación actual."
        },
        disclaimer: {
          title: "Aviso de Seguridad Importante y Exención de Responsabilidad Profesional",
          important: "AVISO CRÍTICO: Las herramientas de cálculo proporcionadas están destinadas únicamente como un 'Sistema de Apoyo a la Decisión' para la planificación de la sujeción de carga.",
          content: "La Plataforma no proporciona asesoramiento de ingeniería profesional. Todos los resultados deben ser revisados y aprobados por un inspector de carga calificado, un experto en trincaje o una 'Persona Competente' según lo definido por el Código CTU de la OMI/OIT/CEPE y el Código CSS de la OMI antes de su implementación. La responsabilidad final recae en el Transportista y el Capitán según el derecho marítimo internacional."
        },
        license: {
          title: "Propiedad Intelectual y Licencia",
          subtitle: "El código fuente, los algoritmos y el diseño de la Plataforma están protegidos por las leyes internacionales de propiedad intelectual:",
          items: [
            "A los usuarios se les otorga una licencia limitada y no transferible para usar la Plataforma; no se transfiere la propiedad.",
            "La entrada precisa de datos de carga y unidad de transporte es responsabilidad exclusiva del usuario.",
            "Está estrictamente prohibido realizar ingeniería inversa o extraer el código fuente.",
            "Los informes PDF son específicos para la carga descrita y no se pueden reutilizar para diferentes configuraciones."
          ]
        },
        liability: {
          title: "Limitación de Responsabilidad",
          content: "En ningún caso Lashing Calculator o sus proveedores serán responsables por cualquier daño (incluyendo, sin limitación, daños por pérdida de datos o ganancias, o debido a la interrupción del negocio) que surja del uso o la imposibilidad de usar el Servicio.",
          cap_title: "Límite de Responsabilidad Monetaria",
          cap_content: "En la medida máxima permitida por la ley, nuestra responsabilidad acumulada total por todas y cada una de las reclamaciones que surjan de este Servicio o estén relacionadas con él no excederá la cantidad pagada por usted por el Servicio durante los doce (12) meses anteriores a la reclamación, o cien dólares estadounidenses (US$100.00), lo que sea mayor.",
          not_responsible: "No somos responsables de:",
          not_responsible_items: [
            "Daños resultantes de una ejecución física inadecuada del trincaje",
            "Datos de entrada inexactos proporcionados por el usuario",
            "Cambios ambientales más allá de los parámetros de cálculo",
            "Falla estructural de la unidad de transporte o los puntos de trincaje",
            "Especificaciones inexactas del fabricante (MSL, fricción, etc.)",
            "Daños consecuentes, incidentales o punitivos",
            "Pérdida de beneficios, oportunidades de negocio o buena voluntad"
          ]
        },
        third_party: {
          title: "Aviso Legal sobre Datos de Terceros",
          content: "La Plataforma incorpora datos de fuentes de terceros, incluyendo fabricantes y estándares internacionales de seguridad.",
          sub_content: "No ofrecemos garantías con respecto a la exactitud o fiabilidad de los datos de terceros. Todas las especificaciones del fabricante son proporcionadas por sus respectivos fabricantes."
        },
        accuracy: {
          title: "Precisión Técnica",
          content: "Si bien nos esforzamos por la precisión, los materiales pueden contener errores técnicos o tipográficos. No garantizamos que todos los materiales estén siempre actualizados o sean precisos."
        },
        source_responsibility: {
          title: "Fuentes de Datos y Responsabilidad del Usuario",
          important: "La exactitud de todos los cálculos depende totalmente de los parámetros que usted proporcione.",
          utilize_content: "El Servicio utiliza varias fuentes de datos para los cálculos:",
          data_sources: [
            { title: "Valores MSL", desc: "Derivados de las especificaciones del fabricante; no se realiza ninguna verificación independiente." },
            { title: "Coeficientes de Fricción", desc: "Basados en estándares de la industria; la fricción en el mundo real puede variar." },
            { title: "Puntos de Amarre", desc: "Basados en las especificaciones de la unidad; la integridad física es responsabilidad del usuario." },
            { title: "Códigos CTU/CSS", desc: "Alineados con los estándares internacionales de seguridad marítima." }
          ],
          user_responsible_items: [
            "Verificar todos los parámetros de entrada (peso, dimensiones, posición del CdG)",
            "Asegurarse de que el equipo de trincaje coincida con los materiales específicos utilizados",
            "Confirmar la aplicabilidad del coeficiente de fricción a las condiciones específicas de la carga",
            "Validar físicamente la integridad del punto de amarre",
            "Contratar a profesionales calificados para aprobar los resultados antes de la implementación"
          ]
        },
        verification: {
          title: "Requisito de Verificación Profesional",
          content: "Todos los resultados de los cálculos deben ser revisados y aprobados por una persona calificada según lo definido por el Código CTU antes de su aplicación. La Plataforma proporciona soporte de cálculo únicamente y no constituye asesoramiento de ingeniería."
        },
        indemnification: {
          title: "Indemnización",
          content: "Usted acepta indemnizar al Proveedor de la Plataforma por cualquier reclamo de terceros resultante de:",
          items: [
            "Cualquier violación de estos Términos de Servicio",
            "Provisión de datos de entrada inexactos o incompletos",
            "Uso de especificaciones de equipo obsoletas o incorrectas",
            "No contratar a profesionales calificados para la verificación",
            "Implementación de planes sin revisión profesional independiente",
            "Accidentes o daños derivados de la ejecución física"
          ]
        },
        governing_law: {
          title: "Ley Aplicable y Jurisdicción",
          content: "Estos términos se rigen por las convenciones marítimas internacionales y las leyes de la jurisdicción donde el Proveedor está registrado. Las disputas están sujetas a la jurisdicción exclusiva de los tribunales competentes en esa ubicación."
        }
      },
      detailed_terms: [
        {
          title: "1. Formación del Acuerdo",
          content: [
            "Estos Términos de Servicio ('Términos') constituyen un acuerdo legalmente vinculante entre usted ('Usuario', 'Cliente') y JADE CONSULTANCY AND TRADING LTD ('Proveedor', 'nosotros', 'nuestro'), que rige el acceso y uso de la plataforma de aplicación web Lashing Calculator.",
            "Al acceder o utilizar la Plataforma, o al celebrar un contrato de suscripción o venta para el Servicio, usted acepta irrevocablemente estar obligado por estos Términos.",
            "La aceptación de un Acuerdo de Venta constituye la aceptación automática de estos Términos."
          ]
        },
        {
          title: "2. Solo para Uso Comercial",
          content: [
            "La Plataforma se proporciona estrictamente sobre una base de empresa a empresa (B2B).",
            "Usted confirma que actúa en el curso de sus negocios y no como consumidor bajo ninguna legislación de protección al consumidor.",
            "La Plataforma está diseñada para asistir en los cálculos de seguridad y trincaje de carga para los modos de transporte marítimo, por carretera y ferroviario, así como sus combinaciones multimodales. El marco regulatorio aplicable a cualquier uso de la Plataforma depende del modo de transporte pertinente y las jurisdicciones de carga, tránsito y descarga. Los Usuarios son los únicos responsables de identificar y cumplir toda la normativa específica del modo de transporte aplicable."
          ]
        },
        {
          title: "3. Suscripción, Tarifas y Pago",
          content: [
            "3.1 Plazo de Suscripción: El plazo de suscripción es de un (1) año a partir de la fecha de activación. No se aplica renovación automática. La renovación requiere una solicitud por escrito del Usuario y el pago completo del nuevo período de suscripción.",
            "3.2 Tarifas y Moneda: Todas las tarifas se facturan y pagan en GBP (£). El pago debe realizarse en la cuenta bancaria en GBP del Proveedor. Las tarifas se pagan por adelantado.",
            "3.3 Notificaciones de Vencimiento: La Plataforma puede enviar notificaciones de recordatorio un (1) mes, una (1) semana y un (1) día antes del vencimiento. Dichas notificaciones se proporcionan solo por conveniencia y no constituyen renovación."
          ]
        },
        {
          title: "4. Cancelación y Terminación",
          content: [
            "4.1 Terminación por el Usuario: El Usuario puede cancelar en cualquier momento. El acceso continuará hasta el final del período de suscripción actual. No se aplica reembolso excepto como se indica expresamente a continuación.",
            "4.2 Terminación por el Proveedor: El Proveedor puede rescindir o suspender el Servicio en cualquier momento por razones comerciales legítimas, legales, técnicas o de seguridad, siempre que, cuando sea razonablemente posible, el Proveedor notifique con antelación. Si la terminación es iniciada por el Proveedor, el Usuario tendrá derecho a un reembolso prorrateado por la parte no utilizada del período de suscripción."
          ]
        },
        {
          title: "5. Fallo del Servicio y Reembolsos",
          content: [
            "Si la Plataforma se vuelve materialmente inutilizable por razones técnicas no causadas por el Usuario durante un período continuo de más de cinco (5) días consecutivos, el Usuario tendrá derecho a un reembolso prorrateado, calculado diariamente sobre la tarifa de suscripción anual por los días no utilizados.",
            "Las interrupciones temporales, el rendimiento degradado, las ventanas de mantenimiento o los problemas de compatibilidad no constituirán inutilidad."
          ]
        },
        {
          title: "6. Cuentas de Usuario y Licencias",
          content: [
            "6.1 Concesión de Licencia: El Proveedor otorga al Usuario una licencia limitada, no exclusiva e intransferible para acceder y utilizar la Plataforma solo para fines comerciales internos. No se transfieren derechos de propiedad.",
            "6.2 Política de Dispositivo Único: Cada cuenta de usuario se puede utilizar en un dispositivo a la vez, a menos que se acuerde lo contrario por escrito en un Acuerdo de Venta aplicable. Los inicios de sesión simultáneos están prohibidos. El Proveedor se reserva el derecho de monitorear los patrones de uso para detectar el mal uso. Cuando se sospeche razonablemente un mal uso, el Proveedor puede suspender el acceso mientras investiga. Las infracciones confirmadas pueden resultar en la terminación sin reembolso.",
            "6.3 Obligaciones del Usuario: Para generar informes, el Usuario debe proporcionar: Nombre de la empresa, Logotipo de la empresa, Número de móvil, Teléfono fijo, Dirección de correo electrónico. Se deben proporcionar detalles de facturación de la empresa válidos para la facturación."
          ]
        },
        {
          title: "7. Aviso de Seguridad Importante y Descargo de Responsabilidad Profesional",
          content: [
            "AVISO CRÍTICO: La Plataforma es solo un Sistema de Apoyo a la Decisión para la planificación de la seguridad de la carga para todos los modos de transporte, incluyendo transporte marítimo, por carretera, ferroviario y sus combinaciones multimodales.",
            "La Plataforma no proporciona asesoramiento profesional de ingeniería, legal o de seguridad.",
            "La Plataforma no está certificada, aprobada ni respaldada por ningún estado del pabellón, sociedad de clasificación, autoridad portuaria, autoridad de transporte por carretera, administrador de infraestructura ferroviaria ni organismo regulador.",
            "Todos los resultados de los cálculos deben ser revisados y aprobados por un profesional de carga competente: Código CTU OMI/OIT/CEPE y Código CSS OMI (marítimo); EN 12195 / VDI 2700 y guía nacional de transporte por carretera aplicable (carretera); Directrices de Carga UIC (ferroviario).",
            "La responsabilidad final por la seguridad de la carga recae en la parte responsable bajo el marco jurídico aplicable al modo de transporte: para marítimo, el transportista, propietario del buque y/o capitán bajo la ley marítima internacional aplicable; para carretera, el transportista, operador del vehículo y/o conductor bajo el Convenio CMR; para ferroviario, el transportista y operador ferroviario bajo el marco CIM/COTIF. En transporte multimodal, cada transportista modal es responsable de su segmento.",
            "El Proveedor no acepta ninguna responsabilidad por las decisiones tomadas en base a los resultados de la Plataforma.",
            "Para transporte marítimo: Los resultados no cumplen obligaciones documentales legales bajo SOLAS Regla VI/5-2. El cumplimiento de P&I, ISM e IACS es responsabilidad del Usuario.",
            "Para transporte por carretera: Los resultados deben validarse contra la serie EN 12195 y VDI 2700. Los operadores del Reino Unido deben cumplir también con el Código de Práctica de Aseguración de Carga del DfT.",
            "Para transporte ferroviario: Los resultados deben validarse contra las Directrices de Carga UIC, los requisitos TSI y los requisitos del administrador de infraestructura ferroviaria nacional aplicable.",
            "Las autoridades portuarias, de transporte por carretera y ferroviarias individuales pueden imponer requisitos adicionales. El cumplimiento de todas las normativas locales es responsabilidad exclusiva del Usuario.",
            "Aviso sobre Mercancías Peligrosas: La Plataforma está diseñada únicamente para la planificación de la seguridad de carga general. No ha sido diseñada ni validada para mercancías peligrosas sujetas a ADR (carretera), el Código IMDG (marítimo), RID (ferroviario) o reglamentos equivalentes. Los Usuarios que manipulen mercancías peligrosas deben obtener asesoramiento especializado de un Asesor de Seguridad para el Transporte de Mercancías Peligrosas (DGSA) antes de cualquier uso operativo."
          ]
        },
        {
          title: "8. Propiedad Intelectual",
          content: [
            "La Plataforma, incluido su código fuente, algoritmos, cálculos, estructura y diseño, está protegida por las leyes de propiedad intelectual.",
            "Reverse Engineering, extracción o reproducción está estrictamente prohibido.",
            "Los informes en PDF son válidos solo para la configuración de carga específica descrita y no pueden reutilizarse.",
            "La entrada de datos precisa es responsabilidad exclusiva del Usuario."
          ]
        },
        {
          title: "9. Fuentes de Datos, Informes y Responsabilidad del Usuario",
          content: [
            "La precisión del cálculo depende completamente de los parámetros proporcionados por el Usuario.",
            "Las fuentes de datos incluyen: valores MSL (especificaciones del fabricante, sin verificación independiente), coeficientes de fricción (estándares de la industria, las condiciones reales pueden variar), puntos de trincaje (especificaciones de la unidad, la integridad física es responsabilidad del Usuario).",
            "Marcos regulatories aplicables por modo de transporte: Código CTU (IMO/OIT/CEPE — multimodal, aplicable a todos los modos), Código CSS (Manual de Seguridad de la Carga de la OMI — solo transporte marítimo), VDI 2700 / EN 12195-1 a EN 12195-4 (transporte por carretera, estándar europeo), Código de Práctica de Aseguración de Carga del DfT del Reino Unido (transporte por carretera, jurisdicción del Reino Unido), Directrices de Carga UIC / TSI (transporte ferroviario), Reglas Uniformes CIM/COTIF (contratos internacionales de transporte ferroviario). Los Usuarios son los únicos responsables de identificar y aplicar el estándar reglamentario correcto para su modo de transporte y jurisdicción específicos.",
            "El Usuario es el único responsable de: Verificar todos los datos de entrada, Asegurar la selección correcta del equipo, Confirmar la aplicabilidad en el mundo real, Validar físicamente los arreglos de trincaje, Contratar profesionales calificados antes de la implementación.",
            "Los informes se generan en función de las entradas en el momento de la creación. El Proveedor no tiene la obligación de conservar los datos de cálculo o los informes, y el Usuario es responsable del archivo."
          ]
        },
        {
          title: "10. Descargo de Responsabilidad de Precisión Técnica",
          content: [
            "La Plataforma se proporciona utilizando una habilidad y cuidado razonables consistentes con los estándares de la industria generalmente aceptados para servicios SaaS. Si bien se realizan esfuerzos razonables para garantizar la precisión, la Plataforma puede contener errores técnicos o tipográficos. No se ofrece ninguna garantía de que los materiales sean completos, actuales o libres de errores.",
            "Los datos y estándares de terceros pueden cambiar con el tiempo. Las actualizaciones no se aplican retroactivamente a los informes generados anteriormente."
          ]
        },
        {
          title: "11. Limitación de Responsabilidad",
          content: [
            "En la medida máxima permitida por la ley inglesa: El Proveedor no será responsable de ninguna pérdida o daño que surja del uso o la confianza en la Plataforma, incluyendo: Pérdida o daño de carga, Daño al buque, Daño al vehículo, Daño al material rodante ferroviario, Lesiones personales o muerte (en la medida máxima permitida por la ley), Interrupción del negocio, Pérdida de beneficios, fondo de comercio u oportunidad, Daños consecuentes, incidentales o punitivos.",
            "En la medida máxima permitida por la ley aplicable, el Proveedor excluye toda responsabilidad derivada de negligencia, tergiversación o incumplimiento de deberes legales, excepto cuando dicha exclusión esté prohibida por la ley.",
            "Límite de Responsabilidad: A menos que se acuerde lo contrario por escrito en un Acuerdo de Venta firmado, la responsabilidad acumulada total del Proveedor no excederá las tarifas pagadas por el Usuario en los doce (12) meses anteriores a la reclamación.",
            "Nada excluye la responsabilidad por fraude o muerte causada por negligencia cuando lo prohíba la ley."
          ]
        },
        {
          title: "12. Indemnización",
          content: [
            "El Usuario acepta indemnizar y eximir de responsabilidad al Proveedor frente a cualquier reclamo de terceros que surja de: Incumplimiento de estos Términos, Datos de entrada inexactos o incompletos, Uso de especificaciones incorrectas u obsoletas, Falta de obtención de verificación profesional, Ejecución física de arreglos de trincaje, Mal uso de credenciales o acceso no autorizado causado por el Usuario."
          ]
        },
        {
          title: "13. Cumplimiento de Control de Exportaciones y Sanciones",
          content: [
            "El Usuario representa y garantiza que no está sujeto a ninguna sanción impuesta por la Unión Europea, el Reino Unido, las Naciones Unidas u otras autoridades aplicables.",
            "El Usuario no utilizará la Plataforma en violación de las leyes de control de exportaciones o sanciones aplicables y será el único responsable de garantizar el uso legal en todas las jurisdicciones."
          ]
        },
        {
          title: "14. Descargo de Responsabilidad de Ciberseguridad",
          content: [
            "El Proveedor implementa medidas de seguridad técnicas y organizativas razonables en línea con la práctica de la industria, teniendo en cuenta la naturaleza de los datos procesados. Sin embargo, el Proveedor no garantiza que la Plataforma esté completamente libre de incidentes cibernéticos, malware, ransomware, acceso no autorizado o violaciones de seguridad, incluidas las derivadas de sistemas o redes de terceros."
          ]
        },
        {
          title: "15. Divulgación de Seguros",
          content: [
            "El Proveedor mantiene un seguro de indemnización profesional y cibernético adecuado de acuerdo con la práctica de la industria. Nada en este Acuerdo se interpretará como una cobertura de seguro, garantías o indemnización para el Usuario o cualquier tercero."
          ]
        },
        {
          title: "16. Sin Confianza",
          content: [
            "El Usuario reconoce que no ha confiado en ninguna declaración, representación o garantía que no se establezca expresamente en este Acuerdo.",
            "No se debe confiar en la Plataforma como sustituto del juicio profesional, la certificación, la aprobación regulatoria o el cumplimiento legal."
          ]
        },
        {
          title: "17. Asunción del Usuario Profesional",
          content: [
            "La Plataforma está destinada al uso por profesionales experimentados en el sector marítimo y logístico. El Proveedor no asume ninguna obligación de explicar los principios marítimos, las obligaciones de seguridad o los requisitos reglamentarios al Usuario."
          ]
        },
        {
          title: "18. Descargo de Responsabilidad de Uso Crítico para la Seguridad",
          content: [
            "La Plataforma no está diseñada ni destinada para la toma de decisiones operativas en tiempo real durante operaciones de carga activa o situaciones de emergencia."
          ]
        },
        {
          title: "19. Sin Derechos de Terceros",
          content: [
            "Ninguna persona que no sea parte de este Acuerdo tendrá ningún derecho bajo la Ley de Contratos (Derechos de Terceros) de 1999 para hacer cumplir cualquier término de este Acuerdo."
          ]
        },
        {
          title: "20. Fuerza Mayor",
          content: [
            "El Proveedor no será responsable por el incumplimiento o retraso en el desempeño causado por eventos fuera de su control razonable, incluidos casos fortuitos, guerras, incidentes cibernéticos, fallas en el servicio en la nube, disputas laborales o acciones gubernamentales."
          ]
        },
        {
          title: "21. Ley Aplicable, Ley Obligatoria de la UE y Jurisdicción",
          content: [
            "Estos Términos se rigen por la Ley Inglesa.",
            "Los tribunales de Inglaterra y Gales tendrán jurisdicción exclusiva.",
            "Nada en estos Términos excluirá la aplicación de disposiciones obligatorias de la ley de la UE que no puedan ser derogadas por contrato bajo el Reglamento (CE) No 593/2008 (Roma I), cuando sea aplicable."
          ]
        },
        {
          title: "22. Precedencia del Idioma",
          content: [
            "Este Acuerdo está redactado en inglés. En caso de cualquier traducción, prevalecerá la versión en inglés."
          ]
        },
        {
          title: "23. General",
          content: [
            "Si alguna disposición de estos Términos se considera inválida o inaplicable, las disposiciones restantes permanecerán en pleno vigor.",
            "El Proveedor puede ceder estos Términos como parte de una fusión, venta o transferencia comercial.",
            "Las cláusulas relacionadas con la responsabilidad, la indemnización, la propiedad intelectual, la ley aplicable y los descargos de responsabilidad sobrevivirán a la terminación."
          ]
        },
        {
          title: "24. Protección de Datos y Privacidad",
          content: [
            "24.1 En relación con la prestación de la Plataforma, el Usuario actúa como Responsable del Tratamiento y el Proveedor actúa como Encargado del Tratamiento en el sentido del RGPD del Reino Unido, el RGPD de la UE y la Ley de Protección de Datos de 2018.",
            "24.2 El Proveedor procesará los Datos Personales únicamente siguiendo instrucciones documentadas del Usuario y de acuerdo con las Leyes de Protección de Datos Aplicables.",
            "24.3 El Anexo de Procesamiento de Datos establecido en el Anexo 1 forma parte integral de estos Términos y rige el procesamiento de Datos Personales por parte del Proveedor en nombre del Usuario.",
            "24.4 En caso de conflicto entre estos Términos y el Anexo de Procesamiento de Datos, el Anexo de Procesamiento de Datos prevalecerá en relación con los asuntos de protección de datos."
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. Definiciones",
            "A los efectos de este Anexo de Procesamiento de Datos ('DPA'), los términos 'Datos Personales', 'Procesamiento', 'Controlador', 'Procesador' y 'Autoridad de Supervisión' tendrán los significados dados en el RGPD del Reino Unido y el RGPD de la UE.",
            "",
            "2. Alcance y Roles",
            "2.1 El Usuario es el Controlador de los Datos Personales procesados en relación con el uso de la Plataforma.",
            "2.2 El Proveedor actúa únicamente como Procesador y no procesará Datos Personales para ningún otro propósito que no sea proporcionar la Plataforma.",
            "",
            "3. Naturaleza y Propósito del Procesamiento",
            "Propósito: Prestación de servicios SaaS, administración de cuentas, soporte técnico y generación de informes.",
            "Categorías de Interesados: Usuarios autorizados, empleados, contratistas.",
            "Tipos de Datos Personales: Detalles de contacto profesional (nombre, correo electrónico, teléfono), detalles de identificación de la empresa.",
            "Categorías Especiales: Ninguna prevista.",
            "",
            "4. Obligaciones del Procesador",
            "El Proveedor deberá:",
            "a) Procesar Datos Personales solo siguiendo instrucciones documentadas del Usuario",
            "b) Asegurar que el personal autorizado para procesar Datos Personales esté sujeto a obligaciones de confidencialidad",
            "c) Implementar medidas técnicas y organizativas apropiadas para proteger los Datos Personales",
            "d) Asistir al Usuario, cuando se solicite razonablemente, con las solicitudes de derechos de los interesados",
            "e) Notificar al Usuario sin demora indebida al tener conocimiento de una Violación de Datos Personales",
            "f) Eliminar o devolver los Datos Personales al finalizar los Servicios, a menos que la ley exija su retención",
            "",
            "5. Sub-procesadores",
            "5.1 El Usuario autoriza al Proveedor a contratar sub-procesadores para servicios de alojamiento, infraestructura y soporte.",
            "5.2 El Proveedor se asegurará de que los sub-procesadores estén sujetos a obligaciones de protección de datos no menos protectoras que las establecidas en este DPA.",
            "",
            "6. Transferencias Internacionales",
            "Cuando los Datos Personales se transfieran fuera del Reino Unido o del EEE, el Proveedor se asegurará de que existan las garantías adecuadas, incluidos los Acuerdos Internacionales de Transferencia de Datos del Reino Unido o las Cláusulas Contractuales Estándar de la UE, según corresponda.",
            "",
            "7. Medidas de Seguridad",
            "El Proveedor mantendrá medidas de seguridad técnicas y organizativas razonables adecuadas a la naturaleza de los datos y los riesgos involucrados, incluidos los controles de acceso, el cifrado cuando corresponda y los procedimientos de respuesta a incidentes.",
            "",
            "8. Auditorías",
            "A solicitud razonable por escrito, y no más de una vez al año, el Proveedor pondrá a disposición la información necesaria para demostrar el cumplimiento de este DPA, sujeto a requisitos de confidencialidad y seguridad.",
            "",
            "9. Responsabilidad",
            "La responsabilidad derivada de este DPA estará sujeta a las limitaciones y exclusiones de responsabilidad establecidas en los Términos de Servicio.",
            "",
            "10. Ley Aplicable",
            "Este DPA se regirá e interpretará de acuerdo con las leyes de Inglaterra y Gales."
          ]
        }
      ],
      detailed_privacy: [
        {
          title: "1. Introducción",
          content: [
            "Esta Política de Privacidad explica cómo JADE CONSULTANCY AND TRADING LTD, una empresa que opera bajo las leyes de Inglaterra y Gales ('Proveedor', 'nosotros', 'nos', 'nuestro'), recopila, utiliza, almacena y protege los datos personales cuando utiliza la aplicación web Lashing Calculator ('Plataforma').",
            "Nos comprometemos a proteger los datos personales de acuerdo con el Reglamento General de Protección de Datos del Reino Unido (UK GDPR) y la Ley de Protección de Datos de 2018."
          ]
        },
        {
          title: "2. Responsable del Tratamiento",
          content: [
            "A los efectos de la ley de protección de datos aplicable, el Proveedor actúa como responsable del tratamiento en relación con los datos personales procesados a través de la Plataforma."
          ]
        },
        {
          title: "3. Información que Recopilamos",
          content: [
            "3.1 Datos de Cuenta y Contacto: Nombre, Nombre de la empresa, Dirección de correo electrónico comercial, Números de teléfono, Credenciales de inicio de sesión, Detalles de facturación. Estos datos se utilizan estrictamente para la administración de cuentas, autenticación, facturación y comunicaciones de servicio.",
            "3.2 Datos Industriales y Operativos: Parámetros y configuraciones de carga, Cálculos de trincaje y entradas de informes. Estos datos se tratan como Información Industrial Confidencial. No se analizan para reventa comercial, elaboración de perfiles o fines de marketing.",
            "3.3 Datos Técnicos: Dirección IP, Tipo de navegador, Información del dispositivo y de la sesión, Registros de acceso. Estos datos se recopilan para seguridad, monitoreo del rendimiento y detección de uso indebido."
          ]
        },
        {
          title: "4. Base Legal para el Procesamiento",
          content: [
            "Procesamos datos personales sobre las siguientes bases legales bajo el Artículo 6 del UK GDPR:",
            "Necesidad contractual – para proporcionar y operar la Plataforma",
            "Intereses legítimos – para garantizar la seguridad, prevenir el uso indebido y mejorar el rendimiento del servicio",
            "Obligación legal – para cumplir con los requisitos contables, fiscales y reglamentarios",
            "Consentimiento, cuando se obtenga explícitamente (por ejemplo, comunicaciones opcionales)"
          ]
        },
        {
          title: "5. Cómo Utilizamos Su Información",
          content: [
            "Utilizamos los datos recopilados para:",
            "Proporcionar, operar y mantener la Plataforma",
            "Procesar cálculos y generar informes profesionales",
            "Gestionar suscripciones, facturación y atención al cliente",
            "Garantizar la alineación con los marcos de seguridad internacionales (Código CTU / Código CSS)",
            "Monitorear la seguridad del sistema y prevenir el acceso no autorizado",
            "Mejorar y desarrollar la funcionalidad de la Plataforma",
            "Nosotros no vendemos datos personales a terceros."
          ]
        },
        {
          title: "6. Intercambio de Datos y Terceros",
          content: [
            "Los datos personales solo pueden compartirse con:",
            "Proveedores de infraestructura de alojamiento y nube",
            "Proveedores de servicios de pago y facturación",
            "Asesores profesionales (legales, contables, seguros)",
            "Autoridades cuando lo requiera la ley",
            "Todos los terceros están sujetos a obligaciones apropiadas de confidencialidad y protección de datos."
          ]
        },
        {
          title: "7. Transferencias Internacionales de Datos",
          content: [
            "Cuando los datos personales se transfieren fuera del Reino Unido o el EEE, garantizamos que se apliquen las salvaguardias adecuadas, que incluyen:",
            "Decisiones de adecuación",
            "Cláusulas Contractuales Estándar (SCC)",
            "Mecanismos legales equivalentes de transferencia"
          ]
        },
        {
          title: "8. Retención de Datos",
          content: [
            "Retenemos datos personales solo durante el tiempo que sea necesario:",
            "Datos de cuenta y facturación: durante la duración del contrato y según lo exija la ley",
            "Registros técnicos: retenidos con fines de seguridad y auditoría durante períodos limitados",
            "Datos de cálculo industrial: retenidos solo según sea necesario para proporcionar el Servicio",
            "Los usuarios siguen siendo responsables de archivar sus propios informes y resultados de cálculo."
          ]
        },
        {
          title: "9. Seguridad de Datos",
          content: [
            "Implementamos medidas técnicas y organizativas razonables para proteger los datos personales contra el acceso no autorizado, pérdida, alteración o divulgación, incluidos controles de acceso, cifrado y monitoreo.",
            "Sin embargo, no se puede garantizar que ningún sistema sea completamente seguro."
          ]
        },
        {
          title: "10. Sus Derechos",
          content: [
            "Bajo el UK GDPR, usted tiene derecho a:",
            "Acceder a sus datos personales",
            "Rectificar datos inexactos",
            "Solicitar la eliminación (sujeto a obligaciones legales)",
            "Restringir u oponerse al procesamiento",
            "Portabilidad de datos",
            "Retirar el consentimiento cuando corresponda",
            "Las solicitudes pueden realizarse utilizando los datos de contacto a continuación."
          ]
        },
        {
          title: "11. Quejas",
          content: [
            "Si cree que se han infringido sus derechos de protección de datos, tiene derecho a presentar una queja ante la Oficina del Comisionado de Información del Reino Unido (ICO)."
          ]
        },
        {
          title: "12. Cambios en Esta Política",
          content: [
            "Podemos actualizar esta Política de Privacidad de vez en cuando. Las versiones actualizadas estarán disponibles en la Plataforma con una fecha de vigencia revisada."
          ]
        },
        {
          title: "13. Contáctenos",
          content: [
            "Para consultas sobre privacidad o protección de datos:",
            "Correo electrónico: info@lashingplan.com"
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "Protección de Datos y Privacidad",
          content: "Nos comprometemos a proteger su privacidad. Esta política explica cómo se recopila, utiliza y protege su información de acuerdo con los estándares mundiales."
        },
        collection: {
          title: "Información que Recopilamos",
          personal_title: "Datos de la Cuenta",
          personal_desc: "Información profesional básica utilizada para la gestión de cuentas e informes técnicos.",
          calc_title: "Datos Industriales",
          calc_desc: "Parámetros de carga y configuraciones. Estos datos se tratan como 'Información Industrial Confidencial'."
        },
        usage: {
          title: "Cómo Utilizamos la Información",
          intro: "Utilizamos la información recopilada de las siguientes maneras:",
          items: [
            "Para proporcionar, operar y mantener el servicio seguro",
            "Para procesar cálculos y generar informes PDF profesionales",
            "Para garantizar el cumplimiento de las normas internacionales de seguridad (CTU/CSS)",
            "Para mejorar y ampliar las capacidades del servicio"
          ]
        },
        compliance: {
          title: "Cumplimiento Global",
          content: "Tomamos medidas razonables para garantizar que usted tenga control sobre sus datos de acuerdo con los estándares GDPR del Reino Unido y la Ley de Protección de Datos de 2018.",
          note: "Nota: no vendemos sus datos personales a terceros."
        },
        retention: {
          title: "Retención de Datos",
          content: "Retenemos su información personal solo el tiempo que sea necesario para el servicio y según lo requieran las obligaciones legales."
        },
        contact: {
          title: "Contáctenos",
          content: "Para preguntas sobre derechos de datos o privacidad:",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. Introducción",
          content: [
            "Esta Política de Cookies explica cómo JADE CONSULTANCY AND TRADING LTD (“nosotros”, “nos”, “nuestro”) utiliza cookies y tecnologías similares en el sitio web y la aplicación web Lashing Calculator (“Plataforma”).",
            "Esta política debe leerse junto con nuestra Política de Privacidad."
          ]
        },
        {
          title: "2. ¿Qué son las Cookies?",
          content: [
            "Las cookies son pequeños archivos de texto almacenados en su dispositivo cuando visita un sitio web. Se utilizan ampliamente para permitir la funcionalidad del sitio web, mejorar la experiencia del usuario y proporcionar información a los operadores del sitio web."
          ]
        },
        {
          title: "3. Marco Legal",
          content: [
            "Utilizamos cookies de acuerdo con:",
            "El Reglamento de Privacidad y Comunicaciones Electrónicas del Reino Unido (PECR)",
            "La Directiva de Privacidad Electrónica de la UE (ePrivacy Directive)",
            "El Reglamento General de Protección de Datos del Reino Unido (UK GDPR)",
            "A excepción de las cookies estrictamente necesarias, las cookies se utilizan solo con su consentimiento previo."
          ]
        },
        {
          title: "4. Tipos de Cookies que Utilizamos",
          content: [
            "4.1 Cookies Estrictamente Necesarias",
            "Estas cookies son esenciales para el funcionamiento de la Plataforma y permiten funciones básicas como:",
            "Autenticación de usuario",
            "Gestión de sesiones",
            "Seguridad y prevención de fraudes",
            "Estas cookies no requieren consentimiento y no pueden desactivarse a través de nuestra configuración de cookies.",
            "Base legal: Necesario para la prestación del servicio solicitado por el usuario.",
            "",
            "4.2 Cookies Analíticas",
            "Las cookies analíticas nos ayudan a comprender cómo se utiliza la Plataforma para que podamos mejorar el rendimiento y la usabilidad.",
            "Los ejemplos incluyen:",
            "Recuento de visitas a la página",
            "Fuentes de tráfico",
            "Patrones de uso",
            "Las cookies analíticas se colocan solo después de haber dado su consentimiento explícito.",
            "Base legal: Consentimiento bajo las reglas de UK GDPR y ePrivacy."
          ]
        },
        {
          title: "5. Proveedores de Cookies",
          content: [
            "Las cookies analíticas pueden ser proporcionadas por proveedores de análisis de terceros de confianza. Estos proveedores procesan datos de acuerdo con sus propias políticas de privacidad y las leyes de protección de datos aplicables.",
            "No utilizamos cookies para publicidad dirigida o perfiles de marketing."
          ]
        },
        {
          title: "6. Duración de las Cookies",
          content: [
            "Las cookies pueden ser:",
            "Cookies de sesión, que caducan al cerrar el navegador",
            "Cookies persistentes, que permanecen en su dispositivo durante un período definido o hasta que se eliminen",
            "La vida útil específica de las cookies depende de su propósito y proveedor."
          ]
        },
        {
          title: "7. Gestión de sus Preferencias de Cookies",
          content: [
            "Puede gestionar o retirar su consentimiento de cookies en cualquier momento:",
            "Utilizando la configuración de cookies o el banner de consentimiento disponible en la Plataforma",
            "Ajustando la configuración de su navegador para bloquear o eliminar cookies",
            "Tenga en cuenta que deshabilitar ciertas cookies puede afectar la funcionalidad de la Plataforma."
          ]
        },
        {
          title: "8. Cambios en esta Política de Cookies",
          content: [
            "Podemos actualizar esta Política de Cookies de vez en cuando. Cualquier cambio se publicará en la Plataforma con una fecha de vigencia actualizada."
          ]
        },
        {
          title: "9. Contáctenos",
          content: [
            "Si tiene alguna pregunta sobre nuestro uso de cookies, contáctenos en:",
            "Correo electrónico: info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "Política de Cookies",
        last_updated: "Fecha de Vigencia: 4 de febrero de 2026",
        intro_title: "Introducción",
        intro_content: "Esta Política de Cookies explica cómo se utilizan las cookies cuando visita el sitio web de Lashing Calculator.",
        types_title: "Tipos de Cookies que Utilizamos",
        essential_title: "Cookies Esenciales",
        essential_desc: "Estas cookies son estrictamente necesarias para que el sitio web funcione correctamente y no se pueden desactivar en nuestros sistemas.",
        analytics_title: "Cookies Analíticas",
        analytics_desc: "Estas cookies nos permiten contar las visitas y las fuentes de tráfico para que podamos medir y mejorar el rendimiento de nuestro sitio.",
        manage_title: "Gestión de sus Preferencias de Cookies",
        manage_content: "Puede gestionar sus preferencias de cookies en cualquier momento a través de la configuración de su navegador.",
        contact_title: "Contáctenos",
        contact_text: "Si tiene alguna pregunta sobre nuestra política de cookies, contáctenos."
      },
      sales_agreement: {
        title: "Acuerdo de Venta",
        content: "Este acuerdo de venta describe los términos y condiciones para la provisión y el uso de los servicios de Lashing Calculator. La compra de nuestros servicios significa su aceptación de todos los términos de este acuerdo."
      },
      legal_notice: {
        title: "Aviso Legal",
        content: "La información en este sitio web es solo para fines de información general. Si bien Lashing Calculator se esfuerza por mantener la información actualizada y correcta, no hacemos representaciones ni garantías de ningún tipo."
      },
      footer_note: "Al utilizar la plataforma, usted confirma que ha leído y acepta los avisos de seguridad, riesgos técnicos y términos legales, incluyendo el requisito de verificación profesional de todos los resultados."
    }
  },

  // ===================================================================
  // DEUTSCH
  // ===================================================================
  "Deutsch": {
    code: "de",
    name: "Deutsch",
    flag: "Germany",

    hub: {
      nav: {
        products: "Produkte",
        insights: "Markteinblicke",
        resources: "Ressourcen",
        go_to_app: "Zur App gehen"
      },
      hero: {
        badge: "FRACHTLOGISTIK-HUB DER NÄCHSTEN GENERATION",
        status: "Systemstatus: Optimal",
        title_main: "Berechnete Festigkeit",
        title_italic: "absolute sicherheit",
        title_1: "Planung für",
        title_2: "Maximale Sicherheit.",
        description: "LashingPlan ist ein zentrales Ökosystem für professionelle Ladungssicherungswerkzeuge, präzise berechnet und konform mit internationalen Standards.",
        explore: "Produkte erkunden",
        launch_product: "PRODUKT STARTEN",
        read_blog: "Blog lesen",
        portal_access: "PORTAL-ZUGANG V2.0",
        stability_index: "STABILITÄTSINDEX",
        archives: "ARCHIV",
        archives_subtitle: "Bleiben Sie mit professionellen Sicherheitsstandards und Logistiktrends auf dem Laufenden."
      },
      footer: {
        tagline: "Ein Produkt technischer Exzellenz. Entwickelt für diejenigen, die absolute Präzision in der maritimen Sicherheit verlangen.",
        systems: "SYSTEME",
        resources: "RESSOURCEN",
        legal: "RECHTLICHES",
        privacy: "Datenschutz",
        compliance: "Compliance",
        terms: "Bedingungen",
        status_all: "LASHING PLAN GLOBAL // ALLE SYSTEME BETRIEBSBEREIT",
        follow: "Folgen Sie uns"
      },
      products: {
        title: "Unsere Produkte",
        subtitle: "Professionelle Tools für globale Versand-Compliance und Ladungssicherheit.",
        context: "01 / Produkte",
        calculator: {
          title: "Lashing Calculator",
          description: "Die branchenübliche Engine zur Berechnung der Ladungssicherungsanforderungen für Straße, See und Schiene.",
          button: "App starten"
        },
        inspection: {
          title: "Inspection Pro",
          description: "Fotogestütztes digitales Inspektions- und sofortiges Zertifikatserstellungstool für Felduntersuchungen.",
          status: "DEMNÄCHST"
        },
        risk: {
          title: "Risk Advisor",
          description: "KI-gestütztes Stabilitätsanalyse- und Versandrisikobewertungssystem.",
          status: "DEMNÄCHST"
        },
        stats: {
          calculations: "BERECHNUNGEN",
          countries: "LÄNDER",
          compliance: "COMPLIANCE",
          precision: "PRÄZISION"
        },
        blog_articles: {
          article_1: {
            title: "Cargo Securing: Lashing",
            description: "Securing cargo to the transport unit before shipment is the foundation of logistics safety..."
          },
          article_2: {
            title: "Innovative Cargo Freight Solutions",
            description: "Forward-thinking cargo solutions emphasizing pioneering innovation and reliable lashing..."
          },
          article_3: {
            title: "Understanding IMO/CTU Code Compliance",
            description: "A deep dive into the international standards regulating cargo transport in containers..."
          },
          article_4: {
            title: "Enhancing Cargo Safety Standards",
            description: "Improving safety with modern protocols, technology integration, and proven safety practices..."
          }
        }
      },
      insights: {
        title: "Markteinblicke",
        subtitle: "Erfahren Sie von unseren Experten mehr über Ladungssicherungsstandards, Branchen-Compliance und die neuesten Logistiktrends.",
        all_articles: "Alle Artikel ansehen"
      }
    },
    blog: {
      nav: {
        back: "Zurück zum Start"
      },
      header: {
        title_1: "Einblicke von",
        title_2: "LashingPlan"
      },
      article: {
        read_time: "Min. Lesezeit",
        share: "Teilen",
        read_more: "Weiterlesen",
        cta_title: "Bereit, Ihre Ladung zu sichern?",
        cta_desc: "Schließen Sie sich Tausenden von Profis an, die unseren Tools für präzise und konforme Zurrberechnungen vertrauen.",
        cta_button: "Jetzt mit der Berechnung beginnen"
      },
      footer: {
        copyright: "© 2026 LashingPlan Blog. Alle Einblicke vorbehalten."
      }
    },

    app: {
      title: "Zurrrechner",
      logout: "Abmelden",
      theme_toggle: "Dunkel- / Hellmodus",
      profile: "Profil",
      reports: "Feldberichte",
      login: {
        title: "Anmelden",
        subtitle: "Melden Sie sich an Ihrem Konto an",
        email: "E-Mail oder Benutzername",
        email_placeholder: "Geben Sie Ihre E-Mail-Adresse oder Benutzernamen ein",
        password: "Passwort",
        password_placeholder: "Geben Sie Ihr Passwort ein",
        forgot_password: "Passwort vergessen?",
        submit: "Anmelden",
        no_account: "Noch kein Konto?",
        signup: "Registrieren",
        back_to_home: "Zurück zur Startseite",
        consent: "Durch Fortfahren akzeptieren Sie die {terms} und {privacy}.",
        surveyor_portal: "Sind Sie Gutachter (Surveyor)? Zum Portal wechseln"
      },
      cog3d: {
        title: "3D-Schwerpunkt (CoG) Ansicht",
        subtitle: "Der Schwerpunkt (CoG) ist der Gleichgewichtspunkt der Ladung und bestimmt das Kippverhalten.",
        drag_hint: "Zum Drehen ziehen"
      },
      lashing3d: {
        title: "3D-Zurrsystem-Diagramm",
        subtitle: "Interaktive Ansicht der Zurrkonfiguration"
      },
      calculator: {
        title: "Ladungsrechner",
        cargo_details: "Ladedetails",
        weight: "Gewicht (MT)",
        cog: {
          title: "Schwerpunkt (cm von der unteren linken Ecke)",
          x: "X (Breite)",
          y: "Y (Tiefe)",
          z: "Z (Höhe)"
        },
        securing_elements: "Sicherelemente",
        wooden_dunnage: {
          label: "Holzunterlagen verwenden",
          description: "Erhöht die Reibung und verhindert das Rutschen",
          details: "Details zu Holzunterlagen",
          width: "Breite (cm)"
        },
        loop_lashing: "Rundumzurrung",
        direct_lashing: "Direktzurrung",
        select_rope_type: "Seiltyp auswählen",
        stopper_forces: "Stopperkräfte (daN)",
        direction: {
          forward: "Vorwärts",
          backward: "Rückwärts",
          right: "Rechts",
          left: "Links"
        }
      },
      results: {
        title: "Ergebnisse",
        lashing_diagram: "Zurrdiagramm",
        error: "Fehler",
        loading: "Wird geladen...",
        no_results: "Keine Ergebnisse gefunden",
        values: {
          sliding: "Rutschen:",
          tipping: "Kippen:",
          friction: "Reibung:",
          securing_element: "Sicherelement:",
          net_sliding: "Netto-Rutschen:",
          net_tipping: "Netto-Kippen:"
        }
      }
    },
    landing: {
      contact: {
        label: "Vertrieb kontaktieren",
        title_1: "Bereit, Ihre ",
        title_2: "Frachtoperationen zu sichern?",
        description: "Rüsten Sie auf Surveyor-Algorithmen der Enterprise-Klasse auf. Kontaktieren Sie unser Engineering-Team, um benutzerdefinierte API-Integrationen, Volumenlizenzen oder Premium-Funktionen zu besprechen.",
        email_us: "E-Mail senden",
        enterprise_support: "Enterprise-Support",
        support_desc: "24/7 Priority SLA verfügbar",
        success_title: "Nachricht gesendet!",
        success_desc: "Unser Engineering-Team wird sich innerhalb von 24 Stunden bei Ihnen melden.",
        form: {
          name: "Vollständiger Name",
          placeholder_name: "Max Mustermann",
          company: "Unternehmen",
          placeholder_company: "Logistics GmbH",
          email: "E-Mail-Adresse",
          placeholder_email: "max@unternehmen.de",
          message: "Ihre Nachricht",
          placeholder_message: "Wie können wir Ihnen helfen, Ihre Laschvorgänge zu skalieren?",
          send: "Anfrage senden"
        }
      },
      hero_badge: "Von Ladungssicherungsprofis weltweit geschätzt",
      hero_badge_imo: "IMO-konform",
      hero_badge_imo_sub: "Internationale Standards",
      hero_badge_3d: "3D-Visualisierung",
      hero_badge_3d_sub: "Echtzeit",
      hero_title_1: "Professionelle",
      hero_title_2: "Zurrintelligenz",
      hero_subtitle: "Globale Standards. Präzise Algorithmen. Professionelle Berichte. Die fortschrittlichste Ladungssicherungsplattform der Branche.",
      start_calculating: "Einloggen",
      view_demo: "Demo ansehen",
      stats: {
        modes: "6+ Transportmodi",
        platform: "Cloud-Plattform",
        platform_status: "Gesichert",
        access: "Globaler Zugriff"
      },
      features_title_1: "Leistung trifft",
      features_title_2: "Compliance",
      features_subtitle: "Von Ladungssicherheitsexperten für anspruchsvolle Schifffahrtsabläufe entwickelt.",
      features: {
        calculations: {
          title: "Sofortige Berechnungen",
          desc: "Berechnen Sie Zurrkräfte in Echtzeit mit unserem fortschrittlichen Algorithmus."
        },
        pdf: {
          title: "Professionelle PDF-Berichte",
          desc: "Erstellen Sie branchenübliche Berichte mit Unternehmensbranding und detaillierter Analyse."
        },
        compliance: {
          title: "CTU-basierte Bewertung",
          desc: "Vereinfachte Entscheidungshilfe auf Basis des IMO / ILO / UNECE CTU-Codes."
        },
        multimodal: {
          title: "Multimodaler Transport",
          desc: "Unterstützung für Straßen-, Schienen- und Seetransport. Container, Flatracks, Schiffe und mehr."
        },
        global: {
          title: "Globale Standards",
          desc: "Nachvollziehbare Bewertung auf Basis des CTU-Codes 2014 und des Informationsmaterials."
        },
        secure: {
          title: "Sicher & Vertraulich",
          desc: "Ihre Berechnungsdaten sind durch Sicherheit auf Enterprise-Niveau geschützt."
        }
      },
      transport: {
        title_1: "Einheitliche",
        title_2: "Sicherheit",
        subtitle: "Nahtlose Berechnungen über die gesamte Logistikkette hinweg.",
        modes: {
          road: { name: "Straße", desc: "LKW-Anhänger und Tieflader" },
          rail: { name: "Schiene", desc: "Eisenbahnwaggon, Container, Flatrack und LKW-Anhänger" },
          sea: { name: "See", desc: "Container, LKW-Anhänger, Flatrack, Tieflader, Roll-Trailer, Schiff" },
          combined: { name: "Kombiniert", desc: "Straße-See, Straße-Schiene, Straße-See-Schiene" }
        }
      },
      how_it_works: {
        badge: "Wie es funktioniert",
        title_1: "Professionelle Analyse in",
        title_2: "3 einfachen Schritten",
        subtitle: "Komplexe Ladungssicherungsberechnungen in Minuten abschließen",
        step1: {
          title: "Frachtdetails eingeben",
          desc: "Geben Sie Frachtabmessungen, Gewicht, Schwerpunkt und Containertyp in das System ein."
        },
        step2: {
          title: "Automatische Berechnung",
          desc: "Das System berechnet automatisch die optimale Zurranordnung gemäß IMO-Standards",
          desc_highlight: " und ermöglicht manuelle Änderungen im Plan."
        },
        step3: {
          title: "Bericht erhalten",
          desc: "Erstellen Sie einen professionellen PDF-Bericht mit 3D-Visualisierung und detaillierten Berechnungen."
        }
      },
      benefits: {
        title: "Für kritische Einsätze entwickelt",
        subtitle: "Weltweit vertrauen Experten, Besichtiger und Terminalbetreiber auf uns, um die Sicherheit kritischer Lasten zu gewährleisten.",
        list: [
          "Sparen Sie Stunden bei manuellen Berechnungen",
          "Reduzieren Sie Materialabfall durch Optimierung",
          "Sicherstellen der vollständigen Einhaltung von Vorschriften",
          "Bieten Sie Kunden professionelle Dokumentationen",
          "Mehrsprachige Unterstützung (EN, TR, ES, DE, ZH, JA)",
          "PDF-Berichte mit Unternehmensbranding"
        ],
        cta_card_title: "Standardisieren Sie noch heute Ihre Zurrplanung",
        cta_card_desc: "Nutzen Sie einen Enterprise-Rechner für Ihr Team und stellen Sie sicher, dass alle Sendungen zu 100% konform sind.",
        cta_card_trial: "Ohne Kreditkarte testen"
      },
      footer: {
        desc: "Schließen Sie sich weltweit Profis an, die unseren intelligenten Zurrrechner nutzen.",
        columns: {
          service: "Dienstleistungen",
          company: "Unternehmen",
          support: "Support"
        },
        links: {
          privacy: "Datenschutzzentrum",
          compliance: "Regeln",
          cookies: "Cookie-Richtlinie",
          security: "Sicherheit",
          help: "Hilfezentrum",
          enterprise: "Unternehmen",
          contact: "Experten kontaktieren",
          force_analysis: "Kraftanalyse",
          pdf_generator: "PDF-Generator",
          optimizer: "Optimierer",
          api_docs: "API-Dokumentation",
          cookie_settings: "Cookie-Einstellungen"
        }
      },
      footer_copyright: "2025 Lashing Calculator. Ein Dienst von JADE CONSULTANCY AND TRADING LTD. Alle Rechte vorbehalten.",

      pricing_simple: "Einfache, ",
      pricing_transparent: "Transparente",
      pricing_title: " Preise",
      compare_features: "Alle Funktionen vergleichen",
      footer_global: "Global",
      footer_encrypted: "Verschlüsselt",
      footer_high_availability: "Hohe Verfügbarkeit",
      demo_request: {
        title: "Demo anfordern",
        subtitle: "Sehen Sie, wie der Lashing Calculator Ihre Ladungssicherungsabläufe transformieren kann.",
        cta_title: "Bereit, es in Aktion zu sehen?",
        cta_subtitle: "Vereinbaren Sie einen persönlichen Termin mit unseren Frachtspezialisten und entdecken Sie, wie wir Ihre Ladungssicherung weltweit standardisieren können.",
        form: {
          full_name: "Vollständiger Name",
          work_email: "Geschäftliche E-Mail",
          company: "Unternehmensname",
          job_title: "Berufsbezeichnung",
          phone: "Telefonnummer",
          fleet_size: "Flottengröße / Monatliche Sendungen",
          industry: "Branche",
          message: "Nachricht (Optional)",
          submit: "Demo-Anfrage senden",
          success_title: "Anfrage erhalten",
          success_message: "Ihre Demo-Anfrage wurde erfolgreich übermittelt. Unser Team wird Sie in Kürze kontaktieren."
        }
      },
      price_inquiry: {
        title: "Angebot anfordern",
        subtitle: "Kontaktieren Sie uns für individuelle Preise und Unternehmenslösungen.",
        form: {
          full_name: "Vollständiger Name",
          work_email: "Geschäftliche E-Mail",
          company: "Unternehmensname",
          phone: "Telefonnummer",
          message: "Nachricht (Optional)",
          submit: "Anfrage senden",
          success_title: "Anfrage gesendet",
          success_message: "Ihre Preisanfrage ist eingegangen. Unser Vertriebsteam wird sich in Kürze mit einem individuellen Angebot bei Ihnen melden."
        }
      }
    },

    profile: {
      title: "Unternehmensprofil",
      subtitle: "Verwalten Sie Ihre Unternehmensinformationen für PDF-Berichte",
      logo_label: "Firmenlogo",
      logo_note: "Das Logo kann nur von Administratoren aktualisiert werden.",
      company_name: "Mein Profil",
      address: "Vollständige Adresse",
      phone: "Telefon / Mobil",
      email: "E-Mail (Konto)",
      tax_office: "Finanzamt",
      tax_number: "Steuernummer",
      company_email_label: "Firmen-E-Mail (Sichtbar im PDF)",
      user_name_label: "Benutzername (Konto)",
      terms_accepted: "Allgemeine Geschäftsbedingungen akzeptiert am",
      terms_not_accepted: "Allgemeine Geschäftsbedingungen noch nicht akzeptiert",
      view_terms: "Bedingungen anzeigen",
      accept_terms: "Bedingungen akzeptieren",
      review_accept: "Überprüfen & Akzeptieren",
      admin_note: "Hinweis: Unternehmensdetails werden von Administratoren verwaltet.",
      cancel: "Abbrechen",
      accept: "Bedingungen akzeptieren",
      modal_title: "Allgemeine Geschäftsbedingungen",
      modal_subtitle: "Bitte lesen und akzeptieren Sie unsere Bedingungen, um fortzufahren.",
      modal_agree: "Durch die Annahme erklären Sie sich mit den {tos} und der {privacy} einverstanden.",
      modal_ack: "Sie erkennen an, dass die Software ein Werkzeug ist und die endgültige Verantwortung für Zurrpläne beim zertifizierten Fachmann liegt.",
      checkbox_label: "Ich habe die Allgemeinen Geschäftsbedingungen gelesen und akzeptiere sie",
      save: "Änderungen speichern",
      saving: "Speichern...",
      success_message: "Profil erfolgreich aktualisiert.",
      error_message: "Aktualisierungsfehler: {error}",
      credits_title: "Guthaben- und Nutzungsinformationen",
      calc_credits: "Berechnungsguthaben",
      pdf_credits: "PDF-Berichtsguthaben",
      monthly_increment: "Monatliche Erhöhung",
      next_increment: "Nächste Aufladung",
      remaining: "Verbleibend",
      lashing_display_label: "Anzeige-Format",
      lashing_display_msl: "MSL (z.B. MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (z.B. CL200 HDB12L)",
      pdf_theme_title: "PDF Design Theme",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Dark Turquoise & Slate Gray",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Corporate Navy & Light Gray",
      theme_standart_title: "Standard",
      theme_standart_desc: "Original App Colors"
    },
    surveys: {
      title: "Feldberichte",
      create_new: "Neuen Bericht erstellen",
      total_ops: "Gesamtoperationen",
      pending_photos: "Anstehende Fotos",
      completed_assignments: "Abgeschlossene Zuweisungen",
      current_reports: "Aktuelle Berichte",
      search_placeholder: "ID oder Projekt suchen...",
      no_reports_title: "Noch keine Berichte",
      no_reports_desc: "Beginnen Sie mit Ihrer ersten Lashing-Berechnung, um einen Feldbericht zu erstellen.",
      create_now: "Jetzt erstellen",
      unnamed_project: "Unbenanntes Projekt",
      created_date: "ERSTELLT",
      data_status: "Daten",
      uploaded: "Anwendungsfotos hochgeladen",
      waiting: "Warten auf Anwendungsfotos",
      surveyor: "Surveyor",
      waiting_response: "Warten auf Antwort",
      under_review: "In Prüfung",
      report_ready: "Bericht bereit",
      cancelled_rejected: "Abgebrochen / Abgelehnt",
      send_link: "Feld-Upload-Link senden",
      upload_manually: "Feldfotos manuell hochladen",
      assign_new: "Neuen Surveyor zuweisen",
      assign_surveyor: "Surveyor zuweisen",
      assigned: "Zugewiesen",
      summary_pdf: "Zusammenfassung PDF",
      surveyor_pdf: "Surveyor PDF",
      assignment_success: "Surveyor-Zuweisung erfolgreich!",
      old_report_error: "Sicherer Link für alte Berichte nicht generiert. Bitte erstellen Sie den Bericht neu.",
      session_expired: "Sitzung abgelaufen. Bitte Seite neu laden oder erneut anmelden.",
      pdf_error: "Fehler beim Erstellen der PDF.",
      download_failed: "Download fehlgeschlagen: ",
      wait: "Warten...",
      connecting: "Verbinde...",
      access_error: "Zugriffsfehler",
      modal: {
        title: "Surveyor zuweisen",
        search_placeholder: "Suche nach Stadt, Hafen oder Namen...",
        list_error: "Surveyor-Liste konnte nicht geladen werden.",
        assign_failed: "Zuweisung fehlgeschlagen.",
        loading: "Laden...",
        not_found: "Kein Surveyor gefunden.",
        unknown_location: "Unbekannt",
        assigning: "Zuweisung erfolgt...",
        assign_button: "Diesen Surveyor zuweisen"
      },
      guest: {
        title: "Portal für die Überwachung der Feldimplementierung",
        subtitle: "Hier können Sie Feldfotos für Ihre Lashing-Anwendung hochladen.",
        invalid_link: "Ungültiger oder abgelaufener Link.",
        upload_success_title: "Hochladen erfolgreich",
        upload_success_desc: "Feldfotos für Ihre Lashing-Anwendung wurden erfolgreich gespeichert. Ihr Bericht ist nun bereit zur Überprüfung und PDF-Genehmigung.",
        close_window: "Fenster schließen",
        info_title: "1. Informationen zum Implementierer (Optional)",
        date: "Datum",
        company_name: "Firmenname",
        company_placeholder: "z.B.: X Logistics",
        implementer_name: "Vollständiger Name des Implementierers",
        phone: "Telefon",
        email: "E-Mail",
        email_placeholder: "name@firma.de",
        photos_title: "2. Bilder der Feldimplementierung (Max 9)",
        photos_desc: "Bitte laden Sie Feldfotos hoch oder machen Sie welche, die den unten stehenden Kategorien entsprechen. Das Limit pro Foto beträgt ~5MB und es wird auf Ihrem Gerät automatisch auf ~1MB komprimiert.",
        take_photo: "Foto aufnehmen / Hochladen",
        uploading: "Fotos werden hochgeladen...",
        confirm_upload: "Alle hochladen und bestätigen",
        min_photo_error: "Bitte fügen Sie mindestens ein Foto hinzu.",
        compression_error: "Foto konnte nicht verarbeitet werden, bitte versuchen Sie es erneut.",
        categories: {
          floor: "Zustand des Bodens der Transporteinheit",
          cargo: "Ladungsplatzierung und Bodenverkeilung",
          front_right: "Lashing: Vorne Rechts",
          front_left: "Lashing: Vorne Links",
          rear_right: "Lashing: Hinten Rechts",
          rear_left: "Lashing: Hinten Links",
          plate: "Kennzeichen (falls vorhanden)",
          seal: "Siegelnummer (falls vorhanden)",
          overall: "Haupt-Gesamtansicht (Finale)"
        }
      },
      portal: {
        title: "Surveyor-Portal",
        subtitle: "Verwalten Sie Ihre zugewiesenen Inspektionsaufgaben durch Anmeldung.",
        login_error: "Bitte geben Sie Ihre E-Mail und Ihr Passwort ein.",
        login_button: "Anmelden",
        logging_in: "Anmeldung läuft...",
        dashboard: {
          connecting: "Verbindung zu Dashboards wird hergestellt...",
          pending_tab: "Eingehende Anfragen",
          active_tab: "Aktive Aufgaben",
          completed_tab: "Vergangene Aufträge",
          logout: "Sicheres Abmelden",
          connected: "Verbunden",
          account_type: "Surveyor-Konto",
          no_records: "Keine Datensätze gefunden",
          no_records_desc: "In diesem Tab gibt es keine aktiven Inspektionsaufgaben anzuzeigen.",
          customer: "Kunde",
          task_date: "AUFGABENDATUM",
          waiting_response: "Warten auf Antwort",
          under_review: "Kundenprüfung",
          delivered: "Bericht geliefert",
          rejected_by_you: "Aufgabe abgelehnt",
          accept_button: "Aufgabe annehmen",
          reject_button: "Ablehnen",
          go_to_report: "Zum Berichtsmenü",
          download_pdf: "PDF-Datei herunterladen",
          downloading_pdf: "Berichts-PDF wird heruntergeladen...",
          download_success: "Bericht erfolgreich heruntergeladen.",
          download_error: "Fehler beim Herunterladen des Berichts.",
          update_error: "Status konnte nicht aktualisiert werden, bitte versuchen Sie es erneut.",
          accept_success: "Aufgabe erfolgreich angenommen!",
          reject_success: "Aufgabe abgelehnt."
        },
        editor: {
          details_title: "Projektdetails",
          project_name: "Projektname",
          weight: "Ladungsgewicht",
          photos_count: "Feldfotos",
          success_title: "Bericht erfolgreich erstellt!",
          success_desc: "Ihr Inspektionsbericht wurde erstellt, unterzeichnet und in die sichere Cloud-Umgebung hochgeladen. Sie können die PDF-Datei herunterladen oder überprüfen, indem Sie auf die Schaltfläche unten klicken.",
          back_to_dashboard: "Zurück zum Dashboard",
          download_pdf: "PDF herunterladen",
          creator_title: "Finaler Survey-Bericht Ersteller",
          assessment_label: "Surveyor-Bewertung",
          assessment_desc: "Dieser Text wird dem Abschnitt Methodik & Schlussfolgerung der finalen PDF hinzugefügt.",
          assessment_default: "Die Inspektion der Ladungen wurde in Übereinstimmung mit internationalen Standards abgeschlossen. Es wurden keine Schäden oder Abweichungen beobachtet.",
          auto_info_note: "Hinweis: Standardinformationen wie Ihr Name, Ihre Unterschrift und Ihr Firmenlogo im System werden automatisch in den PDF-Bericht eingefügt.",
          back_button: "Zurück",
          generate_button: "PDF-Bericht zertifizieren & senden",
          generating: "Wird generiert...",
          generate_error: "Bericht konnte nicht erstellt werden",
          not_found: "Aufgabe nicht gefunden oder Sie sind nicht autorisiert."
        }
      }
    },

    labels: {
      three_d_view: "3D-Ansicht",
      no_results_data: "Keine Ergebnisdaten verfügbar",
      pass: "Bestanden",
      fail: "Durchgefallen",
      reports: "Berichte",
      user_responsible_for: "Sie sind allein verantwortlich für:",
      page_title: "Lashing-Berechnungswerkzeug",
      weight: "Gewicht (MT)",
      transport_mode: "Transportmodus",
      transport_unit: "Transporteinheit",
      friction_surface: "Reibungsfläche",
      select: "Auswählen",
      select_transport_mode_first: "Bitte wählen Sie zuerst den Transportmodus",
      cargo_dimensions: "Ladungsabmessungen & Schwerpunkt (m)",
      cargo_specs: "CARGO SPECS",
      width: "Ladungsbreite",
      length: "Ladungslänge",
      height: "Ladungshöhe",
      cog_width: "Schwerpunkt-Versatz in Breite (m):",
      cog_length: "Schwerpunkt-Versatz in Länge (m):",
      cog_height: "Schwerpunkthöhe (m):",
      cog_width_help: "Schwerpunkt standardmäßig halbe Breite. Ändern Sie bei Abweichung.",
      cog_length_help: "Schwerpunkt standardmäßig halbe Länge. Ändern Sie bei Abweichung.",
      cog_height_help: "Schwerpunkt standardmäßig halbe Höhe. Ändern Sie bei Abweichung.",
      wooden_dunnage_dimensions: "Holzkeile Abmessungen & Anzahl",
      wooden_dunnage_width: "Breite (cm):",
      wooden_dunnage_length: "Länge (cm):",
      stopper_qty: "Metallanschlag (Anzahl)",
      force_analysis: "Kraftanalyseergebnisse (kN)",
      generate_pdf: "PDF Erstellen",
      download_pdf: "PDF Herunterladen",
      wooden_dunnage: "Holzkeile",
      stopper: "Anschlag",
      loop_lashing: "Schlaufen-Zurren",
      max_weight: "Maximalgewicht",
      direct_lashing: "Direktes Zurren",
      loop_rope_select: "Schlaufen-Zurrgurt Auswählen",
      loop_lashing_quantity_per_side: "Anzahl (pro Seite)",
      direct_lashing_quantity: "Anzahl",
      cog_above: "Über dem Schwerpunkt",
      cog_below: "Unter dem Schwerpunkt",
      forward: "Gegen Vorwärtskräfte",
      backward: "Gegen Rückwärtskräfte",
      right: "Gegen Rechtskräfte",
      left: "Gegen Linkskräfte",
      lashing_diagram: "Zurr-Diagramm",
      language: "Sprache",
      calculate: "Berechnen",
      configuration_panel: "Konfigurationsbereich",
      operation_mode: "Betriebsmodus",
      manual_mode: "Rechner",
      lashing_calculator: "Lashing-Rechner",
      direct_lashing_selection: "Direktzurrung-Auswahl",
      reset: "Zurücksetzen",
      print: "Drucken",
      configuration: "Konfiguration",
      use_stopper: "Stopper verwenden",
      logout: "Abmelden",
      results_will_appear_here: "Ergebnisse werden hier angezeigt",
      continue_with_overweight: "Mit Übergewicht fortfahren",
      optimizer_mode: "Optimizer",
      calculator_mode: "Rechner",
      project_description: "Projektbeschreibung (Optional)",
      enter_project_description_hint: "Bitte geben Sie die Projektbeschreibung ein, die auf dem PDF-Bericht erscheinen soll.",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "Im Rechner bearbeiten",
      breakbulk_settings: "Breakbulk Settings",
      flatrack_type: "Flatrack Type",
      orientation: "Ladungsausrichtung",
      longitudinal: "Längs",
      transverse: "Quer",
      flatrack_count: "Empfohlene Flatrack-Anzahl",
      flatrack_gap: "Abstand zwischen Flatracks"
    },

    common: {
      info: "Info",
      yes: "Ja",
      no: "Nein",
      ok: "OK",
      cancel: "Abbrechen",
      confirm: "Bestätigen",
      close: "Schließen",
      save: "Speichern",
      download: "Herunterladen",
      delete: "Löschen",
      edit: "Bearbeiten",
      view: "Ansehen",
      loading: "Lädt...",
      processing: "Verarbeitung...",
      please_wait: "Bitte warten...",
      save_changes: "Änderungen Speichern",
      saving: "Speichern..."
    },

    transport_modes: {
      road: "Straße",
      rail: "Schiene",
      sea_a: "See_A (Welle <8m)",
      sea_b: "See_B (8m < Welle <12m)",
      sea_c: "See_C (Welle >12m)",
      road_sea_a: "Straße+See_A",
      road_sea_b: "Straße+See_B",
      road_sea_c: "Straße+See_C",
      road_rail: "Straße+Schiene",
      road_rail_sea_a: "Straße+Schiene+See_A",
      road_rail_sea_b: "Straße+Schiene+See_B",
      road_rail_sea_c: "Straße+Schiene+See_C"
    },

    transport_unit_groups: {
      containers: "Container",
      open_tops: "Open Tops",
      flatracks: "Flatracks",
      trailers: "Anhänger",
      special: "Andere / Spezial",
    },
    transport_units: {
      container_20: "20' Container",
      container_40: "40' Container",
      container_40_hc: "40' Container HC",
      container_45_hc: "45' Container HC",
      open_top_20: "20' Open Top",
      open_top_20_hc: "20' Open Top HC",
      open_top_40: "40' Open Top",
      open_top_40_hc: "40' Open Top HC",
      flatrack_20: "20' Flatrack",
      flatrack_40: "40' Flatrack",
      lowbed: "Tieflader",
      mafi: "Mafi",
      vessel: "Schiff",
      breakbulk: "Break Bulk",
      truck_trailer_standard: "LKW-Anhänger (Standard)",
      truck_trailer_mega: "LKW-Anhänger (Mega / Jumbo)",
      open_top_container: "Open Top Container"
    },

    materials: {
      asm: "Antirutschmatte (Statisch 0.8µ)",
      asm_06: "Antirutschmatte (Statisch 0.6µ)",
      metal_metal: "Metall-Metall",
      metal_wood: "Metall-Holz",
      wood_wood: "Holz-Holz"
    },

    directions: {
      forward: "Gegen Vorwärtskräfte",
      backward: "Gegen Rückwärtskräfte",
      right: "Gegen Rechtskräfte",
      left: "Gegen Linkskräfte"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "Tonnen"
    },

    errors: {
      zero_input: "Bitte füllen Sie alle erforderlichen Felder aus.",
      no_loop_rope: "Kein kompatibles Schlaufenseil gefunden.",
      no_direct_rope: "Kein kompatibles Direktzurrseil gefunden.",
      calculation_error: "Berechnungsfehler: {error}",
      unexpected_error: "Unerwarteter Fehler: {error}",
      diagram_error: "Diagramm konnte nicht erstellt werden: {error}",
      pdf_error: "PDF-Fehler: {error}",
      loop_config_error: "Schlaufenkonfigurationsfehler: {error}",
      error_halat: "Kein Direktzurrseil ausgewählt.",
      invalid_transport_unit: "Ungültige Transporteinheit: {unit}. Bitte wählen Sie eine gültige Einheit.",
      network_error: "Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.",
      file_not_found: "Datei nicht gefunden.",
      invalid_cargo_dimensions: "WARNUNG: Die gewählte Transporteinheit ist für die Ladung nicht geeignet",
      select_transport_mode: "Bitte wählen Sie einen Transportmodus",
      select_transport_unit: "Bitte wählen Sie eine Transporteinheit",
      select_friction_surface: "Bitte wählen Sie eine Reibungsfläche",
      invalid_weight: "Bitte geben Sie ein gültiges Gewicht ein",
      invalid_dimensions: "Bitte geben Sie gültige Ladeabmessungen ein",
      invalid_cog: "Bitte geben Sie gültige Schwerpunktkoordinaten ein",
      invalid_wooden_dunnage: "Bitte geben Sie gültige Holzkeilabmessungen ein",
      invalid_stopper: "Bitte geben Sie gültige Stoperanzahlen ein",
      select_at_least_one_rope: "Bitte Zurrungen auswählen",
      select_rope_type: "Bitte Seiltyp auswählen",
      dimension_exceeded: "WARNUNG: Die Ladung {dims} überschreitet die Grenzen der gewählten Transporteinheit.",
      dim_width: "BREITE",
      dim_length: "LÄNGE",
      dim_height: "HÖHE",
      cargo_width_greater_than_length: "Die Ladungsbreite darf nicht größer als die Länge sein. Der Wert wurde automatisch an die Länge angepasst.",
      and_conjunction: " und ",
      breakbulk_dim_overflow_width: "⚠️ Ladungsbreite ({cargo_width}m) ist größer oder gleich der gewählten Flatrack-Länge ({flatrack_length}m)! Berechnung kann nicht fortgesetzt werden.",
      breakbulk_dim_overflow_length: "⚠️ Ladungslänge ({cargo_length}m) ist größer oder gleich der gewählten Flatrack-Länge ({flatrack_length}m)! Berechnung kann nicht fortgesetzt werden."
    },

    validation: {
      required_field: "Dieses Feld ist erforderlich",
      invalid_number: "Bitte geben Sie eine gültige Zahl ein",
      positive_number: "Bitte geben Sie eine positive Zahl ein",
      min_value: "Mindestwert: {min}",
      max_value: "Höchstwert: {max}",
      out_of_range: "Wert muss zwischen {min} und {max} liegen",
      invalid_email: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      invalid_format: "Ungültiges Format"
    },

    warnings: {
      weight_exceeded: "Warnung: Die maximale Gewichtskapazität für {unit} ({max_weight} MT) wurde überschritten!",
      overweight_confirmation: "Das eingegebene Ladungsgewicht ({weight} MT) überschreitet das zulässige Höchstgewicht ({max_weight} MT). Bitte bestätigen Sie erneut, um fortzufahren.",
      oversized_width_loop_disabled: "Loop-Lashing wegen Überbreite der Ladung deaktiviert.",
      oversized_width_side_wedges_disabled: "Seitenkeile aufgrund übermßiger Ladebreite deaktiviert.",
      high_cog: "Warnung: Hoher Schwerpunkt erhöht das Kipprisiko.",
      insufficient_lashing: "Warnung: Unzureichende Zurrmittel erkannt.",
      unsaved_changes: "Sie haben ungespeicherte Änderungen. Möchten Sie fortfahren?",
      flatrack_dunnage_required: "Die Verwendung von Holzkeilen wird empfohlen, um das Verrutschen der Ladung in Vorwärts-Rückwärts-Richtungen auf Flatrack-Transporteinheiten zu verhindern. Möchten Sie die Berechnung ohne Hinzufügen von Holzkeilen fortsetzen?",
      terms_acceptance_required: "You must read and accept the Terms of Service to perform calculations. You can access the Terms of Service from your Profile page.",
      flatrack_count_low: "Achtung: Die ausgewählte Flatrack-Anzahl ({selected}) ist geringer als die empfohlene Anzahl ({recommended})!"
    },

    success: {
      pdf_created: "PDF erfolgreich erstellt!",
      calculation_complete: "Berechnung abgeschlossen!",
      data_saved: "Daten erfolgreich gespeichert.",
      export_complete: "Export erfolgreich abgeschlossen."
    },

    table_columns: {
      direction: "Richtung",
      sliding: "Rutschen",
      tipping: "Kippen",
      friction: "Reibung",
      wooden_dunnage: "Holzkeile",
      stopper: "Anschlag",
      looplash: "Schlaufen\nzurrung",
      direct_lashing: "Direktzurrung",
      cordstrap_loop: "Cordstrap-Schlaufe",
      cordstrap_direct: "Cordstrap-Direkt",
      total_securing: "Gesamtsicherung",
      net_sliding: "Netto-Rutschen",
      net_tipping: "Netto-Kippen",
      net_sliding_tooltip: "Negative Werte zeigen sichere Befestigung an.",
      net_tipping_tooltip: "Negative Werte zeigen sichere Befestigung an.",
      status: "Status",
      safety_factor: "Sicherheitsfaktor"
    },

    results: {
      safe: "SICHER",
      unsafe: "UNSICHER",
      marginal: "GRENZWERTIG",
      critical: "KRITISCH",
      acceptable: "AKZEPTABEL",
      status: "Status",
      safety_factor: "Sicherheitsfaktor",
      summary: "Zusammenfassung",
      details: "Details",
      notes_title: "Anmerkungen / Annahmen",
      notes_assumed: "",
      notes_cargo_unitized: "Die Ladung wird als Einzelstück betrachtet oder zu einer Ladungseinheit konsolidiert und unitisiert.",
      notes_lashing_above_cog: "Sofern in der Tabelle „Materialverwendung“ nicht anders angegeben, wird davon ausgegangen, dass die Direktzurrungen oberhalb des Schwerpunkts der Ladung angebracht werden.",
      notes_angle_range: "Die spitzen Winkel zwischen den schräg an den Zurrpunkten der Transporteinheit befestigten Direktzurrungen und der Vektorkomponente der Direktzurrungen in Richtung der einwirkenden Kraft müssen zwischen 30° und 60° liegen.",
      notes_edge_protection: "Wo erforderlich, ist ein geeigneter Kanten- und Eckenschutz vorzusehen, um Schäden an der Ladung und/oder den Zurrmitteln zu vermeiden.",
      notes_pretension_force: "Beim Spannen der Gurtzurrungen darf die aufgebrachte Vorspannkraft 50 % des MSL-Wertes der Gurtzurrung nicht überschreiten.",
      notes_strength_limitations: "Es wird davon ausgegangen, dass die Zurrpunkte an der Ladung keine Festigkeitsbeschränkungen haben.",
      notes_distributed_uniformly: "Es wird davon ausgegangen, dass die Last gemäß den Kapazitätskriterien der Transporteinheit gleichmäßig verteilt ist.",
      flatrack_no_dunnage_warning: "Die Verwendung von Holzkeilen wird empfohlen, um das Verrutschen der Ladung in Vorwärts-Rückwärts-Richtungen auf Flatrack-Transporteinheiten zu verhindern. Möchten Sie die Berechnung ohne Hinzufügen von Holzkeilen fortsetzen?"
    },

    tooltips: {
      weight_info: "Geben Sie das Gesamtgewicht der Ladung in metrischen Tonnen ein",
      cog_info: "COG steht für Center of Gravity (Schwerpunkt). Standardmäßig wird davon ausgegangen, dass er sich in der Mitte der Ladung befindet. In der Realität kann der Schwerpunkt jedoch an einer anderen Stelle liegen. Wenn der spezifische COG bekannt ist, kann seine korrekte Position hier eingegeben werden. Eine genaue COG-Positionierung ist kritisch, insbesondere für Kippberechnungen.",
      friction_info: "Wählen Sie die Materialkombination für den Reibungskoeffizienten",
      transport_mode_info: "Beschleunigungswerte variieren je nach Transportmodus",
      lashing_info: "Zurrkräfte verhindern Rutschen und Kippen"
    },
    legal: {
      tos_title: "Allgemeine Geschäftsbedingungen",
      privacy_title: "Datenschutzrichtlinie",
      sales_agreement_title: "Kaufvertrag",
      legal_notice_title: "Rechtlicher Hinweis",
      cookie_policy_title: "Cookie-Richtlinie",
      last_updated: "Gültig ab: 4. Februar 2026",
      return_home: "Zur Startseite",
      sections: {
        acceptance: {
          title: "Annahme der Bedingungen",
          content: "Durch den Zugriff auf und die Nutzung von Lashing Calculator erklären Sie sich mit diesen Bedingungen einverstanden. Wenn Sie nicht einverstanden sind, nutzen Sie den Dienst nicht."
        },
        payment_terms: {
          title: "Zahlungsbedingungen und Abonnement",
          content: "Der Dienst wird auf Abonnementbasis abgerechnet. Zahlungen sind nicht erstattungsfähig. Wir behalten uns das Recht vor, Preise mit Vorankündigung zu ändern."
        },
        termination: {
          title: "Kündigung und Beendigung",
          content: "Sie können Ihr Abonnement jederzeit kündigen. Der Zugriff läuft bis zum Ende des aktuellen Abrechnungszeitraums weiter."
        },
        disclaimer: {
          title: "Wichtiger Sicherheitshinweis & Professioneller Haftungsausschluss",
          important: "WICHTIGER HINWEIS: Die bereitgestellten Berechnungstools sind ausschließlich als 'Entscheidungsunterstützungssystem' für die Ladungssicherungsplanung gedacht.",
          content: "Die Plattform bietet keine professionelle technische Beratung. Alle Ergebnisse müssen vor der Umsetzung von einem qualifizierten Ladungssachverständigen, einem Ladungssicherungsexperten oder einer 'kompetenten Person' gemäß dem IMO/ILO/UNECE CTU Code und dem IMO CSS Code geprüft und genehmigt werden. Die endgültige Verantwortung liegt gemäß internationalem Seerecht beim Frachtführer und dem Kapitän."
        },
        license: {
          title: "Geistiges Eigentum & Lizenz",
          subtitle: "Der Quellcode, die Algorithmen und das Design der Plattform sind durch internationale Gesetze zum geistigen Eigentum geschützt:",
          items: [
            "Benutzern wird eine begrenzte, nicht übertragbare Lizenz zur Nutzung der Plattform gewährt; es wird kein Eigentum übertragen.",
            "Die genaue Eingabe von Ladung und Transporteinheitsdaten liegt in der alleinigen Verantwortung des Benutzers.",
            "Reverse Engineering oder das Extrahieren des Quellcodes ist strengstens untersagt.",
            "PDF-Berichte sind spezifisch für die beschriebene Ladung und können nicht für andere Konfigurationen wiederverwendet werden."
          ]
        },
        liability: {
          title: "Haftungsbeschränkung",
          content: "Der Plattformbetreiber haftet nicht für Schäden (einschließlich Ladungsverlust oder Betriebsunterbrechung), die aus der Nutzung oder der Unfähigkeit zur Nutzung des Dienstes entstehen.",
          cap_title: "Haftungsobergrenze",
          cap_content: "Soweit gesetzlich zulässig, darf die kumulative Gesamthaftung den für den Dienst in den 12 Monaten vor dem Anspruch gezahlten Betrag oder 100,00 USD nicht überschreiten, je nachdem, welcher Betrag höher ist.",
          not_responsible: "Wir sind nicht verantwortlich für:",
          not_responsible_items: [
            "Schäden infolge unsachgemäßer physischer Ausführung der Zurrung",
            "Vom Benutzer bereitgestellte ungenaue Eingabedaten",
            "Umweltveränderungen außerhalb der Berechnungsparameter",
            "Strukturelles Versagen der Transporteinheit oder der Zurrpunkte",
            "Ungenaue Herstellerspezifikationen (MSL, Reibung usw.)",
            "Folge-, Neben- oder Strafschadensersatz",
            "Verlust von Gewinnen, Geschäftsmöglichkeiten oder Firmenwert"
          ]
        },
        third_party: {
          title: "Haftungsausschluss für Daten Dritter",
          content: "Die Plattform enthält Daten aus Quellen Dritter, einschließlich Herstellern und internationalen Sicherheitsstandards.",
          sub_content: "Wir übernehmen keine Gewähr für die Richtigkeit oder Zuverlässigkeit von Daten Dritter. Alle Herstellerspezifikationen werden von den jeweiligen Herstellern bereitgestellt."
        },
        accuracy: {
          title: "Technische Genauigkeit",
          content: "Obwohl wir um Präzision bemüht sind, Materialien können technische oder typografische Fehler enthalten. Wir garantieren nicht, dass alle Materialien immer aktuell oder genau sind."
        },
        source_responsibility: {
          title: "Datenquellen & Benutzerverantwortung",
          important: "Die Genauigkeit aller Berechnungen hängt vollständig von den von Ihnen angegebenen Parametern ab.",
          utilize_content: "Der Dienst nutzt mehrere Datenquellen für Berechnungen:",
          data_sources: [
            { title: "MSL-Werte", desc: "Abgeleitet von Herstellerspezifikationen; es erfolgt keine unabhängige Überprüfung." },
            { title: "Reibungsbeiwerte", desc: "Basierend auf Industriestandards; die reale Reibung kann abweichen." },
            { title: "Zurrpunkte", desc: "Basierend auf Einheitenspezifikationen; die physische Integrität liegt in der Verantwortung des Benutzers." },
            { title: "CTU/CSS-Codes", desc: "Ausgerichtet auf internationale maritime Sicherheitsstandards." }
          ],
          user_responsible_items: [
            "Überprüfen Sie alle Eingabeparameter (Gewicht, Abmessungen, CoG-Position)",
            "Stellen Sie sicher, dass die Ausrüstungsspezifikationen mit den verwendeten Materialien übereinstimmen",
            "Bestätigen Sie die Anwendbarkeit des Reibungsbeiwerts auf spezifische Ladungsbedingungen",
            "Validieren Sie physisch die Integrität der Zurrpunkte",
            "Beauftragen Sie qualifiziertes Fachpersonal mit der Genehmigung der Ergebnisse vor der Umsetzung"
          ]
        },
        verification: {
          title: "Anforderung einer Fachprüfung",
          content: "Alle Berechnungsergebnisse müssen vor der Umsetzung von einer qualifizierten Person gemäß CTU Code geprüft und genehmigt werden. Die Plattform bietet lediglich Berechnungsunterstützung und stellt keine technische Beratung dar."
        },
        indemnification: {
          title: "Freistellung",
          content: "Sie erklären sich damit einverstanden, den Plattformbetreiber von allen Ansprüchen Dritter freizustellen, die aus Folgendem resultieren:",
          items: [
            "Verletzung dieser Allgemeine Geschäftsbedingungen",
            "Bereitstellung ungenauer oder unvollständiger Eingabedaten",
            "Verwendung veralteter oder falscher Ausrüstungsspezifikationen",
            "Unterlassung der professionellen Überprüfung der Ergebnisse",
            "Umsetzung von Plänen ohne unabhängige Prüfung",
            "Unfälle oder Schäden aus der physischen Ausführung"
          ]
        },
        governing_law: {
          title: "Anwendbares Recht & Gerichtsstand",
          content: "Diese Bedingungen unterliegen den internationalen Seerechtsübereinkommen und den Gesetzen des Landes, in dem der Anbieter registriert ist. Streitigkeiten unterliegen der ausschließlichen Zuständigkeit der zuständigen Gerichte an diesem Standort."
        }
      },
      detailed_terms: [
        {
          title: "1. Vertragsabschluss",
          content: [
            "Diese Allgemeine Geschäftsbedingungen ('Bedingungen') stellen eine rechtsverbindliche Vereinbarung zwischen Ihnen ('Benutzer', 'Kunde') und JADE CONSULTANCY AND TRADING LTD ('Anbieter', 'wir', 'unser') dar, die den Zugang zur und die Nutzung der Lashing Calculator Webanwendungsplattform regelt.",
            "Durch den Zugriff auf oder die Nutzung der Plattform oder durch den Abschluss eines Abonnement- oder Kaufvertrags für den Dienst erklären Sie sich unwiderruflich mit diesen Bedingungen einverstanden.",
            "Die Annahme eines Kaufvertrags stellt die automatische Annahme dieser Bedingungen dar."
          ]
        },
        {
          title: "2. Nur für Geschäftliche Nutzung",
          content: [
            "Die Plattform wird streng auf einer Business-to-Business (B2B) Basis bereitgestellt.",
            "Sie bestätigen, dass Sie im Rahmen Ihrer geschäftlichen Tätigkeit handeln und nicht als Verbraucher im Sinne von Verbraucherschutzgesetzen.",
            "Die Plattform ist für die Unterstützung von Ladungssicherungsberechnungen für See-, Straßen- und Schienentransportmodi sowie deren multimodale Kombinationen konzipiert. Der anwendbare Rechtsrahmen hängt vom Transportmodus und den Lade-, Transit- und Entladezuständigkeiten ab. Benutzer sind allein verantwortlich für die Einhaltung aller einschlägigen modalen Vorschriften."
          ]
        },
        {
          title: "3. Abonnement, Gebühren und Zahlung",
          content: [
            "3.1 Abonnementlaufzeit: Die Abonnementlaufzeit beträgt ein (1) Jahr ab dem Aktivierungsdatum. Es erfolgt keine automatische Verlängerung. Eine Verlängerung erfordert einen schriftlichen Antrag des Benutzers und die vollständige Zahlung für den neuen Abonnementzeitraum.",
            "3.2 Gebühren und Währung: Alle Gebühren werden in GBP (£) in Rechnung gestellt und sind zahlbar. Die Zahlung muss auf das GBP-Bankkonto des Anbieters erfolgen. Gebühren sind im Voraus zu zahlen.",
            "3.3 Ablaufbenachrichtigungen: Die Plattform kann Erinnerungsbenachrichtigungen einen (1) Monat, eine (1) Woche und einen (1) Tag vor Ablauf senden. Solche Benachrichtigungen dienen nur der Bequemlichkeit und stellen keine Verlängerung dar."
          ]
        },
        {
          title: "4. Kündigung und Beendigung",
          content: [
            "4.1 Kündigung durch den Benutzer: Der Benutzer kann jederzeit kündigen. Der Zugang bleibt bis zum Ende des aktuellen Abonnementzeitraums bestehen. Es erfolgt keine Rückerstattung, außer wie unten ausdrücklich angegeben.",
            "4.2 Kündigung durch den Anbieter: Der Anbieter kann den Dienst jederzeit aus berechtigten geschäftlichen, rechtlichen, technischen oder sicherheitsrelevanten Gründen kündigen oder aussetzen, vorausgesetzt, dass der Anbieter dies, sofern vernünftigerweise möglich, im Voraus mitteilt. Wenn die Kündigung durch den Anbieter erfolgt, hat der Benutzer Anspruch auf eine anteilige Rückerstattung für den ungenutzten Teil des Abonnementzeitraums."
          ]
        },
        {
          title: "5. Dienstausfall und Rückerstattungen",
          content: [
            "Wenn die Plattform aus technischen Gründen, die nicht vom Benutzer verursacht wurden, für einen ununterbrochenen Zeitraum von mehr als fünf (5) aufeinanderfolgenden Tagen wesentlich unbrauchbar wird, hat der Benutzer Anspruch auf eine anteilige Rückerstattung, die täglich auf der Grundlage der jährlichen Abonnementgebühr für ungenutzte Tage berechnet wird.",
            "Vorübergehende Ausfälle, beeinträchtigte Leistung, Wartungsfenster oder Kompatibilitätsprobleme stellen keine Unbrauchbarkeit dar."
          ]
        },
        {
          title: "6. Benutzerkonten und Lizenzierung",
          content: [
            "6.1 Lizenzgewährung: Der Anbieter gewährt dem Benutzer eine begrenzte, nicht exklusive, nicht übertragbare Lizenz für den Zugriff auf und die Nutzung der Plattform nur für interne Geschäftszwecke. Es werden keine Eigentumsrechte übertragen.",
            "6.2 Einzelgeräte-Richtlinie: Jedes Benutzerkonto darf jeweils auf einem Gerät verwendet werden, sofern in einem anwendbaren Kaufvertrag nicht schriftlich anders vereinbart. Gleichzeitige Anmeldungen sind verboten. Der Anbieter behält sich das Recht vor, Nutzungsmuster zu überwachen, um Missbrauch zu erkennen. Wenn ein Missbrauch vernünftigerweise vermutet wird, kann der Anbieter den Zugang während der Untersuchung aussetzen. Bestätigte Verstöße können zur Kündigung ohne Rückerstattung führen.",
            "6.3 Benutzerpflichten: Um Berichte zu erstellen, muss der Benutzer Folgendes angeben: Firmenname, Firmenlogo, Handynummer, Festnetznummer, E-Mail-Adresse. Gültige Firmenabrechnungsdaten müssen für die Rechnungsstellung angegeben werden."
          ]
        },
        {
          title: "7. Wichtiger Sicherheitshinweis & Professioneller Haftungsausschluss",
          content: [
            "KRITISCHER HINWEIS: Die Plattform ist nur ein Entscheidungshilfesystem für die Planung der Ladungssicherung für alle Verkehrsträger, einschließlich See-, Straßen-, Schienentransport und deren multimodaler Kombinationen.",
            "Die Plattform bietet keine professionelle technische, rechtliche oder sicherheitstechnische Beratung.",
            "Die Plattform ist von keinem Flaggenstaat, keiner Klassifikationsgesellschaft, keiner Hafenbehörde, keiner Straßenverkehrsbehörde, keinem Eisenbahninfrastrukturbetreiber oder Regulierungsbehörde zertifiziert, genehmigt oder gebilligt.",
            "Alle Berechnungsergebnisse müssen von einem qualifizierten Ladungsprofi überprüft und genehmigt werden: IMO/ILO/UNECE CTU-Code und IMO CSS-Code (See); EN 12195 / VDI 2700 und geltende nationale Straßenverkehrsleitlinien (Straße); UIC-Verladerichtlinien (Schiene).",
            "Die endgültige Verantwortung für die Ladungssicherung liegt bei der verantwortlichen Partei gemäß dem für den Verkehrsträger geltenden Rechtsrahmen: für See, der Frachtführer, Schiffseigner und/oder Kapitän gemäß geltendem internationalem Seerecht; für Straße, der Frachtführer, Fahrzeugbetreiber und/oder Fahrer gemäß CMR-Übereinkommen; für Schiene, der Frachtführer und Eisenbahnbetreiber gemäß CIM/COTIF-Rahmen. Bei multimodalem Transport ist jeder modaler Frachtführer für sein Segment verantwortlich.",
            "Der Anbieter übernimmt keine Verantwortung für Entscheidungen, die auf der Grundlage von Plattformausgaben getroffen werden.",
            "Für Seetransport: Plattformausgaben erfüllen keine gesetzlichen Dokumentationspflichten gemäß SOLAS Regel VI/5-2. P&I-Konformität, ISM-Code-Pflichten und IACS-Klassifikationsanforderungen liegen in der Verantwortung des Benutzers.",
            "Für Straßentransport: Plattformausgaben müssen gegen EN 12195-Reihe und VDI 2700 validiert werden. Britische Betreiber müssen zusätzlich den DfT-Verhaltenskodex zur Ladungssicherung einhalten.",
            "Für Schienentransport: Plattformausgaben müssen gegen geltende UIC-Verladerichtlinien, TSI-Anforderungen und Anforderungen des nationalen Eisenbahninfrastrukturmanagers validiert werden.",
            "Einzelne Hafen-, Straßen- und Eisenbahnbehörden können zusätzliche Anforderungen auferlegen. Die Einhaltung aller lokalen Vorschriften liegt in der alleinigen Verantwortung des Benutzers.",
            "Hinweis zu Gefahrgut: Die Plattform ist nur für die allgemeine Ladungssicherungsplanung konzipiert. Sie ist nicht für Gefahrgut nach ADR (Straße), IMDG-Code (See), RID (Schiene) oder gleichwertigen Vorschriften konzipiert oder validiert. Benutzer, die Gefahrgut behandeln, müssen vor dem operativen Einsatz fachkundige Beratung von einem Gefahrgutbeauftragten (DGSA) einholen."
          ]
        },
        {
          title: "8. Geistiges Eigentum",
          content: [
            "Die Plattform, einschließlich ihres Quellcodes, ihrer Algorithmen, Berechnungen, Struktur und ihres Designs, ist durch Gesetze zum Schutz des geistigen Eigentums geschützt.",
            "Reverse Engineering, Extraktion oder Reproduktion ist strengstens untersagt.",
            "PDF-Berichte gelten nur für die beschriebene spezifische Ladungskonfiguration und dürfen nicht wiederverwendet werden.",
            "Die genaue Dateneingabe liegt in der alleinigen Verantwortung des Benutzers."
          ]
        },
        {
          title: "9. Datenquellen, Berichte und Benutzerverantwortung",
          content: [
            "Die Genauigkeit der Berechnung hängt vollständig von den vom Benutzer bereitgestellten Parametern ab.",
            "Zu den Datenquellen gehören: MSL-Werte (Herstellerspezifikationen, keine unabhängige Überprüfung), Reibungskoeffizienten (Industriestandards, tatsächliche Bedingungen können variieren), Zurrpunkte (Gerätespezifikationen, physische Integrität liegt in der Verantwortung des Benutzers).",
            "Anwendbare Regulierungsrahmen nach Verkehrsmodus: CTU-Code (IMO/ILO/UNECE — multimodal, für alle Modi anwendbar), CSS-Code (IMO Ladungssicherungshandbuch — nur Seetransport), VDI 2700 / EN 12195-1 bis EN 12195-4 (Straßentransport, europäischer Standard für Ladungssicherung auf Straßenfahrzeugen), Britischer DfT-Verhaltenskodex zur Ladungssicherung (Straßentransport, britische Zuständigkeit), UIC-Verladerichtlinien / TSI (Schienentransport), CIM/COTIF Einheitliche Rechtsvorschriften (internationale Eisenbahnfrachtverträge). Benutzer sind allein verantwortlich für die Identifizierung und Anwendung des korrekten Standards für ihre spezifischen Verkehrsarten und Rechtsordnungen.",
            "Der Benutzer ist allein verantwortlich für: Überprüfung aller Eingabedaten, Sicherstellung der korrekten Geräteauswahl, Bestätigung der Anwendbarkeit in der realen Welt, Physische Validierung von Zurranordnungen, Beauftragung qualifizierter Fachleute vor der Implementierung.",
            "Berichte werden auf der Grundlage von Eingaben zum Zeitpunkt der Erstellung generiert. Der Anbieter ist nicht verpflichtet, Berechnungsdaten oder Berichte aufzubewahren, und der Benutzer ist für die Archivierung verantwortlich."
          ]
        },
        {
          title: "10. Haftungsausschluss für Technische Genauigkeit",
          content: [
            "Die Plattform wird mit angemessener Sachkenntnis und Sorgfalt bereitgestellt, die den allgemein anerkannten Industriestandards für SaaS-Dienste entspricht. Obwohl angemessene Anstrengungen unternommen werden, um die Genauigkeit zu gewährleisten, kann die Plattform technische oder typografische Fehler enthalten. Es wird keine Garantie dafür übernommen, dass Materialien vollständig, aktuell oder fehlerfrei sind.",
            "Daten und Standards von Drittanbietern können sich im Laufe der Zeit ändern. Aktualisierungen gelten nicht rückwirkend für zuvor erstellte Berichte."
          ]
        },
        {
          title: "11. Haftungsbeschränkung",
          content: [
            "Soweit nach englischem Recht zulässig: Der Anbieter haftet nicht für Verluste oder Schäden, die sich aus der Nutzung oder dem Vertrauen auf die Plattform ergeben, einschließlich: Ladungsverlust oder -beschädigung, Schiffsschäden, Fahrzeugschäden, Schäden an Schienenfahrzeugen, Personenschäden oder Tod (soweit gesetzlich zulässig), Betriebsunterbrechung, Verlust von Gewinnen, Firmenwert oder Geschäftsmöglichkeiten, Folge-, Begleit- oder Strafschäden.",
            "Soweit nach geltendem Recht zulässig, schließt der Anbieter jegliche Haftung aus Fahrlässigkeit, falscher Darstellung oder Verletzung gesetzlicher Pflichten aus, außer wenn ein solcher Ausschluss gesetzlich verboten ist.",
            "Haftungsobergrenze: Sofern in einem unterzeichneten Kaufvertrag nicht schriftlich anders vereinbart, übersteigt die gesamte kumulative Haftung des Anbieters nicht die Gebühren, die der Benutzer in den zwölf (12) Monaten vor dem Anspruch gezahlt hat.",
            "Nichts schließt die Haftung für Betrug oder Tod durch Fahrlässigkeit aus, wo dies gesetzlich verboten ist."
          ]
        },
        {
          title: "12. Schadloshaltung",
          content: [
            "Der Benutzer erklärt sich damit einverstanden, den Anbieter von jeglichen Ansprüchen Dritter freizustellen und schadlos zu halten, die sich ergeben aus: Verstoß gegen diese Bedingungen, Ungenaue oder unvollständige Eingabedaten, Verwendung falscher oder veralteter Spezifikationen, Unterlassung der Einholung einer professionellen Überprüfung, Physische Ausführung von Zurranordnungen, Missbrauch von Anmeldeinformationen oder unbefugter Zugriff durch den Benutzer."
          ]
        },
        {
          title: "13. Exportkontrolle & Einhaltung von Sanktionen",
          content: [
            "Der Benutzer versichert und garantiert, dass er keinen Sanktionen unterliegt, die von der Europäischen Union, dem Vereinigten Königreich, den Vereinten Nationen oder anderen zuständigen Behörden verhängt wurden.",
            "Der Benutzer darf die Plattform nicht unter Verletzung geltender Exportkontroll- oder Sanktionsgesetze nutzen und ist allein dafür verantwortlich, die rechtmäßige Nutzung in allen Gerichtsbarkeiten sicherzustellen."
          ]
        },
        {
          title: "14. Haftungsausschluss für Cybersicherheit",
          content: [
            "Der Anbieter implementiert angemessene technische und organisatorische Sicherheitsmaßnahmen im Einklang mit der Industriepraxis unter Berücksichtigung der Art der verarbeiteten Daten. Der Anbieter garantiert jedoch nicht, dass die Plattform vollständig frei von Cybervorfällen, Malware, Ransomware, unbefugtem Zugriff oder Sicherheitsverletzungen ist, einschließlich solcher, die von Systemen oder Netzwerken Dritter ausgehen."
          ]
        },
        {
          title: "15. Versicherungsoffenlegung",
          content: [
            "Der Anbieter unterhält eine angemessene Berufshaftpflicht- und Cyberversicherung gemäß Industriepraxis. Nichts in dieser Vereinbarung ist so auszulegen, als würde dem Benutzer oder einem Dritten Versicherungsschutz, Garantien oder Entschädigung gewährt."
          ]
        },
        {
          title: "16. Kein Vertrauen",
          content: [
            "Der Benutzer erkennt an, dass er sich nicht auf Aussagen, Zusicherungen oder Garantien verlassen hat, die nicht ausdrücklich in dieser Vereinbarung festgelegt sind.",
            "Die Plattform ist kein Ersatz für professionelles Urteilsvermögen, Zertifizierung, behördliche Genehmigung oder rechtliche Konformität."
          ]
        },
        {
          title: "17. Annahme Professioneller Benutzer",
          content: [
            "Die Plattform ist für die Nutzung durch erfahrene Fachleute aus der Schifffahrt und Logistik bestimmt. Der Anbieter übernimmt keine Pflicht, dem Benutzer maritime Prinzipien, Sicherheitsverpflichtungen oder behördliche Anforderungen zu erklären."
          ]
        },
        {
          title: "18. Haftungsausschluss für Sicherheitskritische Nutzung",
          content: [
            "Die Plattform ist nicht für die operative Entscheidungsfindung in Echtzeit während aktiver Ladungsoperationen oder Notsituationen konzipiert oder bestimmt."
          ]
        },
        {
          title: "19. Keine Rechte Dritter",
          content: [
            "Keine Person, die nicht Partei dieser Vereinbarung ist, hat Rechte gemäß dem Contracts (Rights of Third Parties) Act 1999, eine Bestimmung dieser Vereinbarung durchzusetzen."
          ]
        },
        {
          title: "20. Höhere Gewalt",
          content: [
            "Der Anbieter haftet nicht für Leistungsstörungen oder -verzögerungen, die durch Ereignisse außerhalb seiner angemessenen Kontrolle verursacht werden, einschließlich höherer Gewalt, Krieg, Cybervorfälle, Ausfälle von Cloud-Diensten, Arbeitskämpfe oder behördliche Maßnahmen."
          ]
        },
        {
          title: "21. Geltendes Recht, Zwingendes EU-Recht & Gerichtsstand",
          content: [
            "Diese Bedingungen unterliegen englischem Recht.",
            "Die Gerichte von England und Wales haben die ausschließliche Zuständigkeit.",
            "Nichts in diesen Bedingungen schließt die Anwendung zwingender Bestimmungen des EU-Rechts aus, von denen vertraglich gemäß Verordnung (EG) Nr. 593/2008 (Rom I) nicht abgewichen werden kann, soweit anwendbar."
          ]
        },
        {
          title: "22. Sprachvorrang",
          content: [
            "Diese Vereinbarung ist in englischer Sprache abgefasst. Im Falle einer Übersetzung ist die englische Version maßgebend."
          ]
        },
        {
          title: "23. Allgemeines",
          content: [
            "Sollte eine Bestimmung dieser Bedingungen unwirksam oder undurchsetzbar sein, bleiben die übrigen Bestimmungen vollumfänglich in Kraft.",
            "Der Anbieter kann diese Bedingungen im Rahmen einer Fusion, eines Verkaufs oder einer Geschäftsübertragung abtreten.",
            "Klauseln in Bezug auf Haftung, Entschädigung, geistiges Eigentum, geltendes Recht und Haftungsausschluss bleiben auch nach der Kündigung bestehen."
          ]
        },
        {
          title: "24. Datenschutz & Privatsphäre",
          content: [
            "24.1 Im Zusammenhang mit der Bereitstellung der Plattform handelt der Benutzer als Datenverantwortlicher (Controller) und der Anbieter als Datenverarbeiter (Processor) im Sinne der UK GDPR, EU GDPR und des Data Protection Act 2018.",
            "24.2 Der Anbieter verarbeitet personenbezogene Daten ausschließlich auf dokumentierte Weisung des Benutzers und in Übereinstimmung mit den geltenden Datenschutzgesetzen.",
            "24.3 Der in Anlage 1 (Schedule 1) dargelegte Anhang zur Datenverarbeitung ist integraler Bestandteil dieser Bedingungen und regelt die Verarbeitung personenbezogener Daten durch den Anbieter im Auftrag des Benutzers.",
            "24.4 Im Falle eines Widerspruchs zwischen diesen Bedingungen und dem Anhang zur Datenverarbeitung hat der Anhang zur Datenverarbeitung in Bezug auf Datenschutzangelegenheiten Vorrang."
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. Definitionen",
            "Für die Zwecke dieses Anhangs zur Datenverarbeitung ('DPA'), haben die Begriffe 'Personenbezogene Daten', 'Verarbeitung', 'Verantwortlicher', 'Verarbeiter' und 'Aufsichtsbehörde' die in der UK GDPR und EU GDPR angegebenen Bedeutungen.",
            "",
            "2. Umfang & Rollen",
            "2.1 Der Benutzer ist der Verantwortliche für personenbezogene Daten, die im Zusammenhang mit der Nutzung der Plattform verarbeitet werden.",
            "2.2 Der Anbieter handelt ausschließlich als Verarbeiter und verarbeitet personenbezogene Daten zu keinem anderen Zweck als der Bereitstellung der Plattform.",
            "",
            "3. Art & Zweck der Verarbeitung",
            "Zweck: Bereitstellung von SaaS-Diensten, Kontoverwaltung, technischer Support und Erstellung von Berichten.",
            "Kategorien betroffener Personen: Autorisierte Benutzer, Mitarbeiter, Auftragnehmer.",
            "Arten personenbezogener Daten: Berufliche Kontaktdaten (Name, E-Mail, Telefon), Firmenidentifikationsdaten.",
            "Besondere Kategorien: Keine vorgesehen.",
            "",
            "4. Pflichten des Verarbeiters",
            "Der Anbieter wird:",
            "a) Personenbezogene Daten nur auf dokumentierte Weisung des Benutzers verarbeiten",
            "b) Sicherstellen, dass zur Verarbeitung personenbezogener Daten befugtes Personal Vertraulichkeitsverpflichtungen unterliegt",
            "c) Angemessene technische und organisatorische Maßnahmen zum Schutz personenbezogener Daten implementieren",
            "d) Den Benutzer auf angemessene Anfrage bei Anfragen zu Betroffenenrechten unterstützen",
            "e) Den Benutzer unverzüglich nach Bekanntwerden einer Verletzung des Schutzes personenbezogener Daten benachrichtigen",
            "f) Personenbezogene Daten nach Beendigung der Dienste löschen oder zurückgeben, sofern keine gesetzliche Aufbewahrungspflicht besteht",
            "",
            "5. Unterauftragsverarbeiter",
            "5.1 Der Benutzer ermächtigt den Anbieter, Unterauftragsverarbeiter für Hosting-, Infrastruktur- und Supportdienste einzusetzen.",
            "5.2 Der Anbieter stellt sicher, dass Unterauftragsverarbeiter Datenschutzverpflichtungen unterliegen, die nicht weniger schützend sind als die in diesem DPA festgelegten.",
            "",
            "6. Internationale Übermittlungen",
            "Wenn personenbezogene Daten außerhalb des Vereinigten Königreichs oder des EWR übermittelt werden, stellt der Anbieter sicher, dass geeignete Garantien vorhanden sind, einschließlich internationaler Datenübermittlungsvereinbarungen des Vereinigten Königreichs oder EU-Standardvertragsklauseln, soweit zutreffend.",
            "",
            "7. Sicherheitsmaßnahmen",
            "Der Anbieter unterhält angemessene technische und organisatorische Sicherheitsmaßnahmen, die der Art der Daten und den damit verbundenen Risiken entsprechen, einschließlich Zugangskontrollen, Verschlüsselung (wo angemessen) und Verfahren zur Reaktion auf Vorfällen.",
            "",
            "8. Audits",
            "Auf angemessene schriftliche Anfrage und nicht öfter als einmal pro Jahr stellt der Anbieter Informationen zur Verfügung, die erforderlich sind, um die Einhaltung dieses DPA nachzuweisen, vorbehaltlich der Vertraulichkeits- und Sicherheitsanforderungen.",
            "",
            "9. Haftung",
            "Die Haftung aus diesem DPA unterliegt den in den Allgemeine Geschäftsbedingungen festgelegten Haftungsbeschränkungen und -ausschlüssen.",
            "",
            "10. Geltendes Recht",
            "Dieser DPA unterliegt den Gesetzen von England und Wales und wird in Übereinstimmung mit diesen ausgelegt."
          ]
        }
      ],
      detailed_privacy: [
        {
          title: "1. Einführung",
          content: [
            "Diese Datenschutzrichtlinie erklärt, wie JADE CONSULTANCY AND TRADING LTD, ein Unternehmen, das nach den Gesetzen von England und Wales tätig ist ('Anbieter', 'wir', 'uns', 'unser'), personenbezogene Daten sammelt, verwendet, speichert und schützt, wenn Sie die Webanwendung Lashing Calculator ('Plattform') nutzen.",
            "Wir verpflichten uns, personenbezogene Daten gemäß der britischen Datenschutz-Grundverordnung (UK GDPR) und dem Data Protection Act 2018 zu schützen."
          ]
        },
        {
          title: "2. Verantwortlicher",
          content: [
            "Für die Zwecke des geltenden Datenschutzrechts handelt der Anbieter als Verantwortlicher in Bezug auf personenbezogene Daten, die über die Plattform verarbeitet werden."
          ]
        },
        {
          title: "3. Informationen, die wir sammeln",
          content: [
            "3.1 Konto- und Kontaktdaten: Name, Firmenname, geschäftliche E-Mail-Adresse, Telefonnummern, Anmeldedaten, Rechnungs- und Zahlungsdetails. Diese Daten werden streng für die Kontoverwaltung, Authentifizierung, Abrechnung und Servicekommunikation verwendet.",
            "3.2 Industrie- und Betriebsdaten: Ladungsparameter und Konfigurationen, Ladungssicherungsberechnungen und Berichteingaben. Diese Daten werden als vertrauliche Industrieinformationen behandelt. Sie werden nicht für kommerziellen Weiterverkauf, Profiling oder Marketingzwecke analysiert.",
            "3.3 Technische Daten: IP-Adresse, Browsertyp, Geräte- und Sitzungsinformationen, Zugriffsprotokolle. Diese Daten werden zu Sicherheits-, Leistungsüberwachungs- und Missbrauchserkennungszwecken gesammelt."
          ]
        },
        {
          title: "4. Rechtsgrundlage für die Verarbeitung",
          content: [
            "Wir verarbeiten personenbezogene Daten auf den folgenden Rechtsgrundlagen gemäß Artikel 6 UK GDPR:",
            "Vertragliche Notwendigkeit – zur Bereitstellung und zum Betrieb der Plattform",
            "Berechtigte Interessen – zur Gewährleistung der Sicherheit, Verhinderung von Missbrauch und Verbesserung der Serviceleistung",
            "Rechtliche Verpflichtung – zur Einhaltung von Buchhaltungs-, Steuer- und regulatorischen Anforderungen",
            "Einwilligung, sofern ausdrücklich eingeholt (z. B. optionale Kommunikation)"
          ]
        },
        {
          title: "5. Wie wir Ihre Informationen verwenden",
          content: [
            "Wir verwenden die gesammelten Daten, um:",
            "Die Plattform bereitzustellen, zu betreiben und zu warten",
            "Berechnungen zu verarbeiten und professionelle Berichte zu erstellen",
            "Abonnements, Abrechnungen und Kundensupport zu verwalten",
            "Die Übereinstimmung mit internationalen Sicherheitsrahmenwerken (CTU-Code / CSS-Code) sicherzustellen",
            "Die Systemsicherheit zu überwachen und unbefugten Zugriff zu verhindern",
            "Die Funktionalität der Plattform zu verbessern und weiterzuentwickeln",
            "Wir verkaufen keine personenbezogenen Daten an Dritte."
          ]
        },
        {
          title: "6. Datenweitergabe und Dritte",
          content: [
            "Personenbezogene Daten können nur weitergegeben werden an:",
            "Hosting- und Cloud-Infrastrukturanbieter",
            "Zahlungs- und Abrechnungsdienstleister",
            "Professionelle Berater (Recht, Buchhaltung, Versicherung)",
            "Behörden, sofern gesetzlich vorgeschrieben",
            "Alle Dritten unterliegen angemessenen Vertraulichkeits- und Datenschutzverpflichtungen."
          ]
        },
        {
          title: "7. Internationale Datentransfers",
          content: [
            "Wenn personenbezogene Daten außerhalb des Vereinigten Königreichs oder des EWR übertragen werden, stellen wir sicher, dass angemessene Schutzmaßnahmen vorhanden sind, einschließlich:",
            "Angemessenheitsbeschlüsse",
            "Standardvertragsklauseln (SCCs)",
            "Gleichwertige rechtliche Übertragungsmechanismen"
          ]
        },
        {
          title: "8. Datenaufbewahrung",
          content: [
            "Wir bewahren personenbezogene Daten nur so lange auf, wie es notwendig ist:",
            "Konto- und Rechnungsdaten: für die Dauer des Vertrags und wie gesetzlich vorgeschrieben",
            "Technische Protokolle: für begrenzte Zeiträume zu Sicherheits- und Prüfzwecken aufbewahrt",
            "Industrielle Berechnungsdaten: nur so lange aufbewahrt, wie für die Erbringung des Dienstes erforderlich",
            "Benutzer bleiben für die Archivierung ihrer eigenen Berichte und Berechnungsausgaben verantwortlich."
          ]
        },
        {
          title: "9. Datensicherheit",
          content: [
            "Wir implementieren angemessene technische und organisatorische Maßnahmen, um personenbezogene Daten vor unbefugtem Zugriff, Verlust, Änderung oder Offenlegung zu schützen, einschließlich Zugriffskontrollen, Verschlüsselung und Überwachung.",
            "Es kann jedoch kein System als vollständig sicher garantiert werden."
          ]
        },
        {
          title: "10. Ihre Rechte",
          content: [
            "Nach der UK GDPR haben Sie das Recht auf:",
            "Zugang zu Ihren personenbezogenen Daten",
            "Berichtigung unrichtiger Daten",
            "Löschung (vorbehaltlich gesetzlicher Verpflichtungen)",
            "Einschränkung oder Widerspruch gegen die Verarbeitung",
            "Datenübertragbarkeit",
            "Widerruf der Einwilligung, sofern zutreffend",
            "Anfragen können über die unten angegebenen Kontaktdaten gestellt werden."
          ]
        },
        {
          title: "11. Beschwerden",
          content: [
            "Wenn Sie glauben, dass Ihre Datenschutzrechte verletzt wurden, haben Sie das Recht, eine Beschwerde beim UK Information Commissioner’s Office (ICO) einzureichen."
          ]
        },
        {
          title: "12. Änderungen dieser Richtlinie",
          content: [
            "Wir können diese Datenschutzrichtlinie von Zeit zu Zeit aktualisieren. Aktualisierte Versionen werden auf der Plattform mit einem überarbeiteten Gültigkeitsdatum zur Verfügung gestellt."
          ]
        },
        {
          title: "13. Kontaktieren Sie uns",
          content: [
            "Für Anfragen zum Datenschutz oder zur Datensicherheit:",
            "E-Mail: info@lashingplan.com"
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "Datenschutz & Privatsphäre",
          content: "Wir verpflichten uns, Ihre Privatsphäre zu schützen. Diese Richtlinie erklärt, wie Ihre Informationen gemäß globalen Standards gesammelt, verwendet und geschützt werden."
        },
        collection: {
          title: "Informationen, die wir sammeln",
          personal_title: "Kontodaten",
          personal_desc: "Grundlegende berufliche Informationen, die für die Kontoverwaltung und technische Berichterstattung verwendet werden.",
          calc_title: "Industriedaten",
          calc_desc: "Ladungsparameter und Konfigurationen. Diese Daten werden als 'Vertrauliche Industrieinformationen' behandelt."
        },
        usage: {
          title: "Wie wir Informationen verwenden",
          intro: "Wir verwenden die gesammelten Informationen auf folgende Weise:",
          items: [
            "Bereitstellung, Betrieb und Wartung des sicheren Dienstes",
            "Verarbeitung von Berechnungen und Erstellung professioneller PDF-Berichte",
            "Sicherstellung der Einhaltung internationaler Sicherheitsvorschriften (CTU/CSS)",
            "Verbesserung und Erweiterung der Dienstleistungskapazitäten"
          ]
        },
        compliance: {
          title: "Globale Compliance",
          content: "Wir ergreifen angemessene Maßnahmen, um sicherzustellen, dass Sie gemäß der britischen Datenschutz-Grundverordnung (UK GDPR) und dem Data Protection Act 2018 die Kontrolle über Ihre Daten haben.",
          note: "Hinweis: Wir verkaufen Ihre personenbezogenen Daten nicht an Dritte."
        },
        retention: {
          title: "Datenaufbewahrung",
          content: "Wir bewahren Ihre personenbezogenen Daten nur so lange auf, wie es für den Dienst erforderlich ist und wie es die gesetzlichen Verpflichtungen erfordern."
        },
        contact: {
          title: "Kontakt",
          content: "Für Fragen zu Datenrechten oder Datenschutz:",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. Einführung",
          content: [
            "Diese Cookie-Richtlinie erklärt, wie JADE CONSULTANCY AND TRADING LTD („wir“, „uns“, „unser“) Cookies und ähnliche Technologien auf der Lashing Calculator Website und Webanwendung („Plattform“) verwendet.",
            "Diese Richtlinie sollte zusammen mit unserer Datenschutzrichtlinie gelesen werden."
          ]
        },
        {
          title: "2. Was sind Cookies?",
          content: [
            "Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden, wenn Sie eine Website besuchen. Sie werden häufig verwendet, um die Funktionalität der Website zu ermöglichen, die Benutzererfahrung zu verbessern und Informationen an die Website-Betreiber zu liefern."
          ]
        },
        {
          title: "3. Rechtlicher Rahmen",
          content: [
            "Wir verwenden Cookies in Übereinstimmung mit:",
            "Den britischen Datenschutz- und elektronischen Kommunikationsvorschriften (PECR)",
            "Der EU-ePrivacy-Richtlinie",
            "Der britischen Datenschutz-Grundverordnung (UK GDPR)",
            "Mit Ausnahme von unbedingt notwendigen Cookies werden Cookies nur mit Ihrer vorherigen Zustimmung verwendet."
          ]
        },
        {
          title: "4. Arten von Cookies, die wir verwenden",
          content: [
            "4.1 Unbedingt notwendige Cookies",
            "Diese Cookies sind für den Betrieb der Plattform unerlässlich und ermöglichen Kernfunktionen wie:",
            "Benutzerauthentifizierung",
            "Sitzungsmanagement",
            "Sicherheit und Betrugsprävention",
            "Diese Cookies erfordern keine Zustimmung und können nicht über unsere Cookie-Einstellungen deaktiviert werden.",
            "Rechtsgrundlage: Notwendig für die Bereitstellung des vom Benutzer angeforderten Dienstes.",
            "",
            "4.2 Analyse-Cookies",
            "Analyse-Cookies helfen uns zu verstehen, wie die Plattform genutzt wird, damit wir Leistung und Benutzerfreundlichkeit verbessern können.",
            "Beispiele sind:",
            "Seitenaufrufzahlen",
            "Verkehrsquellen",
            "Nutzungsmuster",
            "Analyse-Cookies werden nur platziert, nachdem Sie Ihre ausdrückliche Zustimmung gegeben haben.",
            "Rechtsgrundlage: Zustimmung gemäß UK GDPR und ePrivacy-Regeln."
          ]
        },
        {
          title: "5. Cookie-Anbieter",
          content: [
            "Analyse-Cookies können von vertrauenswürdigen Drittanbietern bereitgestellt werden. Diese Anbieter verarbeiten Daten in Übereinstimmung mit ihren eigenen Datenschutzrichtlinien und den geltenden Datenschutzgesetzen.",
            "Wir verwenden keine Cookies für gezielte Werbung oder Marketing-Profiling."
          ]
        },
        {
          title: "6. Cookie-Dauer",
          content: [
            "Cookies können sein:",
            "Sitzungscookies, die ablaufen, wenn Sie Ihren Browser schließen",
            "Dauerhafte Cookies, die für einen festgelegten Zeitraum oder bis zur Löschung auf Ihrem Gerät verbleiben",
            "Spezifische Cookie-Laufzeiten hängen von ihrem Zweck und Anbieter ab."
          ]
        },
        {
          title: "7. Verwaltung Ihrer Cookie-Präferenzen",
          content: [
            "Sie können Ihre Cookie-Zustimmung jederzeit verwalten oder widerrufen, indem Sie:",
            "Die auf der Plattform verfügbaren Cookie-Einstellungen oder das Zustimmungsbanner verwenden",
            "Ihre Browsereinstellungen anpassen, um Cookies zu blockieren oder zu löschen",
            "Bitte beachten Sie, dass das Deaktivieren bestimmter Cookies die Funktionalität der Plattform beeinträchtigen kann."
          ]
        },
        {
          title: "8. Änderungen an dieser Cookie-Richtlinie",
          content: [
            "Wir können diese Cookie-Richtlinie von Zeit zu Zeit aktualisieren. Alle Änderungen werden auf der Plattform mit einem aktualisierten Gültigkeitsdatum veröffentlicht."
          ]
        },
        {
          title: "9. Kontaktieren Sie uns",
          content: [
            "Wenn Sie Fragen zu unserer Verwendung von Cookies haben, kontaktieren Sie uns bitte unter:",
            "E-Mail: info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "Cookie-Richtlinie",
        last_updated: "Gültig ab: 4. Februar 2026",
        intro_title: "Einleitung",
        intro_content: "Diese Cookie-Richtlinie erklärt, wie Cookies verwendet werden, wenn Sie die Lashing Calculator-Website besuchen.",
        types_title: "Arten von Cookies, die wir verwenden",
        essential_title: "Essentielle Cookies",
        essential_desc: "Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unbedingt erforderlich und können in unseren Systemen nicht deaktiviert werden.",
        analytics_title: "Analyse-Cookies",
        analytics_desc: "Diese Cookies ermöglichen es uns, Besuche und Traffic-Quellen zu zählen, um die Leistung unserer Website zu messen und zu verbessern.",
        manage_title: "Verwaltung Ihrer Cookie-Einstellungen",
        manage_content: "Sie können Ihre Cookie-Einstellungen jederzeit über Ihre Browsereinstellungen verwalten.",
        contact_title: "Kontaktieren Sie uns",
        contact_text: "Wenn Sie Fragen zu unserer Cookie-Richtlinie haben, kontaktieren Sie uns bitte."
      },
      sales_agreement: {
        title: "Kaufvertrag",
        content: "Dieser Kaufvertrag legt die Bedingungen für die Bereitstellung und Nutzung der Lashing Calculator-Dienste fest. Der Kauf unserer Dienste bedeutet Ihre Zustimmung zu allen Bedingungen dieses Vertrags."
      },
      legal_notice: {
        title: "Rechtlicher Hinweis",
        content: "Die Informationen auf dieser Website dienen nur zu allgemeinen Informationszwecken. Obwohl Lashing Calculator sich bemüht, die Informationen aktuell und korrekt zu halten, geben wir keine Zusicherungen oder Garantien jeglicher Art."
      },
      footer_note: "Durch die Nutzung der Plattform bestätigen Sie, dass Sie die Sicherheitshinweise, technischen Risiken und rechtlichen Bedingungen gelesen und akzeptieren, einschließlich der Anforderung einer professionellen Überprüfung aller Ergebnisse."
    }
  },

  // ===================================================================
  // 中文
  // ===================================================================
  "中文": {
    code: "zh",
    name: "中文",
    flag: "China",

    hub: {
      nav: {
        products: "产品",
        insights: "市场洞察",
        resources: "资源",
        go_to_app: "前往应用"
      },
      hero: {
        badge: "下一代货运物流中心",
        status: "系统状态：最佳",
        title_main: "计算强度",
        title_italic: "绝对安全",
        title_1: "规划",
        title_2: "最大安全性。",
        description: "LashingPlan 是一个专业的货物固定工具中央生态系统，精确计算并符合国际标准。",
        explore: "探索产品",
        launch_product: "启动产品",
        read_blog: "阅读博客",
        portal_access: "门户访问 V2.0",
        stability_index: "稳定性指数",
        archives: "档案",
        archives_subtitle: "了解专业安全标准 and 物流趋势，保持领先。"
      },
      products: {
        title: "我们的产品",
        subtitle: "为全球运输合规和货物安全设计的专业工具。",
        context: "01 / 产品",
        calculator: {
          title: "系固计算器 (Lashing Calculator)",
          description: "用于计算公路、海上和铁路货物固定要求的行业标准引擎。",
          button: "启动应用"
        },
        inspection: {
          title: "Inspection Pro",
          description: "支持照片的数字化检查和现场勘查即时证书生成工具。",
          status: "即将推出"
        },
        risk: {
          title: "风险顾问 (Risk Advisor)",
          description: "由 AI 驱动的稳定性分析和货运风险评估系统。",
          status: "即将推出"
        },
        stats: {
          calculations: "计算次数",
          countries: "国家",
          compliance: "合规性",
          precision: "精确度"
        },
        blog_articles: {
          article_1: {
            title: "Cargo Securing: Lashing",
            description: "Securing cargo to the transport unit before shipment is the foundation of logistics safety..."
          },
          article_2: {
            title: "Innovative Cargo Freight Solutions",
            description: "Forward-thinking cargo solutions emphasizing pioneering innovation and reliable lashing..."
          },
          article_3: {
            title: "Understanding IMO/CTU Code Compliance",
            description: "A deep dive into the international standards regulating cargo transport in containers..."
          },
          article_4: {
            title: "Enhancing Cargo Safety Standards",
            description: "Improving safety with modern protocols, technology integration, and proven safety practices..."
          }
        }
      },
      insights: {
        title: "市场洞察",
        subtitle: "从我们的专家那里了解货物安全标准、行业合规性和最新的物流趋势。",
        all_articles: "查看所有文章"
      },
      footer: {
        tagline: "卓越技术的产物。专为对海上安全要求绝对精确的人士设计。",
        systems: "系统",
        resources: "资源",
        legal: "法律",
        privacy: "隐私",
        compliance: "合规",
        terms: "条款",
        status_all: "LASHING PLAN GLOBAL // 所有系统运行正常",
        follow: "关注我们"
      }
    },
    blog: {
      nav: {
        back: "返回首页"
      },
      header: {
        title_1: "来自",
        title_2: "LashingPlan 的洞察"
      },
      article: {
        read_time: "分钟阅读",
        share: "分享",
        read_more: "阅读更多",
        cta_title: "准备好固定您的货物了吗？",
        cta_desc: "加入成千上万信任我们的工具进行精确、合规的系固计算的专业人士。",
        cta_button: "现在开始计算"
      },
      footer: {
        copyright: "© 2026 LashingPlan 博客。保留所有洞察权。"
      }
    },

    app: {
      title: "系固计算器",
      logout: "退出登录",
      theme_toggle: "深色 / 浅色模式",
      profile: "个人资料",
      reports: "现场报告",
      login: {
        title: "登录",
        subtitle: "登录您的账户",
        email: "电子邮件或用户名",
        email_placeholder: "输入您的电子邮件或用户名",
        password: "密码",
        password_placeholder: "输入您的密码",
        forgot_password: "忘记密码？",
        submit: "登录",
        no_account: "没有账户？",
        signup: "注册",
        back_to_home: "返回首页",
        consent: "继续操作即表示您同意{terms}和{privacy}。",
        surveyor_portal: "您是检验员 (Surveyor) 吗？前往检验员门户"
      },
      cog3d: {
        title: "3D 重心 (CoG) 视图",
        subtitle: "重心 (CoG) 是货物的平衡点，决定了货物的倾覆行为。",
        drag_hint: "拖动以旋转"
      },
      lashing3d: {
        title: "3D 捆扎图",
        subtitle: "捆扎配置的交互式视图"
      },
      calculator: {
        title: "货物计算器",
        cargo_details: "货物详情",
        weight: "重量 (MT)",
        cog: {
          title: "重心（从左下角开始计算，单位：厘米）",
          x: "X (宽度)",
          y: "Y (深度)",
          z: "Z (高度)"
        },
        securing_elements: "固定元件",
        wooden_dunnage: {
          label: "使用木垫块",
          description: "增加摩擦力，防止滑动",
          details: "木垫块详情",
          width: "宽度 (cm)"
        },
        loop_lashing: "环状系固",
        direct_lashing: "直接系固",
        select_rope_type: "选择绳索类型",
        stopper_forces: "止动器力 (daN)",
        direction: {
          forward: "对抗前向力",
          backward: "对抗后向力",
          right: "对抗右向力",
          left: "对抗左向力"
        }
      },
      results: {
        title: "结果",
        lashing_diagram: "系固图",
        error: "错误",
        loading: "加载中...",
        no_results: "未找到结果",
        values: {
          sliding: "滑动:",
          tipping: "倾斜:",
          friction: "摩擦:",
          securing_element: "固定元件:",
          net_sliding: "净滑动:",
          net_tipping: "净倾斜:"
        }
      }
    },
    landing: {
      contact: {
        label: "联系销售",
        title_1: "准备好保障您的",
        title_2: "货物运营了吗？",
        description: "升级到企业级检验员算法。请联系我们的工程团队讨论定制 API 集成、大批量许可或高级功能。",
        email_us: "联系我们",
        enterprise_support: "企业支持",
        support_desc: "提供 24/7 优先 SLA",
        success_title: "消息已发送！",
        success_desc: "我们的工程团队将在 24 小时内与您联系。",
        form: {
          name: "全名",
          placeholder_name: "张明",
          company: "公司",
          placeholder_company: "物流企业",
          email: "电子邮箱",
          placeholder_email: "zhang@company.com",
          message: "您的留言",
          placeholder_message: "我们如何帮您扩展绑扎运营？",
          send: "发送请求"
        }
      },
      hero_badge: "全球载荷系固专业人士信赖",
      hero_badge_imo: "符合 IMO 标准",
      hero_badge_imo_sub: "国际标准",
      hero_badge_3d: "3D 可视化",
      hero_badge_3d_sub: "实时",
      hero_title_1: "专业",
      hero_title_2: "系固智能",
      hero_subtitle: "全球标准。精确算法。专业报告。行业最先进的货物系固平台。",
      start_calculating: "立即计算",
      view_demo: "观看演示",
      stats: {
        modes: "6+ 运输模式",
        platform: "云平台",
        platform_status: "安全",
        access: "全球访问"
      },
      features_title_1: "动力",
      features_title_2: "合规",
      features_subtitle: "由货运安全专家为高风险运输业务打造。",
      features: {
        calculations: {
          title: "即时计算",
          desc: "使用我们的先进算法实时计算系固力。"
        },
        pdf: {
          title: "专业 PDF 报告",
          desc: "生成包含公司品牌和详细分析的行业标准报告。"
        },
        compliance: {
          title: "CTU 参考评估",
          desc: "参考 IMO / ILO / UNECE CTU Code 的简化决策支持。"
        },
        multimodal: {
          title: "多式联运",
          desc: "支持公路、铁路和海运。集装箱、框架箱、船舶等。"
        },
        global: {
          title: "全球标准",
          desc: "参考 CTU Code 2014 及其信息材料的可追溯评估。"
        },
        secure: {
          title: "安全机密",
          desc: "您的计算数据受到企业级安全的保护。"
        }
      },
      transport: {
        title_1: "统一运输",
        title_2: "安全",
        subtitle: "跨越整个物流链的无缝计算。",
        modes: {
          road: { name: "公路", desc: "卡车拖车和低平板车" },
          rail: { name: "铁路", desc: "铁路货车、集装箱、框架箱和卡车拖车" },
          sea: { name: "海运", desc: "集装箱、卡车拖车、框架箱、低平板车、滚装拖车、船舶" },
          combined: { name: "多式联运", desc: "陆海、陆铁、陆海铁" }
        }
      },
      how_it_works: {
        badge: "工作原理",
        title_1: "专业分析",
        title_2: "3个简单步骤",
        subtitle: "在几分钟内完成复杂的系固计算",
        step1: {
          title: "输入货物详情",
          desc: "将货物尺寸、重量、重心和集装箱类型输入系统。"
        },
        step2: {
          title: "自动计算",
          desc: "系统根据IMO标准自动计算最佳系固布置",
          desc_highlight: " 并允许在计划中进行手动更改。"
        },
        step3: {
          title: "获取报告",
          desc: "生成带有3D可视化和详细计算的专业PDF报告。"
        }
      },
      benefits: {
        title: "为不容出错的运营而设计",
        subtitle: "深受全球专家、检验员和码头运营商信赖，确保关键货物的安全。",
        list: [
          "节省数小时的手工计算时间",
          "通过优化减少材料浪费",
          "确保完全符合法规要求",
          "为客户提供专业文档",
          "多语言支持 (EN, TR, ES, DE, ZH, JA)",
          "企业品牌的 PDF 报告"
        ],
        cta_card_title: "立即标准化您的系固计划",
        cta_card_desc: "为您的团队使用企业级计算器，确保所有出货 100% 合规。",
        cta_card_trial: "无需信用卡即可试用"
      },
      footer: {
        desc: "加入全球使用我们智能系固计算器的专业人士行列。",
        columns: {
          service: "服务",
          company: "公司",
          support: "支持"
        },
        links: {
          privacy: "隐私中心",
          compliance: "合规条款",
          cookies: "Cookie 政策",
          security: "安全",
          help: "帮助中心",
          enterprise: "企业",
          contact: "咨询专家",
          force_analysis: "受力分析",
          pdf_generator: "PDF 生成器",
          optimizer: "优化器",
          api_docs: "API 文档",
          cookie_settings: "Cookie 设置"
        }
      },
      footer_copyright: "2025 Lashing Calculator. 由 JADE CONSULTANCY AND TRADING LTD 提供的服务。保留所有权利。",

      pricing_simple: "简单、",
      pricing_transparent: "透明的",
      pricing_title: "价格",
      compare_features: "比较所有功能",
      footer_global: "全球",
      footer_encrypted: "加密",
      footer_high_availability: "高可用性",
      demo_request: {
        title: "请求演示",
        subtitle: "了解 Lashing Calculator 如何变革您的货物安全操作。",
        cta_title: "准备好一睹为快了吗？",
        cta_subtitle: "与我们的货物专家预约个性化演示，了解我们如何在全球范围内标准化您的货物绑扎作业。",
        form: {
          full_name: "全名",
          work_email: "工作邮箱",
          company: "公司名称",
          job_title: "职位",
          phone: "电话号码",
          fleet_size: "车队规模 / 每月货运量",
          industry: "行业",
          message: "留言 (可选)",
          submit: "提交演示请求",
          success_title: "请求已收到",
          success_message: "您的演示请求已成功提交。我们的团队将尽快与您联系。"
        }
      },
      price_inquiry: {
        title: "获取报价",
        subtitle: "联系我们获取定制报价和企业解决方案。",
        form: {
          full_name: "全名",
          work_email: "工作邮箱",
          company: "公司名称",
          phone: "电话号码",
          message: "留言 (可选)",
          submit: "发送咨询",
          success_title: "咨询已发送",
          success_message: "您的价格咨询已收到。我们的销售团队将尽快为您提供定制方案。"
        }
      }
    },

    labels: {
      three_d_view: "3D 视图",
      no_results_data: "暂无结果数据",
      pass: "合格",
      fail: "不合格",
      reports: "报告",
      user_responsible_for: "您独自负责：",
      page_title: "捆扎计算工具",
      weight: "重量 (MT)",
      transport_mode: "运输方式",
      transport_unit: "运输单元",
      friction_surface: "摩擦表面",
      select: "选择",
      select_transport_mode_first: "请先选择运输方式",
      cargo_dimensions: "货物尺寸及重心 (m)",
      cargo_specs: "CARGO SPECS",
      width: "货物宽度",
      length: "货物长度",
      height: "货物高度",
      cog_width: "重心宽度偏移 (m):",
      cog_length: "重心长度偏移 (m):",
      cog_height: "重心高度 (m):",
      cog_width_help: "重心默认为宽度的一半。如有不同请修改。",
      cog_length_help: "重心默认为长度的一半。如有不同请修改。",
      cog_height_help: "重心默认为高度的一半。如有不同请修改。",
      wooden_dunnage_dimensions: "木质垫块尺寸及数量",
      wooden_dunnage_width: "宽度 (cm):",
      wooden_dunnage_length: "长度 (cm):",
      stopper_qty: "金属止挡器 (数量)",
      force_analysis: "力分析结果 (kN)",
      generate_pdf: "生成PDF",
      download_pdf: "下载PDF",
      wooden_dunnage: "木质垫块",
      stopper: "止挡器",
      loop_lashing: "环绕绑扎",
      max_weight: "最大重量",
      direct_lashing: "直接绑扎",
      loop_rope_select: "选择环形系固织带",
      loop_lashing_quantity_per_side: "数量（每侧）",
      direct_lashing_quantity: "数量",
      cog_above: "高于重心",
      cog_below: "低于重心",
      forward: "对抗前向力",
      backward: "对抗后向力",
      right: "对抗右向力",
      left: "对抗左向力",
      lashing_diagram: "系固图",
      language: "语言",
      calculate: "计算",
      configuration_panel: "配置面板",
      operation_mode: "操作模式",
      manual_mode: "计算器",
      lashing_calculator: "绑定计算器",
      direct_lashing_selection: "直线绑扎选择",
      reset: "重置",
      print: "打印",
      configuration: "配置",
      use_stopper: "使用止动器",
      logout: "登出",
      results_will_appear_here: "结果将显示在这里",
      continue_with_overweight: "继续超重运输",
      optimizer_mode: "优化模式",
      calculator_mode: "计算模式",
      project_description: "项目说明（可选）",
      enter_project_description_hint: "请输入将出现在 PDF 报告中的项目说明。",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "在计算器中编辑",
      breakbulk_settings: "Breakbulk Settings",
      flatrack_type: "Flatrack Type",
      orientation: "货物方向",
      longitudinal: "纵向",
      transverse: "横向",
      flatrack_count: "建议的平板箱数量",
      flatrack_gap: "平板箱间隙"
    },

    common: {
      info: "信息",
      yes: "是",
      no: "否",
      ok: "确定",
      cancel: "取消",
      confirm: "确认",
      close: "关闭",
      save: "保存",
      download: "下载",
      delete: "删除",
      edit: "编辑",
      view: "查看",
      loading: "加载中...",
      processing: "处理中...",
      please_wait: "请稍候...",
      save_changes: "保存更改",
      saving: "保存中..."
    },

    transport_modes: {
      road: "公路",
      rail: "铁路",
      sea_a: "海运_A (波浪 <8m)",
      sea_b: "海运_B (8m < 波浪 <12m)",
      sea_c: "海运_C (波浪 >12m)",
      road_sea_a: "公路+海运_A",
      road_sea_b: "公路+海运_B",
      road_sea_c: "公路+海运_C",
      road_rail: "公路+铁路",
      road_rail_sea_a: "公路+铁路+海运_A",
      road_rail_sea_b: "公路+铁路+海运_B",
      road_rail_sea_c: "公路+铁路+海运_C"
    },

    transport_unit_groups: {
      containers: "集装箱",
      open_tops: "开顶集装箱",
      flatracks: "框架集装箱",
      trailers: "拖车",
      special: "其他/特殊",
    },
    transport_units: {
      container_20: "20' 集装箱",
      container_40: "40' 集装箱",
      container_40_hc: "40' 集装箱高箱 (HC)",
      container_45_hc: "45' 集装箱高箱 (HC)",
      open_top_20: "20' 开顶箱",
      open_top_20_hc: "20' 开顶箱高箱 (HC)",
      open_top_40: "40' 开顶箱",
      open_top_40_hc: "40' 开顶箱高箱 (HC)",
      flatrack_20: "20' 框架箱",
      flatrack_40: "40' 框架箱",
      lowbed: "低平板车",
      mafi: "马飞拖车",
      vessel: "船舶",
      breakbulk: "散货 (Break Bulk)",
      truck_trailer_standard: "卡车挂车 (标准)",
      truck_trailer_mega: "卡车挂车 (超大)",
      open_top_container: "开顶集装箱"
    },

    materials: {
      asm: "防滑垫 (静态 0.8µ)",
      asm_06: "防滑垫 (静态 0.6µ)",
      metal_metal: "金属-金属",
      metal_wood: "金属-木材",
      wood_wood: "木材-木材"
    },

    directions: {
      forward: "对抗前向力",
      backward: "对抗后向力",
      right: "对抗右向力",
      left: "对抗左向力"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "吨"
    },

    errors: {
      zero_input: "请填写所有必填字段。",
      no_loop_rope: "未找到兼容的环形绳索。",
      no_direct_rope: "未找到兼容的直接系固绳索。",
      calculation_error: "计算错误：{error}",
      unexpected_error: "意外错误：{error}",
      diagram_error: "无法生成图表：{error}",
      pdf_error: "PDF错误：{error}",
      loop_config_error: "环形配置错误：{error}",
      error_halat: "未选择直接系固绳索。",
      invalid_transport_unit: "无效的运输单元：{unit}。请选择有效的单元。",
      network_error: "网络错误。请检查您的互联网连接。",
      file_not_found: "文件未找到。",
      invalid_cargo_dimensions: "警告：选择的运输单元不适用于该货物",
      select_transport_mode: "请选择运输方式",
      select_transport_unit: "请选择运输单元",
      select_friction_surface: "请选择摩擦表面",
      invalid_weight: "请输入有效的重量",
      invalid_dimensions: "请输入有效的货物尺寸",
      invalid_cog: "请输入有效的重心坐标",
      invalid_wooden_dunnage: "请输入有效的木垫块尺寸",
      invalid_stopper: "请输入有效的止挡器数量",
      select_at_least_one_rope: "请选择至少一种绳索",
      select_rope_type: "请选择绳索类型",
      dimension_exceeded: "警告：货物{dims}超出了所选运输单元的限制。",
      dim_width: "宽度",
      dim_length: "长度",
      dim_height: "高度",
      cargo_width_greater_than_length: "货物宽度不能大于长度。值已自动调整以匹配长度。",
      and_conjunction: " 和 ",
      breakbulk_dim_overflow_width: "⚠️ 货物宽度 ({cargo_width}m) 大于或等于所选框架箱长度 ({flatrack_length}m)！无法进行计算。",
      breakbulk_dim_overflow_length: "⚠️ 货物长度 ({cargo_length}m) 大于或等于所选框架箱长度 ({flatrack_length}m)！无法进行计算。"
    },

    validation: {
      required_field: "此字段为必填项",
      invalid_number: "请输入有效数字",
      positive_number: "请输入正数",
      min_value: "最小值：{min}",
      max_value: "最大值：{max}",
      out_of_range: "值必须在 {min} 和 {max} 之间",
      invalid_email: "请输入有效的电子邮件地址",
      invalid_format: "格式无效"
    },

    warnings: {
      weight_exceeded: "警告：{unit} 的最大重量容量 ({max_weight} MT) 已超出！",
      overweight_confirmation: "输入的货物重量 ({weight} MT) 超过了最大允许重量 ({max_weight} MT)。请再次确认以继续。",
      oversized_width_loop_disabled: "由于货物宽度超限，环形扎带已禁用。",
      oversized_width_side_wedges_disabled: "由于超宽载重，侧部楔块已禁用。",
      high_cog: "警告：重心过高会增加倾覆风险。",
      insufficient_lashing: "警告：检测到绑扎不足。",
      unsaved_changes: "您有未保存的更改。是否继续？",
      flatrack_dunnage_required: "建议使用木垫块来限制Flatrack运输单元上货物在前后方向的滑动。您想在不添加木垫块的情况下继续计算吗？",
      terms_acceptance_required: "You must read and accept the Terms of Service to perform calculations. You can access the Terms of Service from your Profile page.",
      flatrack_count_low: "Attention : le nombre de flatracks sélectionné ({selected}) est inférieur au nombre recommandé ({recommended}) !"
    },

    success: {
      pdf_created: "PDF创建成功！",
      calculation_complete: "计算完成！",
      data_saved: "数据保存成功。",
      export_complete: "导出完成。"
    },

    table_columns: {
      direction: "方向",
      sliding: "滑动",
      tipping: "倾覆",
      friction: "摩擦",
      wooden_dunnage: "木质垫块",
      stopper: "止挡器",
      looplash: "环形\n系固",
      direct_lashing: "直接系固",
      cordstrap_loop: "Cordstrap 环形",
      cordstrap_direct: "Cordstrap 直接",
      total_securing: "总固定力",
      net_sliding: "净滑动",
      net_tipping: "净倾覆",
      net_sliding_tooltip: "负值表示安全固定。",
      net_tipping_tooltip: "负值表示安全固定。",
      status: "状态",
      safety_factor: "安全系数"
    },

    results: {
      safe: "安全",
      unsafe: "危险",
      marginal: "临界",
      critical: "重大",
      acceptable: "可接受",
      status: "状态",
      safety_factor: "安全系数",
      summary: "摘要",
      details: "详情",
      notes_title: "备注 / 假设",
      notes_assumed: "",
      notes_cargo_unitized: "货物被视为单件物品，或合并并集成一个货物单元。",
      notes_lashing_above_cog: "除非“材料使用”表中另有规定，否则假定直接系固应用于货物重心上方。",
      notes_angle_range: "斜接到运输单元系固点的直接系固件与作用力方向上的直接系固件矢量分量之间的锐角必须在30°至60°之间。",
      notes_edge_protection: "必要时应提供适当的边缘和护角，以防止损坏货物和/或系固材料。",
      notes_pretension_force: "张紧织带系固件时，施加的预张紧力不得超过织带系固件MSL值的50%。",
      notes_strength_limitations: "假定货物上的系固点没有强度限制。",
      notes_distributed_uniformly: "假定载荷根据运输单元的容量标准均匀分布。",
      flatrack_no_dunnage_warning: "注意：建议使用木垫块来限制Flatrack运输单元上货物在前后方向的滑动"
    },

    tooltips: {
      weight_info: "输入货物总重量（公吨）",
      cog_info: "COG 代表重心（Center of Gravity）。默认情况下，假设它位于货物的中心。但在现实中，重心可能位于不同的位置。如果已知具体的 COG，可以在此处输入其正确位置。准确的 COG 定位至关重要，特别是对于倾覆计算。",
      friction_info: "选择材料组合以确定摩擦系数",
      transport_mode_info: "加速度值因运输方式而异",
      lashing_info: "系固力可防止滑动和倾覆"
    },
    legal: {
      tos_title: "服务条款",
      privacy_title: "隐私政策",
      sales_agreement_title: "销售协议",
      legal_notice_title: "法律声明",
      cookie_policy_title: "Cookie政策",
      last_updated: "生效日期：2026年2月4日",
      return_home: "返回首页",
      sections: {
        acceptance: {
          title: "条款接受",
          content: "访问和使用 Lashing Calculator 即表示您同意受这些条款的约束。如果您不同意，请勿使用本服务。"
        },
        payment_terms: {
          title: "付款条款和订阅",
          content: "本服务按订阅方式计费。付款不可退款。我们保留提前通知更改价格的权利。"
        },
        termination: {
          title: "取消和终止",
          content: "您可以随时取消订阅。访问权限将持续到当前计费周期结束。"
        },
        disclaimer: {
          title: "重要安全提示与专业免责声明",
          important: "重要提示：所提供的计算工具仅作为货物系固规划的“决策支持系统”。",
          content: "平台不提供专业工程建议。所有结果在实施前必须经合格的货物检验员、系固专家或 IMO/ILO/UNECE CTU Code 及 IMO CSS Code 定义的“主管人员”审查和批准。根据国际海事法，最终责任由承运人和船长承担。"
        },
        license: {
          title: "知识产权与许可",
          subtitle: "平台的源代码、算法和设计受国际知识产权法保护：",
          items: [
            "用户被授予使用平台的有限、不可转让的许可；不发生所有权转移。",
            "准确输入货物和运输单元数据是用户的唯一责任。",
            "严禁对源代码进行逆向工程或提取。",
            "PDF 报告针对所述的特定货物，不得用于不同配置。"
          ]
        },
        liability: {
          title: "责任限制",
          content: "平台提供商不对因使用或无法使用服务而产生的任何损害（包括货物损失或业务中断）承担责任。",
          cap_title: "赔偿限额",
          cap_content: "在法律允许的最大范围内，累计总责任不得超过您在索赔前 12 个月内为服务支付的金额，或 100.00 美元，以较高者为准。",
          not_responsible: "我们不对以下各项负责：",
          not_responsible_items: [
            "系固操作不当导致的损害",
            "用户提供的不准确输入数据",
            "超出计算参数的环境变化",
            "运输单元或系固点的结构失效",
            "制造商规格不准确（MSL、摩擦力等）",
            "间接、偶然或惩罚性损害",
            "利润、商业机会或商誉损失"
          ]
        },
        third_party: {
          title: "第三方数据免责声明",
          content: "平台包含来自第三方的数据源，包括制造商和国际安全标准。",
          sub_content: "我们不对第三方数据的准确性或可靠性作任何保证。所有制造商规格均由其各自的制造商提供。"
        },
        accuracy: {
          title: "技术准确性",
          content: "虽然我们力求准确，但材料可能包含技术或排版错误。我们不保证所有材料始终是最新的或准确的。"
        },
        source_responsibility: {
          title: "数据来源与用户责任",
          important: "所有计算的准确性完全取决于您提供的参数。",
          utilize_content: "本服务利用多种数据源进行计算：",
          data_sources: [
            { title: "MSL 值", desc: "源自制造商规格；不进行独立验证。" },
            { title: "摩擦系数", desc: "基于行业标准；实际摩擦力可能有所不同。" },
            { title: "系固点", desc: "基于单元规格；物理完整性由用户负责。" },
            { title: "CTU/CSS 标准", desc: "符合国际海事安全标准。" }
          ],
          user_responsible_items: [
            "核实所有输入参数（重量、尺寸、重心位置）",
            "确保系固设备规格与实际使用的材料相符",
            "确认摩擦系数对特定货物条件的适用性",
            "物理验证系固点的完整性",
            "在实施前聘请合格专业人员核准结果"
          ]
        },
        verification: {
          title: "专业核实要求",
          content: "所有计算结果在实施前必须经 CTU Code 定义的主管人员审查和批准。平台仅提供计算支持，不构成工程建议。"
        },
        indemnification: {
          title: "赔偿声明",
          content: "您同意就以下原因导致的任何第三方索赔向平台提供商提供赔偿：",
          items: [
            "违反本服务条款",
            "提供不准确或不完整的输入数据",
            "使用陈旧或不正确的设备规格",
            "未能聘请专业人员进行核实",
            "未经独立专业审查即实施方案",
            "物理实施过程中的事故或损害"
          ]
        },
        governing_law: {
          title: "适用法律与管辖权",
          content: "这些条款受国际运输公约及提供商注册地法律管辖名。争议受该地主管法院的专属管辖。"
        }
      },
      detailed_terms: [
        {
          title: "1. 协议的形成",
          content: [
            "这些服务条款（'条款'）构成您（'用户'，'客户'）与 JADE CONSULTANCY AND TRADING LTD（根据英格兰和威尔士法律运营的公司）（'提供商'，'我们'）之间具有法律约束力的协议，管辖对 Lashing Calculator 网络应用程序平台的访问和使用。",
            "通过访问或使用平台，或签订服务订阅或销售协议，您不可撤销地同意受这些条款的约束。",
            "接受销售协议即构成对此等条款的自动接受。"
          ]
        },
        {
          title: "2. 仅限商业用途",
          content: [
            "本平台严格按照企业对企业 (B2B) 的基础提供。",
            "您确认您是在商业过程中行事，而非由于任何消费者保护立法下的消费者。",
            "平台为海运、公路和铁路运输方式及其多模式组合的货物固载计算提供协助。适用于任何平台使用的监管框架取决于相关运输方式和装卸载管辖区域。用户对识别和遵守所有适用的特定运输方式法规承担全部责任。"
          ]
        },
        {
          title: "3. 订阅，费用和付款",
          content: [
            "3.1 订阅期限：订阅期限为自激活之日起一 (1) 年。不适用自动续订。续订需要用户书面请求并全额支付新订阅期的费用。",
            "3.2 费用和货币：所有费用均以英镑 (£) 开具发票并支付。款项必须支付至提供商的英镑银行账户。费用需预付。",
            "3.3 到期通知：平台可能会在到期前一 (1) 个月，一 (1) 周和一 (1) 天发送提醒通知。此类通知仅为方便起见，不构成续订。"
          ]
        },
        {
          title: "4. 取消和终止",
          content: [
            "4.1 用户终止：用户可以随时取消。访问将持续到当前订阅期结束。除下文明确规定外，不适用退款。",
            "4.2 提供商终止：提供商可以出于合法的商业，法律，技术或安全原因随时终止或暂停服务，前提是在合理可行的情况下，提供商应提前通知。如果终止由提供商发起，用户有权获得订阅期未使用部分的按比例退款。"
          ]
        },
        {
          title: "5. 服务故障和退款",
          content: [
            "如果平台因非用户造成的技术原因连续超过五 (5) 天实质上无法使用，用户有权获得按比例退款，该退款根据未使用的天数按年度订阅费每日计算。",
            "临时中断，性能下降，维护窗口或兼容性问题不构成无法使用。"
          ]
        },
        {
          title: "6. 用户账户和许可",
          content: [
            "6.1 许可授予：提供商授予用户有限的，非独占的，不可转让的许可，仅出于内部商业目的访问和使用平台。不转让所有权。",
            "6.2 单一设备政策：每个用户账户一次只能在一台设备上使用，除非在适用的销售协议中另有书面约定。禁止并发登录。提供商保留监控使用模式以检测滥用的权利。如果合理怀疑滥用，提供商可以在调查期间暂停访问。确认的违规行为可能导致终止且不予退款。",
            "6.3 用户义务：要生成报告，用户必须提供：公司名称，公司徽标，手机号码，固定电话号码，电子邮件地址。必须提供有效的公司账单详细信息以开具发票。"
          ]
        },
        {
          title: "7. 重要安全通知与专业免责声明",
          content: [
            "关键通知：该平台是海运、公路、铁路及其多模式组合所有运输方式的货物系固规划决策支持系统。",
            "该平台不提供专业的工程，法律或安全建议。",
            "该平台未经任何船旗国，船级社，港口当局，公路运输当局，铁路基础设施管理者或监管机构的认证，批准或认可。",
            "所有计算结果必须由合格的货物专业人员根据相应方式审查和批准：IMO/ILO/UNECE CTU代码和IMO CSS代码（海运）；EN 12195 / VDI 2700和适用的国家公路指南（公路）；UIC装载指南（铁路）。",
            "货物系固的最终责任属于运输方式适用法律框架下的责任方：海运方面，根据适用国际海事法的承运人、船东和/或船长；公路方面，根据CMR公约的承运人、车辆运营者和/或司机；铁路方面，根据CIM/COTIF框架的承运人和铁路运营商。在多模式运输中，每种方式的承运人负责其自己的运输段。",
            "提供商对基于平台输出做出的决策不承担任何责任。",
            "海运方面：平台输出不满足SOLAS第六章VI/5-2规则下的法定货物记录义务。P&I合规、ISM公约义务和IACS船级要求由用户负责。",
            "公路方面：平台输出必须根据EN 12195系列和VDI 2700进行验证。英国运营商还必须遵守DfT货物固定实务守则。",
            "铁路方面：平台输出必须根据适用的UIC装载指南、TSI要求和国家铁路基础设施管理者要求进行验证。",
            "各港口、公路和铁路当局可能有额外要求。遵守所有当地法规由用户全责。",
            "危险品通知：该平台仅为一般货物系固规划设计，未为适用ADR（公路）、IMDG代码（海运）、RID（铁路）或同等危险品法规的危险品货物设计或验证。处理危险品的用户在操作使用前必须向具有资质的危险品安全顾问（DGSA）寻求专业建议。"
          ]
        },
        {
          title: "8. 知识产权",
          content: [
            "该平台，包括其源代码，算法，计算，结构和设计，受知识产权法保护。",
            "严禁逆向工程，提取或复制。",
            "PDF 报告仅对描述的特定货物配置有效，不得重复使用。",
            "准确的数据输入是用户的唯一责任。"
          ]
        },
        {
          title: "9. 数据来源，报告和用户责任",
          content: [
            "计算准确性完全取决于用户提供的参数。",
            "数据来源包括：MSL 值（制造商规格，无独立验证），摩擦系数（行业标准，实际情况可能有所不同），绑扎点（单元规格，物理完整性由用户负责）。",
            "按运输方式适用的监管框架：CTU代码（IMO/ILO/UNECE — 多模式，适用于所有方式），CSS代码（IMO货物固载手册 — 仅限海运），VDI 2700 / EN 12195-1至EN 12195-4（公路运输，欧洲车辆货物束缚标准），英国DfT货物固定实务守则（公路运输，英国管辖），UIC装载指南/TSI（铁路运输），CIM/COTIF统一规则（国际铁路运输合同）。用户对识别和应用其具体运输方式和管辖区域的正确监管标准承担全部责任。",
            "用户全权负责：验证所有输入数据，确保正确的设备选择，确认现实世界的适用性，物理验证绑扎安排，在实施前聘请合格的专业人员。",
            "报告是根据创建时的输入生成的。提供商没有义务保留计算数据或报告，用户负责存档。"
          ]
        },
        {
          title: "10. 技术准确性免责声明",
          content: [
            "该平台的提供采用了与 SaaS 服务公认行业标准一致的合理技能和谨慎。虽然已做出合理努力以确保准确性，但平台可能包含技术或印刷错误。不保证材料是完整的，最新的或无错误的。",
            "第三方数据和标准可能会随时间变化。更新不追溯适用于以前生成的报告。"
          ]
        },
        {
          title: "11. 责任限制",
          content: [
            "在英国法律允许的最大范围内：提供商不对因使用或依赖平台而引起的任何损失或损害负责，包括：货物灭失或损坏，船舶损坏，车辆损坏，铁路车辆/车厢损坏，人身伤害或死亡（在法律允许的最大范围内），业务中断，利润，商誉或机会损失，后果性，附带性或惩罚性损害。",
            "在适用法律允许的最大范围内，提供商排除所有因疏忽，虚假陈述或违反法定义务而引起的责任，除非法律禁止此类排除。",
            "责任上限：除非在签署的销售协议中另有书面约定，否则提供商的总累计责任不得超过用户在索赔前十二 (12) 个月内支付的费用。",
            "任何内容均不排除法律禁止的欺诈或因疏忽导致的死亡的责任。"
          ]
        },
        {
          title: "12. 赔偿",
          content: [
            "用户同意赔偿并使提供商免受因以下原因引起的任何第三方索赔：违反这些条款，不准确或不完整的输入数据，使用不正确或过时的规格，未能获得专业验证，绑扎安排的物理执行，用户造成的凭据滥用或未经授权的访问。"
          ]
        },
        {
          title: "13. 出口管制与制裁合规",
          content: [
            "用户声明并保证不受欧盟，英国，联合国或其他适用当局实施的任何制裁。",
            "用户不得在违反适用的出口管制或制裁法律的情况下使用平台，并全权负责确保在所有司法管辖区的合法使用。"
          ]
        },
        {
          title: "14. 网络安全免责声明",
          content: [
            "提供商按照行业惯例实施合理的技术和组织安全措施，同时考虑到处理数据的性质。但是，提供商不保证平台完全免受网络事件，恶意软件，勒索软件，未经授权的访问或安全漏洞的影响，包括由第三方系统或网络引起的那些。"
          ]
        },
        {
          title: "15. 保险披露",
          content: [
            "提供商按照行业惯例维持适当的职业赔偿和网络保险。本协议中的任何内容均不得解释为向用户或任何第三方提供保险范围，保证或赔偿。"
          ]
        },
        {
          title: "16. 无依赖",
          content: [
            "用户承认其未依赖本协议中未明确规定的任何声明，陈述或保证。",
            "不得依赖该平台作为专业判断，认证，监管批准或法律合规的替代品。"
          ]
        },
        {
          title: "17. 专业用户假设",
          content: [
            "该平台旨在供经验丰富的海事和物流专业人员使用。提供商不承担向用户解释海事原则，安全义务或监管要求的义务。"
          ]
        },
        {
          title: "18. 安全关键使用免责声明",
          content: [
            "该平台并非设计或旨在用于主动货物操作或紧急情况期间的实时操作决策。"
          ]
        },
        {
          title: "19. 无第三方权利",
          content: [
            "除本协议当事方外，任何人均无权根据 1999 年合同（第三方权利）法强制执行本协议的任何条款。"
          ]
        },
        {
          title: "20. 不可抗力",
          content: [
            "提供商不对因超出其合理控制范围的事件（包括不可抗力，战争，网络事件，云服务故障，劳资纠纷或政府行为）导致的履约失败或延迟负责。"
          ]
        },
        {
          title: "21. 适用法律，欧盟强制性法律与管辖权",
          content: [
            "这些条款受英国法律管辖。",
            "英格兰和威尔士法院拥有专属管辖权。",
            "本条款中的任何内容均不排除根据第 593/2008 号条例 (EC) (Rome I) 不能通过合同减损的欧盟法律强制性规定的适用（如果适用）。"
          ]
        },
        {
          title: "22. 语言优先权",
          content: [
            "本协议以英文起草。如有任何翻译，以英文版本为准。"
          ]
        },
        {
          title: "23. 一般条款",
          content: [
            "如果这些条款的任何规定被认定为无效或不可执行，其余规定应保持完全有效。",
            "提供商可以将这些条款作为合并，出售或业务转让的一部分进行转让。",
            "与责任，赔偿，知识产权，适用法律和免责声明有关的条款在终止后继续有效。"
          ]
        },
        {
          title: "24. 数据保护与隐私",
          content: [
            "24.1 关于平台的提供，用户作为数据控制者 (Controller)，提供商作为数据处理者 (Processor)（在 UK GDPR，EU GDPR 和 2018 年数据保护法的含义内）。",
            "24.2 提供商应仅根据用户的书面指示并按照适用的数据保护法律处理个人数据。",
            "24.3 附表 1 (Schedule 1) 中列出的数据处理附录构成本条款的组成部分，并管辖提供商代表用户处理个人数据。",
            "24.4 如果本条款与数据处理附录之间存在任何冲突，则在数据保护事项方面以数据处理附录为准。"
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. 定义",
            "由于本数据处理附录 (“DPA”) 的目的，“个人数据”，“处理”，“控制者”，“处理者”和“监管机构”等术语应具有 UK GDPR 和 EU GDPR 中赋予的含义。",
            "",
            "2. 范围与角色",
            "2.1 用户是与使用平台相关的处理的个人数据的控制者。",
            "2.2 提供商仅作为处理者行事，不得出于提供平台以外的任何目的处理个人数据。",
            "",
            "3. 处理的性质与目的",
            "目的：提供 SaaS 服务，账户管理，技术支持和生成报告。",
            "数据主体类别：授权用户，员工，承包商。",
            "个人数据类型：专业联系方式（姓名，电子邮件，电话），公司识别详情。",
            "特殊类别：无预期。",
            "",
            "4. 处理者的义务",
            "提供商应：",
            "a) 仅根据用户的书面指示处理个人数据",
            "b) 确保授权处理个人数据的人员承担保密义务",
            "c) 实施适当的技术和组织措施以保护个人数据",
            "d) 在合理要求的情况下，协助用户处理数据主体权利请求",
            "e) 在意识到个人数据泄露后，无故拖延地通知用户",
            "f) 在服务终止时删除或退还个人数据，除非法律要求保留",
            "",
            "5. 子处理者",
            "5.1 用户授权提供商聘请子处理者进行托管，基础设施和支持服务。",
            "5.2 提供商应确保子处理者承担的数据保护义务不低于本 DPA 中规定的义务。",
            "",
            "6. 国际传输",
            "如果个人数据通过英国或欧洲经济区以外传输，提供商应确保采取适当的保障措施，包括英国国际数据传输协议或欧盟标准合同条款（如适用）。",
            "",
            "7. 安全措施",
            "提供商应维持与数据性质和涉及风险相适应的合理技术和组织安全措施，包括访问控制，适当的加密和事件响应程序。",
            "",
            "8. 审计",
            "根据合理的书面请求，且每年不超过一次，提供商应提供必要的信息以证明符合本 DPA，但须遵守保密和安全要求。",
            "",
            "9. 责任",
            "由本 DPA 引起的责任应受服务条款中规定的责任限制和排除的约束。",
            "",
            "10. 适用法律",
            "本 DPA 应受英格兰和威尔士法律管辖并据其解释。"
          ]
        }
      ],
      detailed_privacy: [
        {
          title: "1. 介绍",
          content: [
            "本隐私政策解释了 JADE CONSULTANCY AND TRADING LTD，一家根据英格兰和威尔士法律运营的公司（'提供商'，'我们'，'我们的'），在您使用 Lashing Calculator 网络应用程序（'平台'）时如何收集、使用、存储和保护个人数据。",
            "我们致力于根据英国通用数据保护条例 (UK GDPR) 和 2018 年数据保护法保护个人数据。"
          ]
        },
        {
          title: "2. 数据控制者",
          content: [
            "为了适用的数据保护法的目的，提供商作为通过平台处理的个人数据的数据控制者。"
          ]
        },
        {
          title: "3. 我们收集的信息",
          content: [
            "3.1 账户和联系数据：姓名，公司名称，企业电子邮件地址，电话号码，登录凭据，账单和发票详情。这些数据严格用于账户管理，身份验证，账单和服务通信。",
            "3.2 工业和运营数据：货物参数和配置，绑扎计算和报告输入。这些数据被视为机密工业信息。不用于商业转售，分析或营销目的。",
            "3.3 技术数据：IP 地址，浏览器类型，设备和会话信息，访问日志。收集这些数据用于安全，性能监控和滥用检测。"
          ]
        },
        {
          title: "4. 处理的法律依据",
          content: [
            "我们根据英国 GDPR 第 6 条的以下法律依据处理个人数据：",
            "合同必要性 – 提供和运营平台",
            "合法利益 – 确保安全，防止滥用并提高服务性能",
            "法律义务 – 遵守会计，税务和监管要求",
            "同意，在明确获得的情况下（例如可选通信）"
          ]
        },
        {
          title: "5. 我们如何使用您的信息",
          content: [
            "我们使用收集的数据来：",
            "提供，运营和维护平台",
            "处理计算并生成专业报告",
            "管理订阅，账单和客户支持",
            "确保符合国际安全框架（CTU 代码 / CSS 代码）",
            "监控系统安全并防止未经授权的访问",
            "改进和开发平台功能",
            "我们不会将个人数据出售给第三方。"
          ]
        },
        {
          title: "6. 数据weitergabe和第三方",
          content: [
            "个人数据仅可与以下方共享：",
            "托管和云基础设施提供商",
            "支付和发票服务提供商",
            "专业顾问（法律，会计，保险）",
            "法律要求的当局",
            "所有第三方均受适当的保密和数据保护义务约束。"
          ]
        },
        {
          title: "7. 国际数据传输",
          content: [
            "如果个人数据传输到英国或欧洲经济区以外，我们将确保采取适当的保障措施，包括：",
            "充分性决定",
            "标准合同条款 (SCCs)",
            "同等合法的传输机制"
          ]
        },
        {
          title: "8. 数据保留",
          content: [
            "我们仅在必要时保留个人数据：",
            "账户和账单数据：在合同期限内及法律要求的情况下",
            "技术日志：出于安全和审计目的保留有限时间",
            "工业计算数据：仅在提供服务必要时保留",
            "用户仍负责归档自己的报告和计算输出。"
          ]
        },
        {
          title: "9. 数据安全",
          content: [
            "我们实施合理的技术和组织措施，以保护个人数据免受未经授权的访问，丢失，更改或泄露，包括访问控制，加密和监控。",
            "但是，没有任何系统可以保证完全安全。"
          ]
        },
        {
          title: "10. 您的权利",
          content: [
            "根据英国 GDPR，您有权：",
            "访问您的个人数据",
            "更正不准确的数据",
            "请求删除（受法律义务约束）",
            "限制或反对处理",
            "数据可移植性",
            "在适用情况下撤回同意",
            "可以使用下面的联系方式提出请求。"
          ]
        },
        {
          title: "11. 投诉",
          content: [
            "如果您认为您的数据保护权利受到侵犯，您有权向英国信息专员办公室 (ICO) 投诉。"
          ]
        },
        {
          title: "12. 本政策的变更",
          content: [
            "我们可能会不时更新本隐私政策。更新版本将在平台上发布，并附有修订后的生效日期。"
          ]
        },
        {
          title: "13. 联系我们",
          content: [
            "有关隐私或数据保护的查询：",
            "电子邮件：info@lashingplan.com"
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "数据保护与隐私",
          content: "我们致力于保护您的隐私。本政策解释了我们如何收集、使用和保护您的信息。使用我们的服务即表示您同意本政策。"
        },
        collection: {
          title: "我们收集的信息",
          personal_title: "账户数据",
          personal_desc: "用于账户管理和技术报告的基本专业信息。",
          calc_title: "工业数据",
          calc_desc: "货物参数和配置。这些数据被视为“机密工业信息”。"
        },
        usage: {
          title: "我们如何使用信息",
          intro: "我们将收集的信息用于以下方式：",
          items: [
            "提供、运营和维护安全的服务",
            "处理计算并生成专业 PDF 报告",
            "确保符合国际安全法规 (CTU/CSS)",
            "增强和扩展服务能力"
          ]
        },
        compliance: {
          title: "全球合规",
          content: "我们采取合理步骤，确保您按照英国通用数据保护条例 (UK GDPR) 和 2018 年数据保护法标准控制自己的数据。",
          note: "注意：我们不会向第三方销售您的个人数据。"
        },
        retention: {
          title: "数据保留",
          content: "我们仅在服务所需及法律义务要求的期限内保留您的个人信息。"
        },
        contact: {
          title: "联系我们",
          content: "关于数据权利或隐私的问题：",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. 介绍",
          content: [
            "本 Cookie 政策解释了 [法律实体名称]（'我们'，'我们的'）在 Lashing Calculator 网站和网络应用程序（'平台'）上如何使用 Cookie 和类似技术。",
            "本政策应与我们的隐私政策一起阅读。"
          ]
        },
        {
          title: "2. 什么是 Cookie？",
          content: [
            "Cookie 是您访问网站时存储在您设备上的小型文本文件。它们被广泛用于启用网站功能，增强用户体验，并向网站运营商提供信息。"
          ]
        },
        {
          title: "3. 法律框架",
          content: [
            "我们根据以下规定使用 Cookie：",
            "英国隐私和电子通信条例 (PECR)",
            "欧盟电子隐私指令",
            "英国通用数据保护条例 (UK GDPR)",
            "除了绝对必要的 Cookie 外，仅在您事先同意的情况下使用 Cookie。"
          ]
        },
        {
          title: "4. 我们使用的 Cookie 类型",
          content: [
            "4.1 绝对必要的 Cookie",
            "这些 Cookie 对于平台的运行至关重要，并启用核心功能，例如：",
            "用户认证",
            "会话管理",
            "安全和欺诈预防",
            "这些 Cookie 不需要同意，也无法通过我们的 Cookie 设置禁用。",
            "法律依据：用户请求的服务提供所必需。",
            "",
            "4.2 分析 Cookie",
            "分析 Cookie 帮助我们了解平台的使用情况，以便我们提高性能和可用性。",
            "示例包括：",
            "页面访问计数",
            "流量来源",
            "使用模式",
            "仅在您给予明确同意后才放置分析 Cookie。",
            "法律依据：根据 UK GDPR 和电子隐私规则的同意。"
          ]
        },
        {
          title: "5. Cookie 提供商",
          content: [
            "分析 Cookie 可能由受信任的第三方分析提供商提供。这些提供商根据其自己的隐私政策和适用的数据保护法律处理数据。",
            "我们不使用 Cookie 用于定向广告或营销分析。"
          ]
        },
        {
          title: "6. Cookie 持续时间",
          content: [
            "Cookie 可能是：",
            "会话 Cookie，当您关闭浏览器时过期",
            "持久性 Cookie，在您设备上保留一段定义的时间或直到删除",
            "具体的 Cookie 生命周期取决于其目的和提供商。"
          ]
        },
        {
          title: "7. 管理您的 Cookie 首选项",
          content: [
            "您可以随时通过以下方式管理或撤回您的 Cookie 同意：",
            "使用平台上可用的 Cookie 设置或同意横幅",
            "调整您的浏览器设置以阻止或删除 Cookie",
            "请注意，禁用某些 Cookie 可能会影响平台的功能。"
          ]
        },
        {
          title: "8. 本 Cookie 政策的变更",
          content: [
            "我们可能会不时更新本 Cookie 政策。任何变更都将在平台上发布，并附有更新的生效日期。"
          ]
        },
        {
          title: "9. 联系我们",
          content: [
            "如果您对我们使用 Cookie 有任何疑问，请联系我们：",
            "电子邮件：info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "Cookie政策",
        last_updated: "生效日期：2026年2月4日",
        intro_title: "介绍",
        intro_content: "本Cookie政策解释了您访问Lashing Calculator网站时如何使用Cookie。",
        types_title: "我们使用的Cookie类型",
        essential_title: "基本Cookie",
        essential_desc: "这些Cookie是网站正常运行所必需的，无法在我们的系统中关闭。",
        analytics_title: "分析Cookie",
        analytics_desc: "这些Cookie允许我们统计访问量和流量来源，以便我们衡量和改进网站的性能。",
        manage_title: "管理您的Cookie首选项",
        manage_content: "您可以随时通过浏览器设置管理您的Cookie首选项。",
        contact_title: "联系我们",
        contact_text: "如果您对我们的Cookie政策有任何疑问，请联系我们。"
      },
      sales_agreement: {
        title: "销售协议",
        content: "本销售协议概述了提供和使用Lashing Calculator服务的条款和条件。购买我们的服务即表示您接受本协议的所有条款。"
      },
      legal_notice: {
        title: "法律声明",
        content: "本网站上的信息仅供一般参考。虽然Lashing Calculator努力保持信息最新和正确，但我们不作任何形式的陈述或保证。"
      },
      footer_note: "使用平台，即表示您确认已阅读并接受安全提示、技术风险和法律条款，包括对所有结果进行专业验证的要求。"
    },

    profile: {
      title: "公司简介",
      subtitle: "管理您的PDF报告公司信息",
      logo_label: "公司标志",
      logo_note: "标志只能由管理员更新。",
      company_name: "我的个人资料",
      address: "详细地址",
      phone: "电话 / 手机",
      email: "电子邮件（账户）",
      tax_office: "税务局",
      tax_number: "税号",
      company_email_label: "公司邮箱 (PDF可见)",
      user_name_label: "用户名 (账户)",
      terms_accepted: "条款接受于",
      terms_not_accepted: "尚未接受条款",
      view_terms: "查看条款",
      accept_terms: "接受条款",
      review_accept: "查看并接受条款",
      admin_note: "注意：公司详细信息由管理员管理。",
      cancel: "取消",
      accept: "接受条款",
      modal_title: "服务条款",
      modal_subtitle: "请查看并同意我们的条款以继续。",
      modal_agree: "接受即表示您同意受{tos}和{privacy}的约束。",
      modal_ack: "您承认该软件只是一个工具，绑扎计划的最终责任由认证专业人员承担。",
      checkbox_label: "我已阅读并同意条款和条件",
      save: "保存更改",
      saving: "保存中...",
      success_message: "个人资料更新成功。",
      error_message: "更新错误：{error}",
      credits_title: "额度与使用信息",
      calc_credits: "计算额度",
      pdf_credits: "PDF 报告额度",
      monthly_increment: "每月增量",
      next_increment: "下次续订",
      remaining: "剩余",
      lashing_display_label: "绑扎显示格式",
      lashing_display_msl: "MSL (如 MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (如 CL200 HDB12L)",
      pdf_theme_title: "PDF Design Theme",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Dark Turquoise & Slate Gray",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Corporate Navy & Light Gray",
      theme_standart_title: "Standard",
      theme_standart_desc: "Original App Colors"
    },
    surveys: {
      title: "现场报告",
      create_new: "创建新报告",
      total_ops: "总操作",
      pending_photos: "待传照片",
      completed_assignments: "已完成分配",
      current_reports: "当前报告",
      search_placeholder: "搜索 ID 或项目...",
      no_reports_title: "暂无报告",
      no_reports_desc: "通过进行首次捆扎计算开始准备现场报告。",
      create_now: "立即创建",
      unnamed_project: "未命名项目",
      created_date: "创建日期",
      data_status: "数据状态",
      uploaded: "应用照片已上传",
      waiting: "等待应用照片",
      surveyor: "检验员",
      waiting_response: "等待响应",
      under_review: "审核中",
      report_ready: "报告就绪",
      cancelled_rejected: "已取消/已拒绝",
      send_link: "发送现场上传链接",
      upload_manually: "手动上传现场照片",
      assign_new: "分配新检验员",
      assign_surveyor: "分配检验员",
      assigned: "已分配",
      summary_pdf: "摘要 PDF",
      surveyor_pdf: "检验员 PDF",
      assignment_success: "检验员分配成功！",
      old_report_error: "旧报告未生成安全链接。请重新创建报告。",
      session_expired: "会话已过期。请刷新页面或重新登录。",
      pdf_error: "创建 PDF 时出错。",
      download_failed: "下载失败：",
      wait: "请稍候...",
      connecting: "连接中...",
      access_error: "访问错误",
      modal: {
        title: "分配检验员",
        search_placeholder: "按城市、港口或姓名搜索...",
        list_error: "无法获取检验员列表。",
        assign_failed: "分配失败。",
        loading: "加载中...",
        not_found: "未找到检验员。",
        unknown_location: "未知",
        assigning: "分配中...",
        assign_button: "分配此检验员"
      },
      guest: {
        title: "现场实施监督门户",
        subtitle: "您可以在此处上传绑扎应用的现场照片。",
        invalid_link: "链接无效或已过期。",
        upload_success_title: "上传成功",
        upload_success_desc: "绑扎应用的现场照片已成功保存。您的报告现在可以进行审查和 PDF 批准。",
        close_window: "关闭窗口",
        info_title: "1. 实施者信息（可选）",
        date: "日期",
        company_name: "公司名称",
        company_placeholder: "例如：X 物流",
        implementer_name: "实施者姓名",
        phone: "电话",
        email: "电子邮件",
        email_placeholder: "name@company.com",
        photos_title: "2. 现场实施图像（最多 9 张）",
        photos_desc: "请上传或拍摄符合以下类别的现场照片。每张照片限制 ~5MB，并将在您的设备上自动压缩至 ~1MB。",
        take_photo: "拍照 / 上传照片",
        uploading: "正在上传照片...",
        confirm_upload: "全部上传并确认",
        min_photo_error: "请至少添加一张照片。",
        compression_error: "照片无法处理，请重试。",
        categories: {
          floor: "运输单元地面状况",
          cargo: "货物摆放和地面支垫",
          front_right: "绑扎：右前",
          front_left: "绑扎：左前",
          rear_right: "绑扎：右后",
          rear_left: "绑扎：左后",
          plate: "车牌号（如果有）",
          seal: "封条号（如果有）",
          overall: "主要整体外观（最终）"
        }
      },
      portal: {
        title: "检验员 (Surveyor) 门户",
        subtitle: "登录以管理您分配的检验任务。",
        login_error: "请输入您的电子邮件和密码。",
        login_button: "登录",
        logging_in: "正在登录...",
        dashboard: {
          connecting: "正在连接仪表板...",
          pending_tab: "收到请求",
          active_tab: "进行中任务",
          completed_tab: "历史任务",
          logout: "安全退出",
          connected: "已连接",
          account_type: "检验员账户",
          no_records: "未找到记录",
          no_records_desc: "此选项卡中没有活动的检验任务。",
          customer: "客户",
          task_date: "任务日期",
          waiting_response: "等待响应",
          under_review: "客户审核中",
          delivered: "报告已交付",
          rejected_by_you: "您拒绝了任务",
          accept_button: "接受任务",
          reject_button: "拒绝",
          go_to_report: "进入报告菜单",
          download_pdf: "下载 PDF 文件",
          downloading_pdf: "正在下载报告 PDF...",
          download_success: "报告下载成功。",
          download_error: "下载报告时出错。",
          update_error: "状态无法更新，请重试。",
          accept_success: "任务已成功接受！",
          reject_success: "任务已拒绝。"
        },
        editor: {
          details_title: "项目详情",
          project_name: "项目名称",
          weight: "货物重量",
          photos_count: "现场照片",
          success_title: "报告创建成功！",
          success_desc: "您的检验报告已创建、签署并上传到安全的云环境。您可以单击下面的按钮下载或查看 PDF 文件。",
          back_to_dashboard: "返回仪表板",
          download_pdf: "下载 PDF",
          creator_title: "最终检验报告生成器",
          assessment_label: "检验员评估",
          assessment_desc: "此文本将被添加到最终 PDF 的 Methodology & Conclusion 部分。",
          assessment_default: "货物的检验已按照国际标准完成。未观察到任何损坏或不合规情况。",
          auto_info_note: "注意：系统中的您的姓名、签名和公司徽标等默认信息将自动放置在 PDF 报告中。",
          back_button: "返回",
          generate_button: "认证并发送 PDF 报告",
          generating: "正在生成...",
          generate_error: "无法创建报告",
          not_found: "未找到任务或您无权访问。"
        }
      }
    }
  },

  // ===================================================================
  // 日本語
  // ===================================================================
  "日本語": {
    code: "ja",
    name: "日本語",
    flag: "Japan",

    hub: {
      nav: {
        products: "製品",
        insights: "市場の洞察",
        resources: "リソース",
        go_to_app: "アプリへ"
      },
      hero: {
        badge: "次世代貨物物流ハブ",
        status: "システムステータス：最適",
        title_main: "計算された強度",
        title_italic: "絶対的な安全",
        title_1: "最高の安全のための",
        title_2: "プランニング。",
        description: "LashingPlanは、国際基準に準拠し、精密に計算されたプロフェッショナルな貨物固定ツールのための中心的なエコシステムです。",
        explore: "製品を探索",
        launch_product: "製品を起動",
        read_blog: "ブログを読む",
        portal_access: "ポータルアクセス V2.0",
        stability_index: "安定性指数",
        archives: "アーカイブ",
        archives_subtitle: "プロフェッショナルな安全基準と物流トレンドで一歩先へ。"
      },
      footer: {
        tagline: "技術的卓越性の結晶。海上安全において絶対的な精度を求める方々のために設計されました。",
        systems: "システム",
        resources: "リソース",
        legal: "法的情報",
        privacy: "プライバシー",
        compliance: "コンプライアンス",
        terms: "利用規約",
        status_all: "LASHING PLAN GLOBAL // すべて의 시스템이 정상적으로 운영 중",
        follow: "フォローする"
      },
      products: {
        title: "当社の製品",
        subtitle: "グローバルな輸送コンプライアンスと貨物の安全のために設計されたプロフェッショナルなツール。",
        context: "01 / 製品",
        calculator: {
          title: "ラッシング計算機",
          description: "道路、海上、鉄道の貨物固定要件を計算するための業界標準エンジン。",
          button: "アプリを起動"
        },
        inspection: {
          title: "Inspection Pro",
          description: "写真対応のデジタル検査および現場調査用の即時証明書作成ツール。",
          status: "近日公開"
        },
        risk: {
          title: "リスクアドバイザー",
          description: "AI駆動の安定性分析および出荷リスク評価システム。",
          status: "近日公開"
        },
        stats: {
          calculations: "計算数",
          countries: "国",
          compliance: "コンプライアンス",
          precision: "精度"
        },
        blog_articles: {
          article_1: {
            title: "Cargo Securing: Lashing",
            description: "Securing cargo to the transport unit before shipment is the foundation of logistics safety..."
          },
          article_2: {
            title: "Innovative Cargo Freight Solutions",
            description: "Forward-thinking cargo solutions emphasizing pioneering innovation and reliable lashing..."
          },
          article_3: {
            title: "Understanding IMO/CTU Code Compliance",
            description: "A deep dive into the international standards regulating cargo transport in containers..."
          },
          article_4: {
            title: "Enhancing Cargo Safety Standards",
            description: "Improving safety with modern protocols, technology integration, and proven safety practices..."
          }
        }
      },
      insights: {
        title: "市場の洞察",
        subtitle: "貨物の安全基準、業界のコンプライアンス、最新の物流トレンドについて、当社の専門家から学びましょう。",
        all_articles: "記事をすべて表示"
      }
    },
    blog: {
      nav: {
        back: "ホームに戻る"
      },
      header: {
        title_1: "LashingPlanからの",
        title_2: "インサイト"
      },
      article: {
        read_time: "分で読める",
        share: "シェア",
        read_more: "もっと読む",
        cta_title: "貨物を固定する準備はできましたか？",
        cta_desc: "正確でコンプライアンスに準拠したラッシング計算のために、当社のツールを信頼している何千人ものプロフェッショナルに加わりましょう。",
        cta_button: "今すぐ計算を開始"
      },
      footer: {
        copyright: "© 2026 LashingPlan ブログ。すべてのインサイトを保護します。"
      }
    },

    app: {
      title: "ラッシング計算ツール",
      logout: "ログアウト",
      theme_toggle: "ダーク / ライトモード",
      profile: "プロフィール",
      reports: "現場レポート",
      login: {
        title: "ログイン",
        subtitle: "アカウントにログイン",
        email: "メールアドレスまたはユーザー名",
        email_placeholder: "メールアドレスまたはユーザー名を入力",
        password: "パスワード",
        password_placeholder: "パスワードを入力",
        forgot_password: "パスワードをお忘れですか？",
        submit: "ログイン",
        no_account: "アカウントをお持ちでないですか？",
        signup: "登録",
        back_to_home: "ホームに戻る",
        consent: "続行することで、{terms}と{privacy}に同意したことになります。",
        surveyor_portal: "検査員 (Surveyor) ですか？ポータルへ移動"
      },
      cog3d: {
        title: "3D 重心 (CoG) ビュー",
        subtitle: "重心 (CoG) は貨物のバランスポイントであり、転倒の挙動を決定します。",
        drag_hint: "ドラッグして回転"
      },
      lashing3d: {
        title: "3D ラッシング図",
        subtitle: "ラッシング構成のインタラクティブビュー"
      },
      calculator: {
        title: "貨物計算機",
        cargo_details: "貨物詳細",
        weight: "重量 (MT)",
        cog: {
          title: "重心 (左下隅からのcm)",
          x: "X (幅)",
          y: "Y (奥行き)",
          z: "Z (高さ)"
        },
        securing_elements: "固定要素",
        wooden_dunnage: {
          label: "木製ダンネージを使用",
          description: "摩擦を増加させ、滑りを防止します",
          details: "木製ダンネージ詳細",
          width: "幅 (cm)"
        },
        loop_lashing: "ループラッシング",
        direct_lashing: "ダイレクトラッシング",
        select_rope_type: "ロープタイプを選択",
        stopper_forces: "ストッパー力 (daN)",
        direction: {
          forward: "前方向の力に対して",
          backward: "後方向の力に対して",
          right: "右方向の力に対して",
          left: "左方向の力に対して"
        }
      },
      results: {
        title: "結果",
        lashing_diagram: "ラッシング図",
        error: "エラー",
        loading: "読み込み中...",
        no_results: "結果が見つかりません",
        values: {
          sliding: "スライド:",
          tipping: "傾斜:",
          friction: "摩擦:",
          securing_element: "固定要素:",
          net_sliding: "正味スライド:",
          net_tipping: "正味転倒:"
        }
      }
    },
    landing: {
      contact: {
        label: "セールスに連絡",
        title_1: "貨物運用の",
        title_2: "安全を確保する準備はできましたか？",
        description: "エンタープライズ対応のサーベイヤーアルゴリズムにアップグレードします。カスタムAPI連携、大規模ライセンス、プレミアム機能については、エンジニアリングチームにお問い合わせください。",
        email_us: "メールを送る",
        enterprise_support: "エンタープライズサポート",
        support_desc: "24時間365日の優先SLA利用可能",
        success_title: "メッセージを送信しました！",
        success_desc: "エンジニアリングチームから24時間以内にご連絡いたします。",
        form: {
          name: "氏名",
          placeholder_name: "山田 太郎",
          company: "会社名",
          placeholder_company: "物流株式会社",
          email: "メールアドレス",
          placeholder_email: "yamada@company.com",
          message: "メッセージ",
          placeholder_message: "固縛作業の拡大をどのように支援できますか？",
          send: "リクエストを送信"
        }
      },
      hero_badge: "世界の貨物固定プロフェッショナルが信頼",
      hero_badge_imo: "IMO準拠",
      hero_badge_imo_sub: "国際標準",
      hero_badge_3d: "3D視覚化",
      hero_badge_3d_sub: "リアルタイム",
      hero_title_1: "プロフェッショナルな",
      hero_title_2: "ラッシング・インテリジェンス",
      hero_subtitle: "世界標準。精密なアルゴリズム。プロフェッショナルなレポート。業界で最も先進的な貨物固定プラットフォーム。",
      start_calculating: "計算を開始",
      view_demo: "デモを見る",
      stats: {
        modes: "6種類以上の輸送モード",
        platform: "クラウドプラットフォーム",
        platform_status: "安全",
        access: "24時間年中無休のアクセス"
      },
      features_title_1: "パワーと",
      features_title_2: "コンプライアンス",
      features_subtitle: "貨物安全のエキスパートによって、リスクの高い海運業務のために構築されました。",
      features: {
        calculations: {
          title: "即座に計算",
          desc: "当社の高度なアルゴリズムを使用して、ラッシング力をリアルタイムで分析します。"
        },
        pdf: {
          title: "プロフェッショナルなPDFレポート",
          desc: "会社のブランディングと詳細な分析を含む業界標準のレポートを作成します。"
        },
        compliance: {
          title: "CTU参照評価",
          desc: "IMO / ILO / UNECE CTU Codeを参照した簡易意思決定支援です。"
        },
        multimodal: {
          title: "マルチモーダル輸送",
          desc: "道路、鉄道、海上をサポート。コンテナ、フラットラック、船舶など。"
        },
        global: {
          title: "グローバルスタンダード",
          desc: "CTU Code 2014および関連Informative Materialを参照した追跡可能な評価です。"
        },
        secure: {
          title: "安全かつ機密",
          desc: "計算データはエンタープライズレベルのセキュリティで保護されています。"
        }
      },
      transport: {
        title_1: "統合された輸送",
        title_2: "セキュリティ",
        subtitle: "物流チェーン全体でシームレスな計算を実現します。",
        modes: {
          road: { name: "道路", desc: "トラックトレーラーおよびローベッド" },
          rail: { name: "鉄道", desc: "鉄道車両、コンテナ、フラットラック、およびトラックトレーラー" },
          sea: { name: "海上", desc: "コンテナ、トラックトレーラー、フラットラック、ローベッド、ロールトレーラー、船舶" },
          combined: { name: "複合輸送", desc: "陸海、陸鉄、陸海鉄" }
        }
      },
      how_it_works: {
        badge: "仕組み",
        title_1: "プロフェッショナルな分析を",
        title_2: "3つの簡単なステップで",
        subtitle: "複雑なラッシング計算を数分で完了",
        step1: {
          title: "貨物詳細を入力",
          desc: "貨物の寸法、重量、重心、コンテナタイプをシステムに入力します。"
        },
        step2: {
          title: "自動計算",
          desc: "システムがIMO基準に従って最適なラッシング配置を自動計算します",
          desc_highlight: " 、計画の手動変更も可能です。"
        },
        step3: {
          title: "レポート取得",
          desc: "3D可視化と詳細な計算を含むプロフェッショナルなPDFレポートを生成します。"
        }
      },
      benefits: {
        title: "ミスの許されない運用のために",
        subtitle: "重要な貨物の安全を確保するため、世界中のエキスパート、検数人、ターミナルオペレーターに信頼されています。",
        list: [
          "手動計算の時間を大幅に短縮",
          "最適化により材料の無駄を削減",
          "法規制への完全な準拠を確保",
          "顧客にプロフェッショナルなドキュメントを提供",
          "多言語サポート (EN, TR, ES, DE, ZH, JA)",
          "企業ブランドを反映したPDFレポート"
        ],
        cta_card_title: "今すぐラッシングプランを標準化",
        cta_card_desc: "チームでエンタープライズ級の計算機を使用し、すべての出荷で100%のコンプライアンスを確保しましょう。",
        cta_card_trial: "クレジットカード不要で試用可能"
      },
      footer: {
        desc: "当社のインテリジェントなラッシング計算ツールを使用している世界中のプロフェッショナルに加わりましょう。",
        columns: {
          service: "サービス",
          company: "会社",
          support: "サポート"
        },
        links: {
          privacy: "プライバシーセンター",
          compliance: "コンプライアンス",
          cookies: "Cookieポリシー",
          security: "セキュリティ",
          help: "ヘルプセンター",
          enterprise: "エンタープライズ",
          contact: "エキスパートに相談",
          force_analysis: "力解析",
          pdf_generator: "PDFジェネレーター",
          optimizer: "オプティマイザ",
          api_docs: "APIドキュメント",
          cookie_settings: "クッキー設定"
        }
      },
      footer_copyright: "2025 Lashing Calculator. JADE CONSULTANCY AND TRADING LTDによって提供されるサービス。All rights reserved.",

      pricing_simple: "シンプルで",
      pricing_transparent: "透明な",
      pricing_title: "価格設定",
      compare_features: "すべての機能を比較",
      footer_global: "グローバル",
      footer_encrypted: "暗号化",
      footer_high_availability: "高可用性",
      demo_request: {
        title: "デモをリクエストする",
        subtitle: "Lashing Calculatorが貨物安全業務をどのように変革できるかをご覧ください。",
        cta_title: "実際の動作を確認してみませんか？",
        cta_subtitle: "弊社の貨物専門家によるパーソナライズされたウォークスルーを予約して、ラッシング業務を世界規模でどのように標準化できるかをご確認ください。",
        form: {
          full_name: "氏名",
          work_email: "仕事用メールアドレス",
          company: "会社名",
          job_title: "役職",
          phone: "電話番号",
          fleet_size: "保有台数 / 月間輸送量",
          industry: "業界",
          message: "メッセージ (オプション)",
          submit: "デモをリクエストする",
          success_title: "リクエストを受領しました",
          success_message: "デモのリクエストが正常に送信されました。担当者より近日中にご連絡いたします。"
        }
      },
      price_inquiry: {
        title: "見積もりを依頼する",
        subtitle: "カスタム価格やエンタープライズソリューションについては、お問い合わせください。",
        form: {
          full_name: "氏名",
          work_email: "仕事用メールアドレス",
          company: "会社名",
          phone: "電話番号",
          message: "メッセージ (オプション)",
          submit: "問い合わせを送信",
          success_title: "お問い合わせ送信完了",
          success_message: "価格のお問い合わせを受領しました。営業担当者より、カスタムプランをご提案いたします。"
        }
      }
    },

    labels: {
      three_d_view: "3D 视图",
      no_results_data: "結果データがありません",
      pass: "合格",
      fail: "不合格",
      reports: "レポート",
      user_responsible_for: "お客様は以下の事項について単独で責任を負うものとします：",
      page_title: "ラッシング計算ツール",
      weight: "重量 (MT)",
      transport_mode: "輸送モード",
      transport_unit: "輸送ユニット",
      friction_surface: "摩擦面",
      select_transport_mode_first: "まず輸送モードを選択してください",
      select: "選択",
      cargo_dimensions: "貨物寸法・重心 (m)",
      cargo_specs: "CARGO SPECS",
      width: "貨物幅",
      length: "貨物長さ",
      height: "貨物高さ",
      cog_width: "重心の幅オフセット (m):",
      cog_length: "重心の長さオフセット (m):",
      cog_height: "重心高さ (m):",
      cog_width_help: "重心はデフォルトで幅の半分です。異なる場合は変更してください。",
      cog_length_help: "重心はデフォルトで長さの半分です。異なる場合は変更してください。",
      cog_height_help: "重心はデフォルトで高さの半分です。異なる場合は変更してください。",
      wooden_dunnage_dimensions: "木製ダンネージ寸法・数量",
      wooden_dunnage_width: "幅 (cm):",
      wooden_dunnage_length: "長さ (cm):",
      stopper_qty: "メタルストッパー (数量)",
      force_analysis: "力分析結果 (kN)",
      generate_pdf: "PDF生成",
      download_pdf: "PDFダウンロード",
      wooden_dunnage: "木製ダンネージ",
      stopper: "ストッパー",
      loop_lashing: "ループラッシング",
      max_weight: "最大重量",
      direct_lashing: "ダイレクトラッシング",
      loop_rope_select: "ループラッシングウェビング選択",
      loop_lashing_quantity_per_side: "本数（片側）",
      direct_lashing_quantity: "本数",
      cog_above: "重心より上",
      cog_below: "重心より下",
      forward: "前方向の力に対して",
      backward: "後方向の力に対して",
      right: "右方向の力に対して",
      left: "左方向の力に対して",
      lashing_diagram: "ラッシング図",
      language: "言語",
      calculate: "計算",
      configuration_panel: "設定パネル",
      operation_mode: "動作モード",
      manual_mode: "計算機",
      lashing_calculator: "ラッシング計算機",
      direct_lashing_selection: "ダイレクトラッシング選択",
      reset: "リセット",
      print: "印刷",
      configuration: "設定",
      use_stopper: "ストッパーを使用",
      logout: "ログアウト",
      results_will_appear_here: "結果はここに表示されます",
      continue_with_overweight: "過重量で続行",
      optimizer_mode: "オプティマイザー",
      calculator_mode: "計算モード",
      project_description: "プロジェクト説明（任意）",
      enter_project_description_hint: "PDFレポートに表示されるプロジェクトの説明を入力してください。",
      show_3d_view: "3D CoG View",
      edit_in_manual_mode: "計算機で編集",
      breakbulk_settings: "Breakbulk Settings",
      flatrack_type: "Flatrack Type",
      orientation: "貨物の向き",
      longitudinal: "縦方向",
      transverse: "横方向",
      flatrack_count: "フラットラックの推奨数",
      flatrack_gap: "フラットラック間の隙間"
    },

    common: {
      info: "情報",
      yes: "はい",
      no: "いいえ",
      ok: "OK",
      cancel: "キャンセル",
      confirm: "確認",
      close: "閉じる",
      save: "保存",
      download: "ダウンロード",
      delete: "削除",
      edit: "編集",
      view: "表示",
      loading: "読み込み中...",
      processing: "処理中...",
      please_wait: "お待ちください...",
      save_changes: "変更を保存",
      saving: "保存中..."
    },

    transport_modes: {
      road: "道路",
      rail: "鉄道",
      sea_a: "海上_A (波 <8m)",
      sea_b: "海上_B (8m < 波 <12m)",
      sea_c: "海上_C (波 >12m)",
      road_sea_a: "道路+海上_A",
      road_sea_b: "道路+海上_B",
      road_sea_c: "道路+海上_C",
      road_rail: "道路+鉄道",
      road_rail_sea_a: "道路+鉄道+海上_A",
      road_rail_sea_b: "道路+鉄道+海上_B",
      road_rail_sea_c: "道路+鉄道+海上_C"
    },

    transport_unit_groups: {
      containers: "Conteneurs",
      open_tops: "À Toit Ouvert",
      flatracks: "Flatracks",
      trailers: "Remorques",
      special: "Autres / Spécial",
    },
    transport_units: {
      container_20: "20' コンテナ",
      container_40: "40' コンテナ",
      container_40_hc: "40' コンテナ HC",
      container_45_hc: "45' コンテナ HC",
      open_top_20: "20' オープントップ",
      open_top_20_hc: "20' オープントップ HC",
      open_top_40: "40' オープントップ",
      open_top_40_hc: "40' オープントップ HC",
      flatrack_20: "20' フラットラック",
      flatrack_40: "40' フラットラック",
      lowbed: "ローベッド",
      mafi: "マフィ",
      vessel: "船舶",
      breakbulk: "ブレイクバルク",
      truck_trailer_standard: "トラックトレーラー (標準)",
      truck_trailer_mega: "トラックトレーラー (メガ / ジャンボ)",
      open_top_container: "オープントップコンテナ"
    },

    materials: {
      asm: "滑り止めマット (静的 0.8µ)",
      asm_06: "滑り止めマット (静的 0.6µ)",
      metal_metal: "金属-金属",
      metal_wood: "金属-木材",
      wood_wood: "木材-木材"
    },

    directions: {
      forward: "前方向の力に対して",
      backward: "後方向の力に対して",
      right: "右方向の力に対して",
      left: "左方向の力に対して"
    },

    units: {
      mt: "MT",
      kn: "kN",
      meters: "m",
      kg: "kg",
      tonnes: "トン"
    },

    errors: {
      zero_input: "すべての必須フィールドに入力してください。",
      no_loop_rope: "互換性のあるループロープが見つかりません。",
      no_direct_rope: "互換性のあるダイレクトラッシングロープが見つかりません。",
      calculation_error: "計算エラー：{error}",
      unexpected_error: "予期しないエラー：{error}",
      diagram_error: "図を生成できませんでした：{error}",
      pdf_error: "PDFエラー：{error}",
      loop_config_error: "ループ構成エラー：{error}",
      error_halat: "ダイレクトラッシングロープが選択されていません。",
      invalid_transport_unit: "無効な輸送ユニット：{unit}。有効なユニットを選択してください。",
      network_error: "ネットワークエラー。インターネット接続を確認してください。",
      file_not_found: "ファイルが見つかりません。",
      invalid_cargo_dimensions: "警告：選択された輸送ユニットは貨物に適していません",
      select_transport_mode: "輸送モードを選択してください",
      select_transport_unit: "輸送ユニットを選択してください",
      select_friction_surface: "摩擦面を選択してください",
      invalid_weight: "有効な重量を入力してください",
      invalid_dimensions: "有効な貨物寸法を入力してください",
      invalid_cog: "有効な重心座標を入力してください",
      invalid_wooden_dunnage: "有効な木製ダンネージ寸法を入力してください",
      invalid_stopper: "有効なストッパー数量を入力してください",
      select_at_least_one_rope: "ラッシングを選択してください",
      select_rope_type: "ロープタイプを選択してください",
      dimension_exceeded: "警告：貨物の {dims} が選択された輸送ユニットの制限を超えています。",
      dim_width: "幅",
      dim_length: "長さ",
      dim_height: "高さ",
      cargo_width_greater_than_length: "貨物の幅は長さより大きくすることはできません。値は長さに合わせて自動的に調整されました。",
      and_conjunction: " と ",
      breakbulk_dim_overflow_width: "⚠️ 貨物幅 ({cargo_width}m) が選択されたフラットラックの長さ ({flatrack_length}m) 以上です。計算を続行できません。",
      breakbulk_dim_overflow_length: "⚠️ 貨物長さ ({cargo_length}m) が選択されたフラットラックの長さ ({flatrack_length}m) 以上です。計算を続行できません。"
    },

    validation: {
      required_field: "このフィールドは必須です",
      invalid_number: "有効な数値を入力してください",
      positive_number: "正の数値を入力してください",
      min_value: "最小値：{min}",
      max_value: "最大値：{max}",
      out_of_range: "値は {min} から {max} の間でなければなりません",
      invalid_email: "有効なメールアドレスを入力してください",
      invalid_format: "無効な形式"
    },

    warnings: {
      weight_exceeded: "警告：{unit} の最大重量容量 ({max_weight} MT) を超えています！",
      overweight_confirmation: "入力された貨物重量 ({weight} MT) は最大許容重量 ({max_weight} MT) を超えています。続行するには再度確認してください。",
      oversized_width_loop_disabled: "荷幅がオーバーサイズのため、ループラッシングは無効になっています。",
      oversized_width_side_wedges_disabled: "オーバーサイズの荷幅のため、サイドウェッジは無効になっています。",
      high_cog: "警告：重心が高いと倒れるリスクが高まります。",
      insufficient_lashing: "警告：ラッシングが不十分です。",
      unsaved_changes: "保存されていない変更があります。続行しますか？",
      flatrack_dunnage_required: "Flatrack輸送ユニットで貨物の前後方向の滑りを制限するために、木製ダンネージの使用が推奨されます。木製ダンネージを追加せずに計算を続けますか？",
      terms_acceptance_required: "You must read and accept the Terms of Service to perform calculations. You can access the Terms of Service from your Profile page.",
      flatrack_count_low: "Attenzione: il numero di flatrack selezionato ({selected}) è inferiore al numero raccomandato ({recommended})!"
    },

    success: {
      pdf_created: "PDFが正常に作成されました！",
      calculation_complete: "計算が完了しました！",
      data_saved: "データが正常に保存されました。",
      export_complete: "エクスポートが完了しました。"
    },

    table_columns: {
      direction: "方向",
      sliding: "スライド",
      tipping: "転倒",
      friction: "摩擦",
      wooden_dunnage: "木製ダンネージ",
      stopper: "ストッパー",
      looplash: "ループ\nラッシング",
      direct_lashing: "ダイレクトラッシング",
      cordstrap_loop: "コードストラップループ",
      cordstrap_direct: "コードストラップダイレクト",
      total_securing: "総固定力",
      net_sliding: "正味スライド",
      net_tipping: "正味転倒",
      net_sliding_tooltip: "負の値は安全な固定を示します。",
      net_tipping_tooltip: "負の値は安全な固定を示します。",
      status: "状態",
      safety_factor: "安全率"
    },

    results: {
      safe: "安全",
      unsafe: "危険",
      marginal: "限界",
      critical: "重大",
      acceptable: "許容可能",
      status: "状態",
      safety_factor: "安全率",
      summary: "概要",
      details: "詳細",
      notes_title: "注記 / 仮定",
      notes_assumed: "",
      notes_cargo_unitized: "貨物は単一の品目、または統合され、ユニット化された1つの貨物ユニットと見なされます。",
      notes_lashing_above_cog: "「使用材料」表に特記がない限り、ダイレクトラッシングは荷重の重心より上に適用されるものと想定されます。",
      notes_angle_range: "輸送ユニットのラッシングポイントに角度を付けて取り付けられたダイレクトラッシングと、作用力の方向におけるダイレクトラッシングのベクトル成分との間の鋭角は、30°から60°の間でなければなりません。",
      notes_edge_protection: "貨物および/またはラッシング資材への損傷を防ぐため、必要に応じて適切なエッジおよびコーナープロテクションを提供しなければなりません。",
      notes_pretension_force: "ウェブラッシングを引張るとき、適用されるプレテンション力は、ウェブラッシングのMSL値の50%を超えてはなりません。",
      notes_strength_limitations: "貨物上のラッシングポイントには強度制限がないものと想定されます。",
      notes_distributed_uniformly: "荷重は、輸送ユニットの容量基準に従って均一に分散されているものと想定されます。",
      flatrack_no_dunnage_warning: "注意：Flatrack輸送ユニットで貨物の前後方向の滑りを制限するために木製ダンネージの使用が推奨されます"
    },

    tooltips: {
      weight_info: "貨物の総重量をメトリックトンで入力してください",
      cog_info: "COGは重心（Center of Gravity）の略です。デフォルトでは、貨物の中心にあると想定されています。しかし、実際には重心が別の場所にある場合があります。特定のCOGがわかっている場合は、ここで正しい位置を入力できます。正確なCOGの位置決めは、特に転倒の計算において非常に重要です。",
      friction_info: "摩擦係数の材料組み合わせを選択してください",
      transport_mode_info: "加速度値は輸送モードによって異なります",
      lashing_info: "ラッシング力はスライドと転倒を防ぎます"
    },
    legal: {
      last_updated: "発効日：2026年2月4日",
      return_home: "ホームに戻る",
      tos_title: "サービス利用規約",
      privacy_title: "プライバシーポリシー",
      sales_agreement_title: "販売契約",
      legal_notice_title: "法的通知",
      cookie_policy_title: "クッキーポリシー",
      sections: {
        acceptance: {
          title: "合意の成立",
          content: "本サービス利用規約は、Lashing Calculator（以下「プラットフォーム」）の使用に関し、お客様とプラットフォームとの間で締結される法的拘束力のある契約です。プラットフォームにアクセスまたは使用することにより、お客様はこれらの条件に拘束されることに取消不能な形で同意したものとみなされます。"
        },
        payment_terms: {
          title: "支払い条件とサブスクリプション",
          content: "サービスはサブスクリプションベースで請求されます。支払いは返金不可です。事前の通知をもって価格を変更する権利を留保します。"
        },
        termination: {
          title: "キャンセルと終了",
          content: "サブスクリプションはいつでもキャンセルできます。アクセスは現在の請求サイクルの終了まで継続されます。"
        },
        disclaimer: {
          title: "重要な安全に関するお知らせと専門的な免責事項",
          important: "重要：提供される計算ツールは、貨物固定計画のための「意思決定支援システム」としてのみ提供されています。",
          content: "プラットフォームは専門的なエンジニアリングアドバイスを提供するものではありません。すべての結果は、実施前にIMO/ILO/UNECE CTU CodeおよびIMO CSS Codeで定義された有資格の貨物検数人、固定専門家、または「権限のある者」によってレビューおよび承認される必要があります。国際海事法に基づき、最終的な責任は運送人と船長にあります。"
        },
        license: {
          title: "知的財産およびライセンス",
          subtitle: "プラットフォームのソースコード、アルゴリズム、およびデザインは、国際的な知的財産法によって保護されています。",
          items: [
            "ユーザーには、プラットフォームを使用するための限定的かつ譲渡不可能なライセンスが提供されます。所有権は移転しません。",
            "貨物および輸送ユニットの正確なデータ入力は、ユーザーの唯一の責任です。",
            "ソースコードのリバースエンジニアリングや抽出は厳禁です。",
            "PDFレポートは、記載された特定の貨物専用であり、異なる構成に再利用することはできません。"
          ]
        },
        liability: {
          title: "責任の制限",
          content: "プラットフォーム提供者は、サービスの使用または使用不能から生じるいかなる損害（貨物の紛失や事業の中断を含む）についても责任を負いません。",
          cap_title: "金銭的責任の制限",
          cap_content: "法律で許可される最大限の範囲において、累積総責任は、請求前の12ヶ月間にお客様が支払った金額、または100.00米ドルのいずれか高い方を超えないものとします。",
          not_responsible: "当社は以下について責任を負いません：",
          not_responsible_items: [
            "ラッシングの不適切な物理的実行による損害",
            "ユーザー提供の不正確な入力数据",
            "計算パラメータを超える環境の変化",
            "輸送ユニットまたはラッシングポイントの構造的故障",
            "不正確なメーカー仕様（MSL、摩擦係数など）",
            "間接的、付随的、または懲罰的な損害",
            "利益、ビジネス機会、または営業権の損失"
          ]
        },
        third_party: {
          title: "第三者データ免責事項",
          content: "プラットフォームには、メーカーや国際安全基準などの第三者ソースからのデータが含まれています。",
          sub_content: "当社は、第三者データの正確性や信頼性についていかなる保証も行いません。すべてのメーカー仕様は各メーカーから提供されたものです。"
        },
        accuracy: {
          title: "技術的正確性",
          content: "正確性を期しておりますが、資料には技術的または誤植的な誤りが含まれている可能性があります。私たちは、すべての資料が常に最新または正確であることを保証するものではありません。"
        },
        source_responsibility: {
          title: "データソースとユーザーの責任",
          important: "すべての計算の正確性は、お客様が提供するパラメータに完全に依存します。",
          utilize_content: "本サービスは計算のために複数の数据ソースを利用します：",
          data_sources: [
            { title: "MSL値", desc: "メーカーの仕様に基づいています。独立した検証は行われません。" },
            { title: "摩擦係数", desc: "業界標準に基づいています。実際の摩擦は異なる場合があります。" },
            { title: "ラッシングポイント", desc: "ユニットの仕様に基づいています。物理的な完全性はユーザーの責任です。" },
            { title: "CTU/CSS コード", desc: "国際的な海事安全基準に準拠しています。" }
          ],
          user_responsible_items: [
            "すべての入力パラメータ（重量、尺寸、重心位置）の确认",
            "机器仕様が実際に使用される材料と一致することの確認",
            "特定の貨物条件への摩擦係数の適用の確認",
            "ラッシングポイントの物理的な完全性の検証",
            "実施前にレビューを行うために有資格の専門家を関与させること"
          ]
        },
        verification: {
          title: "専門家による検証要件",
          content: "すべての計算結果は、実施前にCTU Codeで定義された権限のある者によってレビューおよび承認される必要があります。プラットフォームは計算補助のみを提供し、エンジニアリングアドバイスではありません。"
        },
        indemnification: {
          title: "補償",
          content: "お客様は、以下から生じる第三者からの請求について、プラットフォーム提供者を補償することに同意します。",
          items: [
            "本サービス利用規約の違反",
            "不正確または不完全な入力データの提供",
            "古いまたは不正確な機器仕様の使用",
            "専門家による検証の不履行",
            "独立したレビューなしでのプランの実施"
          ]
        },
        governing_law: {
          title: "準拠法",
          content: "これらの条件は、抵触法の原則に関わらず、関連する国際海事法およびプラットフォーム提供者の管轄区域の法律に準拠し、これに従って解釈されます。"
        }
      },
      detailed_terms: [
        {
          title: "1. 契約の成立",
          content: [
            "これらのサービス利用規約（「規約」）は、Lashing Calculator Webアプリケーションプラットフォームへのアクセスおよび使用を規定する、お客様（「ユーザー」、「顧客」）とJADE CONSULTANCY AND TRADING LTD（イングランドおよびウェールズの法律に基づいて運営されている会社、「プロバイダー」、「私たち」）との間の法的拘束力のある契約を構成します。",
            "プラットフォームにアクセスまたは使用することにより、またはサービスのサブスクリプションまたは販売契約を締結することにより、お客様はこれらの規約に拘束されることに取消不能の形で同意したことになります。",
            "販売契約の受諾は、これらの規約の自動的な受諾を構成します。"
          ]
        },
        {
          title: "2. ビジネス用途のみ",
          content: [
            "プラットフォームは、企業間（B2B）ベースでのみ提供されます。",
            "お客様は、消費者保護法に基づく消費者としてではなく、ビジネスの過程で行動していることを確認します。",
            "プラットフォームは、海上、道路および鉄道輸送方式とそれらの複履横断的な組み合わせの貨物固定計算を支援するために設計されています。プラットフォームのいかなる使用にも適用される規制枠組みは、関連する輸送方式および積載・通過・荷下ろしの管轄区域によって異なります。ユーザーは、適用されるすべての輸送方式固有の規制を特定し遵守する責任を単独で負います。"
          ]
        },
        {
          title: "3. サブスクリプション、料金、および支払い",
          content: [
            "3.1 サブスクリプション期間：サブスクリプション期間は、アクティベーション日から1年間です。自動更新は適用されません。更新には、ユーザーからの書面による要求と、新しいサブスクリプション期間の全額支払いが必要です。",
            "3.2 料金と通貨：すべての料金はGBP（£）で請求および支払われます。支払いはプロバイダーのGBP銀行口座に行う必要があります。料金は前払いです。",
            "3.3 期限切れ通知：プラットフォームは、期限切れの1ヶ月、1週間、および1日前にリマインダー通知を送信する場合があります。このような通知は便宜上提供されるものであり、更新を構成するものではありません。"
          ]
        },
        {
          title: "4. キャンセルおよび終了",
          content: [
            "4.1 ユーザーによる終了：ユーザーはいつでもキャンセルできます。アクセスは現在のサブスクリプション期間の終了まで継続します。以下に明示されている場合を除き、返金は適用されません。",
            "4.2 プロバイダーによる終了：プロバイダーは、正当なビジネス、法的、技術的、またはセキュリティ上の理由により、いつでもサービスを終了または一時停止することができます。ただし、合理的に実行可能な場合、プロバイダーは事前に通知するものとします。終了がプロバイダーによって開始された場合、ユーザーはサブスクリプション期間の未使用部分について日割り計算による返金を受ける権利があります。"
          ]
        },
        {
          title: "5. サービスの失敗と返金",
          content: [
            "プラットフォームがユーザーに起因しない技術的な理由により、連続して5日間以上実質的に使用不能になった場合、ユーザーは未使用の日数について年間サブスクリプション料金に対して日割り計算された払い戻しを受ける権利があります。",
            "一時的な停止、パフォーマンスの低下、メンテナンスウィンドウ、または互換性の問題は、使用不能を構成しません。"
          ]
        },
        {
          title: "6. ユーザーアカウントとライセンス",
          content: [
            "6.1 ライセンスの付与：プロバイダーは、内部ビジネス目的でのみプラットフォームにアクセスおよび使用するための限定的、非独占的、譲渡不可のライセンスをユーザーに付与します。所有権は譲渡されません。",
            "6.2 シングルデバイスポリシー：各ユーザーアカウントは、適用される販売契約書で書面により別途合意されていない限り、一度に1つのデバイスでのみ使用できます。同時ログインは禁止されています。プロバイダーは、不正使用を検出するために使用パターンを監視する権利を留保します。不正使用が合理的に疑われる場合、プロバイダーは調査中にアクセスを一時停止することがあります。確認された違反は、返金なしでの終了につながる可能性があります。",
            "6.3 ユーザーの義務：レポートを生成するには、ユーザーは以下を提供する必要があります：会社名、会社ロゴ、携帯電話番号、固定電話番号、メールアドレス。請求書発行のために有効な会社の請求詳細を提供する必要があります。"
          ]
        },
        {
          title: "7. 重要な安全上の通知と専門的な免責事項",
          content: [
            "重要な通知：プラットフォームは、海上、道路、鉄道およびそれらの複履横断的な組み合わせを含むすべての輸送方式の貨物固定計画のための意思決定支援システムです。",
            "プラットフォームは、専門的なエンジニアリング、法的、または安全に関するアドバイスを提供しません。",
            "プラットフォームは、いかなる旗国、船級協会、港湾当局、道路輸送当局、鉄道インフラ管理者、または規制機関によっても認定、承認、または推奨されていません。",
            "すべての計算結果は、該当する方式の資格ある貨物専門家によりレビューおよび承認される必要があります：IMO/ILO/UNECE CTUコードおよびIMO CSSコード（海上）；EN 12195 / VDI 2700および適用される国内道路ガイダンス（道路）；UIC積載ガイドライン（鉄道）。",
            "貨物固定の最終的な責任は、当該輸送方式に適用される法的枠組みの下で責任ある当事者に帰します：海上は適用される国際海事法に基づく運送人、船主および/または船長；道路はCMR条約に基づく運送人、車両運実業者および/または運転手；鉄道はCIM/COTIF枠組みに基づく運送人で鉄道料金業者。複履横断的輸送では、各方式の運送人が自分の区間を負います。",
            "プロバイダーは、プラットフォームの出力に基づいて行われた決定について一切の責任を負いません。",
            "海上輸送について：プラットフォーム出力はSOLAS第6章規則VI/5-2の法定貨物記録義務を満たしません。P&I適合性、ISM条約義務、IACS船級要件はユーザーの責任です。",
            "道路輸送について：プラットフォーム出力はEN 12195シリーズおよびVDI 2700に対して検証される必要があります。英国の事業者はDfT荷物固定実務規範にも適合する必要があります。",
            "鉄道輸送について：プラットフォーム出力は適用されるUIC積載ガイドライン、TSI要件、および国内鉄道インフラ管理者要件に対して検証される必要があります。",
            "個々の港湾、道路および鉄道当局は追加要件を課す場合があります。すべての地元規制への適合はユーザーの完全な責任です。",
            "危険物の通知：プラットフォームは一般的な貨物固定計画のみのために設計されており、ADR（道路）、IMDGコード（海上）、RID（鉄道）、または同等の危険物規制の対象となる危険物貨物には設計または検証されていません。危険物を扱うユーザーは運用使用前に資格のある危険物安全アドバイザー（DGSA）に専門家のアドバイスを求める必要があります。"
          ]
        },
        {
          title: "8. 知的財産",
          content: [
            "プラットフォームは、そのソースコード、アルゴリズム、計算、構造、および設計を含め、知的財産法によって保護されています。",
            "リバースエンジニアリング、抽出、または複製は固く禁じられています。",
            "PDFレポートは、説明されている特定の貨物構成に対してのみ有効であり、再利用することはできません。",
            "正確なデータ入力はユーザーの単独の責任です。"
          ]
        },
        {
          title: "9. データソース、レポート、およびユーザーの責任",
          content: [
            "計算の精度は、ユーザーが提供したパラメータに完全に依存します。",
            "データソースには以下が含まれます：MSL値（メーカー仕様、独立した検証なし）、摩擦係数（業界標準、実際の条件は異なる場合があります）、ラッシングポイント（ユニット仕様、物理的完全性はユーザーの責任です）。",
            "輸送方式ごとの適用される規制枠組み：CTUコード（IMO/ILO/UNECE—複履横断的、全ての方式に適用）、CSSコード（IMO貨物固定マニュアル—海上輸送のみ）、VDI 2700 / EN 12195-1からEN 12195-4（道路輸送、道路车両のわく等拘束の欧州標準）、英国 DfT荷物固定実務規範（道路輸送、英国管辖）、UIC積載ガイドライン/TSI（鉄路輸送）、CIM/COTIF統一規則（国際鉄路貨物運送契約）。ユーザーは自分の輸送方式と管辖区域に対して正しい規制標準を特定し適用する退任を単独で負います。",
            "ユーザーは以下の責任を単独で負います：すべての入力データの検証、正しい機器選択の確認、実世界での適用可能性の確認、ラッシング配置の物理的検証、実施前の資格のある専門家の関与。",
            "レポートは、作成時の入力に基づいて生成されます。プロバイダーは計算データまたはレポートを保持する義務を負わず、ユーザーはアーカイブの責任を負います。"
          ]
        },
        {
          title: "10. 技術的正確性の免責事項",
          content: [
            "プラットフォームは、SaaSサービスの一般的に認められている業界標準と一致する合理的なスキルと注意を払って提供されます。正確性を確保するために合理的な努力が払われていますが、プラットフォームには技術的または誤植が含まれる場合があります。資料が完全、最新、またはエラーがないという保証はありません。",
            "サードパーティのデータと標準は時間の経過とともに変更される場合があります。更新は、以前に生成されたレポートに遡及的に適用されません。"
          ]
        },
        {
          title: "11. 責任の制限",
          content: [
            "英国法で認められる最大限の範囲で：プロバイダーは、プラットフォームの使用または信頼から生じるいかなる損失または損害についても責任を負いません。これには以下が含まれます：貨物の損失または損傷、船舶の損傷、車両の損傷、鉄道車両／車厢の損傷、人身傷害または死亡（法律で認められる最大限の範囲で）、事業の中断、利益、信用の喪失、または機会の損失、結果的、偶発的、または懲罰的損害。",
            "適用法で認められる最大限の範囲で、プロバイダーは、過失、不実表示、または法的義務違反から生じるすべての責任を除外します。ただし、そのような除外が法律で禁止されている場合を除きます。",
            "責任の上限：署名された販売契約書で書面により別途合意されていない限り、プロバイダーの累積責任総額は、請求前の12か月間にユーザーが支払った料金を超えないものとします。",
            "法律で禁止されている場合、詐欺または過失による死亡に対する責任を除外するものではありません。"
          ]
        },
        {
          title: "12. 賠償",
          content: [
            "ユーザーは、以下から生じる第三者の請求からプロバイダーを補償し、無害に保つことに同意します：これらの規約の違反、不正確または不完全な入力データ、不正確または古い仕様の使用、専門的な検証の不取得、ラッシング配置の物理的実行、ユーザーによる資格情報の悪用または不正アクセス。"
          ]
        },
        {
          title: "13. 輸出管理および制裁の遵守",
          content: [
            "ユーザーは、欧州連合、英国、国連、またはその他の適用当局によって課された制裁の対象ではないことを表明および保証します。",
            "ユーザーは、適用される輸出管理または制裁法に違反してプラットフォームを使用してはならず、すべての管轄区域での合法的な使用を確保する単独の責任を負います。"
          ]
        },
        {
          title: "14. サイバーセキュリティ免責事項",
          content: [
            "プロバイダーは、処理されるデータの性質を考慮に入れて、業界の慣行に沿った合理的な技術的および組織的なセキュリティ対策を実装します。ただし、プロバイダーは、サードパーティのシステムまたはネットワークから生じるものを含め、プラットフォームがサイバーインシデント、マルウェア、ランサムウェア、不正アクセス、またはセキュリティ侵害から完全に解放されることを保証しません。"
          ]
        },
        {
          title: "15. 保険の開示",
          content: [
            "プロバイダーは、業界の慣行に従って適切な専門職賠償およびサイバー保険を維持しています。本契約のいかなる規定も、ユーザーまたは第三者に保険適用範囲、保証、または補償を提供するものと解釈されるべきではありません。"
          ]
        },
        {
          title: "16. 信頼なし",
          content: [
            "ユーザーは、本契約に明示的に記載されていない声明、表明、または保証に依存していないことを認めます。",
            "プラットフォームは、専門的な判断、認証、規制当局の承認、または法的コンプライアンスの代替として信頼されるべきではありません。"
          ]
        },
        {
          title: "17. プロフェッショナルユーザーの想定",
          content: [
            "プラットフォームは、経験豊富な海事および物流の専門家による使用を目的としています。プロバイダーは、海事原則、安全義務、または規制要件をユーザーに説明する義務を負いません。"
          ]
        },
        {
          title: "18. 安全性が重要な使用に関する免責事項",
          content: [
            "プラットフォームは、アクティブな貨物操作または緊急事態中のリアルタイムの運用上の意思決定のために設計または意図されていません。"
          ]
        },
        {
          title: "19. 第三者の権利なし",
          content: [
            "本契約の当事者ではない者は、1999年契約（第三者の権利）法（Contracts (Rights of Third Parties) Act 1999）に基づいて本契約の条件を行使する権利を有しません。"
          ]
        },
        {
          title: "20. 不可抗力",
          content: [
            "プロバイダーは、不可抗力、戦争、サイバーインシデント、クラウドサービスの障害、労働争議、または政府の措置など、合理的な管理を超えたイベントによって引き起こされたパフォーマンスの失敗または遅延について責任を負いません。"
          ]
        },
        {
          title: "21. 準拠法、EU強行法および管轄権",
          content: [
            "これらの規約は英国法に準拠します。",
            "英格兰およびウェールズの裁判所が専属管轄権を有します。",
            "これらの規約のいかなる規定も、適用可能な場合、規則（EC）No 593/2008（ローマI）に基づいて契約によって逸脱できないEU法の強行規定の適用を除外するものではありません。"
          ]
        },
        {
          title: "22. 言語の優先順位",
          content: [
            "本契約は英語で作成されています。翻訳がある場合、英語版が優先されます。"
          ]
        },
        {
          title: "23. 一般",
          content: [
            "これらの規約のいずれかの条項が無効または執行不能と判断された場合でも、残りの条項は完全に効力を持ちます。",
            "プロバイダーは、合併、売却、または事業譲渡の一環としてこれらの規約を譲渡することができます。",
            "責任、補償、知的財産、準拠法、および免責事項に関する条項は、終了後も存続します。"
          ]
        },
        {
          title: "24. データ保護とプライバシー",
          content: [
            "24.1 プラットフォームの提供に関連して、ユーザーはデータ管理者（Controller）として機能し、プロバイダーは英国GDPR、EU GDPR、および2018年データ保護法の意味におけるデータ処理者（Processor）として機能します。",
            "24.2 プロバイダーは、ユーザーの文書化された指示に従い、適用されるデータ保護法に従ってのみ個人データを処理します。",
            "24.3 別紙1（Schedule 1）に記載されているデータ処理補足契約は、これらの規約の不可欠な部分を構成し、ユーザーに代わってプロバイダーによる個人データの処理を規定します。",
            "24.4 これらの規約とデータ処理補足契約の間に矛盾がある場合、データ保護事項に関してはデータ処理補足契約が優先されます。"
          ]
        },
        {
          title: "SCHEDULE 1 – DATA PROCESSING ADDENDUM (DPA)",
          content: [
            "1. 定義",
            "本データ処理補足契約（「DPA」）の目的において、「個人データ」、「処理」、「管理者」、「処理者」、および「監督当局」という用語は、英国GDPRおよびEU GDPRで与えられた意味を持ちます。",
            "",
            "2. 範囲と役割",
            "2.1 ユーザーは、プラットフォームの使用に関連して処理される個人データの管理者です。",
            "2.2 プロバイダーは処理者としてのみ機能し、プラットフォームを提供する以外の目的で個人データを処理しません。",
            "",
            "3. 処理の性質と目的",
            "目的：SaaSサービスの提供、アカウント管理、テクニカルサポート、およびレポートの生成。",
            "データ主体のカテゴリ：承認されたユーザー、従業員、請負業者。",
            "個人データの種類：専門的な連絡先詳細（名前、電子メール、電話）、会社識別詳細。",
            "特別なカテゴリ：意図されていません。",
            "",
            "4. 処理者の義務",
            "プロバイダーは以下を行うものとします：",
            "a) ユーザーの文書化された指示に基づいてのみ個人データを処理する",
            "b) 個人データを処理する権限を与えられた担当者が守秘義務の対象であることを確認する",
            "c) 個人データを保護するために適切な技術的および組織的措置を講じる",
            "d) データ主体の権利要求について、合理的に要求された場合、ユーザーを支援する",
            "e) 個人データ侵害に気付いた場合、不当な遅滞なくユーザーに通知する",
            "f) サービスの終了時に、法律で保持が要求されない限り、個人データを削除または返却する",
            "",
            "5. サブプロセッサー",
            "5.1 ユーザーは、プロバイダーがホスティング、インフラストラクチャ、およびサポートサービスのためにサブプロセッサーを関与させることを承認します。",
            "5.2 プロバイダーは、サブプロセッサーが本DPAに規定されているものと同等のデータ保護義務の対象となることを保証します。",
            "",
            "6. 国際転送",
            "個人データが英国またはEEA外に転送される場合、プロバイダーは、必要に応じて、英国の国際データ転送契約またはEU標準契約条項を含む適切な保護措置が講じられていることを保証するものとします。",
            "",
            "7. セキュリティ対策",
            "プロバイダーは、アクセス制御、適切な場合の暗号化、およびインシデント対応手順を含む、データの性質および関連するリスクに適切な合理的かつ技術的な組織的セキュリティ対策を維持するものとします。",
            "",
            "8. 監査",
            "合理的な書面による要求に応じて、年に1回を上限として、プロバイダーは、機密保持およびセキュリティ要件に従い、本DPAへの準拠を実証するために必要な情報を利用可能にするものとします。",
            "",
            "9. 責任",
            "本DPAから生じる責任は、サービス利用規約に規定されている責任の制限および除外の対象となります。",
            "",
            "10. 準拠法",
            "本DPAは、イングランドおよびウェールズの法律に準拠し、それに従って解釈されるものとします。"
          ]
        }
      ],
      detailed_privacy: [
        {
          title: "1. はじめに",
          content: [
            "このプライバシーポリシーは、イングランドおよびウェールズの法律に基づいて運営されている会社であるJADE CONSULTANCY AND TRADING LTD（「プロバイダー」、「私たち」、「私たちを」、「私たちの」）が、WebアプリケーションLashing Calculator（「プラットフォーム」）を使用する際に個人データをどのように収集、使用、保存、保護するかについて説明します。",
            "私たちは、英国一般データ保護規則（UK GDPR）および2018年データ保護法に従って個人データを保護することをお約束します。"
          ]
        },
        {
          title: "2. データ管理者",
          content: [
            "適用されるデータ保護法の目的において、プロバイダーは、プラットフォームを通じて処理される個人データに関するデータ管理者として機能します。"
          ]
        },
        {
          title: "3. 収集する情報",
          content: [
            "3.1 アカウントおよび連絡先データ：名前、会社名、ビジネスメールアドレス、電話番号、ログイン資格情報、請求および支払い詳細。これらのデータは、アカウント管理、認証、請求、およびサービス通信のために厳密に使用されます。",
            "3.2 産業および運用データ：貨物パラメータおよび構成、ラッシング計算、およびレポート入力。これらのデータは機密産業情報として扱われます。商業的な再販、プロファイリング、またはマーケティング目的で分析されることはありません。",
            "3.3 技術データ：IPアドレス、ブラウザタイプ、デバイスおよびセッション情報、アクセスログ。これらのデータは、セキュリティ、パフォーマンス監視、および悪用検出のために収集されます。"
          ]
        },
        {
          title: "4. 処理の法的根拠",
          content: [
            "私たちは、UK GDPR第6条に基づく以下の法的根拠に基づいて個人データを処理します。",
            "契約上の必要性 – プラットフォームの提供および運営のため",
            "正当な利益 – セキュリティの確保、悪用の防止、およびサービスパフォーマンスの向上のため",
            "法的義務 – 会計、税務、および規制要件に準拠するため",
            "同意（明示的に取得された場合）（例：オプションの通信）"
          ]
        },
        {
          title: "5. 情報の使用方法",
          content: [
            "私たちは収集したデータを以下の目的で使用します。",
            "プラットフォームの提供、運営、および維持",
            "計算の処理および専門的なレポートの生成",
            "サブスクリプション、請求、およびカスタマーサポートの管理",
            "国際的な安全フレームワーク（CTUコード/CSSコード）への準拠の確保",
            "システムセキュリティの監視および不正アクセスの防止",
            "プラットフォーム機能の改善および開発",
            "個人データを第三者に販売することはありません。"
          ]
        },
        {
          title: "6. データ共有および第三者",
          content: [
            "個人データは以下とのみ共有される場合があります。",
            "ホスティングおよびクラウドインフラストラクチャプロバイダー",
            "支払いおよび請求サービスプロバイダー",
            "専門アドバイザー（法律、会計、保険）",
            "法律で義務付けられている場合の当局",
            "すべての第三者は、適切な機密保持およびデータ保護の義務を負います。"
          ]
        },
        {
          title: "7. 国際データ転送",
          content: [
            "個人データが英国またはEEA外に転送される場合、以下を含む適切な保護措置が講じられていることを確認します。",
            "十分性認定",
            "標準契約条項（SCC）",
            "同等の法的転送メカニズム"
          ]
        },
        {
          title: "8. データ保持",
          content: [
            "私たちは、必要な期間だけ個人データを保持します。",
            "アカウントおよび請求データ：契約期間中および法律で義務付けられている期間",
            "技術ログ：セキュリティおよび監査目的で限られた期間保持されます",
            "産業計算データ：サービスの提供に必要な期間のみ保持されます",
            "ユーザーは、独自のレポートおよび計算出力をアーカイブする責任を負います。"
          ]
        },
        {
          title: "9. データセキュリティ",
          content: [
            "私たちは、アクセス制御、暗号化、監視など、不正アクセス、紛失、改ざん、または開示から個人データを保護するために、合理的な技術的および組織的措置を講じます。",
            "ただし、完全に安全なシステムは保証できません。"
          ]
        },
        {
          title: "10. あなたの権利",
          content: [
            "UK GDPRに基づき、あなたには以下の権利があります。",
            "個人データへのアクセス",
            "不正確なデータの訂正",
            "削除の要求（法的義務に従う）",
            "処理の制限または異議申し立て",
            "データポータビリティ",
            "該当する場合の同意の撤回",
            "リクエストは、以下の連絡先情報を使用して行うことができます。"
          ]
        },
        {
          title: "11. 苦情",
          content: [
            "データ保護の権利が侵害されたと思われる場合は、英国情報コミッショナーオフィス（ICO）に苦情を申し立てる権利があります。"
          ]
        },
        {
          title: "12. 本ポリシーの変更",
          content: [
            "私たちは、このプライバシーポリシーを随時更新する場合があります。更新されたバージョンは、改訂された発効日とともにプラットフォームで利用可能になります。"
          ]
        },
        {
          title: "13. お問い合わせ",
          content: [
            "プライバシーまたはデータ保護に関するお問い合わせ：",
            "メール：info@lashingplan.com"
          ]
        }
      ],
      privacy_sections: {
        introduction: {
          title: "データ保護とプライバシー",
          content: "私たちは、お客様のプライバシーを保護することをお約束します。このポリシーは、当社のサービスを使用する際にお客様の情報がどのように収集、使用、および保護されるかについて説明します。"
        },
        collection: {
          title: "収集する情報",
          personal_title: "アカウントデータ",
          personal_desc: "アカウント管理および技術レポートに使用される基本的な専門情報。",
          calc_title: "産業データ",
          calc_desc: "貨物パラメータと構成。これらのデータは「機密産業情報」として扱われます。"
        },
        usage: {
          title: "情報の使用方法",
          intro: "私たちは収集した情報を以下の目的で使用します：",
          items: [
            "安全なサービスの提供、運営、および維持",
            "計算の処理および専門的なPDFレポートの生成",
            "国際的な安全基準（CTU/CSS）への準拠の確保",
            "サービス機能の向上と拡張"
          ]
        },
        compliance: {
          title: "グローバルコンプライアンス",
          content: "当社は、英国の一般データ保護規則（UK GDPR）および2018年データ保護法に基づき、お客様がご自身のデータを管理できるよう適切な措置を講じています。",
          note: "注：個人データを第三者に販売することはありません。"
        },
        retention: {
          title: "データの保持",
          content: "当社は、サービスの提供および法的義務の遵守に必要な期間に限り、お客様の個人情報を保持します。"
        },
        contact: {
          title: "お問い合わせ",
          content: "データに関する権利やプライバシーについてのご質問は、こちらまでご連絡ください：",
          email: "info@lashingplan.com"
        }
      },
      detailed_cookie_policy: [
        {
          title: "1. はじめに",
          content: [
            "このCookieポリシーは、[プロバイダーの正式名称]（「私たち」、「私たちを」、「私たちの」）が、Lashing Calculator WebサイトおよびWebアプリケーション（「プラットフォーム」）でCookieおよび類似の技術をどのように使用するかを説明します。",
            "このポリシーは、当社のプライバシーポリシーと一緒に読む必要があります。"
          ]
        },
        {
          title: "2. Cookieとは",
          content: [
            "Cookieは、Webサイトにアクセスしたときにお使いのデバイスに保存される小さなテキストファイルです。それらは、Webサイトの機能を有効にし、ユーザーエクスペリエンスを向上させ、Webサイト運営者に情報を提供するために広く使用されています。"
          ]
        },
        {
          title: "3. 法的枠組み",
          content: [
            "私たちは、以下に従ってCookieを使用します。",
            "英国のプライバシーおよび電子通信規制（PECR）",
            "EU eプライバシー指令",
            "英国一般データ保護規則（UK GDPR）",
            "厳密に必要なCookieを除き、Cookieは事前の同意がある場合にのみ使用されます。"
          ]
        },
        {
          title: "4. 使用するCookieの種類",
          content: [
            "4.1 厳密に必要なCookie",
            "これらのCookieは、プラットフォームの動作に不可欠であり、次のようなコア機能を有効にします。",
            "ユーザー認証",
            "セッション管理",
            "セキュリティと詐欺防止",
            "これらのCookieは同意を必要とせず、Cookie設定から無効にすることはできません。",
            "法的根拠：ユーザーが要求したサービスの提供に必要です。",
            "",
            "4.2 分析Cookie",
            "分析Cookieは、パフォーマンスとユーザビリティを向上させるために、プラットフォームがどのように使用されているかを理解するのに役立ちます。",
            "例としては以下があります。",
            "ページ訪問数",
            "トラフィックソース",
            "使用パターン",
            "分析Cookieは、明示的な同意が得られた後にのみ配置されます。",
            "法的根拠：UK GDPRおよびeプライバシー規則に基づく同意。"
          ]
        },
        {
          title: "5. Cookieプロバイダー",
          content: [
            "分析Cookieは、信頼できるサードパーティの分析プロバイダーによって提供される場合があります。これらのプロバイダーは、独自のプライバシーポリシーおよび適用されるデータ保護法に従ってデータを処理します。",
            "ターゲット広告やマーケティングプロファイリングにはCookieを使用しません。"
          ]
        },
        {
          title: "6. Cookieの期間",
          content: [
            "Cookieは次のいずれかです。",
            "セッションCookie：ブラウザを閉じると期限切れになります。",
            "永続的Cookie：定義された期間、または削除されるまでデバイスに残ります。",
            "特定のCookieの寿命は、その目的とプロバイダーによって異なります。"
          ]
        },
        {
          title: "7. Cookie設定の管理",
          content: [
            "Cookieの同意は、いつでも次の方法で管理または撤回できます。",
            "プラットフォームで利用可能なCookie設定または同意バナーを使用する",
            "ブラウザの設定を調整して、Cookieをブロックまたは削除する",
            "特定のCookieを無効にすると、プラットフォームの機能に影響する場合があることに注意してください。"
          ]
        },
        {
          title: "8. このCookieポリシーの変更",
          content: [
            "このCookieポリシーは随時更新される場合があります。変更は、更新された発効日とともにプラットフォームに公開されます。"
          ]
        },
        {
          title: "9. お問い合わせ",
          content: [
            "Cookieの使用についてご質問がある場合は、以下までお問い合わせください。",
            "メール：info@lashingplan.com"
          ]
        }
      ],
      cookie_policy: {
        title: "クッキーポリシー",
        last_updated: "発効日：2026年2月4日",
        intro_title: "導入",
        intro_content: "このクッキーポリシーは、Lashing Calculatorのウェブサイトを訪問した際にクッキーがどのように使用されるかを説明します。",
        types_title: "使用するクッキーの種類",
        essential_title: "必須クッキー",
        essential_desc: "これらのクッキーはウェブサイトが適切に機能するために不可欠であり、システムでオフにすることはできません。",
        analytics_title: "分析クッキー",
        analytics_desc: "これらのクッキーにより、訪問数やトラフィックソースをカウントし、サイトのパフォーマンスを測定および改善できます。",
        manage_title: "クッキー設定の管理",
        manage_content: "ブラウザの設定を通じて、いつでもクッキーの設定を管理できます。",
        contact_title: "お問い合わせ",
        contact_text: "クッキーポリシーに関するご質問がある場合は、お問い合わせください。"
      },
      sales_agreement: {
        title: "販売契約",
        content: "この販売契約は、Lashing Calculatorサービスの提供および使用に関する条件と条項を定めたものです。当社のサービスを購入することは、この契約のすべての条項に同意したことを意味します。"
      },
      legal_notice: {
        title: "法的通知",
        content: "当ウェブサイト上の情報は一般的な情報提供のみを目的としています。Lashing Calculatorは情報を最新かつ正確に保つよう努めていますが、いかなる種類の表明や保証も行いません。"
      },
      footer_note: "プラットフォームを使用することにより、安全に関する通知、技術的リスク、およびすべての結果の専門的な検証の要件を含む法的条件を読み、同意したことを確認したことになります。"
    },

    profile: {
      title: "会社概要",
      subtitle: "PDFレポート用の会社情報を管理します",
      logo_label: "会社ロゴ",
      logo_note: "ロゴは管理者のみが更新できます。",
      company_name: "マイプロフィール",
      address: "完全な住所",
      phone: "電話 / 携帯",
      email: "メール（アカウント）",
      tax_office: "税務署",
      tax_number: "税番号",
      company_email_label: "会社メール (PDFに表示)",
      user_name_label: "ユーザー名 (アカウント)",
      terms_accepted: "サービス利用規約同意日：",
      terms_not_accepted: "サービス利用規約はまだ同意されていません",
      view_terms: "規約を表示",
      accept_terms: "規約に同意する",
      review_accept: "確認して同意する",
      admin_note: "注：会社詳細は管理者が管理します。",
      cancel: "キャンセル",
      accept: "規約に同意する",
      modal_title: "サービス利用規約",
      modal_subtitle: "続行するには、規約を確認して同意してください。",
      modal_agree: "同意することにより、{tos}および{privacy}に拘束されることに同意したものとみなされます。",
      modal_ack: "ソフトウェアはツールであり、ラッシングプランの最終的な責任は認定された専門家にあることを認めます。",
      checkbox_label: "サービス利用規約を読み、同意しました",
      save: "変更を保存",
      saving: "保存中...",
      success_message: "プロフィールが正常に更新されました。",
      error_message: "更新エラー：{error}",
      credits_title: "クレジットと使用状況",
      calc_credits: "計算クレジット",
      pdf_credits: "PDFレポートクレジット",
      monthly_increment: "毎月の増分",
      next_increment: "次回のチャージ",
      remaining: "残り",
      lashing_display_label: "ラッシング表示形式",
      lashing_display_msl: "MSL (例: MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (例: CL200 HDB12L)",
      pdf_theme_title: "PDF Design Theme",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "Dark Turquoise & Slate Gray",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "Corporate Navy & Light Gray",
      theme_standart_title: "Standard",
      theme_standart_desc: "Original App Colors"
    },
    surveys: {
      title: "現場レポート",
      create_new: "新しいレポートを作成",
      total_ops: "総操作数",
      pending_photos: "写真待ち",
      completed_assignments: "完了した割り当て",
      current_reports: "現在のレポート",
      search_placeholder: "IDまたはプロジェクトを検索...",
      no_reports_title: "レポートはまだありません",
      no_reports_desc: "最初のラッシング計算を行って、現場レポートの作成を開始してください。",
      create_now: "今すぐ作成",
      unnamed_project: "無題のプロジェクト",
      created_date: "作成日",
      data_status: "データ状況",
      uploaded: "申請写真のアップロード完了",
      waiting: "申請写真を待機中",
      surveyor: "検査員",
      waiting_response: "回答待ち",
      under_review: "確認中",
      report_ready: "レポート作成完了",
      cancelled_rejected: "キャンセル / 拒否",
      send_link: "現場アップロードリンクを送信",
      upload_manually: "現場写真を手動でアップロード",
      assign_new: "新しい検査員を割り当て",
      assign_surveyor: "検査員を割り当て",
      assigned: "割り当て済み",
      summary_pdf: "概要PDF",
      surveyor_pdf: "検査員PDF",
      assignment_success: "検査員の割り当てに成功しました！",
      old_report_error: "古いレポートの安全なリンクが生成されていません。レポートを再作成してください。",
      session_expired: "セッションが期限切れです。ページを更新するか、再度ログインしてください。",
      pdf_error: "PDF作成中にエラーが発生しました。",
      download_failed: "ダウンロードに失敗しました：",
      wait: "お待ちください...",
      connecting: "接続中...",
      access_error: "アクセスエラー",
      modal: {
        title: "検査員（Surveyor）を割り当て",
        search_placeholder: "都市、港、または名前で検索...",
        list_error: "検査員リストを取得できませんでした。",
        assign_failed: "割り当てに失敗しました。",
        loading: "読み込み中...",
        not_found: "検査員が見つかりません。",
        unknown_location: "不明",
        assigning: "割り当て中...",
        assign_button: "この検査員を割り当てる"
      },
      guest: {
        title: "現場実施監視ポータル",
        subtitle: "ラッシング箇所の現場写真をこちらからアップロードできます。",
        invalid_link: "無効または期限切れのリンクです。",
        upload_success_title: "アップロード成功",
        upload_success_desc: "ラッシング箇所の現場写真が正常に保存されました。レポートの確認とPDF承認の準備が整いました。",
        close_window: "ウィンドウを閉じる",
        info_title: "1. 実施者情報（任意）",
        date: "日付",
        company_name: "会社名",
        company_placeholder: "例：Xロジスティクス",
        implementer_name: "実施者氏名",
        phone: "電話番号",
        email: "メールアドレス",
        email_placeholder: "name@company.com",
        photos_title: "2. 現場実施画像（最大9枚）",
        photos_desc: "以下のカテゴリーに一致する現場写真をアップロードまたは撮影してください。1枚あたりの制限は約5MBで、デバイス上で自動的に約1MBに圧縮されます。",
        take_photo: "写真を撮影 / アップロード",
        uploading: "写真をアップロード中...",
        confirm_upload: "すべてアップロードして確定",
        min_photo_error: "少なくとも1枚の写真を追加してください。",
        compression_error: "写真を処理できませんでした。もう一度お試しください。",
        categories: {
          floor: "輸送ユニットの床の状態",
          cargo: "貨物の配置と床のチョッキング",
          front_right: "ラッシング：右前",
          front_left: "ラッシング：左前",
          rear_right: "ラッシング：右後",
          rear_left: "ラッシング：左後",
          plate: "ナンバープレート（ある場合）",
          seal: "シール番号（ある場合）",
          overall: "メイン全景（最終）"
        }
      },
      portal: {
        title: "検査員ポータル",
        subtitle: "ログインして、割り当てられた検査タスクを管理します。",
        login_error: "メールアドレスとパスワードを入力してください。",
        login_button: "ログイン",
        logging_in: "ログイン中...",
        dashboard: {
          connecting: "ダッシュボードに接続中...",
          pending_tab: "受信リクエスト",
          active_tab: "進行中のタスク",
          completed_tab: "過去の案件",
          logout: "安全なログアウト",
          connected: "接続済み",
          account_type: "検査員アカウント",
          no_records: "レコードが見つかりません",
          no_records_desc: "このタブに表示するアクティブな検査タスクはありません。",
          customer: "顧客",
          task_date: "タスク日",
          waiting_response: "回答待ち",
          under_review: "顧客確認中",
          delivered: "レポート提出済み",
          rejected_by_you: "タスクを拒否しました",
          accept_button: "タスクを承認",
          reject_button: "拒否",
          go_to_report: "レポートメニューへ",
          download_pdf: "PDFファイルをダウンロード",
          downloading_pdf: "レポートPDFをダウンロード中...",
          download_success: "レポートが正常にダウンロードされました。",
          download_error: "レポートのダウンロード中にエラーが発生しました。",
          update_error: "ステータスを更新できませんでした。もう一度お試しください。",
          accept_success: "タスクを正常に承認しました！",
          reject_success: "タスクを拒否しました。"
        },
        editor: {
          details_title: "プロジェクト詳細",
          project_name: "プロジェクト名",
          weight: "貨物重量",
          photos_count: "現場写真",
          success_title: "レポートが正常に作成されました！",
          success_desc: "検査レポートが作成され、署名され、安全なクラウド環境にアップロードされました。下のボタンをクリックして、PDFファイルをダウンロードまたは確認できます。",
          back_to_dashboard: "ダッシュボードに戻る",
          download_pdf: "PDFをダウンロード",
          creator_title: "最終調査レポート作成者",
          assessment_label: "検査員による評価",
          assessment_desc: "このテキストは、最終PDFの「Methodology & Conclusion」セクションに追加されます。",
          assessment_default: "貨物の検査は国際基準に従って完了しました。損傷や不適合は見られませんでした。",
          auto_info_note: "注：システム内の氏名、署名、会社ロゴなどのデフォルト情報は、PDFレポートに自動的に配置されます。",
          back_button: "戻る",
          generate_button: "PDFレポートを認証して送信",
          generating: "生成中...",
          generate_error: "レポートを作成できませんでした",
          not_found: "タスクが見つからないか、権限がありません。"
        }
      }
    }
  }
,

  // ===================================================================
  // KOREAN
  // ===================================================================
    "Korean": {
    code: "ko",
    name: "한국어",
    flag: "South Korea",

    hub: {
      nav: {
        products: "제품",
        insights: "시장 인사이트",
        resources: "자료",
        go_to_app: "앱으로 이동"
      },
      hero: {
        status: "시스템 상태: 최적",
        badge: "차세대 화물 물류 허브",
        title_main: "분석된 강도",
        title_italic: "검증된 안정성",
        title_1: "계획 수립",
        title_2: "최대 보안.",
        description: "LashingPlan은 국제 표준을 준수하며 정밀하게 설계된 화물 고정용 전문 계산 에코시스템입니다.",
        explore: "제품 탐색",
        launch_product: "제품 실행",
        read_blog: "블로그 기사",
        portal_access: "포탈 접속 V2.0",
        stability_index: "안정성 지수",
        archives: "아카이브",
        archives_subtitle: "전문적인 안전 기준과 물류 트렌드로 업계를 선도하세요."
      },
      footer: {
        tagline: "기술적 탁월함의 결과물. 해상 안전에서 절대적인 정밀함을 원하는 전문가를 위해 설계되었습니다.",
        systems: "시스템",
        resources: "자료",
        legal: "법적 고지",
        privacy: "개인정보 처리방침",
        compliance: "규정 준수",
        terms: "이용 약관",
        status_all: "LASHING PLAN 글로벌 // 모든 시스템 정상 작동 중",
        follow: "팔로우하기"
      },
      products: {
        title: "제공 서비스",
        subtitle: "글로벌 운송 표준 준수 및 화물 안전을 위해 설계된 전문 도구입니다.",
        context: "01 / 제품",
        calculator: {
          title: "라싱 계산기",
          description: "도로, 해상, 철도 운송 시 화물 고정 요구 사항을 계산하는 업계 표준 엔진입니다.",
          button: "앱 실행"
        },
        inspection: {
          title: "Inspection Pro",
          description: "현장 조사를 위한 사진 기반 디지털 검사 및 즉각적인 인증서 생성 도구입니다.",
          status: "COMING SOON"
        },
        risk: {
          title: "Risk Advisor",
          description: "AI 기반 안정성 분석 및 선적 리스크 평가 시스템입니다.",
          status: "COMING SOON"
        },
        stats: {
          calculations: "누적 계산",
          countries: "지원 국가",
          compliance: "규정 준수",
          precision: "정밀도"
        },
        blog_articles: {
          article_1: {
            title: "화물 고정: 라싱 가이드",
            description: "선적 전 화물을 운송 단위에 안전하게 고정하는 것은 물류 안전의 기본입니다..."
          },
          article_2: {
            title: "혁신적인 화물 운송 솔루션",
            description: "선구적인 혁신과 신뢰할 수 있는 화물 고정 기술을 강조하는 미래 지향적인 화물 솔루션..."
          },
          article_3: {
            title: "IMO/CTU 코드 규정 준수의 이해",
            description: "컨테이너 화물 운송을 규제하는 국제 표준에 대한 심층 분석..."
          },
          article_4: {
            title: "화물 안전 기준 향상",
            description: "현대적인 프로토콜, 기술 통합 및 입증된 안전 관행을 통한 안전성 개선..."
          }
        }
      },
      insights: {
        title: "시장 인사이트",
        subtitle: "전문적인 안전 기준과 물류 트렌드로 업계를 선도하세요.",
        all_articles: "모든 기사 보기"
      }
    },

    blog: {
      nav: {
        back: "허브로 돌아가기"
      },
      header: {
        title_1: "업계",
        title_2: "인사이트.",
        subtitle: "화물 안전, 국제 표준 및 물류 기술의 최신 동향을 확인하세요."
      },
      article: {
        read_time: "읽기",
        share: "공유",
        read_more: "기사 읽기",
        cta_title: "정밀한 계산이 필요하신가요?",
        cta_desc: "라싱 계산기로 전문적인 화물 고정 안전성을 바로 확인해 보세요.",
        cta_button: "계산기 실행"
      },
      footer: {
        copyright: "© 2026 LashingPlan 블로그. 라싱 계산기 에코시스템의 일부입니다."
      }
    },

    app: {
      title: "라싱 계산기",
      logout: "로그아웃",
      theme_toggle: "다크 / 라이트 모드",
      profile: "프로필",
      reports: "현장 보고서",
      login: {
        title: "라싱 계산기",
        subtitle: "안전한 화물 고정 솔루션",
        email: "이메일 또는 사용자 이름",
        email_placeholder: "이메일 또는 사용자 이름을 입력하세요",
        password: "비밀번호",
        password_placeholder: "비밀번호를 입력하세요",
        forgot_password: "비밀번호를 잊으셨나요?",
        submit: "로그인",
        no_account: "계정이 없으신가요?",
        signup: "회원가입",
        back_to_home: "홈으로 돌아가기",
        consent: "계속 진행함으로써 {terms} 및 {privacy}에 동의합니다.",
        surveyor_portal: "검사원이십니까? 검사원 포털로 이동"
      },
      cog3d: {
        title: "3D 무게 중심 (CoG) 보기",
        subtitle: "무게 중심 (CoG)은 화물의 균형점이며 전도 거동을 결정합니다.",
        drag_hint: "드래그하여 회전"
      },
      lashing3d: {
        title: "3D 라싱 다이어그램",
        subtitle: "라싱 설정의 대화식 보기"
      },
      calculator: {
        title: "화물 계산기",
        cargo_details: "화물 세부 정보",
        weight: "중량 (MT)",
        cog: {
          title: "무게 중심 (좌측 하단 모서리로부터의 cm)",
          x: "X (너비)",
          y: "Y (깊이)",
          z: "Z (높이)"
        },
        securing_elements: "화물 고정 요소",
        wooden_dunnage: {
          label: "목재 더니지 사용",
          description: "마찰력을 높이고 미끄러짐을 방지합니다.",
          details: "목재 더니지 세부 정보",
          width: "너비 (cm)"
        },
        loop_lashing: "루프 라싱",
        direct_lashing: "다이렉트 라싱",
        select_rope_type: "로프 유형 선택",
        stopper_forces: "스토퍼 힘 (daN)",
        direction: {
          forward: "전방",
          backward: "후방",
          right: "우측",
          left: "좌측"
        }
      },
      results: {
        title: "계산 결과",
        lashing_diagram: "라싱 다이어그램",
        error: "오류",
        loading: "로딩 중...",
        no_results: "결과가 없습니다",
        values: {
          sliding: "미끄러짐:",
          tipping: "전도:",
          friction: "마찰:",
          securing_element: "고정 요소:",
          net_sliding: "순 미끄러짐:",
          net_tipping: "순 전도:"
        }
      }
    },

    landing: {
      contact: {
        label: "문의하기",
        title_1: "화물 적재 작업을 안전하게 ",
        title_2: "시작할 준비가 되셨나요?",
        description: "엔터프라이즈 수준의 조사관 알고리즘으로 업그레이드하세요. 엔지니어링 팀과 연락하여 맞춤형 API 통합, 대량 라이선스 계약 또는 프리미엄 기능에 대해 논의해 보세요.",
        email_us: "이메일 보내기",
        enterprise_support: "엔터프라이즈 지원",
        support_desc: "24/7 우선순위 SLA 지원 가능",
        success_title: "메시지 전송 완료!",
        success_desc: "24시간 이내에 엔지니어링 팀에서 연락드리겠습니다.",
        form: {
          name: "성명",
          placeholder_name: "홍길동",
          company: "회사명",
          placeholder_company: "물류 주식회사",
          email: "이메일 주소",
          placeholder_email: "hong@company.com",
          phone: "전화번호",
          placeholder_phone: "+82 10-0000-0000",
          message: "메시지",
          placeholder_message: "귀하의 요구사항에 대해 알려주세요...",
          submit: "메시지 전송",
          sending: "메시지 전송 중...",
          back_to_main: "메인 사이트로 돌아가기"
        }
      },
      hero_badge: "해상 물류",
      hero_badge_imo: "IMO CTU 코드",
      hero_badge_imo_sub: "준수 엔진",
      hero_badge_3d: "3D 시각화",
      hero_badge_3d_sub: "정밀 다이어그램",
      hero_title_1: "글로벌 물류를 위한",
      hero_title_2: "정밀 화물 고정.",
      hero_subtitle: "항만 당국, 해사 감정 법인 및 화물 운송 계획자를 위해 특별히 제작된 최고의 라싱 계산 플랫폼입니다.",
      start_calculating: "계산 시작하기",
      view_demo: "데모 요청",
      demo_modal: {
        title: "데모 요청",
        subtitle: "화물 고정 전문가와의 라이브 워크스루 일정을 잡기 위해 세부 정보를 입력하세요.",
        full_name: "성명",
        email: "회사 이메일",
        company: "회사명",
        phone: "전화번호",
        submit: "요청 제출",
        submitting: "제출 중...",
        success_title: "데모 요청 접수됨",
        success_desc: "일정을 잡기 위해 곧 연락드리겠습니다."
      },
      stats: {
        modes: "6개 이상의 운송 모드",
        platform: "클라우드 플랫폼",
        platform_status: "보안 유지",
        access: "전 세계 접속"
      },
      features_title_1: "강력함과",
      features_title_2: "규정 준수의 만남",
      features_subtitle: "주요 선적 운송의 절대적인 안전을 위해 화물 안전 전문가들이 개발했습니다.",
      features: {
        calculations: {
          title: "실시간 계산",
          desc: "고도화된 알고리즘을 통해 라싱 힘을 실시간으로 계산해 드립니다."
        },
        pdf: {
          title: "전문적인 PDF 보고서",
          desc: "회사의 브랜드와 상세한 분석 내용을 포함한 업계 표준 보고서를 생성하세요."
        },
        compliance: {
          title: "CTU 기반 평가",
          desc: "IMO / ILO / UNECE CTU Code를 참고한 간소화된 의사결정 지원입니다."
        },
        multimodal: {
          title: "다중 모드 운송",
          desc: "도로, 철도, 해상 운송 지원. 컨테이너, 플랫랙, 선박 등 모든 타입 대응."
        },
        global: {
          title: "글로벌 표준",
          desc: "CTU Code 2014 및 관련 Informative Material을 참고한 추적 가능한 평가입니다."
        },
        secure: {
          title: "안전 및 비밀 유지",
          desc: "고객님의 계산 데이터는 엔터프라이즈급 보안 수준으로 기밀 관리됩니다."
        }
      },
      transport: {
        title_1: "통합 운송",
        title_2: "안전 관리",
        subtitle: "전체 물류망에 걸친 끊김 없는 연산 처리.",
        modes: {
          road: { name: "도로 운송", desc: "트럭 트레일러 및 로우베드" },
          rail: { name: "철도 운송", desc: "철도 화차, 컨테이너, 플랫랙 및 트럭 트레일러" },
          sea: { name: "해상 운송", desc: "컨테이너, 트럭 트레일러, 플랫랙, 로우베드, 롤 트레일러, 선박" },
          combined: { name: "복합 운송", desc: "도로-해상, 도로-철도, 도로-해상-철도" }
        }
      },
      how_it_works: {
        badge: "작동 방식",
        title_1: "간단한 3단계로 진행하는",
        title_2: "전문가용 분석",
        subtitle: "몇 분 만에 복잡한 라싱 힘 계산을 완료할 수 있습니다.",
        step1: {
          title: "화물 세부 정보 입력",
          desc: "화물의 규격, 중량, 무게 중심(CoG) 및 컨테이너 유형을 시스템에 입력합니다."
        },
        step2: {
          title: "자동 연산 처리",
          desc: "시스템이 IMO 표준에 부합하는 최적의 라싱 구성을 자동으로 계산합니다",
          desc_highlight: " (또한 수동으로 설정을 조정할 수 있습니다)."
        },
        step3: {
          title: "보고서 출력",
          desc: "3D 시각화 결과 및 정밀한 계산 내용이 포함된 전문 PDF 보고서를 출력합니다."
        }
      },
      benefits: {
        title: "오류 없는 선적 운송을 위한 엔지니어링",
        subtitle: "임무 중심의 확실한 화물 고정을 위하여 전 세계의 화물 전문가, 검정사 및 터미널 운영사의 신뢰를 받고 있습니다.",
        list: [
          "수동 계산 시간의 단축",
          "최적화를 통한 고정 자재 비용 절감",
          "국제 운송 규정 완벽 준수",
          "고객 제공용 전문 서류 생성",
          "다국어 지원 (EN, TR, ES, DE, ZH, JA, KO)",
          "회사 로고가 박힌 전용 PDF 보고서"
        ],
        cta_card_title: "지금 즉시 라싱 계산 계획을 표준화하세요",
        cta_card_desc: "귀사의 팀을 위해 엔터프라이즈 수준의 계산기를 가동하여 규정 준수 100%를 달성하세요.",
        cta_card_trial: "체험 기간 동안에는 신용카드가 요구되지 않습니다."
      },
      demo_request: {
        title: "데모 요청",
        subtitle: "Lashing Calculator가 화물 안전 작업을 어떻게 혁신할 수 있는지 확인하세요.",
        cta_title: "시작할 준비가 되셨나요?",
        cta_subtitle: "화물 전문가와 함께 맞춤형 데모를 예약하고, 라싱 작업을 세계 표준으로 끌어올리는 방법을 알아보세요.",
        form: {
          full_name: "성명",
          work_email: "회사 이메일",
          company: "회사명",
          job_title: "직위 / 직함",
          phone: "전화번호",
          fleet_size: "선단 규모 / 월별 선적량",
          industry: "산업 분야",
          message: "메시지 (선택 사항)",
          submit: "데모 요청 제출",
          success_title: "요청이 접수되었습니다",
          success_message: "데모 요청이 성공적으로 접수되었습니다. 전문 팀이 곧 연락드리겠습니다."
        }
      },
      price_inquiry: {
        title: "견적 요청",
        subtitle: "맞춤형 가격 및 기업 솔루션에 대해 문의하세요.",
        form: {
          full_name: "성명",
          work_email: "회사 이메일",
          company: "회사명",
          phone: "전화번호",
          message: "메시지 (선택 사항)",
          submit: "문의하기",
          success_title: "요청이 접수되었습니다",
          success_message: "견적 요청이 성공적으로 접수되었습니다. 영업팀이 맞춤 견적을 가지고 곧 연락드리겠습니다."
        }
      },
      pricing: {
        title: "가격 책정 요금제",
        subtitle: "모든 규모의 팀을 위해 투명하고 심플한 요금제를 제안합니다."
      },
      pricing_simple: "투명하고 ",
      pricing_transparent: "합리적인",
      pricing_title: " 요금제 안내",
      compare_features: "모든 기능 비교하기",
      footer: {
        desc: "지능형 라싱 계산 에코시스템을 사용하는 글로벌 해운 물류망에 합류하세요.",
        columns: {
          service: "서비스",
          company: "회사 정보",
          support: "고객 지원"
        },
        links: {
          privacy: "개인정보 처리방침",
          compliance: "서비스 이용약관",
          cookies: "쿠키 수집방침",
          security: "보안 안내",
          help: "도움말 센터",
          enterprise: "엔터프라이즈",
          contact: "전문가 문의",
          force_analysis: "하중 분석기",
          pdf_generator: "PDF 생성기",
          optimizer: "옵티마이저",
          api_docs: "API 연동 문서",
          cookie_settings: "쿠키 설정 변경"
        }
      }
    },

    labels: {
      three_d_view: "3D 보기",
      no_results_data: "결과 데이터 없음",
      pass: "합격",
      fail: "불합격",
      reports: "보고서",
      user_responsible_for: "귀하의 단독 책임 사항:",
      page_title: "라싱 계산기",
      weight: "중량 (MT)",
      transport_mode: "운송 모드",
      transport_unit: "운송 단위",
      friction_surface: "마찰 면",
      select: "선택",
      select_transport_mode_first: "먼저 운송 모드를 선택해 주세요.",
      cargo_dimensions: "화물 크기",
      cargo_specs: "화물 제원",
      width: "Cargo 너비 (m)",
      length: "Cargo 길이 (m)",
      height: "Cargo 높이 (m)",
      cog_width: "CoG 너비 (m)",
      cog_length: "CoG 길이 (m)",
      cog_height: "CoG 높이 (m)",
      cog_width_help: "CoG는 기본적으로 너비의 절반입니다. 다를 경우 변경하세요.",
      cog_length_help: "CoG는 기본적으로 길이의 절반입니다. 다를 경우 변경하세요.",
      cog_height_help: "CoG는 기본적으로 높이의 절반입니다. 다를 경우 변경하세요.",
      wooden_dunnage_dimensions: "목재 더니지 크기 및 수량",
      wooden_dunnage_width: "너비 (cm):",
      wooden_dunnage_length: "높이 (cm):",
      stopper_qty: "메탈 Stoper 수량",
      force_analysis: "힘 분석 결과 (kN)",
      generate_pdf: "PDF 생성",
      download_pdf: "PDF 다운로드",
      wooden_dunnage: "목재\n더니지",
      stopper: "스토퍼",
      loop_lashing: "루프 라싱",
      max_weight: "최대 중량",
      direct_lashing: "다이렉트\n라싱",
      loop_rope_select: "루프 로프 선택",
      loop_lashing_quantity_per_side: "수량 (측면당)",
      direct_lashing_quantity: "수량",
      cog_above: "무게 중심 위",
      cog_below: "무게 중심 아래",
      forward: "전방",
      backward: "후방",
      right: "우측",
      left: "좌측",
      lashing_diagram: "라싱 다이어그램",
      language: "언어",
      calculate: "계산하기",
      configuration_panel: "설정 패널",
      operation_mode: "작업 모드",
      manual_mode: "수동 계산기",
      lashing_calculator: "라싱 계산기",
      direct_lashing_selection: "다이렉트 라싱 선택",
      reset: "초기화",
      print: "인쇄",
      configuration: "설정",
      use_stopper: "스토퍼 사용",
      logout: "로그아웃",
      results_will_appear_here: "결과가 여기에 표시됩니다.",
      continue_with_overweight: "과중량으로 계속 진행",
      optimizer_mode: "옵티마이저",
      calculator_mode: "수동 계산기",
      project_description: "프로젝트 설명 (선택 사항)",
      enter_project_description_hint: "PDF 보고서에 표시될 프로젝트 설명을 입력하십시오.",
      show_3d_view: "3D 무게 중심 보기",
      edit_in_manual_mode: "수동 계산기에서 편집",
      breakbulk_settings: "벌크 화물 설정",
      flatrack_type: "플랫랙 유형",
      orientation: "화물 방향",
      longitudinal: "길이 방향",
      transverse: "너비 방향",
      flatrack_count: "제안된 플랫랙 수",
      flatrack_gap: "플랫랙 간격",
    },

    common: {
      info: "정보",
      yes: "예",
      no: "아니오",
      ok: "확인",
      cancel: "취소",
      confirm: "확인",
      close: "닫기",
      save: "저장",
      download: "다운로드",
      delete: "삭제",
      edit: "편집",
      view: "보기",
      loading: "로딩 중...",
      processing: "처리 중...",
      please_wait: "잠시만 기다려 주십시오...",
      save_changes: "변경 사항 저장",
      saving: "저장 중..."
    },

    transport_modes: {
      road: "도로",
      rail: "철도",
      sea_a: "해상 A (파고 < 8m)",
      sea_b: "해상 B (8m < 파고 < 12m)",
      sea_c: "해상 C (파고 > 12m)",
      road_sea_a: "도로 + 해상 A",
      road_sea_b: "도로 + 해상 B",
      road_sea_c: "도로 + 해상 C",
      road_rail: "도로 + 철도",
      road_rail_sea_a: "도로 + 철도 + 해상 A",
      road_rail_sea_b: "도로 + 철도 + 해상 B",
      road_rail_sea_c: "도로 + 철도 + 해상 C"
    },

    transport_unit_groups: {
      containers: "컨테이너",
      open_tops: "오픈탑 컨테이너",
      flatracks: "플랫랙 컨테이너",
      trailers: "트레일러",
      special: "기타 / 특수 화물",
    },
    transport_units: {
      container_20: "20피트 컨테이너",
      container_40: "40피트 컨테이너",
      container_40_hc: "40피트 하이큐브 컨테이너",
      container_45_hc: "45피트 하이큐브 컨테이너",
      open_top_20: "20피트 오픈탑",
      open_top_20_hc: "20피트 오픈탑 HC",
      open_top_40: "40피트 오픈탑",
      open_top_40_hc: "40피트 오픈탑 HC",
      flatrack_20: "20피트 플랫랙",
      flatrack_40: "40피트 플랫랙",
      lowbed: "로우베드 트레일러",
      mafi: "롤 트레일러 (마피)",
      vessel: "선박",
      breakbulk: "벌크 화물 (Break Bulk)",
      truck_trailer_standard: "일반 트럭 트레일러",
      truck_trailer_mega: "메가 / 점보 트랙 트레일러",
      open_top_container: "오픈탑 컨테이너"
    },

    materials: {
      asm: "미끄럼 방지 매트 (정적 마찰 0.8µ)",
      asm_06: "미끄럼 방지 매트 (정적 마찰 0.6µ)",
      metal_metal: "금속-금속",
      metal_wood: "금속-목재",
      wood_wood: "목재-목재"
    },

    directions: {
      forward: "전방 미끄러짐 방지",
      backward: "후방 미끄러짐 방지",
      right: "우측 미끄러짐 방지",
      left: "좌측 미끄러짐 방지"
    },

    units: {
      mt: "MT",
      kn: "kN",
      dan: "daN",
      m: "m",
      cm: "cm",
      deg: "°"
    },

        errors: {
      zero_input: "필수 입력 항목을 작성해 주십시오.",
      no_loop_rope: "호환되는 루프 라싱 웨빙을 찾을 수 없습니다.",
      no_direct_rope: "호환되는 다이렉트 라싱 웨빙을 찾을 수 없습니다.",
      calculation_error: "계산 오류: {error}",
      unexpected_error: "예상치 못한 오류: {error}",
      diagram_error: "다이어그램을 생성할 수 없습니다: {error}",
      pdf_error: "PDF 오류: {error}",
      loop_config_error: "루프 구성 오류: {error}",
      error_halat: "다이렉트 라싱 웨빙이 선택되지 않았습니다.",
      invalid_transport_unit: "잘못된 운송 단위: {unit}. 유효한 단위를 선택해 주십시오.",
      network_error: "네트워크 오류. 인터넷 연결을 확인해 주십시오.",
      file_not_found: "파일을 찾을 수 없습니다.",
      invalid_cargo_dimensions: "경고: 선택한 운송 단위가 화물에 적합하지 않습니다.",
      select_transport_mode: "운송 모드를 선택해 주십시오.",
      select_transport_unit: "운송 단위를 선택해 주십시오.",
      select_friction_surface: "마찰 표면을 선택해 주십시오.",
      invalid_weight: "유효한 중량을 입력해 주십시오.",
      invalid_dimensions: "유효한 화물 치수를 입력해 주십시오.",
      invalid_cog: "유효한 무게 중심 좌표를 입력해 주십시오.",
      invalid_wooden_dunnage: "유효한 목재 더니지 치수를 입력해 주십시오.",
      invalid_stopper: "유효한 스토퍼 수량을 입력해 주십시오.",
      select_at_least_one_rope: "라싱을 선택해 주십시오.",
      select_rope_type: "로프 유형을 선택해 주십시오.",
      weight_limit: "입력한 무게가 운송 단위의 최대 무게 용량을 초과합니다.",
      lashing_angle: "라싱 각도는 30°에서 60° 사이여야 합니다.",
      dimension_exceeded: "경고: 화물 {dims}이(가) 선택한 운송 단위의 한계를 초과했습니다.",
      dim_width: "너비",
      dim_length: "길이",
      dim_height: "높이",
      cargo_width_greater_than_length: "화물 너비는 길이보다 클 수 없습니다. 값이 자동으로 길이에 맞게 조정되었습니다.",
      and_conjunction: " 및 ",
      breakbulk_dim_overflow_width: "⚠️ 화물 너비({cargo_width}m)가 선택한 플랫랙 길이({flatrack_length}m) 이상입니다! 계산을 진행할 수 없습니다.",
      breakbulk_dim_overflow_length: "⚠️ 화물 길이({cargo_length}m)가 선택한 플랫랙 길이({flatrack_length}m) 이상입니다! 계산을 진행할 수 없습니다."
    },

    validation: {
      required_field: "이 필드는 필수 입력 항목입니다.",
      invalid_number: "유효한 숫자를 입력해 주십시오."
    },

    warnings: {
      weight_exceeded: "경고: {unit}의 최대 중량 한도({max_weight} MT)를 초과했습니다!",
      overweight_confirmation: "입력된 화물 중량({weight} MT)이 최대 허용 중량({max_weight} MT)을 초과했습니다. 계속 진행하려면 다시 확인하십시오."
    },

    success: {
      pdf_created: "PDF가 생성되었습니다!",
      calculation_complete: "계산 완료!"
    },

    table_columns: {
      direction: "방향",
      sliding: "미끄러짐",
      tipping: "전도",
      friction: "마찰",
      wooden_dunnage: "목재 더니지",
      stopper: "스토퍼",
      looplash: "루프 라싱",
      direct_lashing: "다이렉트 라싱",
      cordstrap_loop: "Cordstrap 루프",
      cordstrap_direct: "Cordstrap 다이렉트",
      total_securing: "총 고정력",
      net_sliding: "순 미끄러짐",
      net_tipping: "순 전도",
      net_sliding_tooltip: "음수 값은 안전하게 고정되었음을 나타냅니다.",
      net_tipping_tooltip: "음수 값은 안전하게 고정되었음을 나타냅니다.",
      status: "상태",
      safety_factor: "안전 계수"
    },

    results: {
      safe: "안전",
      unsafe: "불안전",
      marginal: "한계",
      critical: "위험",
      acceptable: "허용 가능",
      status: "상태",
      safety_factor: "안전 계수",
      summary: "요약",
      details: "상세 정보",
      notes_title: "참고 및 가정 사항",
      notes_assumed: "",
      notes_cargo_unitized: "화물은 단일 품목이거나 하나의 화물 단위로 통합 및 유닛화된 것으로 간주됩니다.",
      notes_lashing_above_cog: "“자재 사용” 표에 별도로 기재되지 않은 한, 다이렉트 라싱은 화물의 무게 중심 위에 적용되는 것으로 가정합니다.",
      notes_angle_range: "운송 단위의 고정 지점에 비스듬히 부착된 다이렉트 라싱과 작용하는 힘 방향의 벡터 성분 사이의 예각은 30°에서 60° 사이여야 합니다.",
      notes_edge_protection: "화물 및/또는 라싱 자재의 손상을 방지하기 위해 필요한 경우 적절한 모서리 보호대가 제공되어야 합니다.",
      notes_pretension_force: "웹 라싱을 인장할 때 적용되는 예비 인장력은 웹 라싱 MSL 값의 50%를 초과하지 않아야 합니다.",
      notes_strength_limitations: "화물의 고정 지점에는 강도 제한이 없는 것으로 가정합니다.",
      notes_distributed_uniformly: "화물은 운송 단위의 적재 기준에 따라 균등하게 분배되는 것으로 가정합니다.",
      flatrack_no_dunnage_warning: "참고: 플랫랙 운송 단위에서 화물의 전후방 미끄러짐을 제한하기 위해 목재 더니지 사용을 권장합니다."
    },

    tooltips: {
      weight_info: "화물의 총 중량을 메트릭 톤 단위로 입력하십시오.",
      cog_info: "COG는 무게 중심(Center of Gravity)을 나타냅니다. 기본적으로 화물의 중앙에 위치하는 것으로 가정하나, 실제로는 무게 중심이 다른 위치에 있을 수 있습니다. 특정 COG를 알고 있는 경우 여기에 올바른 위치를 입력할 수 있습니다. 정확한 COG 배치는 특히 전도 계산에 매우 중요합니다.",
      friction_info: "마찰 계수의 재질 조합을 선택하십시오.",
      transport_mode_info: "가속도 계수는 운송 모드에 따라 다릅니다.",
      lashing_info: "라싱 힘은 미끄러짐과 전도를 방지합니다."
    },

    legal: {
      tos_title: "이용 약관",
      privacy_title: "개인정보 처리방침",
      sales_agreement_title: "판매 계약",
      legal_notice_title: "법적 고지",
      cookie_policy_title: "쿠키 정책",
      last_updated: "시행일: 2026년 2월 4일",
      return_home: "홈으로 돌아가기",
      detailed_terms: [
        {
          title: "1. 계약의 성립",
          content: [
            "본 이용 약관('약관')은 귀하('사용자', '고객')와 영국 및 웨일스 법률에 따라 설립 및 운영되는 JADE CONSULTANCY AND TRADING LTD('제공업체', '당사') 간에 체결되는 법적으로 구속력 있는 계약이며, Lashing Calculator 웹 애플리케이션 플랫폼에 대한 액세스 및 이용을 규율합니다.",
            "플랫폼에 액세스하거나 플랫폼을 이용하는 행위, 또는 서비스 이용을 위한 구독 또는 판매 계약을 체결하는 행위는 본 약관에 동의하는 것으로 간주됩니다.",
            "판매 계약의 수락은 본 약관의 자동 수락을 수반합니다.",
            "문서 버전: v4.0. 시행일: 2026년 3월 1일."
          ]
        },
        {
          title: "2. 기업용 전용 서비스",
          content: [
            "본 플랫폼은 기업 간 거래(B2B) 목적으로만 엄격하게 제공됩니다.",
            "귀하는 소비자 보호 법률에 따른 소비자가 아닌, 기업 활동의 과정에서 본 플랫폼을 이용함을 확인합니다.",
            "본 플랫폼은 해상, 도로, 철도 운송 모드 및 이들의 복합 운송을 아우르는 화물 고정 및 라싱 계산을 보조하기 위해 설계되었습니다. 본 플랫폼의 이용에 적용되는 규제 프레임워크는 관련된 운송 모드와 적재, 경유 및 양하 관할 구역에 따라 다릅니다. 사용자는 모든 적용 가능한 운송 모드별 규정을 파악하고 이를 준수할 책임을 단독으로 집니다."
          ]
        },
        {
          title: "3. 허용되는 이용 정책",
          content: [
            "3.1 금지된 행위: 귀하는 다음 행위를 하지 않는 것에 동의합니다:",
            "a) 불법적인 목적이나 현지, 주, 국가 또는 국제 법률을 위반하여 플랫폼을 사용하는 행위.",
            "b) 플랫폼을 제공하는 데 사용되는 소프트웨어를 해독, 역컴파일, 분해 또는 리버스 엔지니어링하려는 행위.",
            "c) 당사의 서면 동의 없이 로봇, 스파이더, 스크래퍼 또는 기타 자동화된 수단을 사용하여 플랫폼에 액세스하는 행위.",
            "d) 바이러스, 웜, 결함, 트로이 목마 또는 기타 파괴적인 성격의 항목을 전송하는 행위.",
            "e) 시스템 무결성이나 보안을 방해 또는 훼손하거나, 플랫폼을 실행하는 서버와의 통신을 해독하려는 행위.",
            "3.2 당국 명령: 제공업체가 사용자의 플랫폼 이용과 관련하여 EU 회원국 또는 기타 권한 있는 당국으로부터 법적 구속력이 있는 명령을 받는 경우, 제공업체는 법적으로 허용되는 범위 내에서 사용자에게 통지하고 해당 명령을 준수합니다."
          ]
        },
        {
          title: "4. 구독, 요금 및 지불",
          content: [
            "4.1 구독 기간: 구독 기간은 활성화일로부터 1년입니다. 자동 갱신은 적용되지 않습니다. 갱신을 위해서는 사용자의 서면 요청과 새 구독 기간에 대한 전액 지불이 필요합니다.",
            "4.2 요금 및 통화: 모든 요금은 제공업체의 GBP 은행 계좌로 GBP(£)로 청구 및 지불됩니다. 요금은 선불로 지급됩니다.",
            "4.3 만료 알림: 플랫폼은 만료 1개월 전, 1주일 전 및 1일 전에 만료 알림을 보낼 수 있습니다. 이러한 알림은 편의를 위해서만 제공되며 갱신을 보장하지 않습니다.",
            "4.4 상계 기각: 사용자는 법률에 의해 요구되는 경우를 제외하고 어떠한 형태의 상계, 반소, 공제 또는 원천징수 없이 청구된 전액을 납부해야 합니다.",
            "4.5 연체: 사용자가 납기일까지 지불하지 않을 경우, 제공업체는 다른 구제책을 침해하지 않고 다음을 수행할 수 있습니다: (a) 1998년 상거래 채무(이자) 연체에 관한 법률에 따른 법정 연체 이자를 부과합니다. (b) 지불이 완료될 때까지 플랫폼에 대한 액세스를 일시 중단합니다."
          ]
        },
        {
          title: "5. 해지 및 일시 중단",
          content: [
            "5.1 사용자에 의한 해지: 사용자는 언제든지 취소할 수 있습니다. 플랫폼 이용 권한은 현재 구독 기간이 끝날 때까지 유지됩니다. 아래 명시된 경우를 제외하고 환불은 불가능합니다.",
            "5.2 제공업체에 의한 해지: 제공업체는 합당한 비즈니스, 법적, 기술적 또는 보안상의 이유로 서비스를 해지하거나 일시 중단할 수 있습니다. 중대한 계약 위반, 보안 사고, 법적 강제 또는 파산의 경우를 제외하고, 제공업체는 서비스 해지 전 최소 30일 전에 서면 통지를 제공합니다. 제공업체에 의해 해지가 개시되는 경우, 사용자는 미사용 기간에 대해 비례 배분된 환불을 받을 권리가 있습니다.",
            "5.3 해지 시 데이터 내보내기: 구독 만료 또는 해지 시, 사용자는 데이터가 영구적으로 삭제되기 전 14일 동안 데이터를 내보내거나 다운로드할 수 있습니다. 제공업체는 해지일 전에 서면 요청이 제출된 경우 데이터 내보내기에 필요한 합당한 지원을 제공합니다."
          ]
        },
        {
          title: "6. 서비스 불이행 및 환불",
          content: [
            "사용자의 귀책사유가 아닌 기술적인 이유로 플랫폼을 연속 5일 이상 실질적으로 이용할 수 없는 경우, 사용자는 미사용 일수에 대해 연간 구독 요금을 일할 계산한 금액을 환불받을 권리가 있습니다.",
            "임시 장애, 성능 저하, 정기 점검 또는 호환성 문제는 플랫폼 불능 상태로 간주되지 않습니다.",
            "제공업체는 정기 점검을 제외하고 월별 가동 시간을 99% 이상으로 유지하는 것을 목표로 합니다. 정기 점검은 가능한 한 최소 48시간 전에 미리 고지됩니다. 제공업체는 특정 가동 시간을 보장하지 않으며, 서면 판매 계약에 명시적으로 반영되지 않는 한 가동 시간 조항은 구속력 있는 SLA를 형성하지 않습니다."
          ]
        },
        {
          title: "7. 사용자 계정 및 라이선스",
          content: [
            "7.1 라이선스 부여: 제공업체는 내부 비즈니스 목적으로만 플랫폼에 액세스하고 이용할 수 있는 제한적이고 비독점적이며 양도 불가능한 라이선스를 부여합니다. 소유권은 이전되지 않습니다.",
            "7.2 단일 장치 정책: 판매 계약에 별도로 서면 동의하지 않는 한, 각 사용자 계정은 한 번에 하나의 장치에서만 로그인할 수 있습니다. 동시 로그인은 금지됩니다. 제공업체는 남용을 방지하기 위해 사용 패턴을 모니터링할 권리가 있습니다. 남용이 의심되는 경우 조사가 완료될 때까지 액세스를 일시 중단할 수 있습니다. 위반이 확인되면 환불 없이 해지될 수 있습니다.",
            "7.3 사용자 의무: 보고서를 생성하려면 사용자는 회사명, 회사 로고, 휴대전화 번호, 일반 전화번호, 이메일 주소를 입력해야 합니다. 결제 송장을 발행하기 위해 유효한 회사 청구 정보가 제공되어야 합니다.",
            "7.4 계정 보안: 귀하는 비밀번호와 계정 정보의 비밀을 유지할 책임이 있으며, 귀하의 계정 하에 발생하는 모든 활동에 대해 전적으로 책임을 집니다."
          ]
        },
        {
          title: "8. 중요 안전 공지 및 전문 책임 부인",
          content: [
            "중요 공지: 본 플랫폼은 해상, 도로, 철도 운송 및 이들의 복합 운송을 위한 화물 고정 계획을 수립할 때 사용하는 의사 결정 지원 시스템(Decision Support System)입니다.",
            "본 플랫폼은 전문적인 공학적, 법적 또는 안전 상담을 제공하지 않습니다.",
            "본 플랫폼은 어떠한 기선 등록국, 선급 협회, 항만 당국, 도로 교통 당국, 철도 기반 시설 관리자 또는 기타 규제 기관의 인증, 승인 또는 보증을 받지 않았습니다.",
            "모든 계산 결과는 관련 운송 모드 규정에 정의된 자격을 갖춘 화물 전문가 또는 '유자격자(Competent Person)'에 의해 검토되고 승인되어야 합니다: IMO/ILO/UNECE CTU 코드 및 IMO CSS 코드 (해상 운송); EN 12195 / VDI 2700 및 해당 국가 도로 교통 당국 지침 (도로 운송); UIC 적재 지침 및 해당 국가 철도 기반 시설 관리자 요건 (철도 운송).",
            "화물 고정에 대한 최종 책임은 전적으로 적용 가능한 법적 프레임워크에 따른 책임자에게 있습니다: 해상 운송의 경우 적용 가능한 국제 해상법에 따른 운송인, 선박 소유자, 운영자 및/또는 선장; 도로 운송의 경우 CMR 협약 및 해당 국가의 도로 운송 규정에 따른 운송인, 차량 운영자 및/또는 운전사; 철도 운송의 경우 CIM/COTIF 프레임워크 및 해당 국가의 철도 규정에 따른 운송인 및 철도 기업. 복합 운송에서는 각 구간의 운송인이 해당 구간의 책임을 집니다.",
            "제공업체는 플랫폼 결과물에 기초하여 내려진 결정에 대해 어떠한 책임도 지지 않습니다.",
            "이 플랫폼을 사용함으로써 귀하는 모든 결과에 대한 전문가 검증 의무를 포함하여 안전 공지, 기술적 리스크 및 법적 약관을 읽고 수락했음을 확인합니다."
          ]
        }
      ]
    },

    profile: {
      title: "사용자 및 회사 프로필",
      subtitle: "여기에서 사용자 및 회사 정보를 관리할 수 있습니다.",
      logo_label: "회사 로고",
      logo_note: "로고는 관리자만 업데이트할 수 있습니다.",
      company_name: "내 프로필",
      address: "상세 주소",
      phone: "전화 / 휴대폰",
      email: "이메일 (계정)",
      tax_office: "세무서",
      tax_number: "사업자 등록 번호",
      company_email_label: "회사 이메일 (PDF에 표시)",
      user_name_label: "사용자 이름 (계정)",
      terms_accepted: "이용 약관 동의 일시:",
      terms_not_accepted: "이용 약관에 동의하지 않음",
      view_terms: "이용 약관 보기",
      accept_terms: "이용 약관 동의",
      review_accept: "이용 약관 검토 및 동의",
      admin_note: "참고: 회사 세부 정보는 관리자가 관리합니다.",
      cancel: "취소",
      accept: "이용 약관 동의",
      modal_title: "이용 약관",
      modal_subtitle: "진행하려면 약관을 검토하고 동의해 주십시오.",
      modal_agree: "동의함으로써 귀하는 {tos} 및 {privacy}의 적용을 받는 데 동의하게 됩니다.",
      modal_ack: "귀하는 본 소프트웨어가 보조 도구일 뿐이며, 라싱 계획에 대한 최종 책임은 공인된 전문가에게 있음을 인정합니다.",
      checkbox_label: "이용 약관을 읽었으며 이에 동의합니다.",
      save: "변경 사항 저장",
      saving: "저장 중...",
      success_message: "프로필이 성공적으로 업데이트되었습니다.",
      error_message: "업데이트 오류: {error}",
      credits_title: "크레딧 및 사용 정보",
      calc_credits: "계산 크레딧",
      pdf_credits: "PDF 보고서 크레딧",
      monthly_increment: "월별 충전량",
      next_increment: "다음 충전일",
      remaining: "남은 크레딧",
      lashing_display_label: "라싱 표시 형식",
      lashing_display_msl: "MSL (예: MSL 3750daN)",
      lashing_display_cordstrap: "Cordstrap (예: CL200 HDB12L)",
      pdf_theme_title: "PDF 디자인 테마",
      theme_teal_title: "Modern Teal",
      theme_teal_desc: "다크 터코이즈 및 슬레이트 그레이",
      theme_navy_title: "Classic Navy",
      theme_navy_desc: "기업 네이비 및 라이트 그레이",
      theme_standart_title: "표준",
      theme_standart_desc: "기본 앱 색상"
    },

    surveys: {
      title: "현장 보고서",
      create_new: "새 보고서 생성",
      total_ops: "총 작업 수",
      pending_photos: "대기 중인 사진",
      completed_assignments: "완료된 작업",
      current_reports: "현재 보고서",
      search_placeholder: "ID 또는 프로젝트 검색...",
      no_reports_title: "보고서가 아직 없습니다",
      no_reports_desc: "현장 보고서를 준비하려면 먼저 라싱 계산을 시작해 보세요.",
      create_now: "지금 생성",
      unnamed_project: "이름 없는 프로젝트",
      created_date: "생성일",
      data_status: "데이터 상태",
      uploaded: "신청 사진 업로드됨",
      waiting: "신청 사진 대기 중",
      surveyor: "검사원",
      waiting_response: "회신 대기 중",
      under_review: "검토 중",
      report_ready: "보고서 준비 완료",
      cancelled_rejected: "취소됨 / 거부됨",
      send_link: "현장 업로드 링크 전송",
      upload_manually: "현장 사진 수동 업로드",
      assign_new: "새로운 검사원 지정",
      assign_surveyor: "검사원 지정",
      assigned: "배정됨",
      summary_pdf: "요약 PDF",
      surveyor_pdf: "검사원 PDF",
      assignment_success: "검사원 지정이 완료되었습니다!",
      old_report_error: "이전 보고서의 경우 보안 링크가 생성되지 않았습니다. 보고서를 다시 생성하십시오.",
      session_expired: "세션이 만료되었습니다. 페이지를 새로 고치거나 다시 로그인하십시오.",
      pdf_error: "PDF 생성 중 오류가 발생했습니다.",
      download_failed: "다운로드 실패: ",
      wait: "대기...",
      connecting: "연결 중...",
      access_error: "액세스 권한 오류",
      modal: {
        title: "검사원 지정",
        search_placeholder: "도시, 항구 또는 이름으로 검색...",
        list_error: "검사원 목록을 가져오지 못했습니다.",
        assign_failed: "지정에 실패했습니다.",
        loading: "로딩 중...",
        not_found: "지정 가능한 검사원이 없습니다.",
        unknown_location: "알 수 없음",
        assigning: "배정 중...",
        assign_button: "이 검사원 배정"
      },
      guest: {
        title: "현장 실사 포털",
        subtitle: "라싱 실사 이미지를 여기에 업로드할 수 있습니다.",
        invalid_link: "유효하지 않거나 만료된 링크입니다.",
        upload_success_title: "업로드 성공",
        upload_success_desc: "라싱 실사 이미지가 성공적으로 저장되었습니다. 보고서를 승인하고 PDF로 내보낼 준비가 완료되었습니다.",
        close_window: "창 닫기",
        info_title: "1. 실사자 정보 (선택 사항)",
        date: "날짜",
        company_name: "회사명",
        company_placeholder: "예: X 물류",
        implementer_name: "실사자 성명",
        phone: "전화번호",
        email: "이메일",
        email_placeholder: "name@company.com",
        photos_title: "2. 현장 적용 이미지 (최대 12장)",
        photos_desc: "아래 범주에 맞춰 현장 사진을 업로드하거나 촬영하십시오. 이미지당 업로드 제한은 약 5MB이며, 기기에서 자동으로 약 1MB로 압축됩니다."
      },
      dashboard: {
        connecting: "Panellere bağlanılıyor...",
        pending_tab: "Gelen Talepler",
        active_tab: "Aktif Görevler",
        completed_tab: "Geçmiş İşler",
        logout: "Güvenli Çıkış",
        connected: "Connected",
        account_type: "Surveyor Hesabı",
        no_records: "Kayıt bulunamadı",
        no_records_desc: "Bu sekmede görüntülenecek aktif bir denetim görevi yok.",
        customer: "Müşteri",
        task_date: "GÖREV TARİHİ",
        waiting_response: "Yanıt Bekliyor",
        under_review: "Müşteri Onayında",
        delivered: "Rapor Teslim Edildi",
        rejected_by_you: "Görev Reddedildi",
        accept_button: "Görevi Kabul Et",
        reject_button: "Reddet",
        go_to_report: "Rapor Menüsüne Git",
        download_pdf: "PDF Dosyasını İndir",
        downloading_pdf: "Rapor PDF'i indiriliyor...",
        download_success: "Rapor başarıyla indirildi.",
        download_error: "Rapor indirme hatası.",
        update_error: "Durum güncellenemedi, lütfen tekrar deneyin.",
        accept_success: "Görev başarıyla kabul edildi!",
        reject_success: "Görev reddedildi."
      },
      editor: {
        details_title: "Proje Detayları",
        project_name: "Proje Adı",
        weight: "Kargo Ağırlığı",
        photos_count: "Saha Fotoğrafları",
        success_title: "Rapor Başarıyla Oluşturuldu!",
        success_desc: "Denetim raporunuz oluşturulmuş, imzalanmış ve güvenli bulut ortamına yüklenmiştir. Aşağıdaki butona tıklayarak PDF dosyasını indirebilir veya inceleyebilirsiniz.",
        back_to_dashboard: "Panele Geri Dön",
        download_pdf: "PDF İndir",
        creator_title: "Nihai Denetim Raporu Oluşturan",
        assessment_label: "Denetçi Değerlendirmesi",
        assessment_desc: "Bu metin nihai PDF'in 'Metodoloji ve Sonuç' bölümüne eklenecektir.",
        assessment_default: "Kargoların denetimi uluslararası standartlara uygun olarak tamamlanmıştır. Herhangi bir hasar veya uygunsuzluk gözlenmemiştir.",
        auto_info_note: "Not: Sistemdeki adınız, imzanız ve şirket logonuz gibi varsayılan bilgiler otomatik olarak PDF raporuna yerleştirilecektir.",
        back_button: "Geri",
        generate_button: "PDF Raporunu Onayla ve Gönder",
        generating: "Oluşturuluyor...",
        generate_error: "Rapor oluşturulamadı",
        not_found: "Görev bulunamadı veya yetkiniz yok."
      }
    }
  }
};

// ===================================================================
// METADATA
// ===================================================================
export const LANGUAGE_METADATA = {
  version: "0.3.1",
  last_updated: "2025-11-01",
  default_language: "Türkçe",
  supported_languages: ["Türkçe", "English", "Español", "Deutsch", "中文", "日本語", "Korean"],
  fallback_language: "English",
  cjk_languages: ["中文", "日本語", "Korean"],
  pdf_font_notice: {
    "中文": "PDF生成需要中文字体支持",
    "日本語": "PDF生成には日本語フォント의 지원이 필요합니다",
    "Korean": "PDF 생성에는 한국어 글꼴 지원이 필요합니다"
  }
} as const;
// ===================================================================
// LEGACY COMPATIBILITY LAYER / HELPER CLASS
// ===================================================================
export class LanguageManager {
  private data: LanguageData;

  constructor(data: LanguageData) {
    this.data = JSON.parse(JSON.stringify(data));
  }

  get code() { return this.data.code; }
  get name() { return this.data.name; }
  get flag() { return this.data.flag; }
  get app() { return this.data.app; }
  get landing() { return this.data.landing; }
  get labels() { return this.data.labels; }
  get common() { return this.data.common; }
  get transport_modes() { return this.data.transport_modes; }
  get transport_units() { return this.data.transport_units; }
  get materials() { return this.data.materials; }
  get directions() { return this.data.directions; }
  get units() { return this.data.units; }
  get errors() { return this.data.errors; }
  get validation() { return this.data.validation; }
  get warnings() { return this.data.warnings; }
  get success() { return this.data.success; }
  get table_columns() { return this.data.table_columns; }
  get results() { return this.data.results; }
  get tooltips() { return this.data.tooltips; }
  get legal() { return this.data.legal; }
  get profile() { return this.data.profile; }
  get surveys() { return this.data.surveys; }

  // Legacy accessor for translation function
  t(key: string, params?: Record<string, any>): string {
    const keys = key.split(".");
    let value: any = this.data;

    for (const k of keys) {
      if (value === undefined || value === null) break;
      value = value[k];
    }

    if (typeof value !== "string") return key;

    let result = value;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        result = result.replace(`{${k}}`, String(v));
      });
    }
    return result;
  }
}

// ===================================================================
// EXPORTS
// ===================================================================
export const languages: Record<string, LanguageManager> = Object.entries(
  LANGUAGES
).reduce((acc, [key, data]) => {
  acc[key] = new LanguageManager(data);
  return acc;
}, {} as Record<string, LanguageManager>);

// Compatibility: Default export the managers object
export default languages;
